/**
ModeratorPanel.jsx  ·  Studio — Dual Theme (Dark/Light)
Route: /studio  (no ProtectedRoute — panel manages session itself)
import ModeratorPanel from "./pages/a/ModeratorPanel";
<Route path="/studio" element={} />
*/
import React, { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext, Fragment } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config";
import logoImg from "../../assets/1111.png";

// ─── DARK THEME PALETTE ─── near-black + cherry-violet accents ──────────────
const D = {
  bg:            "#0d0c0f",
  bgDeep:        "#080709",
  card:          "#131117",
  cardHover:     "#1a1820",
  cardMid:       "#100e14",
  border:        "rgba(180,120,200,0.09)",
  borderMid:     "rgba(180,120,200,0.18)",
  glow:          "rgba(180,100,180,0.03)",
  primary:       "#b06090",
  primaryDim:    "rgba(176,96,144,0.14)",
  primaryMid:    "rgba(176,96,144,0.24)",
  accent:        "#d48ab0",
  accentDim:     "rgba(212,138,176,0.09)",
  green:         "#3ecf8e",
  greenDim:      "rgba(62,207,142,0.12)",
  orange:        "#f0a429",
  orangeDim:     "rgba(240,164,41,0.12)",
  red:           "#e8514a",
  redDim:        "rgba(232,81,74,0.12)",
  blue:          "#9b8ef5",
  blueDim:       "rgba(155,142,245,0.12)",
  cyan:          "#c084e8",
  cyanDim:       "rgba(192,132,232,0.12)",
  fg:            "#d8cee0",
  fgMid:         "#8a7a96",
  fgSub:         "#52445e",
  fgFaint:       "#2e2836",
  gradient:      "linear-gradient(135deg, #5c2050 0%, #8a3570 100%)",
};

// ─── LIGHT THEME PALETTE ─── base: #FFF3E6 ─────────────────────────────────
const T = {
  bg:            "#FFF3E6",
  bgDeep:        "#FFE8D0",
  card:          "#ffffff",
  cardHover:     "#FFF8F2",
  cardMid:       "#FFF8F2",
  border:        "#F0DCCA",
  borderMid:     "#E0C4A8",
  glow:          "rgba(56,25,49,0.02)",
  primary:       "#381931",
  primaryDim:    "rgba(56,25,49,0.09)",
  primaryMid:    "rgba(56,25,49,0.17)",
  accent:        "#6b2e58",
  accentDim:     "rgba(107,46,88,0.08)",
  accentHover:   "#4d2040",
  accentLight:   "#8a3e70",
  green:         "#2a7a50",
  greenDim:      "rgba(42,122,80,0.10)",
  orange:        "#b8620a",
  orangeDim:     "rgba(184,98,10,0.09)",
  red:           "#c0392b",
  redDim:        "rgba(192,57,43,0.09)",
  blue:          "#381931",
  blueDim:       "rgba(56,25,49,0.09)",
  cyan:          "#7a3060",
  cyanDim:       "rgba(122,48,96,0.09)",
  fg:            "#1a0c14",
  fgMid:         "#4a2838",
  fgSub:         "#8a6070",
  fgFaint:       "#c0a0b0",
  gradient:      "linear-gradient(135deg, #381931 0%, #7d3468 100%)",
};

// ─── THEME CONTEXT ──────────────────────────────────────────────────────────
const ThemeContext = createContext({ theme: "dark", colors: D, toggleTheme: () => {} });
const useTheme = () => useContext(ThemeContext);

// ─── GLOBAL CSS GENERATOR ───────────────────────────────────────────────────
function generateStudioCSS(colors) {
  return `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
.st*{box-sizing:border-box;margin:0;padding:0}
.st{font-family:'Inter',sans-serif;background:${colors.bg};color:${colors.fg};min-height:100vh;transition:background .3s,color .3s}
.st-mono{font-family:'JetBrains Mono',monospace}
.st ::-webkit-scrollbar{width:4px;height:4px}
.st ::-webkit-scrollbar-track{background:${colors.bgDeep}}
.st ::-webkit-scrollbar-thumb{background:${colors.fgFaint};border-radius:4px}
.st ::-webkit-scrollbar-thumb:hover{background:${colors.fgSub}}
@keyframes stFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes stFadeIn{from{opacity:0}to{opacity:1}}
@keyframes stSpin{to{transform:rotate(360deg)}}
@keyframes stPulse{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes stSlideIn{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}
@keyframes stFlash{0%,100%{background:${colors.card}}50%{background:${colors.primaryMid}}}
@keyframes skeletonShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
.st-fade{animation:stFade .25s ease both}
.st-spin{animation:stSpin .65s linear infinite}
.st-pulse{animation:stPulse 2s ease infinite}
.st-slide{animation:stSlideIn .3s cubic-bezier(.32,0,.67,0)}
.st-card{background:${colors.card};border:1px solid ${colors.border};border-radius:12px;transition:border-color .18s,box-shadow .18s,background .3s}
.st-card:hover{border-color:${colors.borderMid};box-shadow:0 4px 24px rgba(0,0,0,.1)}
.st-input{background:${colors.cardMid};border:1px solid ${colors.border};border-radius:8px;padding:9px 13px;font-family:'Inter',sans-serif;font-size:13px;color:${colors.fg};outline:none;transition:border-color .15s,box-shadow .15s,background .3s;width:100%}
.st-input:focus{border-color:${colors.primary};box-shadow:0 0 0 3px ${colors.primaryDim}}
.st-input::placeholder{color:${colors.fgFaint}}
.st-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;border:none;border-radius:8px;font-family:'Inter',sans-serif;font-weight:600;font-size:13px;cursor:pointer;transition:all .15s;letter-spacing:.01em}
.st-btn:disabled{opacity:.4;cursor:not-allowed}
.st-btn-primary{background:${colors.gradient};color:#fff;padding:10px 20px}
.st-btn-primary:hover:not(:disabled){filter:brightness(1.08)}
.st-btn-ghost{background:transparent;color:${colors.fgMid};border:1px solid ${colors.border};padding:8px 16px}
.st-btn-ghost:hover:not(:disabled){border-color:${colors.borderMid};color:${colors.fg};background:${colors.glow}}
.st-btn-danger{background:${colors.redDim};color:${colors.red};border:1px solid rgba(192,57,43,.25);padding:8px 14px}
.st-btn-danger:hover:not(:disabled){background:rgba(192,57,43,.15)}
.st-btn-success{background:${colors.greenDim};color:${colors.green};border:1px solid rgba(26,107,58,.25);padding:8px 14px}
.st-btn-sm{padding:5px 10px !important;font-size:12px !important;border-radius:6px !important}
.st-table{width:100%;border-collapse:collapse}
.st-table th{text-align:left;font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:${colors.fgSub};padding:10px 16px;border-bottom:1px solid ${colors.border};background:${colors.bgDeep};position:sticky;top:0;z-index:1}
.st-table td{padding:11px 16px;border-bottom:1px solid ${colors.border};font-size:13px;vertical-align:middle}
.st-table tr:last-child td{border-bottom:none}
.st-table tbody tr{cursor:pointer;transition:background .12s}
.st-table tbody tr:hover td{background:${colors.cardHover}}
.st-drawer-overlay{position:fixed;inset:0;top:0;left:0;right:0;bottom:0;width:100vw;height:100vh;z-index:200;display:flex;align-items:flex-start;justify-content:center;padding:24px 16px 16px}
.st-drawer-bd{position:fixed;inset:0;top:0;left:0;right:0;bottom:0;width:100vw;height:100vh;background:rgba(5,5,14,.6);backdrop-filter:blur(8px)}
.st-drawer{position:relative;width:min(1320px,100%);max-height:calc(100vh - 48px);background:${colors.card};border:1px solid ${colors.borderMid};border-radius:16px;overflow-y:auto;overflow-x:hidden;padding:32px 32px;box-shadow:0 24px 80px rgba(0,0,0,.4);z-index:201;animation:stFade .25s ease both;word-break:break-word;overflow-wrap:anywhere;color:${colors.fg};font-family:'Inter',sans-serif}
.st-chat-bubble{max-width:76%;padding:9px 13px;border-radius:14px;font-size:13px;line-height:1.55;word-break:break-word}
.st-chat-mine{background:${colors.primaryMid};color:${colors.fg};border-bottom-right-radius:4px;margin-left:auto}
.st-chat-theirs{background:${colors.cardHover};color:${colors.fg};border-bottom-left-radius:4px}
.st-chat-system{background:${colors.greenDim};color:${colors.green};margin:0 auto;font-size:12px;text-align:center;border-radius:8px;padding:6px 14px}
.st-modal{position:fixed;inset:0;z-index:300;display:flex;align-items:center;justify-content:center;padding:20px}
.st-modal-bg{position:absolute;inset:0;background:rgba(5,5,14,.6);backdrop-filter:blur(8px)}
.st-modal-box{position:relative;background:${colors.card};border:1px solid ${colors.borderMid};border-radius:16px;padding:28px;width:100%;max-width:900px;max-height:calc(100vh - 48px);overflow-y:auto;box-shadow:0 24px 80px rgba(0,0,0,.4);animation:stFade .22s ease both;word-break:break-word;overflow-wrap:anywhere;color:${colors.fg};font-family:'Inter',sans-serif}
.st-tag{display:inline-flex;align-items:center;padding:2px 8px;border-radius:99px;font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase}
.st-score{display:flex;align-items:center;gap:8px;min-width:80px}
.st-score-bar{flex:1;height:3px;background:${colors.border};border-radius:99px;overflow:hidden}
.st-score-fill{height:100%;border-radius:99px;transition:width .6s}
.st-toast-wrap{position:fixed;bottom:20px;right:20px;z-index:500;display:flex;flex-direction:column;gap:8px}
.st-toast{background:${colors.card};border:1px solid ${colors.border};color:${colors.fg};padding:12px 18px;border-radius:10px;font-size:13px;box-shadow:0 4px 24px rgba(0,0,0,.3);animation:stFade .2s ease both;max-width:340px}
.st-toast-ok{border-color:${colors.green};background:${colors.greenDim}}
.st-toast-err{border-color:${colors.red};background:${colors.redDim}}
.st-toast-warn{border-color:${colors.orange};background:${colors.orangeDim}}
.st-section-title{font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:${colors.primary};margin-bottom:10px}
.st-meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:20px}
.st-meta-cell{background:${colors.cardMid};border:1px solid ${colors.border};border-radius:8px;padding:10px 12px}
.st-meta-key{font-size:9px;color:${colors.fgSub};font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:3px}
.st-meta-val{font-size:12px;color:${colors.fgMid};font-family:'JetBrains Mono',monospace}
.st-pill-tabs{display:flex;gap:1px;background:${colors.bgDeep};border-radius:10px;padding:3px;border:1px solid ${colors.border}}
.st-pill-tab{padding:7px 14px;border-radius:8px;border:none;background:transparent;color:${colors.fgSub};font-family:'Inter',sans-serif;font-weight:500;font-size:12.5px;cursor:pointer;transition:all .15s;white-space:nowrap}
.st-pill-tab.active{background:${colors.primaryDim};color:${colors.accent};font-weight:600}
.st-stat-num{font-size:28px;font-weight:700;color:${colors.fg};line-height:1}
.st-stat-label{font-size:10px;color:${colors.fgSub};font-weight:600;letter-spacing:.07em;text-transform:uppercase;margin-top:4px}
.st-float-card{border-radius:20px;transition:box-shadow .2s,background .3s}
.st-mobile-nav{display:none}
.st-table-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
.st-kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}
.st-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
@media(max-width:767px){
.st{padding-bottom:76px}
.st-mobile-nav{display:flex;position:fixed;bottom:0;left:0;right:0;z-index:150;height:68px;background:${colors.card};border-top:1px solid ${colors.border};padding:0 2px;align-items:stretch;gap:0;box-shadow:0 -4px 20px rgba(0,0,0,.15)}
.st-mobile-nav-btn{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;background:none;border:none;cursor:pointer;padding:5px 1px;font-family:inherit;min-width:0;position:relative;border-radius:10px;margin:4px 1px;transition:background .15s;overflow:hidden}
.st-mobile-nav-btn.active{background:${colors.primaryDim}}
.st-mobile-nav-icon{display:flex;align-items:center;justify-content:center;width:22px;height:22px;flex-shrink:0}
.st-mobile-nav-label{font-size:9px;font-weight:600;letter-spacing:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:100%;text-align:center;padding:0 3px;line-height:1.2;color:${colors.fgSub}}
.st-mobile-nav-btn.active .st-mobile-nav-label{color:${colors.accent}}
.st-mobile-nav-dot{position:absolute;top:4px;right:8px;width:7px;height:7px;border-radius:50%;background:${colors.orange}}
.st-card{border-radius:14px!important}
.st-input{font-size:16px!important;padding:11px 13px}
.st-btn{min-height:44px}
.st-btn-sm{min-height:36px!important}
.st-drawer-overlay{padding:0!important;align-items:flex-start!important;padding-top:60px!important}
.st-drawer{width:100%!important;max-width:100%!important;max-height:calc(100vh - 60px - 62px)!important;border-radius:20px 20px 0 0!important;padding:20px 16px 32px!important}
.st-modal{padding:12px!important;align-items:center!important;justify-content:center!important;padding-top:12px!important}
.st-modal-box{padding:20px 16px!important;max-width:calc(100vw - 24px)!important;width:100%!important;border-radius:16px!important;max-height:calc(100vh - 80px)!important;overflow-y:auto!important}
.st-meta-grid{grid-template-columns:1fr!important}
.st-pill-tabs{overflow-x:auto;flex-wrap:nowrap!important;padding-bottom:4px}
.st-pill-tab{white-space:nowrap;padding:7px 11px!important;font-size:11.5px!important}
.st-toast-wrap{bottom:74px!important;left:12px!important;right:12px!important;max-width:none!important}
.st-toast{max-width:none!important}
.st-table th{padding:8px 12px!important;font-size:9px!important}
.st-table td{padding:9px 12px!important;font-size:12px!important}
.st-kpi-grid{grid-template-columns:1fr 1fr!important}
.st-grid-2{grid-template-columns:1fr!important}
.st-stat-num{font-size:22px!important}
}`;
}

function injectCSS(colors) {
  const existing = document.getElementById("studio-theme-css");
  if (existing) {
    existing.textContent = generateStudioCSS(colors);
  } else {
    const s = document.createElement("style");
    s.id = "studio-theme-css";
    s.textContent = generateStudioCSS(colors);
    document.head.appendChild(s);
  }
}

// ─── MOBILE HOOK ──────────────────────────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = e => setMobile(e.matches);
    mq.addEventListener("change", handler);
    setMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
}
async function api(path, { method = "GET", body, token } = {}) {
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  const r = await fetch(`${API_URL}/api/mod${path}`, {
    method, headers: h, body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
const ToastCtx = React.createContext({ show: () => {} });
function ToastProvider({ children }) {
  const [list, setList] = useState([]);
  const show = useCallback((msg, type = "ok") => {
    const id = Date.now() + Math.random();
    setList(p => [...p, { id, msg, type }]);
    setTimeout(() => setList(p => p.filter(t => t.id !== id)), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={{ show }}>
      {children}
      <div className="st-toast-wrap">
        {list.map(t => (
          <div key={t.id} className={`st-toast st-toast-${t.type}`}>
            {t.type === "ok" ? "✓ " : t.type === "err" ? "✕ " : "⚠ "}{t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
const useToast = () => React.useContext(ToastCtx).show;

// ─── ATOMS ────────────────────────────────────────────────────────────────────
function Spinner({ size = 18 }) {
  const { colors } = useTheme();
  return <div className="st-spin" style={{
    width: size, height: size, flexShrink: 0,
    border: `2px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: "50%",
  }} />;
}

function Btn({ children, variant = "primary", loading, sm, style, className = "", ...p }) {
  return (
    <button className={`st-btn st-btn-${variant}${sm ? " st-btn-sm" : ""} ${className}`}
      disabled={loading || p.disabled} style={style} {...p}>
      {loading ? <Spinner size={14} /> : children}
    </button>
  );
}

function Tag({ color, bg, children, style }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  return <span className="st-tag" style={{ color, background: bg || c + "18", ...style }}>{children}</span>;
}

function Avatar({ user, size = 36 }) {
  const { colors } = useTheme();
  const [err, setErr] = useState(false);
  const initials = (user?.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const hue = ((user?.id || 0) * 53) % 360;
  if (!err && user?.avatar_url) {
    return <img src={user.avatar_url} alt="" onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover",
        border: `2px solid ${colors.border}`, flexShrink: 0 }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `linear-gradient(135deg, hsl(${hue},60%,35%), hsl(${hue+40},55%,25%))`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.36, fontWeight: 700, color: `hsl(${hue},70%,80%)`,
      border: `2px solid ${colors.border}` }}>
      {initials}
    </div>
  );
}

function ScoreBar({ score }) {
  const { colors } = useTheme();
  if (!score) return <span style={{ color: colors.fgFaint, fontSize: 11 }}>—</span>;
  const pct = Math.min(100, score);
  const color = pct >= 75 ? colors.green : pct >= 50 ? colors.primary : colors.orange;
  return (
    <div className="st-score" style={{ minWidth: 60 }}>
      <div className="st-score-bar"><div className="st-score-fill" style={{ width: `${pct}%`, background: color }} /></div>
      <span className="st-mono" style={{ fontSize: 11, color, fontWeight: 600, minWidth: 24 }}>{Math.round(pct)}</span>
    </div>
  );
}

function SectionTitle({ children }) {
  const { colors } = useTheme();
  return <div className="st-section-title" style={{ color: colors.primary }}>{children}</div>;
}

function MetaCell({ label, value, copy = false }) {
  const displayVal = value ?? "—";
  const copyable = copy && value && value !== "—";
  return (
    <div className="st-meta-cell">
      <div className="st-meta-key">{label}</div>
      <div className="st-meta-val" style={{ display: "flex", alignItems: "center", gap: 2 }}>
        {displayVal}
        {copyable && <CopyBtn text={String(value)} />}
      </div>
    </div>
  );
}

function Pagination({ page, pages, onChange }) {
  const { colors } = useTheme();
  if (pages <= 1) return null;
  const nums = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(pages, page + 2); i++) nums.push(i);
  return (
    <div style={{ display: "flex", gap: 4, justifyContent: "center", marginTop: 16, alignItems: "center" }}>
      <Btn variant="ghost" sm disabled={page === 1} onClick={() => onChange(1)}>«</Btn>
      <Btn variant="ghost" sm disabled={page === 1} onClick={() => onChange(page - 1)}>‹</Btn>
      {nums.map(n => (
        <button key={n} onClick={() => onChange(n)} style={{
          width: 30, height: 30, border: `1px solid ${n === page ? colors.primary : colors.border}`,
          borderRadius: 6, background: n === page ? colors.primaryDim : "transparent",
          color: n === page ? colors.accent : colors.fgMid, cursor: "pointer",
          fontFamily: "inherit", fontSize: 12, fontWeight: n === page ? 700 : 400,
        }}>{n}</button>
      ))}
      <Btn variant="ghost" sm disabled={page === pages} onClick={() => onChange(page + 1)}>›</Btn>
      <Btn variant="ghost" sm disabled={page === pages} onClick={() => onChange(pages)}>»</Btn>
    </div>
  );
}

function ConfirmModal({ title, text, onConfirm, onClose, danger }) {
  const { colors } = useTheme();
  return (
    <WindowModal
      windowId={_safeWindowId("confirm", title)}
      title={title || "Confirm"}
      onClose={onClose}
      width={440}
      height={260}
      minHeight={220}
    >
      <div style={{ fontSize: 13, color: colors.fgMid, lineHeight: 1.65, marginBottom: 22 }}>{text}</div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant={danger ? "danger" : "primary"} onClick={() => { onConfirm(); onClose(); }}>Confirm</Btn>
      </div>
    </WindowModal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  WINDOW SYSTEM  — desktop multi-window UX
//  Drawers и FormModal-окна на ПК становятся полноценными desktop-окнами:
//  можно перетаскивать за заголовок, ресайзить за края, сворачивать в трей
//  снизу, разворачивать на весь экран. Позиция и размер каждого окна
//  запоминаются в localStorage по `windowId`.
//  На мобилках всё деградирует в обычный fullscreen-overlay.
// ════════════════════════════════════════════════════════════════════════════

const WIN_TITLE_H = 34;
const WIN_MIN_W   = 360;
const WIN_MIN_H   = 240;
const WIN_MARGIN  = 8;

function _winStorageKey(id) { return `mod_win_${id}`; }
function _safeWindowId(prefix, value) {
  const slug = String(value ?? "window")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 70) || "window";
  return `${prefix}_${slug}`;
}

function _loadWinRect(id) {
  try {
    const r = JSON.parse(localStorage.getItem(_winStorageKey(id)) || "null");
    if (r && typeof r.x === "number" && typeof r.y === "number" &&
        typeof r.w === "number" && typeof r.h === "number") return r;
  } catch (_) {}
  return null;
}

function _saveWinRect(id, rect) {
  try { localStorage.setItem(_winStorageKey(id), JSON.stringify(rect)); } catch (_) {}
}

const WindowMgrContext = createContext(null);

function WindowMgrProvider({ children }) {
  const [wins, setWins] = useState([]);   // [{id, title, icon, isMin, z}]
  const topZRef = useRef(1000);

  const register = useCallback((info) => {
    topZRef.current += 1;
    const z = topZRef.current;
    setWins(prev => prev.find(w => w.id === info.id)
      ? prev.map(w => w.id === info.id ? { ...w, ...info, z, isMin: false } : w)
      : [...prev, { isMin: false, z, ...info }]);
  }, []);

  const unregister = useCallback((id) => {
    setWins(prev => prev.filter(w => w.id !== id));
  }, []);

  const update = useCallback((id, patch) => {
    setWins(prev => prev.map(w => w.id === id ? { ...w, ...patch } : w));
  }, []);

  const bringToFront = useCallback((id) => {
    topZRef.current += 1;
    const z = topZRef.current;
    setWins(prev => prev.map(w => w.id === id ? { ...w, z, isMin: false } : w));
  }, []);

  const value = useMemo(() => ({ wins, register, unregister, update, bringToFront }),
    [wins, register, unregister, update, bringToFront]);
  return <WindowMgrContext.Provider value={value}>{children}</WindowMgrContext.Provider>;
}

function useWindowMgr() {
  const v = useContext(WindowMgrContext);
  return v || {
    wins: [], register: () => {}, unregister: () => {},
    update: () => {}, bringToFront: () => {},
  };
}


// ─── Single resize handle ───────────────────────────────────────────────────
function _ResizeHandle({ dir, onStart }) {
  const base = { position: "absolute", background: "transparent", zIndex: 2 };
  const map = {
    n:  { ...base, top: 0,    left: 8,   right: 8,  height: 5, cursor: "ns-resize" },
    s:  { ...base, bottom: 0, left: 8,   right: 8,  height: 5, cursor: "ns-resize" },
    e:  { ...base, right: 0,  top: 8,    bottom: 8, width: 5,  cursor: "ew-resize" },
    w:  { ...base, left: 0,   top: 8,    bottom: 8, width: 5,  cursor: "ew-resize" },
    ne: { ...base, top: 0,    right: 0,  width: 12, height: 12, cursor: "nesw-resize" },
    nw: { ...base, top: 0,    left: 0,   width: 12, height: 12, cursor: "nwse-resize" },
    se: { ...base, bottom: 0, right: 0,  width: 14, height: 14, cursor: "nwse-resize" },
    sw: { ...base, bottom: 0, left: 0,   width: 12, height: 12, cursor: "nesw-resize" },
  };
  return <div onMouseDown={e => onStart(dir, e)} style={map[dir]} />;
}


// ─── Main window primitive ─────────────────────────────────────────────────
function WindowFrame({
  windowId, title, icon = null, onClose, children,
  defaultWidth = 760, defaultHeight = 580,
  minWidth = WIN_MIN_W, minHeight = WIN_MIN_H,
  bodyPadding = 0,
}) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const mgr = useWindowMgr();

  // Initial rect: saved → defaults centered
  const [rect, setRect] = useState(() => {
    if (typeof window === "undefined") return { x: 40, y: 40, w: defaultWidth, h: defaultHeight };
    const saved = _loadWinRect(windowId);
    if (saved) {
      // Clamp to viewport
      const w = Math.max(minWidth, Math.min(saved.w, window.innerWidth - 2 * WIN_MARGIN));
      const h = Math.max(minHeight, Math.min(saved.h, window.innerHeight - 2 * WIN_MARGIN));
      const x = Math.max(WIN_MARGIN, Math.min(saved.x, window.innerWidth - w - WIN_MARGIN));
      const y = Math.max(WIN_MARGIN, Math.min(saved.y, window.innerHeight - WIN_TITLE_H));
      return { x, y, w, h };
    }
    const w = Math.min(defaultWidth, window.innerWidth - 2 * WIN_MARGIN);
    const h = Math.min(defaultHeight, window.innerHeight - 2 * WIN_MARGIN);
    return {
      x: Math.round((window.innerWidth - w) / 2),
      y: Math.round((window.innerHeight - h) / 2),
      w, h,
    };
  });

  const [isMax, setIsMax] = useState(false);
  const preMaxRef = useRef(null);

  // Register in manager (desktop only)
  useEffect(() => {
    if (isMobile) return;
    mgr.register({ id: windowId, title, icon });
    return () => mgr.unregister(windowId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowId, title, isMobile]);

  // Persist rect (skip when maximized — saved is pre-max snapshot via preMaxRef)
  useEffect(() => {
    if (!isMobile && !isMax) _saveWinRect(windowId, rect);
  }, [rect, windowId, isMax, isMobile]);

  // Pull window state from manager
  const me = mgr.wins.find(w => w.id === windowId);
  const isMin = !!me?.isMin;
  const zIndex = me?.z || 200;
  const canClose = typeof onClose === "function";

  // ── Drag from title bar ───────────────────────────────────────────────
  const dragRef = useRef(null);
  function onTitleMouseDown(e) {
    if (e.target.closest("button")) return;     // ignore clicks on min/max/close
    if (isMax) return;
    mgr.bringToFront(windowId);
    dragRef.current = { x: e.clientX, y: e.clientY, rx: rect.x, ry: rect.y };
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
    e.preventDefault();
  }
  function onDragMove(e) {
    const d = dragRef.current; if (!d) return;
    setRect(prev => {
      const x = Math.max(-prev.w + 80, Math.min(window.innerWidth - 80, d.rx + e.clientX - d.x));
      const y = Math.max(0,            Math.min(window.innerHeight - WIN_TITLE_H, d.ry + e.clientY - d.y));
      return { ...prev, x, y };
    });
  }
  function onDragEnd() {
    dragRef.current = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
  }

  // ── Resize from edges/corners ─────────────────────────────────────────
  const resizeRef = useRef(null);
  function startResize(dir, e) {
    e.preventDefault(); e.stopPropagation();
    if (isMax) return;
    mgr.bringToFront(windowId);
    resizeRef.current = { dir, sx: e.clientX, sy: e.clientY, base: { ...rect } };
    window.addEventListener("mousemove", onResizeMove);
    window.addEventListener("mouseup", onResizeEnd);
  }
  function onResizeMove(e) {
    const s = resizeRef.current; if (!s) return;
    const dx = e.clientX - s.sx, dy = e.clientY - s.sy;
    let { x, y, w, h } = s.base;
    if (s.dir.includes("e")) w = Math.max(minWidth, s.base.w + dx);
    if (s.dir.includes("s")) h = Math.max(minHeight, s.base.h + dy);
    if (s.dir.includes("w")) {
      const nw = Math.max(minWidth, s.base.w - dx);
      x = s.base.x + (s.base.w - nw); w = nw;
    }
    if (s.dir.includes("n")) {
      const nh = Math.max(minHeight, s.base.h - dy);
      y = s.base.y + (s.base.h - nh); h = nh;
    }
    setRect({ x, y, w, h });
  }
  function onResizeEnd() {
    resizeRef.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", onResizeEnd);
  }

  function toggleMax() {
    if (isMax) {
      if (preMaxRef.current) setRect(preMaxRef.current);
      setIsMax(false);
    } else {
      preMaxRef.current = rect;
      setRect({
        x: WIN_MARGIN,
        y: WIN_MARGIN,
        w: window.innerWidth  - 2 * WIN_MARGIN,
        h: window.innerHeight - 2 * WIN_MARGIN - 50,   // место под трей снизу
      });
      setIsMax(true);
    }
  }

  function minimize() { mgr.update(windowId, { isMin: true }); }
  function focusWin() { mgr.bringToFront(windowId); }

  // ─── MOBILE FALLBACK: original full-screen overlay ──────────────────────
  if (isMobile) {
    return ReactDOM.createPortal(
      <div className="st-modal" onMouseDown={focusWin}>
        <div className="st-modal-bg" onClick={canClose ? onClose : undefined} />
        <div className="st-modal-box" style={{ maxWidth: defaultWidth, padding: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 16px", borderBottom: `1px solid ${colors.border}`,
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.fg, display: "flex", alignItems: "center", gap: 8 }}>
              {icon}{title}
            </div>
            <button onClick={onClose} disabled={!canClose} style={{
              background: "none", border: "none", color: colors.fgSub,
              fontSize: 18, cursor: canClose ? "pointer" : "default", padding: "2px 6px",
              opacity: canClose ? 1 : .35,
            }}>✕</button>
          </div>
          <div style={{ padding: bodyPadding || 16 }}>{children}</div>
        </div>
      </div>,
      document.body
    );
  }

  // ─── DESKTOP: floating draggable/resizable window ───────────────────────
  return ReactDOM.createPortal(
    <div
      onMouseDown={focusWin}
      style={{
        position: "fixed",
        left: rect.x, top: rect.y, width: rect.w, height: rect.h,
        background: colors.cardMid || colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        boxShadow: "0 14px 44px rgba(0,0,0,0.55)",
        display: isMin ? "none" : "flex",
        flexDirection: "column",
        zIndex: zIndex,
        overflow: "hidden",
      }}
    >
      {/* ── Title bar ── */}
      <div
        onMouseDown={onTitleMouseDown}
        onDoubleClick={toggleMax}
        style={{
          height: WIN_TITLE_H, flexShrink: 0,
          display: "flex", alignItems: "center", gap: 8,
          padding: "0 8px 0 12px",
          background: colors.bgDeep,
          borderBottom: `1px solid ${colors.border}`,
          cursor: isMax ? "default" : "grab",
          userSelect: "none",
        }}
      >
        {icon ? <span style={{ display: "inline-flex", alignItems: "center", color: colors.fgSub }}>{icon}</span> : null}
        <span style={{
          flex: 1, fontSize: 12.5, fontWeight: 600, color: colors.fg,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {title}
        </span>
        <button onClick={minimize} title="Minimize"
                style={_titleBtnStyle(colors)}>—</button>
        <button onClick={toggleMax} title={isMax ? "Restore" : "Maximize"}
                style={_titleBtnStyle(colors)}>{isMax ? "❐" : "▢"}</button>
        <button onClick={onClose} disabled={!canClose} title="Close"
                style={{ ..._titleBtnStyle(colors), color: "#ff6b6b", opacity: canClose ? 1 : .35, cursor: canClose ? "pointer" : "default" }}>✕</button>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflow: "auto", minHeight: 0, padding: bodyPadding }}>
        {children}
      </div>

      {/* ── Resize handles (only if not maximized) ── */}
      {!isMax && (
        <>
          <_ResizeHandle dir="n"  onStart={startResize} />
          <_ResizeHandle dir="s"  onStart={startResize} />
          <_ResizeHandle dir="e"  onStart={startResize} />
          <_ResizeHandle dir="w"  onStart={startResize} />
          <_ResizeHandle dir="ne" onStart={startResize} />
          <_ResizeHandle dir="nw" onStart={startResize} />
          <_ResizeHandle dir="se" onStart={startResize} />
          <_ResizeHandle dir="sw" onStart={startResize} />
        </>
      )}
    </div>,
    document.body
  );
}

function _titleBtnStyle(colors) {
  return {
    width: 28, height: 22, padding: 0, border: "none",
    background: "transparent", color: colors.fgSub,
    cursor: "pointer", borderRadius: 4,
    fontSize: 13, lineHeight: 1, fontFamily: "inherit",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  };
}


// ─── Tray for minimized windows ─────────────────────────────────────────────
function WindowModal({
  windowId, title, children, onClose,
  width = 640, height = 520,
  minWidth = WIN_MIN_W, minHeight = WIN_MIN_H,
  bodyPadding = 20, icon = null,
}) {
  return (
    <WindowFrame
      windowId={windowId || _safeWindowId("modal", title)}
      title={title || "Dialog"}
      icon={icon}
      onClose={onClose}
      defaultWidth={width}
      defaultHeight={height}
      minWidth={minWidth}
      minHeight={minHeight}
      bodyPadding={bodyPadding}
    >
      {children}
    </WindowFrame>
  );
}

function MinimizedWindowsTray() {
  const { wins, update, bringToFront } = useWindowMgr();
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  if (isMobile) return null;
  const min = wins.filter(w => w.isMin);
  if (min.length === 0) return null;

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", bottom: 8, left: "50%",
      transform: "translateX(-50%)",
      display: "flex", gap: 6, padding: 5,
      background: colors.bgDeep,
      border: `1px solid ${colors.border}`,
      borderRadius: 10,
      boxShadow: "0 4px 18px rgba(0,0,0,0.4)",
      zIndex: 9000, maxWidth: "92vw", overflowX: "auto",
    }}>
      {min.map(w => (
        <button
          key={w.id}
          onClick={() => { update(w.id, { isMin: false }); bringToFront(w.id); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px",
            border: `1px solid ${colors.border}`,
            background: colors.cardMid || colors.bg,
            color: colors.fg, borderRadius: 7,
            cursor: "pointer", fontFamily: "inherit",
            fontSize: 12, fontWeight: 600,
            maxWidth: 200, whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis",
          }}
          title={`Restore: ${w.title}`}
        >
          {w.icon}<span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{w.title}</span>
        </button>
      ))}
    </div>,
    document.body
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  FormModal — теперь оборачивает контент в desktop-окно (на мобиле fallback).
// ════════════════════════════════════════════════════════════════════════════
function FormModal({ title, children, onClose, width = 640, windowId }) {
  const { colors } = useTheme();
  const id = windowId || _safeWindowId("form", title || "modal");
  return (
    <WindowFrame
      windowId={id}
      title={title || "Dialog"}
      onClose={onClose}
      defaultWidth={width}
      defaultHeight={Math.min(620, (typeof window !== "undefined" ? window.innerHeight - 120 : 620))}
      bodyPadding={20}
    >
      {children}
    </WindowFrame>
  );
}

// ─── STATUS TAGS ──────────────────────────────────────────────────────────────
function MatchStatus({ s }) {
  const { colors } = useTheme();
  const m = { active: [colors.green, "Active"], pending: [colors.orange, "Pending"],
    rejected: [colors.red, "Rejected"], cancelled: [colors.fgSub, "Cancelled"] };
  const [c, l] = m[s] || [colors.fgSub, s];
  return <Tag color={c}>{l}</Tag>;
}

function RoleTag({ r }) {
  const { colors } = useTheme();
  const m = { admin: colors.primary, event_admin: colors.blue, event_user: colors.cyan, user: colors.fgSub };
  return <Tag color={m[r] || colors.fgSub}>{r}</Tag>;
}

function EventStatusTag({ s }) {
  const { colors } = useTheme();
  const m = { published: colors.green, archived: colors.orange, cancelled: colors.red, draft: colors.fgSub };
  return <Tag color={m[s] || colors.fgSub}>{s}</Tag>;
}

function FeedbackStatusTag({ s }) {
  const { colors } = useTheme();
  const m = { new: colors.orange, contacted: colors.blue, resolved: colors.green };
  return <Tag color={m[s] || colors.fgSub}>{s}</Tag>;
}

function PillTabs({ tabs, active, onChange }) {
  return (
    <div className="st-pill-tabs" style={{ flexWrap: "wrap" }}>
      {tabs.map(t => (
        <button key={t.id} className={`st-pill-tab${active === t.id ? " active" : ""}`}
          onClick={() => onChange(t.id)}>{t.label}</button>
      ))}
    </div>
  );
}

function FilterRow({ children }) {
  return <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>{children}</div>;
}

function SearchInput({ placeholder, onChange, style }) {
  const ref = useRef();
  function handle(e) {
    clearTimeout(ref.current);
    const v = e.target.value;
    ref.current = setTimeout(() => onChange(v), 380);
  }
  return <input className="st-input" placeholder={placeholder} onChange={handle} style={{ maxWidth: 260, minWidth: 120, ...style }} />;
}

// ─── CHART COMPONENTS ─────────────────────────────────────────────────────────
function SparkLine({ data = [], color, h = 36, w = 100 }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  if (data.length < 2) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * (w - 4) + 2;
    const y = h - 4 - ((d.count / max) * (h - 8));
    return [x, y];
  });
  const lineStr = pts.map(([x, y]) => `${x},${y}`).join(" ");
  const areaStr = `2,${h - 2} ${lineStr} ${w - 2},${h - 2}`;
  const gradId = `sg${c.replace(/[#(),.% ]/g, "")}`;
  return (
    <svg width={w} height={h} style={{ display: "block", overflow: "visible" }}>
      <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={c} stopOpacity="0.35" />
        <stop offset="100%" stopColor={c} stopOpacity="0" />
      </linearGradient>
      <polygon points={areaStr} fill={`url(#${gradId})`} />
      <polyline points={lineStr} fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      {pts.length > 0 && (
        <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r="2.5" fill={c} />
      )}
    </svg>
  );
}

function BarChartSvg({ data = [], color, h = 64, gap = 2 }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  const w = 100;
  const totalGap = gap * Math.max(0, data.length - 1);
  const barW = Math.max(0.6, (w - totalGap) / data.length);
  return (
    <svg viewBox={`0 0 100 ${h}`} preserveAspectRatio="none"
      style={{ display: "block", width: "100%", height: h }}>
      {data.map((r, i) => {
        const bh = Math.max(0.6, (r.count / max) * (h - 4));
        const x = i * (barW + gap);
        return (
          <g key={i}>
            <rect x={x} y={h - bh} width={barW} height={bh} rx="0.6" fill={c}
              opacity={0.25 + (r.count / max) * 0.7}>
              <title>{r.day || i}: {r.count}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}

// ─── CHART_COLORS — фиксированная палитра графиков ─────────────────────────
//  Подобраны так, чтобы хорошо контрастировать друг с другом и в светлой,
//  и в тёмной теме. Не используем `colors.primary/cyan/accent` — в light-теме
//  они почти одинакового тёмно-винного оттенка и линии сливаются.
const CHART_COLORS = {
  registrations: "#3b82f6", // ярко-синий
  matches:       "#10b981", // изумрудный
  chats:         "#a855f7", // фиолетовый
  messages:      "#f59e0b", // янтарный
  events:        "#ec4899", // розовый
  feedback:      "#ef4444", // красный
  users:         "#3b82f6",
  bans:          "#dc2626",
};

// ─── ChartTooltip — плавающий тултип у точки на графике ─────────────────
//  x/y задаются в координатах viewport. Тултип сам решает, ставить себя
//  справа от точки или слева (если иначе вылезет за край окна).
function ChartTooltip({ x, y, items, dateLabel }) {
  const { colors } = useTheme();
  const ref = React.useRef(null);
  const [size, setSize] = React.useState({ w: 180, h: 80 });
  React.useLayoutEffect(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    }
  }, [items, dateLabel]);
  if (!items?.length) return null;
  const M = 8;
  // Center horizontally on cursor, clamp to screen edges
  const left = Math.max(M, Math.min(x - size.w / 2, window.innerWidth - size.w - M));
  // Show above cursor; fall back to below if not enough room
  const top = y - size.h - 12 >= M ? y - size.h - 12 : y + 16;
  return ReactDOM.createPortal(
    <div ref={ref} style={{
      position: "fixed", left, top, zIndex: 9999,
      background: colors.card, border: `1px solid ${colors.borderMid}`,
      borderRadius: 8, padding: "8px 10px",
      boxShadow: "0 8px 24px rgba(0,0,0,.28)", pointerEvents: "none",
      minWidth: 160, color: colors.fg,
    }}>
      {dateLabel && (
        <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
          letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>
          {dateLabel}
        </div>
      )}
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center",
          gap: 8, fontSize: 11, padding: "2px 0" }}>
          <span style={{ width: 8, height: 8, borderRadius: 99,
            background: it.color, flexShrink: 0 }} />
          <span style={{ flex: 1, color: colors.fgMid }}>{it.label}</span>
          <span style={{ color: colors.fg, fontWeight: 700,
            fontFamily: "'JetBrains Mono', monospace" }}>
            {it.value?.toLocaleString("en") ?? "—"}
          </span>
        </div>
      ))}
    </div>,
    document.body
  );
}

// ─── MultiLineChart ─────────────────────────────────────────────────────────
//  Несколько линий-метрик на одном графике с сеткой, подписями оси Y и X,
//  hover-тултипом со значениями ВСЕХ рядов на этой дате.
//  По умолчанию ряды нормализованы по своему максимуму (форма кривых).
function MultiLineChart({ series = [], h = 180, normalize = "per-series" }) {
  const { colors } = useTheme();
  const wrapRef = React.useRef(null);
  const [hover, setHover] = React.useState(null); // { idx, screenX, screenY }

  React.useEffect(() => {
    const clear = () => setHover(null);
    window.addEventListener("scroll", clear, true);
    return () => window.removeEventListener("scroll", clear, true);
  }, []);

  const valid = (series || []).filter(s => s.data && s.data.length >= 2);
  if (!valid.length) return null;

  // Все ряды должны иметь одинаковую длину (даты совпадают);
  // используем длину первого валидного.
  const len = valid[0].data.length;
  // Геометрия SVG в «логических» единицах
  const W = 1000;             // широкий viewBox для гладкости
  const H = h;
  const padL = 44;            // место для подписей оси Y
  const padR = 12;
  const padT = 10;
  const padB = 22;            // место для подписей оси X
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  // Если normalize === "per-series" — каждый ряд масштабируется к своему max
  // и Y-ось показывает "%". Если "shared" — общий max и абсолютные значения.
  const sharedMax = Math.max(
    ...valid.flatMap(s => s.data.map(d => d.count)), 1
  );
  const linesGeom = valid.map((s, idx) => {
    const max = normalize === "per-series"
      ? Math.max(...s.data.map(d => d.count), 1)
      : sharedMax;
    const pts = s.data.map((d, i) => {
      const x = padL + (i / Math.max(1, len - 1)) * innerW;
      const y = padT + innerH - (d.count / max) * innerH;
      return { x, y, count: d.count, day: d.day };
    });
    return { ...s, pts, max, idx };
  });

  // Подписи по Y (4 уровня — 0/25/50/75/100)
  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const yLabel = (t) => normalize === "per-series"
    ? `${Math.round(t * 100)}%`
    : Math.round(sharedMax * t).toLocaleString("en");

  // X-метки — берём 5 равноотстоящих точек
  const xTickIdx = Array.from({ length: 5 }, (_, i) =>
    Math.round((i / 4) * (len - 1))
  );

  const handleMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const scaleX = rect.width / W;
    const xPx = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, (xPx - padL * scaleX) / (innerW * scaleX)));
    const idx = Math.max(0, Math.min(len - 1, Math.round(ratio * (len - 1))));
    const svgX = padL + ratio * innerW;
    setHover({ idx, svgX, screenX: e.clientX, screenY: e.clientY });
  };

  const tooltipItems = hover ? linesGeom.map(s => ({
    label: s.label, color: s.color, value: s.data[hover.idx]?.count ?? 0,
  })) : [];
  const tooltipDate = hover && valid[0].data[hover.idx]?.day
    ? new Date(valid[0].data[hover.idx].day).toLocaleDateString("en",
        { day: "2-digit", month: "long", year: "numeric" })
    : "";

  return (
    <div ref={wrapRef}
      style={{ position: "relative", width: "100%" }}
      onMouseMove={handleMove}
      onMouseLeave={() => setHover(null)}
    >
      <svg viewBox={`0 0 ${W} ${H}`}
        style={{ display: "block", width: "100%", height: "auto", minHeight: Math.round(H * 0.55) }}>
        {/* Горизонтальная сетка + подписи оси Y */}
        {yTicks.map(t => {
          const y = padT + innerH - t * innerH;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - padR} y2={y}
                stroke={colors.border}
                strokeWidth={t === 0 ? 1 : 0.6}
                strokeDasharray={t === 0 ? "" : "3 4"} />
              <text x={padL - 6} y={y + 3} textAnchor="end"
                style={{ fontSize: 10, fill: colors.fgFaint,
                  fontFamily: "'JetBrains Mono', monospace" }}>
                {yLabel(t)}
              </text>
            </g>
          );
        })}

        {/* Линии и заливка */}
        {linesGeom.map(({ pts, color, idx }) => {
          const lineStr = pts.map(p => `${p.x},${p.y}`).join(" ");
          const areaStr = `${pts[0].x},${padT + innerH} ${lineStr} ${pts[pts.length-1].x},${padT + innerH}`;
          const gradId = `mlg${idx}${color.replace(/[#(),.% ]/g, "")}`;
          return (
            <g key={idx}>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor={color} stopOpacity="0.22" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
              <polygon points={areaStr} fill={`url(#${gradId})`} />
              <polyline points={lineStr} fill="none" stroke={color}
                strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          );
        })}

        {/* X-метки (даты) */}
        {xTickIdx.map(i => {
          const x = padL + (i / Math.max(1, len - 1)) * innerW;
          const day = valid[0].data[i]?.day;
          if (!day) return null;
          const dt = new Date(day);
          return (
            <text key={i} x={x} y={H - 6} textAnchor="middle"
              style={{ fontSize: 10, fill: colors.fgFaint }}>
              {dt.toLocaleDateString("en", { day: "2-digit", month: "short" })}
            </text>
          );
        })}

        {/* Hover: вертикальная линия + точки на пересечениях */}
        {hover && (() => {
          const x = hover.svgX;
          return (
            <g>
              <line x1={x} y1={padT} x2={x} y2={padT + innerH}
                stroke={colors.fgSub} strokeWidth="0.8" strokeDasharray="3 3"
                opacity="0.6" />
              {linesGeom.map(({ pts, color, idx }) => (
                <circle key={idx} cx={x} cy={pts[hover.idx].y} r="4"
                  fill={color} stroke={colors.card} strokeWidth="2" />
              ))}
            </g>
          );
        })()}
      </svg>
      {hover && (
        <ChartTooltip x={hover.screenX} y={hover.screenY}
          items={tooltipItems} dateLabel={tooltipDate} />
      )}
    </div>
  );
}

// ─── CalendarHeatmap ────────────────────────────────────────────────────────
//  GitHub-style ячейки за N дней — цвет зависит от интенсивности (count / max).
function CalendarHeatmap({ data = [], color, cellSize = 16, gap = 3 }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  // Раскладываем дни по неделям. Сетка: 5–6 строк (по дням недели), N колонок.
  const cells = data.map(d => {
    const date = d.day ? new Date(d.day) : null;
    const dow = date ? (date.getDay() + 6) % 7 : 0; // Пн=0 ... Вс=6
    return { ...d, date, dow };
  });
  // Группируем по неделям (по понедельникам)
  const weeks = [];
  let week = Array(7).fill(null);
  cells.forEach((c, i) => {
    if (c.dow === 0 && i > 0) { weeks.push(week); week = Array(7).fill(null); }
    week[c.dow] = c;
  });
  weeks.push(week);
  const intensity = (count) => {
    if (count === 0) return 0;
    const t = count / max;
    if (t < 0.25) return 0.25;
    if (t < 0.5)  return 0.45;
    if (t < 0.75) return 0.7;
    return 1;
  };
  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
      {/* Подписи дней недели */}
      <div style={{ display: "flex", flexDirection: "column", gap, paddingTop: 1 }}>
        {days.map((d, i) => (
          <div key={d} style={{
            height: cellSize, fontSize: 9, color: colors.fgFaint,
            lineHeight: `${cellSize}px`,
            visibility: i % 2 === 1 ? "visible" : "hidden",
          }}>{d}</div>
        ))}
      </div>
      {/* Сетка */}
      <div style={{ display: "flex", gap, overflowX: "auto" }}>
        {weeks.map((wk, wi) => (
          <div key={wi} style={{ display: "flex", flexDirection: "column", gap }}>
            {wk.map((cell, di) => (
              <div key={di} title={cell ? `${cell.day}: ${cell.count}` : ""}
                style={{
                  width: cellSize, height: cellSize, borderRadius: 3,
                  background: cell ? (cell.count > 0
                    ? `${c}${Math.round(intensity(cell.count) * 255).toString(16).padStart(2, "0")}`
                    : colors.cardMid)
                    : "transparent",
                  border: `1px solid ${cell && cell.count > 0 ? "transparent" : colors.border}`,
                  cursor: cell ? "default" : "default",
                }} />
            ))}
          </div>
        ))}
      </div>
      {/* Легенда */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginLeft: "auto",
        fontSize: 9, color: colors.fgFaint, alignItems: "flex-end" }}>
        <span>more</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[1, 0.7, 0.45, 0.25].map(t => (
            <div key={t} style={{
              width: cellSize - 4, height: cellSize - 4, borderRadius: 2,
              background: `${c}${Math.round(t * 255).toString(16).padStart(2, "0")}`,
            }} />
          ))}
          <div style={{
            width: cellSize - 4, height: cellSize - 4, borderRadius: 2,
            background: colors.cardMid, border: `1px solid ${colors.border}`,
          }} />
        </div>
        <span>less</span>
      </div>
    </div>
  );
}

// ─── DayOfWeekChart ─────────────────────────────────────────────────────────
//  Bar-chart по средней активности дня недели (Пн-Вс) за весь период данных.
function DayOfWeekChart({ data = [], color, h = 80 }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  if (!data.length) return null;
  // Считаем среднее для каждого дня недели
  const buckets = Array.from({ length: 7 }, () => ({ sum: 0, n: 0 }));
  data.forEach(d => {
    if (!d.day) return;
    const dow = (new Date(d.day).getDay() + 6) % 7;
    buckets[dow].sum += d.count;
    buckets[dow].n   += 1;
  });
  const avgs = buckets.map(b => b.n > 0 ? b.sum / b.n : 0);
  const max  = Math.max(...avgs, 1);
  const labels = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "flex-end", height: h, gap: 6 }}>
        {avgs.map((v, i) => {
          const pct = v / max;
          return (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 9, color: colors.fgFaint, fontFamily: "monospace" }}>
                {v.toFixed(1)}
              </span>
              <div style={{
                width: "100%", height: `${Math.max(2, pct * (h - 22))}px`,
                background: c, opacity: 0.3 + pct * 0.65, borderRadius: 3,
                transition: "all .35s",
              }} title={`${labels[i]}: ${v.toFixed(1)} avg`} />
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {labels.map((l, i) => (
          <div key={l} style={{ flex: 1, fontSize: 10, color: colors.fgSub, textAlign: "center",
            fontWeight: i === 5 || i === 6 ? 700 : 500 }}>
            {l}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CumulativeAreaChart ────────────────────────────────────────────────────
//  Накопительный рост: area-chart по сумме count'ов до текущего дня.
//  С сеткой, подписями осей Y/X и hover-тултипом.
function CumulativeAreaChart({ series = [], h = 200 }) {
  const { colors } = useTheme();
  const wrapRef = React.useRef(null);
  const [hover, setHover] = React.useState(null);

  React.useEffect(() => {
    const clear = () => setHover(null);
    window.addEventListener("scroll", clear, true);
    return () => window.removeEventListener("scroll", clear, true);
  }, []);

  const valid = (series || []).filter(s => s.data && s.data.length >= 2);
  if (!valid.length) return null;
  const len = valid[0].data.length;

  const W = 1000;
  const padL = 52, padR = 12, padT = 10, padB = 22;
  const innerW = W - padL - padR;
  const innerH = h - padT - padB;

  // Накопительные ряды
  const cumSeries = valid.map((s, idx) => {
    let acc = 0;
    const cum = s.data.map(d => { acc += d.count; return { day: d.day, count: acc }; });
    return { ...s, cum, idx };
  });
  const allMax = Math.max(...cumSeries.flatMap(s => s.cum.map(d => d.count)), 1);

  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const xTickIdx = Array.from({ length: 5 }, (_, i) =>
    Math.round((i / 4) * (len - 1))
  );

  const linesGeom = cumSeries.map(s => ({
    ...s,
    pts: s.cum.map((d, j) => ({
      x: padL + (j / Math.max(1, len - 1)) * innerW,
      y: padT + innerH - (d.count / allMax) * innerH,
      count: d.count, day: d.day,
    })),
  }));

  const handleMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const scaleX = rect.width / W;
    const xPx = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, (xPx - padL * scaleX) / (innerW * scaleX)));
    const idx = Math.max(0, Math.min(len - 1, Math.round(ratio * (len - 1))));
    const svgX = padL + ratio * innerW;
    setHover({ idx, svgX, screenX: e.clientX, screenY: e.clientY });
  };

  const tooltipItems = hover ? linesGeom.map(s => ({
    label: s.label, color: s.color, value: s.cum[hover.idx]?.count ?? 0,
  })) : [];
  const tooltipDate = hover && valid[0].data[hover.idx]?.day
    ? new Date(valid[0].data[hover.idx].day).toLocaleDateString("en",
        { day: "2-digit", month: "long", year: "numeric" })
    : "";

  return (
    <div ref={wrapRef}
      style={{ position: "relative", width: "100%" }}
      onMouseMove={handleMove}
      onMouseLeave={() => setHover(null)}
    >
      <svg viewBox={`0 0 ${W} ${h}`}
        style={{ display: "block", width: "100%", height: "auto", minHeight: Math.round(h * 0.55) }}>
        {/* Сетка + подписи Y */}
        {yTicks.map(t => {
          const y = padT + innerH - t * innerH;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - padR} y2={y}
                stroke={colors.border}
                strokeWidth={t === 0 ? 1 : 0.6}
                strokeDasharray={t === 0 ? "" : "3 4"} />
              <text x={padL - 6} y={y + 3} textAnchor="end"
                style={{ fontSize: 10, fill: colors.fgFaint,
                  fontFamily: "'JetBrains Mono', monospace" }}>
                {Math.round(allMax * t).toLocaleString("en")}
              </text>
            </g>
          );
        })}

        {/* Заливка + линии */}
        {linesGeom.map(({ pts, color, idx }) => {
          const lineStr = pts.map(p => `${p.x},${p.y}`).join(" ");
          const areaStr = `${pts[0].x},${padT + innerH} ${lineStr} ${pts[pts.length-1].x},${padT + innerH}`;
          const gradId = `cag${idx}${color.replace(/[#(),.% ]/g, "")}`;
          return (
            <g key={idx}>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor={color} stopOpacity="0.30" />
                <stop offset="100%" stopColor={color} stopOpacity="0.04" />
              </linearGradient>
              <polygon points={areaStr} fill={`url(#${gradId})`} />
              <polyline points={lineStr} fill="none" stroke={color}
                strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          );
        })}

        {/* X-метки */}
        {xTickIdx.map(i => {
          const x = padL + (i / Math.max(1, len - 1)) * innerW;
          const day = valid[0].data[i]?.day;
          if (!day) return null;
          const dt = new Date(day);
          return (
            <text key={i} x={x} y={h - 6} textAnchor="middle"
              style={{ fontSize: 10, fill: colors.fgFaint }}>
              {dt.toLocaleDateString("en", { day: "2-digit", month: "short" })}
            </text>
          );
        })}

        {/* Hover */}
        {hover && (() => {
          const x = hover.svgX;
          return (
            <g>
              <line x1={x} y1={padT} x2={x} y2={padT + innerH}
                stroke={colors.fgSub} strokeWidth="0.8" strokeDasharray="3 3"
                opacity="0.6" />
              {linesGeom.map(({ pts, color, idx }) => (
                <circle key={idx} cx={x} cy={pts[hover.idx].y} r="4"
                  fill={color} stroke={colors.card} strokeWidth="2" />
              ))}
            </g>
          );
        })()}
      </svg>
      {hover && (
        <ChartTooltip x={hover.screenX} y={hover.screenY}
          items={tooltipItems} dateLabel={tooltipDate} />
      )}
    </div>
  );
}

// ─── DonutChart ─────────────────────────────────────────────────────────────
//  Кольцевая диаграмма для категориальных распределений (статусы матчей и т.п.).
function DonutChart({ slices = [], size = 140, thickness = 18, centerLabel, centerSub }) {
  const { colors } = useTheme();
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (!total) return null;
  const r  = (size - thickness) / 2;
  const cx = size / 2, cy = size / 2;
  // Накопительный угол
  let acc = 0;
  const arcs = slices.map(sl => {
    const a0 = acc / total * Math.PI * 2 - Math.PI / 2;
    acc += sl.value;
    const a1 = acc / total * Math.PI * 2 - Math.PI / 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    return { ...sl, d: `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}` };
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <svg width={size} height={size}>
        {arcs.map((a, i) => (
          <path key={i} d={a.d} fill="none" stroke={a.color}
            strokeWidth={thickness} strokeLinecap="butt"
            opacity={0.92}>
            <title>{a.label}: {a.value}</title>
          </path>
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle"
          style={{ fontSize: 18, fontWeight: 800, fill: colors.fg,
            fontFamily: "'JetBrains Mono', monospace" }}>
          {centerLabel ?? total}
        </text>
        {centerSub && (
          <text x={cx} y={cy + 12} textAnchor="middle"
            style={{ fontSize: 9, fill: colors.fgSub, letterSpacing: ".06em" }}>
            {centerSub}
          </text>
        )}
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}>
        {slices.map((s, i) => {
          const pct = Math.round((s.value / total) * 100);
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3,
                background: s.color, flexShrink: 0 }} />
              <span style={{ flex: 1, color: colors.fgMid, overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</span>
              <span style={{ color: colors.fg, fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace" }}>{s.value}</span>
              <span style={{ color: colors.fgFaint, fontSize: 10, minWidth: 30,
                textAlign: "right" }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── HBars ──────────────────────────────────────────────────────────────────
//  Горизонтальные полосы — для top-категорий и подобных списков.
function HBars({ items = [], color, max: forcedMax }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  if (!items.length) return (
    <div style={{ fontSize: 11, color: colors.fgFaint, padding: "8px 0" }}>no data</div>
  );
  const max = forcedMax ?? Math.max(...items.map(it => it.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map((it, i) => {
        const pct = (it.value / max) * 100;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10,
            fontSize: 11, color: colors.fgMid }}>
            <span style={{ minWidth: 90, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.label}</span>
            <div style={{ flex: 1, height: 6, background: colors.border,
              borderRadius: 99, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: c,
                borderRadius: 99, transition: "width .6s ease" }} />
            </div>
            <span style={{ minWidth: 36, textAlign: "right", color: colors.fg,
              fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
              {it.value.toLocaleString("en")}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── MoMCompareCard ─────────────────────────────────────────────────────────
//  Карточка month-over-month: текущий период vs предыдущий.
function MoMCompareCard({ label, current, prev, color }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  const cur = Number(current ?? 0);
  const pre = Number(prev    ?? 0);
  const delta = cur - pre;
  const pct   = pre > 0 ? Math.round((delta / pre) * 100) : (cur > 0 ? 100 : 0);
  const dir   = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const trendC = dir === "up" ? colors.green : dir === "down" ? colors.red : colors.fgSub;
  return (
    <div className="st-card" style={{ padding: "14px 16px" }}>
      <div style={{ fontSize: 9, color: colors.fgSub, fontWeight: 700,
        letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: c,
          fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
          {cur.toLocaleString("en")}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: trendC }}>
          {dir === "up" ? "↑" : dir === "down" ? "↓" : "→"} {Math.abs(pct)}%
        </span>
      </div>
      <div style={{ height: 4, background: colors.border, borderRadius: 99,
        overflow: "hidden", display: "flex" }}>
        <div style={{
          width: `${Math.min(100, cur > pre ? 100 : (cur / Math.max(1, pre)) * 100)}%`,
          background: c, transition: "width .6s",
        }} />
      </div>
      <div style={{ fontSize: 10, color: colors.fgFaint, marginTop: 4,
        display: "flex", justifyContent: "space-between" }}>
        <span>prev. 30 days: <span style={{ color: colors.fgSub, fontWeight: 600 }}>
          {pre.toLocaleString("en")}</span></span>
        <span style={{ color: trendC }}>
          {delta > 0 ? "+" : ""}{delta.toLocaleString("en")}
        </span>
      </div>
    </div>
  );
}

// ─── ChartLegend ────────────────────────────────────────────────────────────
function ChartLegend({ items = [] }) {
  const { colors } = useTheme();
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6,
          fontSize: 11, color: colors.fgMid }}>
          <span style={{ width: 10, height: 3, borderRadius: 2,
            background: it.color, flexShrink: 0 }} />
          <span>{it.label}</span>
          {it.value !== undefined && (
            <span style={{ color: colors.fg, fontWeight: 700, fontFamily: "monospace" }}>
              · {it.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── ClickableChartCard ─────────────────────────────────────────────────────
//  Карточка с 30-дневным графиком, кликом открывает MetricDetailModal.
function ClickableChartCard({ title, subtitle, data, color, accent, onOpen }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  const total = (data || []).reduce((s, r) => s + r.count, 0);
  const peak  = data?.length ? Math.max(...data.map(r => r.count)) : 0;
  const avg   = data?.length ? Math.round(total / data.length) : 0;
  // Сравнение последних 7 дней vs предыдущих 7 (внутри 30-дневного окна)
  const last7  = (data || []).slice(-7).reduce((s, r) => s + r.count, 0);
  const prev7  = (data || []).slice(-14, -7).reduce((s, r) => s + r.count, 0);
  const trend  = prev7 > 0 ? Math.round(((last7 - prev7) / prev7) * 100) : (last7 > 0 ? 100 : 0);
  const trendColor = trend > 0 ? colors.green : trend < 0 ? colors.red : colors.fgSub;
  const trendIcon  = trend > 0 ? "↑" : trend < 0 ? "↓" : "→";
  return (
    <button onClick={onOpen} className="st-card"
      style={{
        padding: "16px 18px", textAlign: "left", border: `1px solid ${colors.border}`,
        background: "transparent", color: colors.fg, cursor: "pointer",
        transition: "transform .12s, border-color .12s, box-shadow .12s",
        display: "flex", flexDirection: "column", gap: 10,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = c + "80";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = colors.border;
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 4 }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 10, color: colors.fgFaint }}>{subtitle}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: trendColor, fontWeight: 700 }}>
            {trendIcon} {Math.abs(trend)}%
          </span>
          <span style={{ fontSize: 10, color: colors.fgFaint }}>vs prev 7d</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: c,
          fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
          {total.toLocaleString("en")}
        </span>
        <span style={{ fontSize: 10, color: colors.fgSub }}>last 30 days</span>
      </div>
      <BarChartSvg data={data || []} color={c} h={48} gap={1.2} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: colors.fgFaint }}>
        <span>peak <span style={{ color: c, fontWeight: 700 }}>{peak}</span></span>
        <span>avg <span style={{ color: c, fontWeight: 700 }}>{avg}</span></span>
        <span style={{ color: accent || colors.primary }}>details →</span>
      </div>
    </button>
  );
}

// ─── MetricDetailModal ──────────────────────────────────────────────────────
//  Модалка с детальной разбивкой по метрике за выбранный период.
//  Берёт данные с GET /mod/stats/series?metric=...&days=...
function MetricDetailModal({ token, metric, label, color, onClose, onNavigate }) {
  const { colors } = useTheme();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    api(`/stats/series?metric=${metric}&days=${days}`, { token })
      .then(setData)
      .catch(e => setErr(e.message || "Failed to load"));
  }, [metric, days, token]);
  const c = color || colors.primary;
  const ranges = [
    { id: 7,  label: "7d"  },
    { id: 30, label: "30d" },
    { id: 90, label: "90d" },
  ];
  return (
    <WindowModal
      windowId={_safeWindowId("metric", metric)}
      title={label || "Metric Detail"}
      onClose={onClose}
      width={1000}
      height={680}
      minWidth={520}
    >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
              letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 4 }}>Detail</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: colors.fg }}>
              {label}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {ranges.map(r => (
              <button key={r.id} onClick={() => setDays(r.id)}
                style={{
                  padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                  background: days === r.id ? c + "22" : "transparent",
                  color: days === r.id ? c : colors.fgSub,
                  border: `1px solid ${days === r.id ? c + "60" : colors.border}`,
                  cursor: "pointer",
                }}>
                {r.label}
              </button>
            ))}
            <button onClick={onClose} style={{
              background: "none", border: "none", color: colors.fgSub,
              fontSize: 18, cursor: "pointer", padding: "2px 6px",
            }}>✕</button>
          </div>
        </div>

        {err && (
          <div style={{ color: colors.red, fontSize: 13, padding: "20px 0" }}>
            Failed to load: {err}
          </div>
        )}
        {!err && !data && (
          <div style={{ padding: "40px 0", display: "flex", justifyContent: "center" }}>
            <Spinner size={22} />
          </div>
        )}
        {data && (
          <>
            {/* Сводка периодов */}
            <div style={{
              display: "grid", gap: 10, marginBottom: 18,
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            }}>
              {[
                { l: "Total",  v: data.total?.toLocaleString("en") ?? "—" },
                { l: "Avg/day",v: data.avg ?? "—" },
                { l: "Peak",   v: data.max ?? "—" },
                { l: "Prev period", v: data.prev_total?.toLocaleString("en") ?? "—" },
                { l: "Change",
                  v: `${data.change_pct > 0 ? "+" : ""}${data.change_pct ?? 0}%`,
                  c: data.change_pct > 0 ? colors.green : data.change_pct < 0 ? colors.red : colors.fgSub },
              ].map(({ l, v, c: vc }) => (
                <div key={l} style={{
                  background: colors.cardMid, borderRadius: 8,
                  padding: "10px 12px", border: `1px solid ${colors.border}`,
                }}>
                  <div style={{ fontSize: 9, color: colors.fgSub, fontWeight: 700,
                    letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>{l}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: vc || c,
                    fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Большой график */}
            <div style={{ background: colors.cardMid, borderRadius: 10,
              padding: "14px 14px 10px", border: `1px solid ${colors.border}`, marginBottom: 16 }}>
              <BarChartSvg data={data.daily} color={c} h={120} gap={1} />
              <div style={{ display: "flex", justifyContent: "space-between",
                fontSize: 9, color: colors.fgFaint, marginTop: 6 }}>
                <span>{data.daily?.[0]?.day}</span>
                <span>{data.daily?.[Math.floor((data.daily.length - 1) / 2)]?.day}</span>
                <span>{data.daily?.[data.daily.length - 1]?.day}</span>
              </div>
            </div>

            {/* Recent items */}
            {data.recent?.length > 0 && (
              <>
                <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                  letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 8 }}>
                  Recent ({data.recent.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4,
                  maxHeight: 240, overflowY: "auto" }}>
                  {data.recent.map((r, i) => (
                    <div key={r.id ?? i} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 10px", background: colors.card,
                      border: `1px solid ${colors.border}`, borderRadius: 6,
                      fontSize: 12,
                    }}>
                      <span style={{ color: colors.fgSub, fontFamily: "monospace",
                        fontSize: 10, minWidth: 40 }}>#{r.id ?? "—"}</span>
                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.name || r.title || r.email || r.snippet || r.user || `#${r.id}`}
                        {r.status && <span style={{ marginLeft: 8, fontSize: 10, color: colors.fgSub }}>· {r.status}</span>}
                      </span>
                      <span style={{ color: colors.fgFaint, fontSize: 10, flexShrink: 0 }}>
                        {r.at ? new Date(r.at).toLocaleString("en", {
                          day: "2-digit", month: "short",
                          hour: "2-digit", minute: "2-digit",
                        }) : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Кнопка перехода в соответствующую вкладку */}
            {onNavigate && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Btn variant="ghost" sm onClick={onNavigate}>
                  Open full list →
                </Btn>
              </div>
            )}
          </>
        )}
    </WindowModal>
  );
}

function KpiCard({ icon, label, value, sub, color, chart, trend }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  return (
    <div className="st-card" style={{ padding: "20px 22px", display: "flex",
      flexDirection: "column", gap: 0, position: "relative", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".09em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
          <div style={{ fontSize: 34, fontWeight: 800, color: colors.fg, lineHeight: 1,
            letterSpacing: "-.02em", fontFamily: "'JetBrains Mono', monospace" }}>{value ?? "—"}</div>
          {sub && <div style={{ fontSize: 11, color: colors.fgSub, marginTop: 6 }}>{sub}</div>}
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: c + "20",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 15, flexShrink: 0, border: `1px solid ${c}30` }}>
          {icon}
        </div>
      </div>
      {chart && <div style={{ marginTop: 4 }}>{chart}</div>}
      {trend !== undefined && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11,
          color: trend > 0 ? colors.green : trend < 0 ? colors.red : colors.fgSub, marginTop: 8 }}>
          <span style={{ fontWeight: 700 }}>{trend > 0 ? "↑" : trend < 0 ? "↓" : "→"}</span>
          <span style={{ fontWeight: 600 }}>{Math.abs(trend)}</span>
          <span style={{ color: colors.fgSub }}>this week</span>
        </div>
      )}
    </div>
  );
}

function FunnelStep({ label, value, total, color, icon }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: c + "20",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, flexShrink: 0, border: `1px solid ${c}30` }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.fgMid }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>
            {value?.toLocaleString("en") ?? "—"}
            <span style={{ fontSize: 10, color: colors.fgSub, fontWeight: 400 }}> ({pct}%)</span>
          </span>
        </div>
        <div style={{ height: 4, background: colors.border, borderRadius: 99, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: c,
            borderRadius: 99, transition: "width .8s ease" }} />
        </div>
      </div>
    </div>
  );
}

function InsightCard({ icon, color, title, text }) {
  const { colors } = useTheme();
  const c = color || colors.primary;
  return (
    <div style={{ display: "flex", gap: 12, padding: "14px 16px",
      background: c + "0c", border: `1px solid ${c}25`,
      borderRadius: 10, alignItems: "flex-start" }}>
      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12, color: colors.fgSub, lineHeight: 1.65 }}>{text}</div>
      </div>
    </div>
  );
}

function UserMini({ user, sub, onClick }) {
  const { colors } = useTheme();
  if (!user) return null;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", cursor: onClick ? "pointer" : "default" }}
      onClick={onClick}>
      <Avatar user={user} size={24} />
      <div style={{ fontSize: 12, fontWeight: 600, color: onClick ? colors.accent : colors.fg }}>{user.name || "—"}</div>
      {sub && <div style={{ fontSize: 10, color: colors.fgSub }}>{sub}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  CHAT VIEWER DRAWER
// ════════════════════════════════════════════════════════════════════════════
function ChatViewer({ uid1, uid2, token, onClose, onSelectUser }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef();
  useEffect(() => {
    api(`/chats/between/${uid1}/${uid2}`, { token }).then(d => {
      setData(d);
    }).finally(() => setLoading(false));
  }, [uid1, uid2]);
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [data]);
  const msgs = data?.messages || [];
  const u1 = data?.user1;
  const u2 = data?.user2;
  const _chatTitle = u1 && u2
    ? `${u1.name || `#${uid1}`} ↔ ${u2.name || `#${uid2}`}`
    : `Chat #${uid1} ↔ #${uid2}`;
  return (
    <WindowFrame
      windowId={`chat_${Math.min(uid1, uid2)}_${Math.max(uid1, uid2)}`}
      title={_chatTitle}
      icon="💬"
      onClose={onClose}
      defaultWidth={760}
      defaultHeight={620}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${colors.border}`,
          display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <div style={{ cursor: "pointer" }} onClick={() => onSelectUser?.(uid1)}>
              <Avatar user={u1} size={34} />
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 600, cursor: "pointer", color: colors.accent }}
                onClick={() => onSelectUser?.(uid1)}>
                {u1?.name || `#${uid1}`}
              </span>
              <span style={{ color: colors.fgSub, margin: "0 8px" }}>↔</span>
              <span style={{ fontWeight: 600, cursor: "pointer", color: colors.accent }}
                onClick={() => onSelectUser?.(uid2)}>
                {u2?.name || `#${uid2}`}
              </span>
            </div>
            {data?.chat_info?.compatibility_score && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <ScoreBar score={data.chat_info.compatibility_score} />
                {data.chat_info.match_category && <Tag color={colors.blue}>{data.chat_info.match_category}</Tag>}
              </div>
            )}
          </div>
        </div>
        {data?.chat_info?.match_reason && (
          <div style={{ padding: "12px 24px", background: colors.primaryDim,
            borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 600,
              textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>Match reason</div>
            <div style={{ fontSize: 12, color: colors.fgMid, lineHeight: 1.55,
              whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{data.chat_info.match_reason}</div>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px",
          display: "flex", flexDirection: "column", gap: 10 }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}>
              <Spinner size={32} />
            </div>
          ) : msgs.length === 0 ? (
            <div style={{ textAlign: "center", color: colors.fgSub, paddingTop: 60, fontSize: 14 }}>
              No messages
            </div>
          ) : (
            <>
              {msgs.map((m, i) => {
                const isU1 = m.sender_id === uid1;
                const isSys = m.sender_id === 0;
                const isNewDay = i === 0 || new Date(m.created_at).toDateString() !== new Date(msgs[i-1]?.created_at).toDateString();
                return (
                  <React.Fragment key={m.id}>
                    {isNewDay && (
                      <div style={{ textAlign: "center", padding: "6px 0" }}>
                        <span style={{ background: colors.cardHover, padding: "3px 10px", borderRadius: 99,
                          fontSize: 10, color: colors.fgSub, fontWeight: 600 }}>
                          {new Date(m.created_at).toLocaleDateString("en", { day: "numeric", month: "long" })}
                        </span>
                      </div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column",
                      alignItems: isSys ? "center" : isU1 ? "flex-start" : "flex-end" }}>
                      <div className={`st-chat-bubble ${isSys ? "st-chat-system" : isU1 ? "st-chat-theirs" : "st-chat-mine"}`}>
                        {m.content}
                        <span style={{ fontSize: 10, color: colors.fgFaint, marginTop: 3,
                          marginLeft: isU1 && !isSys ? 4 : 0, marginRight: !isU1 && !isSys ? 4 : 0 }}>
                          {m.created_at ? new Date(m.created_at).toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" }) : ""}
                          {!isSys && <span style={{ marginLeft: 5, color: colors.fgFaint }}>
                            {isU1 ? (u1?.name || `#${uid1}`) : (u2?.name || `#${uid2}`)}
                          </span>}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={bottomRef} />
            </>
          )}
        </div>
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${colors.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ cursor: "pointer" }} onClick={() => onSelectUser?.(uid1)}>
              <Avatar user={u1} size={26} />
              <div style={{ color: colors.fgSub, fontSize: 12, alignSelf: "center" }}>{msgs.length} messages</div>
            </div>
            <div style={{ marginLeft: "auto", cursor: "pointer" }} onClick={() => onSelectUser?.(uid2)}>
              <Avatar user={u2} size={26} />
            </div>
          </div>
        </div>
      </div>
    </WindowFrame>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  USER DRAWER
// ════════════════════════════════════════════════════════════════════════════
function UserDrawer({ userId, token, onClose, onSelectUser, onOpenChat }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [actLoading, setActLoading] = useState(false);
  const toast = useToast();
  const load = () => {
    setLoading(true);
    api(`/users/${userId}`, { token }).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [userId]);
  async function doPost(path, body, msg, method = "POST") {
    setActLoading(true);
    try {
      await api(`/users/${userId}${path}`, { method, body, token });
      toast(msg);
      setModal(null);
      load();
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(false); }
  }
  // Business card state — hooks must be before any early returns
  const [cardData, setCardData] = React.useState(null);
  const [cardForm, setCardForm] = React.useState({ phone: "", company: "", address: "" });
  const [cardEditing, setCardEditing] = React.useState(false);
  const [cardSaving, setCardSaving] = React.useState(false);
  const loadCard = React.useCallback(() => {
    api(`/users/${userId}/business-card`, { token }).then(d => {
      setCardData(d);
      setCardForm({ phone: d.phone || "", company: d.company || "", address: d.address || "" });
    }).catch(() => {});
  }, [userId, token]);
  React.useEffect(() => { if (userId) loadCard(); }, [userId]);
  async function saveCard() {
    setCardSaving(true);
    try {
      await api(`/users/${userId}/business-card`, { method: "PUT", body: cardForm, token });
      toast("Business card saved");
      setCardEditing(false);
      loadCard();
    } catch(e) { toast(e.message, "err"); }
    finally { setCardSaving(false); }
  }

  if (!data && !loading) return null;
  const u = data?.user;
  const stats = data?.stats || {};
  const tabDefs = [
    { id: "info",     label: "Profile" },
    { id: "card",     label: "Business Card" },
    { id: "matches",  label: `Matches (${data?.matches?.length || 0})` },
    { id: "chats",    label: `Chats (${data?.chats?.length || 0})` },
    { id: "events",   label: `Events (${data?.events?.length || 0})` },
    { id: "requests", label: `Requests (${data?.search_requests?.length || 0})` },
    { id: "actions",  label: "Actions" },
  ];
  const _userTitle = data?.user
    ? `${data.user.name} · #${userId}`
    : `User #${userId}`;
  return (
    <WindowFrame
      windowId={`user_${userId}`}
      title={_userTitle}
      icon="👤"
      onClose={onClose}
      defaultWidth={960}
      defaultHeight={720}
      bodyPadding={24}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}><Spinner size={36} /></div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 20 }}>
            <Avatar user={u} size={56} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{u.name}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                <RoleTag r={u.role} />
                {u.oauth_provider && <Tag color={colors.blue}>{u.oauth_provider}</Tag>}
                {!u.has_password && <Tag color={colors.fgSub}>no password</Tag>}
                {stats.is_blacklisted && <Tag color={colors.red}>Blacklisted</Tag>}
                <div className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                  #{u.id} · {u.email || u.username || "no email"}
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
            {[
              ["Matches", stats.match_count, colors.primary],
              ["Active Matches", stats.active_match_count, colors.green],
              ["Chats", stats.chat_count, colors.cyan],
              ["Sent", stats.sent_messages, colors.fg],
              ["Events", stats.event_count, colors.blue],
              ["Invites", `${stats.invites_used}/${stats.invites_sent}`, colors.orange],
            ].map(([l, v, c]) => (
              <div key={l} style={{ background: colors.cardMid, border: `1px solid ${colors.border}`,
                borderRadius: 8, padding: "8px 12px", textAlign: "center", minWidth: 64 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: c }}>{v ?? 0}</div>
                <div style={{ fontSize: 9, color: colors.fgSub, fontWeight: 600, textTransform: "uppercase",
                  letterSpacing: ".06em", marginTop: 2 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 20 }}>
            <PillTabs tabs={tabDefs} active={tab} onChange={setTab} />
          </div>
          {tab === "info" && (
            <div className="st-fade">
              {u.about && (
                <>
                  <SectionTitle>About</SectionTitle>
                  <p style={{ fontSize: 13, color: colors.fgMid, lineHeight: 1.7, marginBottom: 18 }}>{u.about}</p>
                </>
              )}
              {u.request && (
                <>
                  <SectionTitle>Request</SectionTitle>
                  <p style={{ fontSize: 13, color: colors.fgMid, lineHeight: 1.7, marginBottom: 18 }}>{u.request}</p>
                </>
              )}
              <SectionTitle>Metadata</SectionTitle>
              <div className="st-meta-grid">
                <MetaCell label="ID"         value={u.id}               copy />
                <MetaCell label="Registered" value={u.created_at ? new Date(u.created_at).toLocaleDateString("en") : "—"} />
                <MetaCell label="Email"      value={u.email || "—"}     copy />
                <MetaCell label="Username"   value={u.username || "—"}  copy />
                <MetaCell label="OAuth"      value={u.oauth_provider || "—"} />
                <MetaCell label="Active Event" value={u.active_event_id || "—"} />
                <MetaCell label="About Updated" value={u.about_last_updated ? new Date(u.about_last_updated).toLocaleDateString("en") : "—"} />
                <MetaCell label="Password"   value={u.has_password ? "Set" : "None"} />
              </div>
              {u.social_links && Object.keys(u.social_links).length > 0 && (
                <>
                  <SectionTitle>Social Links</SectionTitle>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
                    {Object.entries(u.social_links).map(([k, v]) => (
                      <a key={k} href={v} target="_blank" rel="noreferrer" title={v}
                        style={{ background: colors.primaryDim, border: `1px solid ${colors.borderMid}`,
                          borderRadius: 7, padding: "5px 12px", fontSize: 12, color: colors.accent,
                          textDecoration: "none", maxWidth: "100%", overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {k}: {v}
                      </a>
                    ))}
                  </div>
                </>
              )}
              {data.ban_history?.length > 0 && (
                <>
                  <SectionTitle>Ban History</SectionTitle>
                  {data.ban_history.map((b, i) => (
                    <div key={i} style={{ background: colors.redDim, border: `1px solid rgba(192,57,43,.2)`,
                      borderRadius: 8, padding: "10px 14px", marginBottom: 8, fontSize: 12,
                      whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.55 }}>
                      <span style={{ color: colors.red, fontWeight: 600 }}>
                        {b.at ? new Date(b.at).toLocaleString("en") : "—"}
                      </span>
                      {" · "}{b.by}{b.reason && ` · «${b.reason}»`}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
          {tab === "card" && (
            <div className="st-fade">
              <SectionTitle>Business Card</SectionTitle>
              {!cardEditing ? (
                <div>
                  <div className="st-meta-grid" style={{ marginBottom: 16 }}>
                    <div className="st-meta-cell">
                      <div className="st-meta-key">📞 Phone</div>
                      <div className="st-meta-val">{cardData?.phone || "—"}</div>
                    </div>
                    <div className="st-meta-cell">
                      <div className="st-meta-key">🏢 Company</div>
                      <div className="st-meta-val">{cardData?.company || "—"}</div>
                    </div>
                    <div className="st-meta-cell" style={{ gridColumn: "1 / -1" }}>
                      <div className="st-meta-key">📍 Address</div>
                      <div className="st-meta-val" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{cardData?.address || "—"}</div>
                    </div>
                  </div>
                  <Btn sm onClick={() => setCardEditing(true)}>✏️ Edit</Btn>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, color: colors.fgSub, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".07em", display: "block", marginBottom: 5 }}>Phone</label>
                    <input className="st-input" placeholder="+1 234 567 8900"
                      value={cardForm.phone} onChange={e => setCardForm(f => ({...f, phone: e.target.value}))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: colors.fgSub, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".07em", display: "block", marginBottom: 5 }}>Company</label>
                    <input className="st-input" placeholder="Acme Corp"
                      value={cardForm.company} onChange={e => setCardForm(f => ({...f, company: e.target.value}))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: colors.fgSub, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".07em", display: "block", marginBottom: 5 }}>Address</label>
                    <textarea className="st-input" rows={3} placeholder="Street, City, Country"
                      value={cardForm.address} onChange={e => setCardForm(f => ({...f, address: e.target.value}))}
                      style={{ resize: "vertical" }} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn loading={cardSaving} onClick={saveCard}>Save</Btn>
                    <Btn variant="ghost" sm onClick={() => { setCardEditing(false); setCardForm({ phone: cardData?.phone||"", company: cardData?.company||"", address: cardData?.address||"" }); }}>Cancel</Btn>
                  </div>
                </div>
              )}
            </div>
          )}
          {tab === "matches" && (
            <div className="st-fade">
              {!data.matches?.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 32, fontSize: 13 }}>No matches</div>
              ) : data.matches.map(m => (
                <div key={m.match_id} style={{ padding: "12px 0", borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ cursor: "pointer" }} onClick={() => m.partner && onSelectUser(m.partner.id)}>
                      <Avatar user={m.partner} size={30} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: colors.accent, cursor: "pointer" }}
                          onClick={() => m.partner && onSelectUser(m.partner.id)}>
                          {m.partner?.name || "—"}
                        </span>
                        <MatchStatus s={m.status} />
                        {m.category && <Tag color={colors.blue}>{m.category}</Tag>}
                        {m.chat_created && (
                          <button onClick={() => m.partner && onOpenChat(userId, m.partner.id)}
                            style={{ background: colors.greenDim, border: `1px solid ${colors.green}44`, borderRadius: 6,
                              padding: "2px 8px", fontSize: 11, color: colors.green, cursor: "pointer",
                              fontFamily: "inherit", fontWeight: 600 }}>
                            Chat
                          </button>
                        )}
                      </div>
                      {m.compatibility_score && (
                        <div style={{ marginBottom: 4 }}><ScoreBar score={m.compatibility_score} /></div>
                      )}
                      {m.match_reason && (
                        <p style={{ fontSize: 11, color: colors.fgSub, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {m.match_reason}
                        </p>
                      )}
                      <div className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, marginTop: 3 }}>
                        #{m.match_id} · Me: {m.my_status || "—"} · Partner: {m.partner_status || "—"}
                        {m.created_at ? " · " + new Date(m.created_at).toLocaleDateString("en") : ""}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === "chats" && (
            <div className="st-fade">
              {!data.chats?.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 32, fontSize: 13 }}>No chats</div>
              ) : data.chats.map(ch => (
                <div key={ch.chat_id} style={{ padding: "12px 0", borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ cursor: "pointer" }} onClick={() => ch.partner && onSelectUser(ch.partner.id)}>
                      <Avatar user={ch.partner} size={32} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: colors.accent, cursor: "pointer" }}
                          onClick={() => ch.partner && onSelectUser(ch.partner.id)}>
                          {ch.partner?.name || `#${ch.partner_id}`}
                        </span>
                        {ch.match_category && <Tag color={colors.blue}>{ch.match_category}</Tag>}
                        {ch.compatibility_score && <ScoreBar score={ch.compatibility_score} />}
                      </div>
                      {ch.last_message?.content && (
                        <p style={{ fontSize: 12, color: colors.fgSub, lineHeight: 1.5 }}>
                          {ch.last_message.is_mine ? "Me: " : ""}{ch.last_message.content}
                        </p>
                      )}
                      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 5 }}>
                        <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                          {ch.message_count} msgs
                          {ch.last_message_at ? " · " + new Date(ch.last_message_at).toLocaleDateString("en") : ""}
                        </span>
                        <button onClick={() => onOpenChat(userId, ch.partner_id)}
                          style={{ background: colors.primaryDim, border: `1px solid ${colors.borderMid}`,
                            borderRadius: 6, padding: "3px 10px", fontSize: 11, color: colors.accent,
                            cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                          View →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === "events" && (
            <div className="st-fade">
              {!data.events?.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 32, fontSize: 13 }}>No events</div>
              ) : data.events.map(e => (
                <div key={e.id} style={{ padding: "10px 0", borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{e.title}</div>
                  <div className="st-mono" style={{ fontSize: 11, color: colors.fgSub }}>
                    {e.event_date || "—"} · {e.location || "—"}
                  </div>
                  <div style={{ marginTop: 4 }}><EventStatusTag s={e.status} /></div>
                </div>
              ))}
            </div>
          )}
          {tab === "requests" && (
            <div className="st-fade">
              {!data.search_requests?.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 32, fontSize: 13 }}>No requests</div>
              ) : data.search_requests.map(sr => (
                <div key={sr.id} style={{ padding: "12px 0", borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{sr.title || "Untitled"}</span>
                    {sr.category && <Tag color={colors.cyan}>{sr.category}</Tag>}
                    <Tag color={sr.is_active ? colors.green : colors.fgSub}>{sr.status}</Tag>
                    <span className="st-mono" style={{ fontSize: 11, color: colors.fgSub, marginLeft: "auto" }}>
                      {sr.total_matches} matches
                    </span>
                  </div>
                  {sr.request_text && (
                    <p style={{ fontSize: 12, color: colors.fgSub, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {sr.request_text}
                    </p>
                  )}
                  {sr.created_at && (
                    <div className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, marginTop: 4 }}>
                      {new Date(sr.created_at).toLocaleDateString("en")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {tab === "actions" && (
            <div className="st-fade" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ background: colors.cardMid, border: `1px solid ${colors.border}`,
                borderRadius: 10, padding: 16 }}>
                <SectionTitle>Access Control</SectionTitle>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Btn sm onClick={() => { setForm({}); setModal("message"); }}>✉ Message</Btn>
                  <Btn sm variant="ghost" onClick={() => { setForm({}); setModal("password"); }}>🔑 Change Password</Btn>
                  <Btn sm variant="ghost" onClick={() => { setForm({ role: u.role }); setModal("role"); }}>Change Role</Btn>
                </div>
              </div>
              <div style={{ background: colors.cardMid, border: `1px solid ${colors.border}`,
                borderRadius: 10, padding: 16 }}>
                <SectionTitle>Wallet</SectionTitle>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <Btn sm onClick={() => { setForm({ amount: "", note: "" }); setModal("balance"); }}>
                    💰 Adjust Balance
                  </Btn>
                  <span style={{ fontSize: 11, color: colors.fgSub }}>
                    + add, − deduct
                  </span>
                </div>
              </div>
              <div style={{ background: colors.redDim, border: `1px solid rgba(192,57,43,.2)`,
                borderRadius: 10, padding: 16 }}>
                <SectionTitle>Danger Zone</SectionTitle>
                <Btn variant="danger" sm onClick={() => setModal("delete")}>🗑 Delete User</Btn>
              </div>
            </div>
          )}
          {modal === "message" && (
            <FormModal title="System Message" windowId={`user_${userId}_message`} onClose={() => setModal(null)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <textarea className="st-input" rows={4} placeholder="Message text..."
                  value={form.content || ""} onChange={e => setForm({ content: e.target.value })}
                  style={{ resize: "vertical" }} />
                <Btn loading={actLoading}
                  onClick={() => doPost("/send-message", { content: form.content }, "Message sent")}>
                  Send
                </Btn>
              </div>
            </FormModal>
          )}
          {modal === "password" && (
            <FormModal title="Change Password" windowId={`user_${userId}_password`} onClose={() => setModal(null)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <input className="st-input" type="password" placeholder="New password (min. 6 chars)"
                  value={form.pass || ""} onChange={e => setForm({ pass: e.target.value })} />
                <Btn loading={actLoading}
                  onClick={() => doPost("/change-password", { new_password: form.pass }, "Password changed")}>
                  Save
                </Btn>
              </div>
            </FormModal>
          )}
          {modal === "role" && (
            <FormModal title="Change Role" windowId={`user_${userId}_role`} onClose={() => setModal(null)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <select className="st-input" value={form.role || "user"}
                  onChange={e => setForm({ role: e.target.value })}>
                  {["user", "event_user", "event_admin", "admin"].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <Btn loading={actLoading}
                  onClick={() => doPost("/change-role", { role: form.role }, "Role updated")}>
                  Apply
                </Btn>
              </div>
            </FormModal>
          )}
          {modal === "balance" && (
            <FormModal title={`Adjust Balance — ${u.name}`} windowId={`user_${userId}_balance`} onClose={() => setModal(null)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 12, color: colors.fgSub }}>
                  Positive amount adds, negative deducts.
                </div>
                <input className="st-input" type="text" inputMode="decimal" autoFocus
                  placeholder="Amount, e.g.: 100 or -25.50"
                  value={form.amount ?? ""}
                  onChange={e => setForm({ ...form, amount: e.target.value })} />
                <input className="st-input" type="text" maxLength={200}
                  placeholder="Comment (optional)"
                  value={form.note ?? ""}
                  onChange={e => setForm({ ...form, note: e.target.value })} />
                <Btn loading={actLoading} onClick={async () => {
                  const amt = parseFloat(String(form.amount ?? "").replace(",", "."));
                  if (!isFinite(amt) || amt === 0) {
                    toast("Amount must be a number ≠ 0", "err");
                    return;
                  }
                  setActLoading(true);
                  try {
                    const res = await api(`/wallets/${userId}/adjust`, {
                      method: "POST",
                      body: { amount: amt, note: form.note || null },
                      token,
                    });
                    toast(`Balance: ${res.old_balance.toFixed(2)} → ${res.new_balance.toFixed(2)} ${u.currency || "EUR"}`);
                    setModal(null);
                  } catch (e) {
                    toast(e.message, "err");
                  } finally {
                    setActLoading(false);
                  }
                }}>
                  Apply
                </Btn>
              </div>
            </FormModal>
          )}
          {modal === "delete" && (
            <FormModal title="Delete User" windowId={`user_${userId}_delete`} onClose={() => setModal(null)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ background: colors.redDim, border: `1px solid rgba(192,57,43,.3)`,
                  borderRadius: 8, padding: "12px 14px", fontSize: 13, color: colors.red }}>
                  ⚠ This action is irreversible. All data will be permanently deleted.
                </div>
                <input className="st-input" placeholder="Reason (optional)"
                  value={form.reason || ""} onChange={e => setForm({ ...form, reason: e.target.value })} />
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: colors.fgMid }}>
                  <input type="checkbox" checked={form.bl || false}
                    onChange={e => setForm({ ...form, bl: e.target.checked })} />
                  Add email to blacklist
                </label>
                <Btn variant="danger" loading={actLoading}
                  onClick={() => {
                    doPost("", { reason: form.reason, add_to_blacklist: form.bl || false },
                      "User deleted", "DELETE").then(() => onClose());
                  }}>
                  Delete permanently
                </Btn>
              </div>
            </FormModal>
          )}
        </>
      )}
    </WindowFrame>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  PARTICIPANT DETAIL MODAL
// ════════════════════════════════════════════════════════════════════════════
function ParticipantDetailModal({ eventId, userId, token, onClose, onSelectUser, onOpenChat }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const toast = useToast();
  useEffect(() => {
    api(`/events/${eventId}/participants/${userId}/detail`, { token })
      .then(setData).finally(() => setLoading(false));
  }, [eventId, userId]);
  async function changeStatus(s) {
    setStatusLoading(true);
    try {
      await api(`/events/${eventId}/participants/${userId}/status`, { method: "POST", body: { new_status: s }, token });
      toast(`Status: ${s}`);
      setData(d => d ? { ...d, registration: { ...d.registration, status: s } } : d);
    } catch (e) { toast(e.message, "err"); }
    finally { setStatusLoading(false); }
  }
  const u = data?.user;
  const reg = data?.registration;
  return (
    <WindowModal
      windowId={`participant_${eventId}_${userId}`}
      title="Participant Details"
      onClose={onClose}
      width={1000}
      height={680}
      minWidth={520}
    >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, alignItems: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: colors.fg }}>Participant Details</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: colors.fgSub, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={28} /></div>
        ) : !u ? (
          <div style={{ color: colors.fgSub, textAlign: "center" }}>Not found</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16 }}>
              <div style={{ cursor: "pointer" }} onClick={() => { onClose(); onSelectUser(u.id); }}>
                <Avatar user={u} size={48} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: colors.accent, cursor: "pointer" }}
                  onClick={() => { onClose(); onSelectUser(u.id); }}>{u.name}</div>
                <div className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                  #{u.id} · {u.email || "—"}
                </div>
                {data.phone && (
                  <div style={{ fontSize: 12, color: colors.green, marginTop: 4 }}>📞 {data.phone}</div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <Tag color={reg?.status === "registered" ? colors.green : colors.orange}>{reg?.status}</Tag>
                <div className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, marginTop: 4 }}>
                  {reg?.registered_at ? new Date(reg.registered_at).toLocaleDateString("en") : "—"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: colors.fgSub, alignSelf: "center" }}>Status: </span>
              {["registered", "pending", "attended", "cancelled"].map(s => (
                <Btn key={s} sm variant={reg?.status === s ? "primary" : "ghost"}
                  loading={statusLoading} onClick={() => changeStatus(s)}>
                  {s}
                </Btn>
              ))}
            </div>
            {u.about && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700, letterSpacing: ".07em",
                  textTransform: "uppercase", marginBottom: 4 }}>About</div>
                <p style={{ fontSize: 12, color: colors.fgMid, lineHeight: 1.6 }}>{u.about}</p>
              </div>
            )}
            {u.request && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700, letterSpacing: ".07em",
                  textTransform: "uppercase", marginBottom: 4 }}>Request</div>
                <p style={{ fontSize: 12, color: colors.fgMid, lineHeight: 1.6 }}>{u.request}</p>
              </div>
            )}
            {data.search_requests?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700, letterSpacing: ".07em",
                  textTransform: "uppercase", marginBottom: 8 }}>
                  Search Requests ({data.search_requests.length})
                </div>
                {data.search_requests.map(sr => (
                  <div key={sr.id} style={{ background: colors.cardMid, border: `1px solid ${colors.border}`,
                    borderRadius: 8, padding: "10px 12px", marginBottom: 6 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, fontSize: 12, color: colors.fg }}>{sr.title || "Untitled"}</span>
                      {sr.category && <Tag color={colors.cyan}>{sr.category}</Tag>}
                      <Tag color={sr.is_active ? colors.green : colors.fgSub}>{sr.is_active ? "active" : "inactive"}</Tag>
                      <span className="st-mono" style={{ fontSize: 11, color: colors.primary, marginLeft: "auto" }}>
                        {sr.total_matches} matches
                      </span>
                    </div>
                    {sr.request_text && (
                      <p style={{ fontSize: 11, color: colors.fgSub, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {sr.request_text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {data.matches?.length > 0 && (
              <div>
                <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700, letterSpacing: ".07em",
                  textTransform: "uppercase", marginBottom: 8 }}>
                  Matches ({data.matches.length})
                </div>
                {data.matches.slice(0, 10).map(m => {
                  const partner = m.user1?.id === userId ? m.user2 : m.user1;
                  return (
                    <div key={m.id} style={{ display: "flex", gap: 10, alignItems: "center",
                      padding: "8px 0", borderBottom: `1px solid ${colors.border}` }}>
                      <div style={{ cursor: "pointer" }} onClick={() => { onClose(); onSelectUser(partner?.id); }}>
                        <Avatar user={partner} size={28} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, fontSize: 12, color: colors.accent, cursor: "pointer" }}
                          onClick={() => { onClose(); onSelectUser(partner?.id); }}>
                          {partner?.name || "—"}
                        </span>
                        <ScoreBar score={m.compatibility_score} />
                        <MatchStatus s={m.status} />
                        {m.chat_created && (
                          <button onClick={() => { onClose(); onOpenChat(userId, partner?.id); }}
                            style={{ background: colors.greenDim, border: "none", borderRadius: 6,
                              padding: "3px 7px", fontSize: 11, color: colors.green, cursor: "pointer" }}>◈</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
    </WindowModal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  EVENT DRAWER
// ════════════════════════════════════════════════════════════════════════════
function EventDrawer({ eventId, token, onClose, onSelectUser, onOpenChat }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");
  const [actLoading, setActLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [openParticipantIds, setOpenParticipantIds] = useState([]);
  const [pSearch, setPSearch] = useState("");
  const [pStatusF, setPStatusF] = useState("");
  const [mStatusF, setMStatusF] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recreateOpen, setRecreateOpen] = useState(false);
  const [adminPicker, setAdminPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerRoleF, setPickerRoleF] = useState("");
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const pickerRef = useRef();
  const toast = useToast();
  const confirm = useConfirm();
  const winMgr = useWindowMgr();
  function openParticipant(uid) {
    if (!uid) return;
    const id = Number(uid);
    setOpenParticipantIds(prev => prev.includes(id) ? prev : [...prev, id]);
    winMgr.bringToFront(`participant_${eventId}_${id}`);
  }
  function closeParticipant(uid) {
    const id = Number(uid);
    setOpenParticipantIds(prev => prev.filter(x => x !== id));
  }
  const load = () => {
    setLoading(true);
    api(`/events/${eventId}`, { token }).then(d => {
      setData(d);
      const ev = d?.event || {};
      setEditForm({
        title: ev.title || "", location: ev.location || "",
        event_date: ev.event_date || "", event_time: ev.event_time || "",
        capacity: ev.capacity ?? "", organizer_name: ev.organizer_name || "",
        is_free: ev.is_free ?? true, ticket_price: ev.ticket_price ?? "",
        currency: ev.currency || "EUR", allow_external_matching: ev.allow_external_matching ?? false,
        description: ev.description || "",
      });
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [eventId]);
  useEffect(() => {
    if (!adminPicker) return;
    clearTimeout(pickerRef.current);
    pickerRef.current = setTimeout(() => {
      setPickerLoading(true);
      const p = pickerSearch ? `?search=${encodeURIComponent(pickerSearch)}` : "";
      api(`/users/simple${p}`, { token }).then(setPickerResults).finally(() => setPickerLoading(false));
    }, 300);
  }, [pickerSearch, adminPicker]);
  async function saveEdit() {
    setActLoading(true);
    try {
      const body = { ...editForm };
      if (body.capacity === "" || body.capacity === null) delete body.capacity;
      else body.capacity = Number(body.capacity);
      if (body.ticket_price === "" || body.ticket_price === null) delete body.ticket_price;
      else body.ticket_price = Number(body.ticket_price);
      await api(`/events/${eventId}`, { method: "PUT", body, token });
      toast("Event updated");
      setEditMode(false);
      load();
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(false); }
  }
  async function setStatus(s) {
    setActLoading(true);
    try {
      await api(`/events/${eventId}/status`, { method: "POST", body: { new_status: s }, token });
      toast(`Status → ${s}`);
      setData(d => d ? { ...d, event: { ...d.event, status: s } } : d);
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(false); }
  }
  async function deleteEvent() {
    setActLoading(true);
    try {
      await api(`/events/${eventId}`, { method: "DELETE", token });
      toast("Event deleted");
      onClose();
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(false); setConfirmDelete(false); }
  }
  async function removeParticipant(uid) {
    const ok = await confirm("Remove participant?", "The participant will be removed from this event.");
    if (!ok) return;
    try {
      await api(`/events/${eventId}/participants/${uid}`, { method: "DELETE", token });
      toast("Participant removed");
      setData(d => d ? { ...d, participants: d.participants.filter(p => p.user?.id !== uid),
        stats: { ...d.stats, participants: (d.stats?.participants || 1) - 1 } } : d);
    } catch (e) { toast(e.message, "err"); }
  }
  async function toggleSR(srId, curActive) {
    try {
      const r = await api(`/events/${eventId}/search-requests/${srId}/toggle`, { method: "POST", token });
      setData(d => d ? { ...d, search_requests: d.search_requests.map(sr =>
        sr.id === srId ? { ...sr, is_active: r.is_active } : sr) } : d);
    } catch (e) { toast(e.message, "err"); }
  }
  async function deleteSR(srId) {
    const ok = await confirm("Delete request?", `Search request #${srId} will be deleted.`);
    if (!ok) return;
    try {
      await api(`/events/${eventId}/search-requests/${srId}`, { method: "DELETE", token });
      toast("Request deleted");
      setData(d => d ? { ...d, search_requests: d.search_requests.filter(sr => sr.id !== srId),
        stats: { ...d.stats, search_requests: (d.stats?.search_requests || 1) - 1 } } : d);
    } catch (e) { toast(e.message, "err"); }
  }
  async function addEventAdmin(uid) {
    try {
      await api(`/events/${eventId}/admins`, { method: "POST", body: { user_id: uid }, token });
      toast("Admin added");
      load();
      setAdminPicker(false);
    } catch (e) { toast(e.message, "err"); }
  }
  async function removeEventAdmin(uid) {
    const ok = await confirm("Remove admin role?", `User #${uid} will lose event admin privileges.`);
    if (!ok) return;
    try {
      await api(`/events/${eventId}/admins/${uid}`, { method: "DELETE", token });
      toast("Admin removed");
      setData(d => d ? { ...d, event_admins: d.event_admins.filter(ea => ea.user?.id !== uid) } : d);
    } catch (e) { toast(e.message, "err"); }
  }
  function exportCSV() {
    window.open(`${window.API_URL || ""}/api/mod/events/${eventId}/export?token=${token}`, "_blank");
  }
  const filteredParticipants = useMemo(() => {
    if (!data?.participants) return [];
    return data.participants.filter(p => {
      const u = p.user || {};
      const matchSearch = !pSearch || (u.name || "").toLowerCase().includes(pSearch.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(pSearch.toLowerCase());
      const matchStatus = !pStatusF || p.status === pStatusF;
      return matchSearch && matchStatus;
    });
  }, [data?.participants, pSearch, pStatusF]);
  const filteredMatches = useMemo(() => {
    if (!data?.matches) return [];
    return data.matches.filter(m => !mStatusF || m.status === mStatusF);
  }, [data?.matches, mStatusF]);
  if (!data && !loading) return null;
  const ev = data?.event || {};
  const stats = data?.stats || {};
  const tabDefs = [
    { id: "info",         label: "General" },
    { id: "participants", label: `Participants (${data?.participants?.length || 0})` },
    { id: "matches",      label: `Matches (${data?.matches?.length || 0})` },
    { id: "requests",     label: `Requests (${data?.search_requests?.length || 0})` },
    { id: "referrals",    label: `Referrals (${data?.referrals?.length || 0})` },
    { id: "admins",       label: `Admins (${data?.event_admins?.length || 0})` },
  ];
  return (
    <WindowFrame
      windowId={`event_${eventId}`}
      title={ev.title || `Event #${eventId}`}
      onClose={onClose}
      defaultWidth={1120}
      defaultHeight={760}
      minWidth={620}
      minHeight={420}
      bodyPadding={24}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}><Spinner size={36} /></div>
      ) : (
        <div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{ev.title}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <EventStatusTag s={ev.status} />
                {ev.is_free ? <Tag color={colors.green}>Free</Tag> : <Tag color={colors.orange}>{ev.ticket_price} {ev.currency}</Tag>}
                {ev.allow_external_matching && <Tag color={colors.primary}>External Matching</Tag>}
                {ev.capacity && <Tag color={colors.fgSub}>Cap. {ev.capacity}</Tag>}
              </div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none",
              color: colors.fgSub, fontSize: 18, cursor: "pointer", flexShrink: 0 }}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {[["Participants", stats.participants, colors.fg],
              ["Requests", stats.search_requests, colors.primary],
              ["Matches", stats.matches, colors.green],
              ["Referrals", stats.referrals, colors.cyan]].map(([l, v, c]) => (
              <div key={l} style={{ background: colors.cardMid, border: `1px solid ${colors.border}`,
                borderRadius: 8, padding: "8px 14px", textAlign: "center", flex: 1, minWidth: 80 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: c }}>{v || 0}</div>
                <div style={{ fontSize: 9, color: colors.fgSub, textTransform: "uppercase",
                  letterSpacing: ".07em", fontWeight: 600 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 20 }}>
            <PillTabs tabs={tabDefs} active={tab} onChange={setTab} />
          </div>
          {tab === "info" && (
            <>
              {!editMode ? (
                <>
                  <div className="st-meta-grid" style={{ marginBottom: 16 }}>
                    <MetaCell label="Date" value={ev.event_date || "—"} />
                    <MetaCell label="Time" value={ev.event_time || "—"} />
                    <MetaCell label="Location" value={ev.location || "—"} />
                    <MetaCell label="Organizer" value={ev.organizer_name || "—"} />
                    <MetaCell label="Capacity" value={ev.capacity ?? "∞"} />
                    <MetaCell label="Invite code" value={ev.invite_code || "—"} />
                    <MetaCell label="Slug" value={ev.slug || "—"} />
                    <MetaCell label="Created" value={ev.created_at ? new Date(ev.created_at).toLocaleString("en") : "—"} />
                  </div>
                  {ev.description && (
                    <div style={{ marginBottom: 16 }}>
                      <SectionTitle>Description</SectionTitle>
                      <p style={{ fontSize: 13, color: colors.fgMid, lineHeight: 1.7 }}>{ev.description}</p>
                    </div>
                  )}
                  {ev.matching_categories?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <SectionTitle>Matching Categories</SectionTitle>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {ev.matching_categories.map(c => <Tag key={c} color={colors.primary}>{c}</Tag>)}
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                    <SectionTitle style={{ alignSelf: "center" }}>Event Status: </SectionTitle>
                    {["published", "archived", "cancelled"].map(s => (
                      <Btn key={s} sm variant={ev.status === s ? "primary" : "ghost"}
                        loading={actLoading} onClick={() => setStatus(s)}>
                        {s}
                      </Btn>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, paddingTop: 16,
                    borderTop: `1px solid ${colors.border}`, flexWrap: "wrap" }}>
                    <Btn variant="ghost" onClick={() => setEditMode(true)}>✏ Edit</Btn>
                    <Btn variant="ghost" onClick={exportCSV}>↓ Export CSV</Btn>
                    <Btn variant="primary" onClick={() => setRecreateOpen(true)}>
                      ↻ Recreate
                    </Btn>
                    <Btn variant="danger" sm style={{ marginLeft: "auto" }}
                      onClick={() => setConfirmDelete(true)}>🗑 Delete Event</Btn>
                  </div>
                </>
              ) : (
                <>
                  <SectionTitle>Edit Event</SectionTitle>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                    {[
                      ["Title", "title", "text"],
                      ["Location", "location", "text"],
                      ["Date (YYYY-MM-DD)", "event_date", "date"],
                      ["Time (HH:MM)", "event_time", "time"],
                      ["Capacity", "capacity", "number"],
                      ["Organizer", "organizer_name", "text"],
                      ["Price", "ticket_price", "number"],
                      ["Currency", "currency", "text"],
                    ].map(([label, key, type]) => (
                      <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                          letterSpacing: ".07em", textTransform: "uppercase" }}>{label}</span>
                        <input type={type} className="st-input"
                          value={editForm[key] ?? ""}
                          onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} />
                      </label>
                    ))}
                  </div>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                    <span style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                      letterSpacing: ".07em", textTransform: "uppercase" }}>Description</span>
                    <textarea className="st-input" rows={3}
                      value={editForm.description ?? ""}
                      onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                      style={{ resize: "vertical" }} />
                  </label>
                  <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                      <input type="checkbox" checked={editForm.is_free ?? true}
                        onChange={e => setEditForm(f => ({ ...f, is_free: e.target.checked }))} />
                      <span style={{ fontSize: 13, color: colors.fgMid }}>Free</span>
                    </label>
                    <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                      <input type="checkbox" checked={editForm.allow_external_matching ?? false}
                        onChange={e => setEditForm(f => ({ ...f, allow_external_matching: e.target.checked }))} />
                      <span style={{ fontSize: 13, color: colors.fgMid }}>External Matching</span>
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn variant="ghost" onClick={() => setEditMode(false)}>Cancel</Btn>
                    <Btn loading={actLoading} onClick={saveEdit}>Save</Btn>
                  </div>
                </>
              )}
            </>
          )}
          {tab === "participants" && (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                <input className="st-input" style={{ maxWidth: 220 }} placeholder="Search by name, email..."
                  onChange={e => setPSearch(e.target.value)} />
                <select className="st-input" style={{ width: 130 }} value={pStatusF}
                  onChange={e => setPStatusF(e.target.value)}>
                  <option value="">All statuses</option>
                  {["registered", "pending", "attended", "cancelled"].map(s =>
                    <option key={s} value={s}>{s}</option>
                  )}
                </select>
                <span style={{ fontSize: 11, color: colors.fgSub, marginLeft: "auto" }}>
                  {filteredParticipants.length} / {data?.participants?.length || 0}
                </span>
                <Btn sm variant="ghost" onClick={exportCSV}>↓ CSV</Btn>
              </div>
              {!filteredParticipants.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 32 }}>No participants</div>
              ) : filteredParticipants.map((p, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "center",
                  padding: "10px 0", borderBottom: `1px solid ${colors.border}`,
                  cursor: "pointer" }}
                  onClick={() => openParticipant(p.user?.id)}>
                  <Avatar user={p.user} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: colors.accent }}>
                        {p.user?.name || "—"}
                      </span>
                      <Tag color={p.status === "registered" ? colors.green : p.status === "attended"
                        ? colors.cyan : colors.orange}>{p.status}</Tag>
                      {p.search_request_count > 0 && <Tag color={colors.primary}>{p.search_request_count} req.</Tag>}
                      {p.match_count > 0 && <Tag color={colors.green}>{p.match_count} matches</Tag>}
                    </div>
                    <div className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, marginTop: 2 }}>
                      #{p.user?.id} · {p.user?.email || "—"} ·{" "}
                      {p.registered_at ? new Date(p.registered_at).toLocaleDateString("en") : "—"}
                    </div>
                  </div>
                  <Btn sm variant="danger" onClick={e => { e.stopPropagation(); removeParticipant(p.user?.id); }}>✕</Btn>
                </div>
              ))}
            </>
          )}
          {tab === "matches" && (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                {["", "active", "pending", "rejected", "cancelled"].map(s => (
                  <button key={s || "all"} onClick={() => setMStatusF(s)} style={{
                    padding: "6px 10px", borderRadius: 7, border: `1px solid ${s === mStatusF ? colors.primary : colors.border}`,
                    background: s === mStatusF ? colors.primaryDim : "transparent",
                    color: s === mStatusF ? colors.accent : colors.fgSub,
                    cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12,
                  }}>{s || "All"}</button>
                ))}
                <span style={{ fontSize: 11, color: colors.fgSub, alignSelf: "center", marginLeft: "auto" }}>
                  {filteredMatches.length}
                </span>
              </div>
              {!filteredMatches.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 32 }}>No matches</div>
              ) : filteredMatches.map(m => (
                <div key={m.id} style={{ padding: "10px 0", borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flex: 1, minWidth: 0 }}>
                      <UserMini user={m.user1} onClick={() => onSelectUser(m.user1?.id)} />
                      <span style={{ color: colors.fgFaint, fontSize: 12 }}>↔</span>
                      <UserMini user={m.user2} onClick={() => onSelectUser(m.user2?.id)} />
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      <ScoreBar score={m.compatibility_score} />
                      <MatchStatus s={m.status} />
                      {m.user1 && m.user2 && (
                        <button onClick={() => onOpenChat(m.user1.id, m.user2.id)}
                          style={{ background: colors.greenDim, border: "none", borderRadius: 6,
                            padding: "4px 8px", fontSize: 11, color: colors.green, cursor: "pointer" }}>
                          ◈
                        </button>
                      )}
                    </div>
                  </div>
                  {m.match_reason && (
                    <p style={{ fontSize: 11, color: colors.fgSub, marginTop: 5, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {m.match_reason}
                    </p>
                  )}
                </div>
              ))}
            </>
          )}
          {tab === "requests" && (
            <>
              {!data.search_requests?.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 32 }}>No requests</div>
              ) : data.search_requests.map(sr => (
                <div key={sr.id} className="st-card" style={{ padding: "12px 14px", marginBottom: 6 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{sr.title || "Untitled"}</span>
                        {sr.category && <Tag color={colors.cyan}>{sr.category}</Tag>}
                        <Tag color={sr.is_active ? colors.green : colors.fgSub}>
                          {sr.is_active ? "active" : "inactive"}
                        </Tag>
                        <span className="st-mono" style={{ fontSize: 11, color: colors.primary }}>
                          {sr.total_matches} matches
                        </span>
                      </div>
                      {sr.user && (
                        <div style={{ cursor: "pointer", marginBottom: 4 }}
                          onClick={() => onSelectUser(sr.user.id)}>
                          <span style={{ fontSize: 12, color: colors.accent }}>↳ {sr.user.name}</span>
                        </div>
                      )}
                      {sr.request_text && (
                        <p style={{ fontSize: 11, color: colors.fgSub, lineHeight: 1.5, marginTop: 2, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {sr.request_text}
                        </p>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0, flexDirection: "column",
                      alignItems: "flex-end" }}>
                      <Btn sm variant="ghost" onClick={() => toggleSR(sr.id, sr.is_active)}>
                        {sr.is_active ? "⏸ Stop" : "▶ Enable"}
                      </Btn>
                      <Btn sm variant="danger" onClick={() => deleteSR(sr.id)}>✕</Btn>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {tab === "referrals" && (
            <>
              {!data.referrals?.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 32 }}>No referrals</div>
              ) : data.referrals.map(r => (
                <div key={r.id} className="st-card" style={{ padding: "12px 14px", marginBottom: 6 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={{ cursor: "pointer" }} onClick={() => r.user && onSelectUser(r.user.id)}>
                      <Avatar user={r.user} size={32} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: colors.accent, cursor: "pointer" }}
                        onClick={() => r.user && onSelectUser(r.user.id)}>
                        {r.user?.name || "—"}
                      </span>
                      <div className="st-mono" style={{ fontSize: 11, color: colors.fgSub }}>{r.ref_code}</div>
                      {r.user?.email && (
                        <div style={{ fontSize: 11, color: colors.fgFaint }}>{r.user.email}</div>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, textAlign: "center" }}>
                      {[["👁", r.scan_count, "Scans"], ["✦", r.unique_scan_count, "Unique"],
                        ["✓", r.registration_count, "Reg."]].map(([icon, val, label]) => (
                        <div key={label} style={{ background: colors.cardMid, borderRadius: 6, padding: "6px 8px" }}>
                          <div style={{ fontSize: 14, fontWeight: 700,
                            color: label === "Reg." ? colors.green : colors.fgMid }}>{icon} {val}</div>
                          <div style={{ fontSize: 9, color: colors.fgSub, textTransform: "uppercase",
                            letterSpacing: ".05em" }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {tab === "admins" && (
            <>
              <div style={{ marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 11, color: colors.fgSub }}>
                  {data.event_admins?.length ? `${data.event_admins.length} administrator${data.event_admins.length !== 1 ? "s" : ""}` : "No administrators yet"}
                </div>
                <Btn sm onClick={() => setAdminPicker(true)}>+ Add Admin</Btn>
              </div>
              {!data.event_admins?.length ? (
                <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 48,
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 28, opacity: .3 }}>🛡</div>
                  <div style={{ fontSize: 13 }}>No event admins assigned</div>
                </div>
              ) : (
                <table className="st-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Global Role</th>
                      <th>Added</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.event_admins.map((ea, i) => (
                      <tr key={i}>
                        <td>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <div style={{ cursor: "pointer", flexShrink: 0 }} onClick={() => ea.user && onSelectUser(ea.user.id)}>
                              <Avatar user={ea.user} size={28} />
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, color: colors.accent, cursor: "pointer" }}
                                onClick={() => ea.user && onSelectUser(ea.user.id)}>
                                {ea.user?.name || "—"}
                              </div>
                              <div className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                                #{ea.user?.id} · {ea.user?.email || "—"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td><RoleTag r={ea.user?.role || "user"} /></td>
                        <td>
                          <span className="st-mono" style={{ fontSize: 11, color: colors.fgSub }}>
                            {ea.created_at ? new Date(ea.created_at).toLocaleDateString("en") : "—"}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Btn sm variant="danger" onClick={() => removeEventAdmin(ea.user?.id)}>✕</Btn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          {openParticipantIds.map(uid => (
            <ParticipantDetailModal
              key={`participant_${eventId}_${uid}`}
              eventId={eventId} userId={uid} token={token}
              onClose={() => closeParticipant(uid)}
              onSelectUser={onSelectUser}
              onOpenChat={onOpenChat}
            />
          ))}
          {confirmDelete && (
            <ConfirmModal
              title="Delete event?"
              text={`"${ev.title}" will be permanently deleted along with all registrations.`}
              danger
              onConfirm={deleteEvent}
              onClose={() => setConfirmDelete(false)}
            />
          )}
          {recreateOpen && (
            <RecreateEventModal
              event={ev}
              token={token}
              onClose={() => setRecreateOpen(false)}
              onCreated={(newEv) => { setRecreateOpen(false); onClose && onClose(); }}
            />
          )}
          {adminPicker && (
            <FormModal title="Add Event Administrator" windowId={`event_${eventId}_admin_picker`} onClose={() => { setAdminPicker(false); setPickerSearch(""); setPickerResults([]); setPickerRoleF(""); }} width={500}>
              <input className="st-input" placeholder="Name / email / @username / #id..." autoFocus
                value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
                style={{ marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
                {["", "admin", "event_admin", "event_user", "user"].map(r => {
                  const labels = { "": "All", admin: "Admin", event_admin: "Ev.Admin", event_user: "Ev.User", user: "User" };
                  const active = (pickerRoleF || "") === r;
                  return (
                    <button key={r || "all"} onClick={() => setPickerRoleF(r)} style={{
                      padding: "4px 10px", borderRadius: 6,
                      border: `1px solid ${active ? colors.primary : colors.border}`,
                      background: active ? colors.primaryDim : "transparent",
                      color: active ? colors.accent : colors.fgSub,
                      cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 11, transition: "all .15s",
                    }}>{labels[r] || r}</button>
                  );
                })}
              </div>
              {pickerLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 20 }}><Spinner size={20} /></div>
              ) : (
                <div style={{ maxHeight: 340, overflowY: "auto" }}>
                  {(() => {
                    const all = Array.isArray(pickerResults) ? pickerResults : (pickerResults?.users || []);
                    const filtered = pickerRoleF ? all.filter(u => u.role === pickerRoleF) : all;
                    const existingIds = new Set((data?.event_admins || []).map(ea => ea.user?.id));
                    if (!filtered.length) return (
                      <div style={{ textAlign: "center", color: colors.fgSub, padding: "24px 0", fontSize: 13 }}>No users found</div>
                    );
                    return filtered.map(u => {
                      const isAlready = existingIds.has(u.id);
                      return (
                        <div key={u.id} style={{
                          display: "flex", gap: 10, alignItems: "center",
                          padding: "8px 6px", borderBottom: `1px solid ${colors.border}`,
                          cursor: isAlready ? "default" : "pointer",
                          background: isAlready ? colors.greenDim : "transparent",
                          opacity: isAlready ? 0.65 : 1, borderRadius: 6,
                        }}
                        onMouseEnter={e => { if (!isAlready) e.currentTarget.style.background = colors.cardHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isAlready ? colors.greenDim : "transparent"; }}
                        onClick={() => !isAlready && addEventAdmin(u.id)}>
                          <Avatar user={u} size={28} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: colors.fg }}>{u.name}</div>
                            <div className="st-mono" style={{ fontSize: 10, color: colors.fgSub,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              #{u.id} · {u.email || "—"}{u.username ? ` · @${u.username}` : ""}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
                            <RoleTag r={u.role || "user"} />
                            {isAlready ? <Tag color={colors.green}>✓</Tag> : <Tag color={colors.primary}>+</Tag>}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
              {(() => {
                const all = Array.isArray(pickerResults) ? pickerResults : (pickerResults?.users || []);
                return all.length ? <div style={{ fontSize: 10, color: colors.fgFaint, textAlign: "right", marginTop: 8 }}>{all.length} users</div> : null;
              })()}
            </FormModal>
          )}
        </div>
      )}
    </WindowFrame>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TABS: STATS
// ════════════════════════════════════════════════════════════════════════════
function TabStats({ token, onNavigateTab }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [drills, setDrills] = useState([]); // [{ metric, label, color, tab }]
  const winMgr = useWindowMgr();
  function openDrill(next) {
    setDrills(prev => prev.some(d => d.metric === next.metric) ? prev : [...prev, next]);
    winMgr.bringToFront(_safeWindowId("metric", next.metric));
  }
  function closeDrill(metric) {
    setDrills(prev => prev.filter(d => d.metric !== metric));
  }
  useEffect(() => {
    let alive = true;
    const load = () => api("/stats", { token })
      .then(x => { if (alive) setD(x); })
      .catch(e => { if (alive) setErr(e.message); });
    load();
    const id = setInterval(load, 60000); // обновляем каждую минуту
    return () => { alive = false; clearInterval(id); };
  }, [token]);
  if (err) return (
    <div style={{ display: "flex", justifyContent: "center", padding: 60, color: colors.red, fontSize: 14 }}>
      Failed to load: {err}
    </div>
  );
  if (!d) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="st-card" style={{ padding: "18px 20px" }}>
            <SkeletonCard lines={2} />
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="st-card" style={{ padding: "18px 20px", height: 160 }}>
            <SkeletonCard lines={4} />
          </div>
        ))}
      </div>
    </div>
  );
  const matchRate       = d.users.total > 0 ? ((d.matches.total / d.users.total) * 100).toFixed(1) : 0;
  const activeRate      = d.matches.total > 0 ? ((d.matches.active / d.matches.total) * 100).toFixed(1) : 0;
  const avgMsgPerChat   = d.chats.total > 0 ? (d.messages.total / d.chats.total).toFixed(1) : 0;
  const chatConversion  = d.matches.total > 0 ? ((d.chats.total / d.matches.total) * 100).toFixed(1) : 0;
  const weeklyGrowthPct = d.users.total > 0 ? ((d.users.new_this_week / d.users.total) * 100).toFixed(2) : 0;
  const insights = [];
  if (Number(activeRate) < 40)
    insights.push({ icon: "⚡", color: colors.orange, title: "Low active match rate",
      text: `Only ${activeRate}% of matches are active. Users may receive matches but not start conversations. Consider reviewing recommendation quality or adding push notifications.` });
  if (Number(chatConversion) < 50)
    insights.push({ icon: "💬", color: colors.blue, title: "Low match → chat conversion",
      text: `Only ${chatConversion}% of matches led to a chat. Consider an icebreaker message or auto-opening chat on match.` });
  if (Number(avgMsgPerChat) < 5)
    insights.push({ icon: "📉", color: colors.red, title: "Low chat depth",
      text: `Average ${avgMsgPerChat} messages per chat. Users connect but don't sustain conversations. Consider adding conversation starters.` });
  if (d.moderation.feedback_new > 0)
    insights.push({ icon: "📬", color: colors.orange, title: `${d.moderation.feedback_new} new feedback items`,
      text: "There are unprocessed user messages. Open the Feedback tab to respond." });
  if (d.users.new_today > 5)
    insights.push({ icon: "🚀", color: colors.green, title: `Today +${d.users.new_today} new users`,
      text: "High registration day. Make sure matching keeps up with new profiles." });
  if (Number(weeklyGrowthPct) > 5)
    insights.push({ icon: "📈", color: colors.green, title: `${weeklyGrowthPct}% growth this week`,
      text: "Platform is growing fast. Good time to check load and matching quality." });
  if (insights.length === 0)
    insights.push({ icon: "✅", color: colors.green, title: "All looks good",
      text: "Key platform metrics are in a healthy range. Keep monitoring." });
  // 30-day daily series (бэк отдаёт в d.activity_30d), с фолбэком на 7-day series
  // для совместимости со старым форматом ответа.
  const a30 = d.activity_30d || {};
  const sReg     = a30.registrations || d.reg_activity   || [];
  const sMatches = a30.matches       || d.match_activity || [];
  const sChats   = a30.chats         || [];
  const sMsgs    = a30.messages      || [];
  const sEvents  = a30.events        || [];
  const sFb      = a30.feedback      || [];
  // Конфиг карточек drill-down (метрики бэка, лейблы, цвета, целевые вкладки).
  // Используем фиксированную палитру CHART_COLORS — она контрастирует
  // в обеих темах, в отличие от theme-токенов которые в light почти сливаются.
  const charts30 = [
    { metric: "users",    label: "Registrations", color: CHART_COLORS.registrations, sub: "new users · /day", data: sReg,     tab: "users"      },
    { metric: "matches",  label: "New Matches",   color: CHART_COLORS.matches,   sub: "matches · /day",       data: sMatches, tab: "matches"    },
    { metric: "chats",    label: "New Chats",     color: CHART_COLORS.chats,     sub: "chats started · /day", data: sChats,   tab: "chats"      },
    { metric: "messages", label: "Messages",      color: CHART_COLORS.messages,  sub: "messages · /day",      data: sMsgs,    tab: "chats"      },
    { metric: "events",   label: "New Events",    color: CHART_COLORS.events,    sub: "published · /day",     data: sEvents,  tab: "events-all" },
    { metric: "feedback", label: "Feedback",      color: CHART_COLORS.feedback,  sub: "feedback · /day",      data: sFb,      tab: "feedback"   },
  ];
  return (
    <div className="st-fade" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div className="st-kpi-grid">
        <KpiCard icon="👤" label="Users" color={colors.primary}
          value={d.users.total.toLocaleString("en")}
          sub={`today +${d.users.new_today}`}
          trend={d.users.new_this_week}
          chart={isMobile ? null : <SparkLine data={sReg} color={colors.primary} h={32} w={100} />}
        />
        <KpiCard icon="🤝" label="Active Matches" color={colors.green}
          value={d.matches.active.toLocaleString("en")}
          sub={isMobile ? `${activeRate}% active` : `of ${d.matches.total.toLocaleString("en")} total · ${activeRate}%`}
          trend={d.matches.new_this_week}
          chart={isMobile ? null : <SparkLine data={sMatches} color={colors.green} h={32} w={100} />}
        />
        <KpiCard icon="💬" label="Chats" color={colors.cyan}
          value={d.chats.total.toLocaleString("en")}
          sub={isMobile ? `${d.messages.total.toLocaleString("en")} msgs` : `${d.messages.total.toLocaleString("en")} messages`}
        />
        <KpiCard icon="📅" label="Events" color={colors.orange}
          value={d.events.total.toLocaleString("en")}
          sub={d.top_events?.[0]?.title ? `Top: ${d.top_events[0].title.slice(0, isMobile ? 14 : 22)}…` : "no data"}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          BLOCK · COMBINED TREND OVERLAY
          Четыре ключевые метрики на одном графике, нормализованные по своему
          максимуму. Видно ФОРМУ кривых: когда росли вместе, когда расходились.
      ══════════════════════════════════════════════════════════════════ */}
      <div className="st-card" style={{ padding: "20px 22px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
          marginBottom: 14, gap: 8, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
              letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 4 }}>
              Trend overlay · last 30 days
            </div>
            <div style={{ fontSize: 10, color: colors.fgFaint }}>
              series normalized (Y = % of max) — compare shapes
            </div>
          </div>
          <ChartLegend items={[
            { label: "Registrations", color: CHART_COLORS.registrations, value: sReg.reduce((s, r) => s + r.count, 0) },
            { label: "Matches",       color: CHART_COLORS.matches,       value: sMatches.reduce((s, r) => s + r.count, 0) },
            { label: "Chats",         color: CHART_COLORS.chats,         value: sChats.reduce((s, r) => s + r.count, 0) },
            { label: "Messages",      color: CHART_COLORS.messages,      value: sMsgs.reduce((s, r) => s + r.count, 0) },
          ]} />
        </div>
        <MultiLineChart
          h={isMobile ? 200 : 220}
          series={[
            { label: "Registrations", color: CHART_COLORS.registrations, data: sReg },
            { label: "Matches",       color: CHART_COLORS.matches,       data: sMatches },
            { label: "Chats",         color: CHART_COLORS.chats,         data: sChats },
            { label: "Messages",      color: CHART_COLORS.messages,      data: sMsgs },
          ]}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          BLOCK · CALENDAR HEATMAP + DAY-OF-WEEK PATTERN
          Слева — heatmap-сетка по дням (видно «жаркие» и «мёртвые» периоды).
          Справа — средняя активность по дням недели (выходные/будни).
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr", gap: 12 }}>
        <div className="st-card" style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 14 }}>
            Registration Heatmap · 30 days
          </div>
          <CalendarHeatmap data={sReg} color={CHART_COLORS.registrations}
            cellSize={isMobile ? 12 : 16} />
        </div>
        <div className="st-card" style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 14 }}>
            Day-of-week pattern · matches
          </div>
          <DayOfWeekChart data={sMatches} color={CHART_COLORS.matches} h={120} />
          <div style={{ fontSize: 10, color: colors.fgFaint, marginTop: 8 }}>
            Average per weekday — shows the weekly activity cycle
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          BLOCK · CUMULATIVE GROWTH
          Накопительный рост — total за период. Кривая замедляется → рост
          падает, даже если суточные числа колеблются.
      ══════════════════════════════════════════════════════════════════ */}
      <div className="st-card" style={{ padding: "20px 22px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
          marginBottom: 14, gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase" }}>
            Cumulative growth · last 30 days
          </div>
          <ChartLegend items={[
            { label: "Users",   color: CHART_COLORS.users,    value: sReg.reduce((s, r) => s + r.count, 0) },
            { label: "Matches", color: CHART_COLORS.matches,  value: sMatches.reduce((s, r) => s + r.count, 0) },
            { label: "Chats",   color: CHART_COLORS.chats,    value: sChats.reduce((s, r) => s + r.count, 0) },
            { label: "Events",  color: CHART_COLORS.events,   value: sEvents.reduce((s, r) => s + r.count, 0) },
          ]} />
        </div>
        <CumulativeAreaChart
          h={isMobile ? 200 : 230}
          series={[
            { label: "Users",   color: CHART_COLORS.users,    data: sReg },
            { label: "Matches", color: CHART_COLORS.matches,  data: sMatches },
            { label: "Chats",   color: CHART_COLORS.chats,    data: sChats },
            { label: "Events",  color: CHART_COLORS.events,   data: sEvents },
          ]}
        />
      </div>

      {/* ── 30-day grid с кликабельными карточками-графиками ── */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
          marginBottom: 12, gap: 8 }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase" }}>
            Per-metric daily breakdown
          </div>
          <div style={{ fontSize: 10, color: colors.fgFaint }}>
            click a card for detailed breakdown
          </div>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 10,
        }}>
          {charts30.map(c => (
            <ClickableChartCard key={c.metric}
              title={c.label} subtitle={c.sub}
              data={c.data} color={c.color}
              onOpen={() => openDrill({ metric: c.metric, label: c.label, color: c.color, tab: c.tab })}
            />
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          BLOCK · DISTRIBUTIONS
          Donut по статусам матчей + горизонтальные полосы топ-категорий
          + статусы ивентов. Категориальный «снимок» состояния системы.
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
        {/* Match status donut */}
        <div className="st-card" style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 14 }}>
            Match status mix
          </div>
          {(() => {
            // Статусы в БД могут приходить в разных регистрах (Active / active /
            // ACTIVE) — нормализуем к lowercase, схлопываем дубли.
            const raw = d.match_status_breakdown || {};
            const msb = {};
            Object.entries(raw).forEach(([k, v]) => {
              const key = String(k || "unknown").toLowerCase();
              msb[key] = (msb[key] || 0) + (v || 0);
            });
            const slices = [
              { label: "Active",    value: msb.active    || 0, color: colors.green   },
              { label: "Pending",   value: msb.pending   || 0, color: colors.orange  },
              { label: "Rejected",  value: msb.rejected  || 0, color: colors.red     },
              { label: "Cancelled", value: msb.cancelled || 0, color: colors.fgSub   },
            ].filter(s => s.value > 0);
            const known = new Set(["active","pending","rejected","cancelled","unknown"]);
            Object.entries(msb).forEach(([k, v]) => {
              if (!known.has(k) && v > 0)
                slices.push({ label: k, value: v, color: colors.cyan });
            });
            return slices.length > 0 ? (
              <DonutChart slices={slices}
                size={isMobile ? 120 : 140} thickness={16}
                centerLabel={d.matches.total?.toLocaleString("en")}
                centerSub="MATCHES" />
            ) : (
              <div style={{ fontSize: 11, color: colors.fgFaint, padding: "20px 0" }}>
                no data
              </div>
            );
          })()}
        </div>

        {/* Top match categories */}
        <div className="st-card" style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 14 }}>
            Top match categories
          </div>
          <HBars
            color={colors.accent}
            items={(d.match_categories_top || []).map(c => ({
              label: c.category || "—", value: c.count,
            }))}
          />
        </div>

        {/* Events by status */}
        <div className="st-card" style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 14 }}>
            Events by status
          </div>
          {(() => {
            const ebs = d.events_by_status || {};
            // Палитра для статусов ивентов — контрастные, отличные от
            // палитры match-status (там семантика green=active/orange=pending).
            const evPalette = [
              CHART_COLORS.events,        // pink
              CHART_COLORS.registrations, // blue
              CHART_COLORS.matches,       // green
              CHART_COLORS.messages,      // amber
              CHART_COLORS.chats,         // violet
              CHART_COLORS.feedback,      // red
            ];
            const slices = Object.entries(ebs)
              .filter(([, v]) => v > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v], i) => ({
                label: k, value: v,
                color: evPalette[i % evPalette.length],
              }));
            return slices.length > 0 ? (
              <DonutChart slices={slices}
                size={isMobile ? 120 : 140} thickness={16}
                centerLabel={d.events.total?.toLocaleString("en")}
                centerSub="EVENTS" />
            ) : (
              <div style={{ fontSize: 11, color: colors.fgFaint, padding: "20px 0" }}>
                no data
              </div>
            );
          })()}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          BLOCK · MONTH-OVER-MONTH (текущие 30 дней vs предыдущие 30 дней)
      ══════════════════════════════════════════════════════════════════ */}
      <div>
        <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
          letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 12 }}>
          Month-over-month · last 30 days vs previous 30 days
        </div>
        {(() => {
          const prev = d.prev_30d || {};
          const cur30 = (arr) => (arr || []).reduce((s, r) => s + r.count, 0);
          const cards = [
            { label: "Registrations", current: cur30(sReg),     prev: prev.registrations, color: CHART_COLORS.registrations },
            { label: "Matches",       current: cur30(sMatches), prev: prev.matches,       color: CHART_COLORS.matches       },
            { label: "Chats",         current: cur30(sChats),   prev: prev.chats,         color: CHART_COLORS.chats         },
            { label: "Messages",      current: cur30(sMsgs),    prev: prev.messages,      color: CHART_COLORS.messages      },
            { label: "Events",        current: cur30(sEvents),  prev: prev.events,        color: CHART_COLORS.events        },
            { label: "Feedback",      current: cur30(sFb),      prev: prev.feedback,      color: CHART_COLORS.feedback      },
          ];
          return (
            <div style={{ display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 10 }}>
              {cards.map(c => (
                <MoMCompareCard key={c.label} {...c} />
              ))}
            </div>
          );
        })()}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
        <div className="st-card" style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 18 }}>
            Engagement Funnel
          </div>
          {isMobile ? (
            /* ── MOBILE: flat list, no nesting ── */
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { icon: "✅", label: "Registered",   value: d.users.total,   color: colors.primary },
                { icon: "🤝", label: "Got a Match",  value: d.matches.total, color: colors.accent  },
                { icon: "💬", label: "Started Chat", value: d.chats.total,   color: colors.cyan    },
                { icon: "🔥", label: "Match Active", value: d.matches.active, color: colors.green  },
              ].map(({ icon, label, value, color }) => {
                const pct = d.users.total > 0 ? Math.round((value / d.users.total) * 100) : 0;
                return (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: colors.fgMid }}>{label}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color, fontFamily: "'JetBrains Mono',monospace" }}>
                          {value?.toLocaleString("en") ?? "—"}
                          <span style={{ fontSize: 10, color: colors.fgSub, fontWeight: 400 }}> ({pct}%)</span>
                        </span>
                      </div>
                      <div style={{ height: 4, background: colors.border, borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99, transition: "width .8s" }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── DESKTOP: tree with indents ── */
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <FunnelStep icon="✅" label="Registered"
                value={d.users.total} total={d.users.total} color={colors.primary} />
              <div style={{ paddingLeft: 20 }}>
                <div style={{ width: 1, height: 12, background: colors.border, marginBottom: 0 }} />
                <FunnelStep icon="🤝" label="Got a Match"
                  value={d.matches.total} total={d.users.total} color={colors.accent} />
                <div style={{ paddingLeft: 20 }}>
                  <div style={{ width: 1, height: 12, background: colors.border }} />
                  <FunnelStep icon="💬" label="Started Chat"
                    value={d.chats.total} total={d.users.total} color={colors.cyan} />
                  <div style={{ paddingLeft: 20 }}>
                    <div style={{ width: 1, height: 12, background: colors.border }} />
                    <FunnelStep icon="🔥" label="Match Active"
                      value={d.matches.active} total={d.users.total} color={colors.green} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="st-card" style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 18 }}>
            Key Metrics
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 8 : 10 }}>
            {[
              { label: "Match Rate",         value: `${matchRate}%`,      sub: "matches / user",    color: colors.primary },
              { label: "Match Activity", value: `${activeRate}%`,     sub: "active / total",   color: colors.green },
              { label: "Chat Conversion",     value: `${chatConversion}%`, sub: "chats per match",       color: colors.cyan },
              { label: "Chat Depth",   value: avgMsgPerChat,         sub: "msgs / chat",       color: colors.accent },
              { label: "Weekly Growth",    value: `${weeklyGrowthPct}%`, sub: `+${d.users.new_this_week} users`, color: colors.orange },
              { label: "Moderated",      value: d.moderation.blacklisted, sub: "blacklisted", color: colors.red },
            ].map(({ label, value, sub, color }) => (
              <div key={label} style={{ background: colors.cardMid, borderRadius: 8,
                padding: isMobile ? "10px 12px" : "12px 14px", border: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: 9, color: colors.fgSub, fontWeight: 700,
                  letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color,
                  fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: 10, color: colors.fgFaint, marginTop: 4 }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {d.top_events?.length > 0 && (
        <div className="st-card" style={{ padding: "18px 20px" }}>
          <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
            letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 14 }}>
            Top Events by Participants
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {d.top_events.map((e, i) => {
              const fillPct = e.capacity > 0 ? Math.round((e.participants / e.capacity) * 100) : null;
              return (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 14,
                  padding: "10px 14px", background: colors.cardMid, borderRadius: 8,
                  border: `1px solid ${colors.border}` }}>
                  <div style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                    background: i === 0 ? colors.gradient : i === 1 ? colors.accentDim : colors.glow,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800,
                    color: i === 0 ? "#fff" : colors.fgSub }}>{i+1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title}</div>
                    {fillPct !== null && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                        <div style={{ flex: 1, height: 3, background: colors.border, borderRadius: 99, overflow: "hidden" }}>
                          <div style={{ width: `${Math.min(100, fillPct)}%`, height: "100%",
                            background: fillPct > 80 ? colors.red : fillPct > 50 ? colors.orange : colors.green,
                            borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: 10, color: colors.fgSub }}>{fillPct}% full</span>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: colors.primary,
                      fontFamily: "'JetBrains Mono', monospace" }}>{e.participants}</div>
                    {e.capacity && <div style={{ fontSize: 10, color: colors.fgSub }}>/{e.capacity}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <div style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
          letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 12 }}>
          Automatic Insights
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {insights.map((ins, i) => (
            <InsightCard key={i} icon={ins.icon} color={ins.color}
              title={ins.title} text={ins.text} />
          ))}
        </div>
      </div>
      {/* Drill-down модалка */}
      {drills.map(drill => (
        <MetricDetailModal
          key={drill.metric}
          token={token}
          metric={drill.metric}
          label={drill.label}
          color={drill.color}
          onClose={() => closeDrill(drill.metric)}
          onNavigate={drill.tab && onNavigateTab ? () => {
            const t = drill.tab;
            closeDrill(drill.metric);
            onNavigateTab(t);
          } : null}
        />
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TABS: USERS
// ════════════════════════════════════════════════════════════════════════════
function TabUsers({ token, onSelectUser }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]     = useStickyState("users_page", 1);
  const [search, setSearch] = useStickyState("users_search", "");
  const [role, setRole]     = useStickyState("users_role", "");
  const debRef = useRef();
  const load = useCallback((pg, sq, rl) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 30 });
    if (sq) p.set("search", sq);
    if (rl) p.set("role", rl);
    api(`/users?${p}`, { token }).then(setData).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(page, search, role); }, [page, search, role]);
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input className="st-input" placeholder="Search by name, email, about..."
          onChange={e => {
            clearTimeout(debRef.current);
            const v = e.target.value;
            debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 380);
          }} style={{ maxWidth: 280 }} />
        <select className="st-input" style={{ width: 160 }} value={role}
          onChange={e => { setRole(e.target.value); setPage(1); }}>
          <option value="">All roles</option>
          {["user","event_user","event_admin","admin"].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          className="st-btn st-btn-ghost"
          style={{ marginLeft: "auto", gap: 5 }}
          onClick={() => {
            const p = new URLSearchParams({ limit: 5000 });
            if (search) p.set("search_col", "name"); // generic export
            window.open(`${API_URL}/api/mod/db/tables/users/export?${p}&token_header=1`, "_blank");
          }}
          title="Export to CSV (up to 5000 rows)"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          CSV
        </button>
      </div>
      {loading ? (
        <div className="st-card" style={{ overflow: "hidden" }}>
          <div className="st-table-scroll"><table className="st-table"><thead><tr>
            {[1,2,3,4,5,6,7].map(i => <th key={i}><Skeleton height={10} width="60%" /></th>)}
          </tr></thead><tbody>
            {Array.from({length:8}).map((_,i) => <SkeletonRow key={i} cols={7}/>)}
          </tbody></table></div></div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>{data?.total ?? 0} users</div>
          <div className="st-card" style={{ overflow: "hidden" }}>
            <div className="st-table-scroll"><table className="st-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Matches</th>
                  <th>Chats</th>
                  <th>Events</th>
                  <th>Registered</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data?.users?.map(u => {
                  const rowStyle = (() => {
                    if (u.role === "admin") return { borderLeft: `3px solid ${colors.primary}`, background: colors.primaryDim + "55" };
                    if (u.is_blacklisted)  return { borderLeft: `3px solid ${colors.red}`,     background: colors.redDim };
                    return { borderLeft: "3px solid transparent" };
                  })();
                  return (
                  <tr key={u.id} onClick={() => onSelectUser(u.id)} style={rowStyle}>
                    <td>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <Avatar user={u} size={30} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: colors.fg }}>{u.name}</div>
                          <div className="st-mono" style={{ fontSize: 10, color: colors.fgSub, display: "flex", alignItems: "center", gap: 2 }}>
                            <span style={{ cursor: "default" }}>#{u.id}</span>
                            <CopyBtn text={u.id} />
                            {(u.email || u.username) && <>
                              <span style={{ color: colors.fgFaint }}>·</span>
                              <span>{u.email || u.username}</span>
                              <CopyBtn text={u.email || u.username} />
                            </>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td><RoleTag r={u.role} /></td>
                    <td>
                      <span className="st-mono" style={{ fontSize: 12 }}>
                        <span style={{ color: u.active_match_count > 0 ? colors.green : colors.fg, fontWeight: 700 }}>
                          {u.active_match_count}
                        </span>
                        <span style={{ color: colors.fgFaint }}> / {u.match_count}</span>
                      </span>
                    </td>
                    <td><span className="st-mono" style={{ fontSize: 12 }}>{u.message_count || 0}</span></td>
                    <td><span className="st-mono" style={{ fontSize: 12 }}>{u.event_registrations}</span></td>
                    <td><span className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("en") : "—"}
                    </span></td>
                    <td style={{ color: colors.fgSub, fontSize: 16 }}>›</td>
                  </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={p => { setPage(p); }} />
        </>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TABS: CHATS
// ════════════════════════════════════════════════════════════════════════════
function TabCards({ token, onSelectUser }) {
  const { colors } = useTheme();
  const [data, setData]     = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = useStickyState("cards_search", "");
  const [page, setPage]     = useStickyState("cards_page", 1);
  const [selectedCards, setSelectedCards] = React.useState([]); // card detail windows
  const debRef = React.useRef();
  const toast = useToast();
  const winMgr = useWindowMgr();

  function openCard(c) {
    if (!c) return;
    const id = c.card_id || c.user_id;
    setSelectedCards(prev => prev.some(x => (x.card_id || x.user_id) === id) ? prev : [...prev, c]);
    winMgr.bringToFront(`card_${id}`);
  }
  function closeCard(id) {
    setSelectedCards(prev => prev.filter(x => (x.card_id || x.user_id) !== id));
  }

  const load = React.useCallback((pg, sq) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 30 });
    if (sq) p.set("search", sq);
    api(`/cards?${p}`, { token }).then(setData).finally(() => setLoading(false));
  }, [token]);

  React.useEffect(() => { load(page, search); }, [page, search]);

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input className="st-input" placeholder="Search by name, company, phone, address..."
          defaultValue={search}
          onChange={e => {
            clearTimeout(debRef.current);
            const v = e.target.value;
            debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 380);
          }} style={{ maxWidth: 320 }} />
        <div style={{ marginLeft: "auto", fontSize: 11, color: colors.fgSub, alignSelf: "center" }}>
          {data?.total ?? 0} cards
        </div>
      </div>
      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px,1fr))", gap: 12 }}>
          {Array.from({length: 12}).map((_,i) => (
            <div key={i} className="st-card" style={{ padding: 16, borderRadius: 12 }}>
              <Skeleton height={14} width="60%" style={{ marginBottom: 8 }} />
              <Skeleton height={11} width="80%" style={{ marginBottom: 5 }} />
              <Skeleton height={11} width="50%" />
            </div>
          ))}
        </div>
      ) : !data?.items?.length ? (
        <div style={{ color: colors.fgSub, textAlign: "center", paddingTop: 48, fontSize: 13 }}>
          No business cards yet
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px,1fr))", gap: 12 }}>
            {data.items.map(c => (
              <div key={c.card_id} className="st-card" onClick={() => openCard(c)}
                style={{ padding: 16, borderRadius: 12, cursor: "pointer", transition: "all .15s" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                  <Avatar user={c} size={34} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: colors.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: colors.fgSub, fontFamily: "JetBrains Mono, monospace" }}>#{c.user_id}</div>
                  </div>
                </div>
                {c.company && (
                  <div style={{ fontSize: 12, color: colors.accent, fontWeight: 500, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    🏢 {c.company}
                  </div>
                )}
                {c.phone && (
                  <div style={{ fontSize: 12, color: colors.fgMid, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    📞 {c.phone}
                  </div>
                )}
                {c.address && (
                  <div style={{ fontSize: 11, color: colors.fgSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    📍 {c.address}
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 9, color: colors.fgFaint, fontFamily: "JetBrains Mono, monospace" }}>
                  {c.updated_at ? new Date(c.updated_at).toLocaleDateString("en") : "—"}
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={p => setPage(p)} />
        </>
      )}

      {/* Card detail modal */}
      {selectedCards.map(selected => {
        const selectedId = selected.card_id || selected.user_id;
        return (
        <WindowModal
          key={`card_${selectedId}`}
          windowId={`card_${selectedId}`}
          title={selected.name || "Business Card"}
          onClose={() => closeCard(selectedId)}
          width={520}
          height={560}
        >
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 20 }}>
              <Avatar user={selected} size={52} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{selected.name}</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <RoleTag r={selected.role} />
                  <span style={{ fontSize: 11, color: colors.fgSub, fontFamily: "JetBrains Mono, monospace" }}>#{selected.user_id}</span>
                  {selected.email && <span style={{ fontSize: 11, color: colors.fgSub }}>{selected.email}</span>}
                </div>
              </div>
            </div>
            <div style={{ background: colors.cardMid, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 20 }}>
              <SectionTitle>Business Card</SectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>📞 Phone</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: selected.phone ? colors.fg : colors.fgFaint }}>{selected.phone || "—"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>🏢 Company</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: selected.company ? colors.accent : colors.fgFaint }}>{selected.company || "—"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>📍 Address</div>
                  <div style={{ fontSize: 13, color: selected.address ? colors.fgMid : colors.fgFaint, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{selected.address || "—"}</div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <Btn sm onClick={() => { closeCard(selectedId); onSelectUser(selected.user_id); }}>Open User Profile →</Btn>
            </div>
        </WindowModal>
        );
      })}
    </>
  );
}

function TabChats({ token, onSelectUser, onOpenChat }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const load = useCallback((pg) => {
    setLoading(true);
    api(`/chats?page=${pg}&limit=30`, { token }).then(setData).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(page); }, [page]);
  return (
    <>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="st-card" style={{ padding: "14px 16px" }}>
              <SkeletonCard lines={3} />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>{data?.total ?? 0} chats</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {data?.chats?.map(ch => (
              <div key={ch.chat_id} className="st-card" style={{ padding: "14px 16px",
                cursor: "pointer" }}
                onClick={() => onOpenChat(ch.user1?.id, ch.user2?.id)}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ position: "relative", width: 44, height: 36, flexShrink: 0 }}>
                    <Avatar user={ch.user1} size={28} />
                    <div style={{ position: "absolute", bottom: 0, right: 0 }}>
                      <Avatar user={ch.user2} size={22} />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: colors.accent, cursor: "pointer" }}
                        onClick={e => { e.stopPropagation(); onSelectUser(ch.user1?.id); }}>
                        {ch.user1?.name || `#${ch.user1?.id}`}
                      </span>
                      <span style={{ color: colors.fgFaint }}>↔</span>
                      <span style={{ fontWeight: 600, fontSize: 13, color: colors.accent, cursor: "pointer" }}
                        onClick={e => { e.stopPropagation(); onSelectUser(ch.user2?.id); }}>
                        {ch.user2?.name || `#${ch.user2?.id}`}
                      </span>
                    </div>
                    {ch.match_category && <Tag color={colors.blue}>{ch.match_category}</Tag>}
                    {ch.compatibility_score && <ScoreBar score={ch.compatibility_score} />}
                    {ch.last_message?.content && (
                      <p style={{ fontSize: 12, color: colors.fgSub, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {ch.last_message.content.length > 250
                          ? ch.last_message.content.slice(0, 250) + "…"
                          : ch.last_message.content}
                      </p>
                    )}
                    {ch.match_reason && (
                      <p style={{ fontSize: 11, color: colors.fgFaint, marginTop: 4, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {ch.match_reason}
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end",
                    flexShrink: 0, gap: 4 }}>
                    <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                      {ch.message_count} msgs
                    </span>
                    {ch.last_message_at && (
                      <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                        {new Date(ch.last_message_at).toLocaleDateString("en")}
                      </span>
                    )}
                    <span style={{ color: colors.primary, fontSize: 13 }}>◈</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TABS: MATCHES
// ════════════════════════════════════════════════════════════════════════════
function TabMatches({ token, onSelectUser, onOpenChat }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useStickyState("matches_page", 1);
  const [statusF, setStatusF] = useStickyState("matches_status", "");
  const [search, setSearch]   = useStickyState("matches_search", "");
  const [expanded, setExpanded] = useState(null);
  const [actLoading, setActLoading] = useState(null);
  const debRef = useRef();
  const toast = useToast();
  const confirm = useConfirm();
  const load = useCallback((pg, sf, sq) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 25 });
    if (sf) p.set("status", sf);
    if (sq) p.set("search", sq);
    api(`/matches?${p}`, { token }).then(setData).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(page, statusF, search); }, [page, statusF, search]);
  async function setStatus(id, s) {
    setActLoading(id);
    try {
      await api(`/matches/${id}/status`, { method: "POST", body: { new_status: s }, token });
      toast(`Match ${id}: → ${s}`);
      load(page, statusF, search);
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(null); }
  }
  async function deleteMatch(id) {
    const ok = await confirm("Delete match?", `Match #${id} will be permanently deleted.`);
    if (!ok) return;
    try {
      await api(`/matches/${id}`, { method: "DELETE", token });
      toast("Match deleted");
      load(page, statusF, search);
    } catch (e) { toast(e.message, "err"); }
  }
  const STATUS_BTNS = ["", "active", "pending", "rejected", "cancelled"];
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input className="st-input" style={{ maxWidth: 240 }} placeholder="Search by name..."
          onChange={e => {
            clearTimeout(debRef.current);
            const v = e.target.value;
            debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 380);
          }} />
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {STATUS_BTNS.map(s => (
            <button key={s || "all"} onClick={() => { setStatusF(s); setPage(1); }} style={{
              padding: "7px 12px", borderRadius: 7,
              border: `1px solid ${s === statusF ? colors.primary : colors.border}`,
              background: s === statusF ? colors.primaryDim : "transparent",
              color: s === statusF ? colors.accent : colors.fgSub,
              cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12, transition: "all .15s",
            }}>{s || "All"}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="st-card" style={{ overflow: "hidden" }}>
          <table className="st-table"><thead><tr>
            {[1,2,3,4,5].map(i => <th key={i}><Skeleton height={10} width="60%" /></th>)}
          </tr></thead><tbody>
            {Array.from({length:7}).map((_,i) => <SkeletonRow key={i} cols={5}/>)}
          </tbody></table></div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>{data?.total ?? 0} matches</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {data?.matches?.map(m => {
              const borderColor = {
                active: colors.green, pending: colors.orange,
                rejected: colors.red, cancelled: colors.fgFaint,
              }[m.status] || colors.border;
              return (
              <div key={m.id} className="st-card" style={{ overflow: "hidden", borderLeft: `3px solid ${borderColor}` }}>
                <div onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", cursor: "pointer" }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", width: 150, flexShrink: 0 }}>
                    <div style={{ cursor: "pointer" }} onClick={e => { e.stopPropagation(); onSelectUser(m.user1?.id); }}>
                      <Avatar user={m.user1} size={24} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer",
                        color: colors.accent }}
                        onClick={e => { e.stopPropagation(); onSelectUser(m.user1?.id); }}>
                        {m.user1?.name || "—"}
                      </div>
                    </div>
                  </div>
                  <span style={{ color: colors.fgFaint, fontSize: 12 }}>↔</span>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", width: 150, flexShrink: 0 }}>
                    <div style={{ cursor: "pointer" }} onClick={e => { e.stopPropagation(); onSelectUser(m.user2?.id); }}>
                      <Avatar user={m.user2} size={24} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer",
                        color: colors.accent }}
                        onClick={e => { e.stopPropagation(); onSelectUser(m.user2?.id); }}>
                        {m.user2?.name || "—"}
                      </div>
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 60 }}><ScoreBar score={m.compatibility_score} /></div>
                  {m.category && <Tag color={colors.blue}>{m.category}</Tag>}
                  <MatchStatus s={m.status} />
                  <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, flexShrink: 0 }}>
                    {m.created_at ? new Date(m.created_at).toLocaleDateString("en") : "—"}
                  </span>
                  <span style={{ color: colors.fgFaint, fontSize: 12 }}>{expanded === m.id ? "▲" : "▼"}</span>
                </div>
                {expanded === m.id && (
                  <div style={{ padding: "14px 14px 16px", borderTop: `1px solid ${colors.border}`,
                    background: colors.cardMid }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                      <div style={{ background: colors.card, borderRadius: 8, padding: "12px 14px",
                        border: `1px solid ${colors.border}` }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                          <div style={{ cursor: "pointer" }} onClick={() => onSelectUser(m.user1?.id)}>
                            <Avatar user={m.user1} size={28} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: colors.accent, cursor: "pointer" }}
                              onClick={() => onSelectUser(m.user1?.id)}>{m.user1?.name}</div>
                            <Tag color={colors.orange} style={{ fontSize: 10 }}>{m.user1_status || "—"}</Tag>
                          </div>
                        </div>
                        {m.user1?.about && <p style={{ fontSize: 11, color: colors.fgSub, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {m.user1.about}</p>}
                        {m.user1?.request && <p style={{ fontSize: 11, color: colors.fgFaint, marginTop: 4, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          → {m.user1.request}</p>}
                      </div>
                      <div style={{ background: colors.card, borderRadius: 8, padding: "12px 14px",
                        border: `1px solid ${colors.border}` }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                          <div style={{ cursor: "pointer" }} onClick={() => onSelectUser(m.user2?.id)}>
                            <Avatar user={m.user2} size={28} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: colors.accent, cursor: "pointer" }}
                              onClick={() => onSelectUser(m.user2?.id)}>{m.user2?.name}</div>
                            <Tag color={colors.orange} style={{ fontSize: 10 }}>{m.user2_status || "—"}</Tag>
                          </div>
                        </div>
                        {m.user2?.about && <p style={{ fontSize: 11, color: colors.fgSub, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {m.user2.about}</p>}
                        {m.user2?.request && <p style={{ fontSize: 11, color: colors.fgFaint, marginTop: 4, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          → {m.user2.request}</p>}
                      </div>
                    </div>
                    {m.match_reason && (
                      <div style={{ background: colors.primaryDim, borderRadius: 8, padding: "10px 14px",
                        fontSize: 12, color: colors.fgMid, lineHeight: 1.6, marginBottom: 12,
                        whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        <span style={{ color: colors.accent, fontWeight: 700 }}>Reason: </span>
                        {m.match_reason}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>#{m.id}</span>
                      {m.chat_created && (
                        <button onClick={() => onOpenChat(m.user1?.id, m.user2?.id)}
                          style={{ background: colors.greenDim, border: `1px solid ${colors.green}44`, borderRadius: 6,
                            padding: "4px 10px", fontSize: 12, color: colors.green, cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 600 }}>
                          View Chat
                        </button>
                      )}
                      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                        {["active", "pending", "rejected"].filter(s => s !== m.status).map(s => (
                          <Btn key={s} sm variant="ghost"
                            loading={actLoading === m.id}
                            onClick={() => setStatus(m.id, s)}>
                            → {s}
                          </Btn>
                        ))}
                        <Btn sm variant="danger" onClick={() => deleteMatch(m.id)}>✕ Delete</Btn>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );})}
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  RECREATE EVENT MODAL
//  Clone an existing event with a new date (and optionally time / title).
//  All other fields (location, tags, organizer, capacity, pricing, matching
//  settings, table settings) are preserved. Registrations / matches / chats
//  are NOT copied — the recreated event starts clean.
// ════════════════════════════════════════════════════════════════════════════
function RecreateEventModal({ event, token, onClose, onCreated }) {
  const { colors } = useTheme();
  const toast = useToast();

  const todayPlus = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const [newDate,  setNewDate]   = useState(todayPlus(7));
  const [newTime,  setNewTime]   = useState(event?.event_time || "");
  const [newTitle, setNewTitle]  = useState(event?.title || "");
  const [copyAdmins, setCopyAdmins] = useState(true);
  const [busy, setBusy] = useState(false);

  // Detect past event for a helpful hint
  const isPast = (() => {
    if (!event?.event_date) return false;
    try { return new Date(event.event_date) < new Date(new Date().toDateString()); }
    catch (_) { return false; }
  })();

  async function submit() {
    if (!newDate) { toast("Pick a new date", "err"); return; }
    setBusy(true);
    try {
      const body = {
        event_date:  newDate,
        event_time:  newTime || null,
        title:       (newTitle && newTitle !== event.title) ? newTitle : null,
        copy_admins: copyAdmins,
        status:      "published",
      };
      const res = await api(`/events/${event.id}/recreate`, {
        method: "POST", body, token,
      });
      toast(`Event recreated · #${res.id}`);
      onCreated && onCreated(res);
    } catch (e) {
      toast(e.message, "err");
    } finally { setBusy(false); }
  }

  return (
    <FormModal
      title={`Recreate event #${event?.id}${isPast ? "  (past)" : ""}`}
      windowId={`event_${event?.id}_recreate`}
      onClose={busy ? undefined : onClose}
      width={520}
    >
      <div style={{ fontSize: 12, color: colors.fgMid, lineHeight: 1.6, marginBottom: 14 }}>
        A new event will be created with the data below. <b>Everything else</b> —
        location, organizer, tags, capacity, pricing, matching settings, table
        configuration — is copied from the source. Registrations, matches and
        chats are <b>not</b> copied; the new event starts clean.
      </div>

      <div style={{
        background: colors.bgDeep, border: `1px solid ${colors.border}`,
        borderRadius: 8, padding: "8px 12px", marginBottom: 14,
        fontSize: 11, color: colors.fgSub,
      }}>
        <div>Source: <b style={{ color: colors.fg }}>{event?.title || "—"}</b></div>
        <div>Original date: {event?.event_date || "—"} {event?.event_time || ""}</div>
      </div>

      <label style={{ display: "block", fontSize: 11, color: colors.fgSub, fontWeight: 700,
                      letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 4 }}>
        New title (optional)
      </label>
      <input
        className="st-input"
        value={newTitle}
        onChange={e => setNewTitle(e.target.value)}
        placeholder="Same as source"
        disabled={busy}
        style={{ marginBottom: 14 }}
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
                         letterSpacing: ".07em", textTransform: "uppercase" }}>
            New date *
          </span>
          <input
            type="date"
            className="st-input"
            value={newDate}
            min={todayPlus(0)}
            onChange={e => setNewDate(e.target.value)}
            disabled={busy}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: colors.fgSub, fontWeight: 700,
                         letterSpacing: ".07em", textTransform: "uppercase" }}>
            New time
          </span>
          <input
            type="time"
            className="st-input"
            value={newTime || ""}
            onChange={e => setNewTime(e.target.value)}
            disabled={busy}
          />
        </label>
      </div>

      <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer",
                      fontSize: 13, color: colors.fgMid, marginBottom: 18 }}>
        <input type="checkbox" checked={copyAdmins}
               onChange={e => setCopyAdmins(e.target.checked)}
               disabled={busy}
               style={{ accentColor: colors.primary }} />
        Copy co-administrators
      </label>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn variant="primary" onClick={submit} loading={busy}
             disabled={busy || !newDate}>
          ↻ Recreate event
        </Btn>
      </div>
    </FormModal>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  TABS: EVENTS
// ════════════════════════════════════════════════════════════════════════════
function TabEvents({ token, onSelectUser, onOpenChat }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("");
  const [openEventIds, setOpenEventIds] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "", location: "", event_date: "", event_time: "",
    capacity: "", organizer_name: "", is_free: true,
    ticket_price: "", currency: "EUR", allow_external_matching: false,
    description: "", status: "published",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const debRef = useRef();
  const toast = useToast();
  const winMgr = useWindowMgr();
  function openEvent(id) {
    if (!id) return;
    const eid = Number(id);
    setOpenEventIds(prev => prev.includes(eid) ? prev : [...prev, eid]);
    winMgr.bringToFront(`event_${eid}`);
  }
  function closeEvent(id) {
    const eid = Number(id);
    setOpenEventIds(prev => prev.filter(x => x !== eid));
    load(page, search, statusF);
  }
  const load = useCallback((pg, sq, sf) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 20 });
    if (sq) p.set("search", sq);
    if (sf) p.set("status", sf);
    api(`/events?${p}`, { token }).then(setData).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(page, search, statusF); }, [page, search, statusF]);
  async function createEvent() {
    if (!createForm.title.trim()) { toast("Enter a title", "warn"); return; }
    setCreateLoading(true);
    try {
      const body = { ...createForm };
      if (body.capacity === "" || body.capacity == null) delete body.capacity;
      else body.capacity = Number(body.capacity);
      if (body.ticket_price === "" || body.ticket_price == null) delete body.ticket_price;
      else body.ticket_price = Number(body.ticket_price);
      const r = await api("/events", { method: "POST", body, token });
      toast(`Event created #${r.id}`);
      setCreateModal(false);
      setCreateForm({ title: "", location: "", event_date: "", event_time: "",
        capacity: "", organizer_name: "", is_free: true, ticket_price: "", currency: "EUR",
        allow_external_matching: false, description: "", status: "published" });
      load(1, search, statusF);
    } catch (e) { toast(e.message, "err"); }
    finally { setCreateLoading(false); }
  }
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input className="st-input" style={{ maxWidth: 280 }} placeholder="Search by title, city..."
          onChange={e => {
            clearTimeout(debRef.current);
            const v = e.target.value;
            debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 380);
          }} />
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {["", "published", "archived", "cancelled"].map(s => (
            <button key={s || "all"} onClick={() => { setStatusF(s); setPage(1); }} style={{
              padding: "7px 11px", borderRadius: 7,
              border: `1px solid ${s === statusF ? colors.primary : colors.border}`,
              background: s === statusF ? colors.primaryDim : "transparent",
              color: s === statusF ? colors.accent : colors.fgSub,
              cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12, transition: "all .15s",
            }}>{s || "All"}</button>
          ))}
        </div>
        <Btn sm onClick={() => setCreateModal(true)} style={{ marginLeft: "auto" }}>+ Create Event</Btn>
      </div>
      {loading ? (
        <div className="st-card" style={{ overflow: "hidden" }}>
          <table className="st-table"><thead><tr>
            {[1,2,3,4,5].map(i => <th key={i}><Skeleton height={10} width="60%" /></th>)}
          </tr></thead><tbody>
            {Array.from({length:7}).map((_,i) => <SkeletonRow key={i} cols={5}/>)}
          </tbody></table></div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>{data?.total ?? 0} events</div>
          <div className="st-card" style={{ overflow: "hidden" }}>
            <div className="st-table-scroll">
              <table className="st-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Date</th>
                    <th>Participants</th>
                    <th>Matches</th>
                    <th>Requests</th>
                    <th>Ticket</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data?.events?.map(e => (
                    <tr key={e.id} onClick={() => openEvent(e.id)}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{e.title}</div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2, flexWrap: "wrap" }}>
                          {e.location && <span style={{ fontSize: 11, color: colors.fgSub }}>📍 {e.location}</span>}
                          {e.organizer_name && <span style={{ fontSize: 10, color: colors.fgFaint }}>· {e.organizer_name}</span>}
                        </div>
                        <div className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, marginTop: 1 }}>
                          #{e.id}{e.slug ? ` · /${e.slug}` : ""}
                        </div>
                      </td>
                      <td>
                        <span className="st-mono" style={{ fontSize: 11, color: colors.fgMid }}>{e.event_date || "—"}</span>
                        {e.event_time && <div className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>{e.event_time}</div>}
                      </td>
                      <td>
                        <span className="st-mono" style={{ color: colors.primary, fontWeight: 700, fontSize: 13 }}>
                          {e.participants ?? 0}
                          {e.capacity && <span style={{ color: colors.fgFaint, fontWeight: 400 }}>/{e.capacity}</span>}
                        </span>
                        {e.capacity ? (
                          <div style={{ marginTop: 3, height: 3, width: 52, background: colors.border, borderRadius: 99, overflow: "hidden" }}>
                            <div style={{
                              height: "100%", borderRadius: 99,
                              width: `${Math.min(100, Math.round(((e.participants || 0) / e.capacity) * 100))}%`,
                              background: (e.participants || 0) >= e.capacity ? colors.red : colors.primary,
                            }} />
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <span className="st-mono" style={{ color: colors.green, fontWeight: 700, fontSize: 13 }}>{e.matches_count ?? 0}</span>
                      </td>
                      <td>
                        <span className="st-mono" style={{ color: colors.blue, fontWeight: 600, fontSize: 12 }}>
                          {e.search_requests_count ?? e.requests_count ?? "—"}
                        </span>
                      </td>
                      <td>
                        {e.is_free
                          ? <Tag color={colors.green}>Free</Tag>
                          : <span className="st-mono" style={{ fontSize: 11 }}>
                              <span style={{ color: colors.orange, fontWeight: 600 }}>{e.ticket_price}</span>
                              {" "}<span style={{ color: colors.fgSub }}>{e.currency}</span>
                            </span>}
                      </td>
                      <td><EventStatusTag s={e.status} /></td>
                      <td style={{ color: colors.fgSub, fontSize: 16 }}>›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </>
      )}
      {openEventIds.map(eid => (
        <EventDrawer
          key={`event_${eid}`}
          eventId={eid}
          token={token}
          onClose={() => closeEvent(eid)}
          onSelectUser={onSelectUser}
          onOpenChat={onOpenChat}
        />
      ))}
      {createModal && (
        <FormModal title="Create Event" onClose={() => setCreateModal(false)} width={560}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            {[
              ["Title *", "title", "text"],
              ["Location", "location", "text"],
              ["Date (YYYY-MM-DD)", "event_date", "date"],
              ["Time (HH:MM)", "event_time", "time"],
              ["Organizer", "organizer_name", "text"],
              ["Capacity", "capacity", "number"],
              ["Price", "ticket_price", "number"],
              ["Currency", "currency", "text"],
            ].map(([label, key, type]) => (
              <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                  letterSpacing: ".07em", textTransform: "uppercase" }}>{label}</span>
                <input type={type} className="st-input"
                  value={createForm[key] ?? ""}
                  onChange={e => setCreateForm(f => ({ ...f, [key]: e.target.value }))} />
              </label>
            ))}
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
            <span style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
              letterSpacing: ".07em", textTransform: "uppercase" }}>Description</span>
            <textarea className="st-input" rows={2}
              value={createForm.description}
              onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
              style={{ resize: "vertical" }} />
          </label>
          <div style={{ display: "flex", gap: 14, marginBottom: 12, flexWrap: "wrap" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={createForm.is_free}
                onChange={e => setCreateForm(f => ({ ...f, is_free: e.target.checked }))} />
              <span style={{ fontSize: 13, color: colors.fgMid }}>Free</span>
            </label>
            <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={createForm.allow_external_matching}
                onChange={e => setCreateForm(f => ({ ...f, allow_external_matching: e.target.checked }))} />
              <span style={{ fontSize: 13, color: colors.fgMid }}>External Matching</span>
            </label>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: colors.fgMid }}>Status: </span>
            <select className="st-input" style={{ width: 130 }} value={createForm.status}
              onChange={e => setCreateForm(f => ({ ...f, status: e.target.value }))}>
              {["published", "archived", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setCreateModal(false)}>Cancel</Btn>
            <Btn loading={createLoading} onClick={createEvent}>Create</Btn>
          </div>
        </FormModal>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TABS: BLACKLIST
// ════════════════════════════════════════════════════════════════════════════
function TabBlacklist({ token }) {
  const { colors } = useTheme();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useStickyState("blacklist_page", 1);
  const [search, setSearch]     = useStickyState("blacklist_search", "");
  const [showModal, setShowModal] = useState(false);
  const [actLoading, setActLoading] = useState(false);
  // Add form — single smart input
  const [inputVal, setInputVal] = useState("");
  const [sugg, setSugg]         = useState([]);   // user suggestions
  const [sugLoading, setSugLoading] = useState(false);
  const [showSug, setShowSug]   = useState(false);
  const [selUser, setSelUser]   = useState(null); // selected user
  const [reason, setReason]     = useState("");
  const sugTmr  = useRef();
  const blurTmr = useRef();
  const debRef  = useRef();
  const toast   = useToast();
  const confirm = useConfirm();

  // ── load list ─────────────────────────────────────────────────────
  const load = useCallback((pg, sq) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 30 });
    if (sq) p.set("search", sq);
    api(`/blacklist?${p}`, { token }).then(setData).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(page, search); }, [page, search]);

  // ── live-autocomplete ────────────────────────────────────────────────────
  function handleInput(v) {
    setInputVal(v);
    setSelUser(null);
    clearTimeout(sugTmr.current);
    if (!v.trim()) { setSugg([]); setShowSug(false); return; }
    setShowSug(true);
    setSugLoading(true);
    sugTmr.current = setTimeout(() => {
      api(`/users/simple?search=${encodeURIComponent(v)}&limit=20`, { token })
        .then(r => setSugg(Array.isArray(r) ? r : []))
        .catch(() => setSugg([]))
        .finally(() => setSugLoading(false));
    }, 280);
  }

  function pickUser(u) {
    setSelUser(u);
    setInputVal(u.email || u.username || `#${u.id}`);
    setSugg([]); setShowSug(false);
  }

  function openModal() {
    setInputVal(""); setSelUser(null); setSugg([]); setShowSug(false);
    setReason(""); setShowModal(true);
  }

  async function addEntry() {
    const body = {};
    if (reason.trim()) body.reason = reason.trim();
    if (selUser) {
      body.user_id = selUser.id;
      if (selUser.email) body.email = selUser.email;
      if (selUser.name)  body.name  = selUser.name;
    } else {
      const v = inputVal.trim();
      if (!v) return toast("Enter email, @username or #ID", "warn");
      if (v.includes("@") && !v.startsWith("@")) { body.email = v; }
      else if (v.startsWith("#"))  { const n = parseInt(v.slice(1)); if (isNaN(n)) return toast("Invalid ID", "warn"); body.user_id = n; }
      else if (!isNaN(v) && v.length > 0) { body.user_id = parseInt(v); }
      else { body.email = v; }  // username-only — send as email, backend will handle it
    }
    setActLoading(true);
    try {
      await api("/blacklist", { method: "POST", body, token });
      toast("Added to blacklist");
      setShowModal(false);
      load(1, search);
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(false); }
  }

  async function deactivate(id, email) {
    const ok = await confirm("Remove from blacklist?", `${email} will be unblocked.`, { danger: false });
    if (!ok) return;
    try {
      await api(`/blacklist/${id}`, { method: "DELETE", token });
      toast(`Removed: ${email}`);
      load(page, search);
    } catch (e) { toast(e.message, "err"); }
  }

  return (
    <>
      {/* ── toolbar ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input className="st-input" style={{ maxWidth: 280 }}
          placeholder="Search by email, name..."
          onChange={e => {
            clearTimeout(debRef.current);
            const v = e.target.value;
            debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 380);
          }} />
        <Btn onClick={openModal}>+ Add</Btn>
      </div>

      {/* ── table ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Spinner size={28} />
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>
            {data?.total ?? 0} entries
          </div>
          <div className="st-card" style={{ overflow: "hidden" }}>
            <div className="st-table-scroll"><table className="st-table">
              <thead><tr>
                <th>Email</th><th>Name</th><th>User ID</th>
                <th>Reason</th><th>Added By</th><th>Date</th><th />
              </tr></thead>
              <tbody>
                {data?.items?.map(b => (
                  <tr key={b.id} style={{ borderLeft: `3px solid ${colors.red}44` }}>
                    <td>
                      <span className="st-mono" style={{ fontSize: 12 }}>{b.email || "—"}</span>
                      {b.email && <CopyBtn text={b.email} />}
                    </td>
                    <td><span style={{ fontSize: 13 }}>{b.name || "—"}</span></td>
                    <td>
                      <span className="st-mono" style={{ fontSize: 11, color: colors.fgSub }}>{b.user_id || "—"}</span>
                      {b.user_id && <CopyBtn text={b.user_id} />}
                    </td>
                    <td><span title={b.reason || ""} style={{ fontSize: 12, color: colors.fgSub, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{b.reason || "—"}</span></td>
                    <td><span style={{ fontSize: 12, color: colors.fgSub }}>{b.added_by}</span></td>
                    <td><span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                      {b.created_at ? new Date(b.created_at).toLocaleDateString("en") : "—"}
                    </span></td>
                    <td><Btn sm variant="ghost" onClick={() => deactivate(b.id, b.email)}>Remove</Btn></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </>
      )}

      {/* ══ Add to blacklist modal with smart search ══════════════════════ */}
      {showModal && (
        <WindowModal
          windowId="blacklist_add"
          title="Add to Blacklist"
          onClose={() => setShowModal(false)}
          width={640}
          height={560}
        >
            <div style={{ display: "flex", alignItems: "center",
              justifyContent: "space-between", marginBottom: 18 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: colors.fg }}>Add to Blacklist</div>
              <button onClick={() => setShowModal(false)} style={{ background: "none",
                border: "none", color: colors.fgSub, fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>

            {/* Smart input */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 5 }}>
                Email / @username / #ID
              </div>
              <div style={{ position: "relative" }}>
                <input
                  className="st-input"
                  placeholder="email@domain.com,  @username  or  #12345"
                  value={inputVal}
                  autoComplete="off"
                  onChange={e => handleInput(e.target.value)}
                  onFocus={() => sugg.length > 0 && setShowSug(true)}
                  onBlur={() => { blurTmr.current = setTimeout(() => setShowSug(false), 200); }}
                />
                {/* ── suggestions dropdown ─────────────────────────── */}
                {showSug && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 3px)", left: 0, right: 0,
                    background: colors.card, border: `1px solid ${colors.borderMid}`,
                    borderRadius: 9, zIndex: 500, maxHeight: 250, overflowY: "auto",
                    boxShadow: "0 8px 32px rgba(0,0,0,.5)",
                  }}>
                    {sugLoading && (
                      <div style={{ display: "flex", justifyContent: "center", padding: 14 }}>
                        <Spinner size={16} />
                      </div>
                    )}
                    {!sugLoading && sugg.length === 0 && (
                      <div style={{ padding: "12px 14px", color: colors.fgSub, fontSize: 12 }}>
                        No users found — enter email manually
                      </div>
                    )}
                    {!sugLoading && sugg.map(u => (
                      <div key={u.id}
                        onMouseDown={() => { clearTimeout(blurTmr.current); pickUser(u); }}
                        style={{ display: "flex", gap: 10, alignItems: "center",
                          padding: "9px 14px", cursor: "pointer",
                          borderBottom: `1px solid ${colors.border}` }}
                        onMouseEnter={e => e.currentTarget.style.background = colors.cardHover}
                        onMouseLeave={e => e.currentTarget.style.background = ""}
                      >
                        <Avatar user={u} size={28} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {u.name}
                          </div>
                          <div style={{ display: "flex", gap: 8, marginTop: 1 }}>
                            {u.email && (
                              <span className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                                {u.email}
                              </span>
                            )}
                            {u.username && (
                              <span style={{ fontSize: 10, color: colors.fgFaint }}>@{u.username}</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 5, flexShrink: 0, alignItems: "center" }}>
                          <RoleTag r={u.role} />
                          <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                            #{u.id}
                          </span>
                        </div>
                      </div>
                    ))}
                    <div style={{ padding: "6px 14px", borderTop: `1px solid ${colors.border}`,
                      fontSize: 10, color: colors.fgFaint, fontStyle: "italic" }}>
                      Or enter email manually and click Add
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Selected user */}
            {selUser && (
              <div style={{ display: "flex", gap: 10, alignItems: "center",
                background: colors.primaryDim, border: `1px solid ${colors.borderMid}`,
                borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
                <span style={{ color: colors.green }}>✓</span>
                <Avatar user={selUser} size={22} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: colors.accent, fontSize: 13 }}>
                    {selUser.name}
                  </span>
                  <span className="st-mono" style={{ color: colors.fgSub, fontSize: 10, marginLeft: 8 }}>
                    {selUser.email} · #{selUser.id}
                  </span>
                </div>
                <button onClick={() => { setSelUser(null); setInputVal(""); }}
                  style={{ background: "none", border: "none",
                    color: colors.fgSub, cursor: "pointer", fontSize: 14 }}>✕</button>
              </div>
            )}

            {/* Reason + button */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input className="st-input" placeholder="Reason (optional)"
                value={reason} onChange={e => setReason(e.target.value)} />
              <Btn loading={actLoading} onClick={addEntry}>Add to Blacklist</Btn>
            </div>
        </WindowModal>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TYPED CONFIRM MODAL — для критичных операций (bulk delete и т.п.)
//  Пользователь должен ввести точную фразу, иначе кнопка Confirm заблокирована.
//  Защищает от случайного клика "Confirm" на массовом удалении.
// ════════════════════════════════════════════════════════════════════════════
function TypedConfirmModal({ title, text, confirmPhrase, onConfirm, onClose, danger = true, busy }) {
  const { colors } = useTheme();
  const [typed, setTyped] = useState("");
  const match = typed.trim() === confirmPhrase;

  return (
    <WindowModal
      windowId={_safeWindowId("typed_confirm", title)}
      title={title || "Confirm"}
      onClose={busy ? undefined : onClose}
      width={520}
      height={360}
      minHeight={300}
    >
        <div style={{ fontSize: 13, color: colors.fgMid, lineHeight: 1.65, marginBottom: 16 }}>
          {text}
        </div>
        <div style={{ fontSize: 12, color: colors.fgSub, marginBottom: 6 }}>
          Enter <span className="st-mono" style={{
            color: colors.red, background: colors.redDim,
            padding: "1px 6px", borderRadius: 4, userSelect: "none",
          }}>{confirmPhrase}</span> to confirm:
        </div>
        <input
          className="st-input st-mono"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          value={typed}
          onChange={e => setTyped(e.target.value)}
          onPaste={e => e.preventDefault()}  /* нельзя вставить — только набрать руками */
          placeholder={confirmPhrase}
          style={{ marginBottom: 20, borderColor: match ? colors.green : colors.border }}
          disabled={busy}
        />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn
            variant={danger ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={!match || busy}
            loading={busy}
          >
            {danger ? "Delete" : "Confirm"}
          </Btn>
        </div>
    </WindowModal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TABS: FEEDBACK  — с удалением, bulk-удалением и cleanup-режимом
//  Подвкладки: Messages (классический feedback) и Support Chats
// ════════════════════════════════════════════════════════════════════════════
function TabFeedback({ token }) {
  const [sub, setSub] = useStickyState("feedback_sub", "messages");
  return (
    <>
      <PillTabs
        tabs={[
          { id: "messages", label: "Messages"      },
          { id: "support",  label: "Support Chats" },
        ]}
        active={sub}
        onChange={setSub}
      />
      <div style={{ marginTop: 16 }}>
        {sub === "messages" && <FeedbackMessages    token={token} />}
        {sub === "support"  && <FeedbackSupportChats token={token} />}
      </div>
    </>
  );
}


function FeedbackMessages({ token }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useStickyState("feedback_page", 1);
  const [statusF, setStatusF] = useStickyState("feedback_status", "new");
  const [expanded, setExpanded] = useState(null);

  // Режим массового выбора
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected]     = useState(() => new Set());

  // Модалки
  const [deleteOne, setDeleteOne]   = useState(null);  // feedback-объект для одиночного удаления
  const [bulkOpen, setBulkOpen]     = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [busy, setBusy]             = useState(false);

  const toast = useToast();

  const load = useCallback((pg, sf) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 30 });
    if (sf) p.set("status", sf);
    api(`/feedback?${p}`, { token })
      .then(setData)
      .catch(e => toast(e.message, "err"))
      .finally(() => setLoading(false));
  }, [token, toast]);

  useEffect(() => { load(page, statusF); }, [page, statusF, load]);

  // Сброс выбора при переключении фильтров/страниц
  useEffect(() => { setSelected(new Set()); }, [page, statusF]);

  async function setStatus(id, s) {
    try {
      await api(`/feedback/${id}/status`, { method: "POST", body: { new_status: s }, token });
      toast(`Status updated: ${s}`);
      load(page, statusF);
    } catch (e) { toast(e.message, "err"); }
  }

  async function confirmDeleteOne() {
    if (!deleteOne) return;
    setBusy(true);
    try {
      await api(`/feedback/${deleteOne.id}`, { method: "DELETE", token });
      toast(`Feedback #${deleteOne.id} deleted`);
      setDeleteOne(null);
      load(page, statusF);
    } catch (e) {
      toast(e.message, "err");
    } finally {
      setBusy(false);
    }
  }

  async function confirmBulkDelete() {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await api(`/feedback/bulk-delete`, { method: "POST", body: { ids }, token });
      toast(`Deleted ${res.deleted} feedback entries`);
      setSelected(new Set());
      setSelectMode(false);
      setBulkOpen(false);
      load(page, statusF);
    } catch (e) {
      toast(e.message, "err");
    } finally {
      setBusy(false);
    }
  }

  async function confirmCleanup(days) {
    setBusy(true);
    try {
      const res = await api(`/feedback/bulk-delete`, {
        method: "POST",
        body: { status_filter: "resolved", older_than_days: days },
        token,
      });
      toast(`Cleanup: deleted ${res.deleted} old resolved entries`);
      setCleanupOpen(false);
      load(page, statusF);
    } catch (e) {
      toast(e.message, "err");
    } finally {
      setBusy(false);
    }
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }
  function selectAllVisible() {
    const ids = (data?.items || []).map(f => f.id);
    setSelected(new Set(ids));
  }
  function clearSelection() { setSelected(new Set()); }

  return (
    <>
      {/* Фильтры + toolbar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        {["", "new", "contacted", "resolved"].map(s => (
          <button key={s || "all"} onClick={() => { setStatusF(s); setPage(1); }} style={{
            padding: "7px 12px", borderRadius: 7,
            border: `1px solid ${s === statusF ? colors.primary : colors.border}`,
            background: s === statusF ? colors.primaryDim : "transparent",
            color: s === statusF ? colors.accent : colors.fgSub,
            cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12,
          }}>{s || "All"}</button>
        ))}
        <div style={{ flex: 1 }} />
        <Btn sm variant={selectMode ? "primary" : "ghost"} onClick={() => {
          setSelectMode(m => !m); clearSelection();
        }}>
          {selectMode ? "✕ Cancel select" : "☑ Select"}
        </Btn>
        <Btn sm variant="ghost" onClick={() => setCleanupOpen(true)}>
          🧹 Cleanup old
        </Btn>
      </div>

      {/* Панель массовых действий */}
      {selectMode && (
        <div className="st-card" style={{
          padding: "10px 14px", marginBottom: 10,
          borderLeft: `3px solid ${colors.primary}`,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.fg }}>
            Selected: <span style={{ color: colors.primary }}>{selected.size}</span>
          </span>
          <Btn sm variant="ghost" onClick={selectAllVisible} disabled={busy}>Select all visible</Btn>
          <Btn sm variant="ghost" onClick={clearSelection} disabled={busy || selected.size === 0}>Clear</Btn>
          <div style={{ flex: 1 }} />
          <Btn sm variant="danger" disabled={selected.size === 0 || busy}
               onClick={() => setBulkOpen(true)}>
            🗑 Delete selected ({selected.size})
          </Btn>
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="st-card" style={{ padding: "14px 16px" }}>
              <SkeletonCard lines={3} />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>
            {data?.total ?? 0} submissions
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {data?.items?.map(f => {
              const feedbackBorder = { new: colors.orange, contacted: colors.blue, resolved: colors.green }[f.status] || colors.border;
              const isSelected = selected.has(f.id);
              return (
              <div key={f.id} className="st-card" style={{
                overflow: "hidden",
                borderLeft: `3px solid ${isSelected ? colors.primary : feedbackBorder}`,
                background: isSelected ? colors.primaryDim : undefined,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px" }}>
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(f.id)}
                      onClick={e => e.stopPropagation()}
                      style={{ width: 18, height: 18, cursor: "pointer", accentColor: colors.primary }}
                      aria-label={`Select feedback ${f.id}`}
                    />
                  )}
                  <div onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                       style={{ flex: 1, cursor: "pointer" }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{f.name || f.email}</div>
                    <div className="st-mono" style={{ fontSize: 11, color: colors.fgSub, display: "flex", alignItems: "center", gap: 2 }}>
                      {f.email}<CopyBtn text={f.email} />
                    </div>
                    <FeedbackStatusTag s={f.status} />
                    <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, marginLeft: 6 }}>
                      #{f.id} · {f.created_at ? new Date(f.created_at).toLocaleDateString("en") : "—"}
                    </span>
                  </div>
                  <span style={{ color: colors.fgFaint, cursor: "pointer" }}
                        onClick={() => setExpanded(expanded === f.id ? null : f.id)}>
                    {expanded === f.id ? "▲" : "▼"}
                  </span>
                </div>
                {expanded === f.id && (
                  <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${colors.border}`, background: colors.cardMid }}>
                    {/* message рендерится как текст (React экранирует → XSS-safe) */}
                    <p style={{ fontSize: 13, color: colors.fgMid, lineHeight: 1.7, padding: "14px 0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {f.message}
                    </p>
                    {f.phone && <div style={{ fontSize: 12, color: colors.fgSub, marginBottom: 12 }}>📞 {f.phone}</div>}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {["new", "contacted", "resolved"].filter(s => s !== f.status).map(s => (
                        <Btn key={s} sm variant="ghost" onClick={() => setStatus(f.id, s)}>→ {s}</Btn>
                      ))}
                      <div style={{ flex: 1 }} />
                      <Btn sm variant="danger" onClick={() => setDeleteOne(f)}>🗑 Delete</Btn>
                    </div>
                  </div>
                )}
              </div>
              );})}
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </>
      )}

      {/* Модалки */}
      {deleteOne && (
        <ConfirmModal
          title="Delete feedback?"
          text={`Feedback #${deleteOne.id} from ${deleteOne.email} will be permanently deleted. A snapshot is kept in admin action logs.`}
          danger
          onConfirm={confirmDeleteOne}
          onClose={() => !busy && setDeleteOne(null)}
        />
      )}
      {bulkOpen && (
        <TypedConfirmModal
          title={`Delete ${selected.size} feedback entries?`}
          text={`This will permanently delete ${selected.size} feedback item${selected.size === 1 ? "" : "s"}. Each deletion is logged to admin_action_logs. This action cannot be undone.`}
          confirmPhrase={`DELETE ${selected.size}`}
          danger
          busy={busy}
          onConfirm={confirmBulkDelete}
          onClose={() => !busy && setBulkOpen(false)}
        />
      )}
      {cleanupOpen && (
        <CleanupModal
          busy={busy}
          onClose={() => !busy && setCleanupOpen(false)}
          onConfirm={confirmCleanup}
        />
      )}
    </>
  );
}

// Мини-модалка для чистки старых resolved
function CleanupModal({ onConfirm, onClose, busy }) {
  const { colors } = useTheme();
  const [days, setDays] = useState(90);
  const [confirmed, setConfirmed] = useState(false);

  return (
    <WindowModal
      windowId="feedback_cleanup"
      title="Cleanup resolved feedback"
      onClose={busy ? undefined : onClose}
      width={500}
      height={360}
      minHeight={300}
    >
        <div style={{ fontSize: 13, color: colors.fgMid, lineHeight: 1.65, marginBottom: 16 }}>
          Permanently delete all feedback with status <b>resolved</b> older than the threshold.
          Limited to 500 records per call for safety.
        </div>
        <label style={{ display: "block", fontSize: 12, color: colors.fgSub, marginBottom: 6 }}>
          Older than (days):
        </label>
        <input
          type="number"
          min={0}
          max={3650}
          value={days}
          onChange={e => setDays(Math.max(0, Math.min(3650, parseInt(e.target.value || "0", 10))))}
          className="st-input st-mono"
          style={{ marginBottom: 16 }}
          disabled={busy}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: colors.fgMid, marginBottom: 20, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={e => setConfirmed(e.target.checked)}
            style={{ accentColor: colors.primary }}
            disabled={busy}
          />
          I understand this is permanent
        </label>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn
            variant="danger"
            onClick={() => onConfirm(days)}
            disabled={!confirmed || busy}
            loading={busy}
          >Cleanup</Btn>
        </div>
    </WindowModal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  SUPPORT CHATS  — подвкладка Feedback → Support Chats
//  Список саппорт-чатов слева, выбранный чат справа (история + ответ).
//  API:
//    GET  /mod/support/chats         список (фильтры status/only_unanswered/only_mine)
//    GET  /mod/support/chats/{id}    карточка + сообщения
//    POST /mod/support/chats/{id}/reply   { content }
//    POST /mod/support/chats/{id}/status  { status: open|closed }
//    POST /mod/support/chats/{id}/assign  { assignee_id }
//    GET  /mod/support/staff         кому можно назначать
//    GET  /mod/support/stats         сводка
// ════════════════════════════════════════════════════════════════════════════
function FeedbackSupportChats({ token }) {
  const { colors } = useTheme();
  const toast = useToast();

  // ── list state ──────────────────────────────────────────────────────────
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [page, setPage]         = useStickyState("sup_page", 1);
  const [statusF, setStatusF]   = useStickyState("sup_status", "open");
  const [onlyUnans, setOnlyU]   = useStickyState("sup_only_unans", false);
  const [onlyMine, setOnlyMine] = useStickyState("sup_only_mine", false);
  const [loadingList, setLoadingList] = useState(false);

  // ── selected chat state ─────────────────────────────────────────────────
  const [selId, setSelId] = useStickyState("sup_sel_id", null);
  const [chat, setChat]   = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [actBusy, setActBusy] = useState(false);

  // ── modals ──────────────────────────────────────────────────────────────
  const [staffOpen, setStaffOpen]     = useState(false);
  const [closeOpen, setCloseOpen]     = useState(false);

  // ── staff list (for assignment dropdown + manage modal) ─────────────────
  const [staff, setStaff] = useState([]);

  // ── loaders ─────────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const p = new URLSearchParams({ page, limit: 30, status: statusF });
      if (onlyUnans) p.set("only_unanswered", "true");
      if (onlyMine)  p.set("only_mine", "true");
      const d = await api(`/support/chats?${p}`, { token });
      setItems(d.items || []);
      setTotal(d.total || 0);
      setPages(d.pages || 1);
    } catch (e) {
      toast(e.message, "err");
    } finally { setLoadingList(false); }
  }, [token, toast, page, statusF, onlyUnans, onlyMine]);

  useEffect(() => { loadList(); }, [loadList]);

  // auto-refresh list every 15s while tab is open
  useEffect(() => {
    const id = setInterval(loadList, 15000);
    return () => clearInterval(id);
  }, [loadList]);

  const loadChat = useCallback(async (id) => {
    if (!id) { setChat(null); return; }
    try {
      const d = await api(`/support/chats/${id}`, { token });
      setChat(d);
    } catch (e) {
      toast(e.message, "err");
      setChat(null);
    }
  }, [token, toast]);

  useEffect(() => { loadChat(selId); }, [selId, loadChat]);

  // auto-refresh open chat every 10s
  useEffect(() => {
    if (!selId) return;
    const id = setInterval(() => loadChat(selId), 10000);
    return () => clearInterval(id);
  }, [selId, loadChat]);

  // load staff (reusable: also called after grant/revoke role)
  const loadStaff = useCallback(async () => {
    try {
      const d = await api("/support/staff", { token });
      setStaff(d.items || []);
    } catch (_) {}
  }, [token]);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  // ── actions ─────────────────────────────────────────────────────────────
  async function sendReply() {
    const content = reply.trim();
    if (!content || !chat?.id) return;
    setSending(true);
    try {
      await api(`/support/chats/${chat.id}/reply`, {
        method: "POST", body: { content }, token,
      });
      setReply("");
      toast("Reply sent");
      await loadChat(chat.id);
      await loadList();
    } catch (e) { toast(e.message, "err"); }
    finally { setSending(false); }
  }

  async function setChatStatus(next) {
    if (!chat?.id) return;
    setActBusy(true);
    try {
      await api(`/support/chats/${chat.id}/status`, {
        method: "POST", body: { status: next }, token,
      });
      toast(`Chat ${next}`);
      setCloseOpen(false);
      await loadChat(chat.id);
      await loadList();
    } catch (e) { toast(e.message, "err"); }
    finally { setActBusy(false); }
  }
  function toggleStatus() {
    if (!chat?.id) return;
    if (chat.status === "open") setCloseOpen(true);   // подтверждение перед закрытием
    else setChatStatus("open");                       // re-open сразу
  }

  async function assignTo(assigneeId) {
    if (!chat?.id) return;
    setActBusy(true);
    try {
      await api(`/support/chats/${chat.id}/assign`, {
        method: "POST", body: { assignee_id: assigneeId }, token,
      });
      toast(assigneeId ? "Assigned" : "Unassigned");
      await loadChat(chat.id);
      await loadList();
    } catch (e) { toast(e.message, "err"); }
    finally { setActBusy(false); }
  }

  // ── render ──────────────────────────────────────────────────────────────
  const STATUS_FILTERS = [
    { id: "open",   label: "Open"   },
    { id: "closed", label: "Closed" },
    { id: "all",    label: "All"    },
  ];

  return (
    <>
      {/* ─── ВЕРХНИЙ ТУЛБАР: счётчик + управление сотрудниками ─── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        marginBottom: 12, paddingBottom: 10,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <div style={{ fontSize: 12, color: colors.fgSub }}>
          <b style={{ color: colors.fg }}>{staff.length}</b> support staff
        </div>
        <div style={{ flex: 1 }} />
        <Btn sm variant="ghost" onClick={loadStaff}>↻ Refresh staff</Btn>
        <Btn sm variant="primary" onClick={() => setStaffOpen(true)}>
          👤 Manage support staff
        </Btn>
      </div>

    <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 360px) 1fr", gap: 16, minHeight: 520 }}>
      {/* ─── LEFT: list ─── */}
      <aside style={{ borderRight: `1px solid ${colors.border}`, paddingRight: 12, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {/* status filter chips */}
        <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
          {STATUS_FILTERS.map(s => (
            <button key={s.id} onClick={() => { setStatusF(s.id); setPage(1); }} style={{
              padding: "6px 10px", borderRadius: 7,
              border: `1px solid ${s.id === statusF ? colors.primary : colors.border}`,
              background: s.id === statusF ? colors.primaryDim : "transparent",
              color: s.id === statusF ? colors.accent : colors.fgSub,
              cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 11,
            }}>{s.label}</button>
          ))}
        </div>

        {/* extra filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", fontSize: 11, color: colors.fgSub }}>
          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
            <input type="checkbox" checked={onlyUnans}
                   onChange={e => { setOnlyU(e.target.checked); setPage(1); }}
                   style={{ accentColor: colors.primary }} />
            Unanswered
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
            <input type="checkbox" checked={onlyMine}
                   onChange={e => { setOnlyMine(e.target.checked); setPage(1); }}
                   style={{ accentColor: colors.primary }} />
            Mine only
          </label>
          <div style={{ marginLeft: "auto", fontSize: 10, opacity: 0.7 }}>
            {total} total
          </div>
        </div>

        {/* chat list */}
        <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          {loadingList && items.length === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="st-card" style={{ padding: 10 }}>
                  <SkeletonCard lines={2} />
                </div>
              ))}
            </div>
          )}
          {!loadingList && items.length === 0 && (
            <div style={{ padding: 18, fontSize: 12, color: colors.fgSub, textAlign: "center" }}>
              No chats
            </div>
          )}
          {items.map(it => {
            const active = it.id === selId;
            const needsReply = it.status === "open"
              && (it.last_sender_kind === "user" || it.last_sender_kind === "system");
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => setSelId(it.id)}
                className="st-card"
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "10px 12px", marginBottom: 6,
                  border: `1px solid ${active ? colors.primary : colors.border}`,
                  background: active ? colors.primaryDim : "transparent",
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
                  <strong style={{ fontSize: 13, color: active ? colors.accent : colors.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {it.user_name || `User #${it.user_id}`}
                  </strong>
                  <span style={{ fontSize: 10, color: colors.fgSub, flexShrink: 0 }}>
                    {it.last_message_at ? new Date(it.last_message_at).toLocaleString() : "—"}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: colors.fgMid, marginTop: 3,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {it.last_message_preview || "(no messages)"}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 5, fontSize: 10, color: colors.fgSub, flexWrap: "wrap" }}>
                  <span>#{it.id}</span>
                  <span>· {it.status}</span>
                  {it.assignee_name && <span>· @ {it.assignee_name}</span>}
                  {needsReply && (
                    <span style={{ color: colors.orange || "#ffb84d", fontWeight: 600 }}>
                      · needs reply
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* pagination */}
        {pages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 6, marginTop: 8, alignItems: "center" }}>
            <Btn sm variant="ghost" disabled={page <= 1}    onClick={() => setPage(p => Math.max(1, p - 1))}>‹</Btn>
            <span style={{ fontSize: 11, color: colors.fgSub }}>{page} / {pages}</span>
            <Btn sm variant="ghost" disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))}>›</Btn>
          </div>
        )}
      </aside>

      {/* ─── RIGHT: selected chat ─── */}
      <section style={{ display: "flex", flexDirection: "column", minHeight: 520 }}>
        {!chat ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
                        flex: 1, color: colors.fgSub, fontSize: 13 }}>
            Select a chat on the left
          </div>
        ) : (
          <>
            {/* header */}
            <header style={{
              display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
              padding: "0 4px 12px", borderBottom: `1px solid ${colors.border}`,
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600, color: colors.fg, fontSize: 14 }}>
                  {chat.user?.name || `User #${chat.user_id}`}
                  <span style={{ color: colors.fgSub, fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                    {chat.user?.email || ""}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: colors.fgSub, marginTop: 2 }}>
                  Chat #{chat.id} · status: <b style={{ color: chat.status === "open" ? colors.green : colors.fgSub }}>{chat.status}</b>
                  {chat.created_at && ` · created ${new Date(chat.created_at).toLocaleString()}`}
                </div>
              </div>

              <select
                value={chat.assigned_to ?? ""}
                disabled={actBusy}
                onChange={e => assignTo(e.target.value ? Number(e.target.value) : null)}
                className="st-input"
                style={{ fontSize: 11, padding: "4px 8px", maxWidth: 180 }}
              >
                <option value="">— unassigned —</option>
                {staff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.role})
                  </option>
                ))}
              </select>

              <Btn variant={chat.status === "open" ? "danger" : "primary"}
                   disabled={actBusy} onClick={toggleStatus}
                   style={{ fontWeight: 700 }}>
                {chat.status === "open" ? "✕ Close ticket" : "↻ Reopen ticket"}
              </Btn>
            </header>

            {/* messages */}
            <div style={{
              flex: 1, overflowY: "auto", padding: "12px 4px",
              display: "flex", flexDirection: "column", gap: 10, minHeight: 0,
            }}>
              {(chat.messages || []).map(m => {
                const align = m.sender_kind === "user"    ? "flex-start"
                            : m.sender_kind === "support" ? "flex-end"
                            : "center";
                const bg    = m.sender_kind === "user"    ? colors.bgDeep || "rgba(255,255,255,0.04)"
                            : m.sender_kind === "support" ? colors.primaryDim
                            : "transparent";
                const isSys = m.sender_kind === "system";
                return (
                  <div key={m.id} style={{ alignSelf: align, maxWidth: isSys ? "70%" : "78%" }}>
                    <div style={{
                      background: bg, borderRadius: 12,
                      padding: "8px 12px", whiteSpace: "pre-wrap", wordBreak: "break-word",
                      fontSize: 13, color: colors.fg,
                      border: isSys ? `1px dashed ${colors.border}` : `1px solid ${colors.border}`,
                      fontStyle: isSys ? "italic" : "normal",
                    }}>
                      {m.content}
                    </div>
                    <div style={{
                      fontSize: 10, color: colors.fgSub, marginTop: 3,
                      textAlign: align === "flex-end" ? "right" : (align === "center" ? "center" : "left"),
                    }}>
                      {m.sender_name || m.sender_kind}
                      {m.created_at && ` · ${new Date(m.created_at).toLocaleString()}`}
                    </div>
                  </div>
                );
              })}
              {(chat.messages || []).length === 0 && (
                <div style={{ color: colors.fgSub, fontSize: 12, textAlign: "center", padding: 24 }}>
                  No messages yet.
                </div>
              )}
            </div>

            {/* composer */}
            {chat.status === "open" ? (
              <div style={{
                display: "flex", gap: 8, paddingTop: 10,
                borderTop: `1px solid ${colors.border}`,
              }}>
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault(); sendReply();
                    }
                  }}
                  placeholder="Write a reply… (Ctrl+Enter to send)"
                  rows={3}
                  maxLength={4000}
                  className="st-input"
                  style={{ flex: 1, resize: "vertical", fontFamily: "inherit", fontSize: 13, padding: 10 }}
                  disabled={sending}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 6, justifyContent: "flex-end" }}>
                  <Btn variant="primary" onClick={sendReply}
                       disabled={sending || !reply.trim()} loading={sending}>
                    Send
                  </Btn>
                  <span style={{ fontSize: 10, color: colors.fgSub, textAlign: "right" }}>
                    {reply.length}/4000
                  </span>
                </div>
              </div>
            ) : (
              <div style={{
                paddingTop: 12, paddingBottom: 6, fontSize: 12, color: colors.fgSub,
                borderTop: `1px solid ${colors.border}`, textAlign: "center",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
              }}>
                <span>Ticket is closed.</span>
                <Btn sm variant="primary" disabled={actBusy} onClick={toggleStatus}>
                  ↻ Reopen ticket
                </Btn>
              </div>
            )}
          </>
        )}
      </section>
    </div>

    {/* ─── МОДАЛКА: подтверждение закрытия тикета ─── */}
    {closeOpen && chat && (
      <ConfirmModal
        title={`Close ticket #${chat.id}?`}
        text={`This will mark the chat with ${chat.user?.name || `user #${chat.user_id}`} as closed. The user won't be able to send messages until you reopen it. You can reopen at any time.`}
        danger
        busy={actBusy}
        onConfirm={() => setChatStatus("closed")}
        onClose={() => !actBusy && setCloseOpen(false)}
      />
    )}

    {/* ─── МОДАЛКА: управление support-сотрудниками ─── */}
    {staffOpen && (
      <ManageSupportStaffModal
        token={token}
        staff={staff}
        onClose={() => setStaffOpen(false)}
        onChanged={() => { loadStaff(); }}
      />
    )}
    </>
  );
}


// ─── Грант/отзыв роли support (только для admin) ────────────────────────────
function ManageSupportStaffModal({ token, staff, onClose, onChanged }) {
  const { colors } = useTheme();
  const toast = useToast();

  const [userIdInput, setUserIdInput] = useState("");
  const [granting, setGranting]       = useState(false);
  const [revoking, setRevoking]       = useState(null);   // id чьего юзера сейчас отзываем
  const [revokeConfirm, setRevokeConfirm] = useState(null); // {id, name}

  async function grantRole() {
    const id = Number((userIdInput || "").trim());
    if (!id || Number.isNaN(id)) {
      toast("Enter a valid user ID", "err");
      return;
    }
    setGranting(true);
    try {
      const res = await api(`/users/${id}/support-role`, {
        method: "POST", body: { role: "support" }, token,
      });
      toast(res.changed ? `Granted support to #${id}` : `#${id} already had support role`);
      setUserIdInput("");
      onChanged && onChanged();
    } catch (e) { toast(e.message, "err"); }
    finally { setGranting(false); }
  }

  async function revokeRole(id) {
    setRevoking(id);
    try {
      const res = await api(`/users/${id}/support-role`, {
        method: "POST", body: { role: "user" }, token,
      });
      toast(res.changed ? `Revoked support from #${id}` : `#${id} was not support`);
      setRevokeConfirm(null);
      onChanged && onChanged();
    } catch (e) { toast(e.message, "err"); }
    finally { setRevoking(null); }
  }

  return (
    <>
      <FormModal title="Manage support staff" onClose={onClose} width={560}>
        {/* GRANT */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: colors.fg, marginBottom: 6 }}>
            Grant <code>support</code> role
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="st-input st-mono"
              placeholder="User ID (e.g. 42)"
              value={userIdInput}
              onChange={e => setUserIdInput(e.target.value.replace(/[^0-9]/g, ""))}
              disabled={granting}
              style={{ flex: 1 }}
              onKeyDown={e => { if (e.key === "Enter") grantRole(); }}
            />
            <Btn variant="primary" onClick={grantRole}
                 loading={granting} disabled={granting || !userIdInput.trim()}>
              Grant
            </Btn>
          </div>
          <div style={{ fontSize: 11, color: colors.fgSub, marginTop: 6 }}>
            User will be able to read & answer every support chat. Cannot grant to other admins.
          </div>
        </div>

        {/* CURRENT STAFF */}
        <div style={{ fontSize: 12, fontWeight: 600, color: colors.fg, marginBottom: 8 }}>
          Current support staff ({staff.length})
        </div>
        <div style={{ maxHeight: 320, overflowY: "auto", border: `1px solid ${colors.border}`, borderRadius: 8 }}>
          {staff.length === 0 ? (
            <div style={{ padding: 18, fontSize: 12, color: colors.fgSub, textAlign: "center" }}>
              No support staff yet.
            </div>
          ) : staff.map(u => (
            <div key={u.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px",
              borderBottom: `1px solid ${colors.border}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: colors.fg, fontWeight: 600,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {u.name || `User #${u.id}`}
                </div>
                <div className="st-mono" style={{ fontSize: 11, color: colors.fgSub }}>
                  #{u.id} · {u.email || "—"}
                </div>
              </div>
              <Btn sm variant="danger"
                   disabled={revoking === u.id}
                   loading={revoking === u.id}
                   onClick={() => setRevokeConfirm({ id: u.id, name: u.name || `User #${u.id}` })}>
                Revoke
              </Btn>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <Btn variant="ghost" onClick={onClose}>Close</Btn>
        </div>
      </FormModal>

      {revokeConfirm && (
        <ConfirmModal
          title={`Revoke support from ${revokeConfirm.name}?`}
          text={`User #${revokeConfirm.id} will lose access to support chats.`}
          danger
          busy={revoking === revokeConfirm.id}
          onConfirm={() => revokeRole(revokeConfirm.id)}
          onClose={() => revoking !== revokeConfirm.id && setRevokeConfirm(null)}
        />
      )}
    </>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  TABS: CONTENT  — модерация event_comments и match_ratings
//  Верхний контейнер с суб-табами Comments / Ratings
// ════════════════════════════════════════════════════════════════════════════
function TabContent({ token }) {
  const [sub, setSub] = useStickyState("content_sub", "comments");
  return (
    <>
      <PillTabs
        tabs={[
          { id: "comments", label: "Event Comments" },
          { id: "ratings",  label: "Match Ratings"  },
        ]}
        active={sub}
        onChange={setSub}
      />
      <div style={{ marginTop: 16 }}>
        {sub === "comments" && <ContentComments token={token} />}
        {sub === "ratings"  && <ContentRatings  token={token} />}
      </div>
    </>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  HUB COMPONENTS
//  Группируют сродные вкладки в один экран с внутренними pill-табами.
//  Каждая отдельная вкладка по-прежнему доступна напрямую через левое меню —
//  HUB просто даёт быстрый «перекрёсток» из самой популярной точки входа.
// ════════════════════════════════════════════════════════════════════════════
function TabUsersHub({ token, onSelectUser, onOpenChat }) {
  const [sub, setSub] = useStickyState("users_hub_sub", "main");
  return (
    <>
      <PillTabs
        tabs={[
          { id: "main",    label: "Users"   },
          { id: "cards",   label: "Cards"   },
          { id: "chats",   label: "Chats"   },
          { id: "matches", label: "Matches" },
          { id: "wallets", label: "Wallets" },
        ]}
        active={sub}
        onChange={setSub}
      />
      <div style={{ marginTop: 16 }}>
        {sub === "main"    && <TabUsers   token={token} onSelectUser={onSelectUser} />}
        {sub === "cards"   && <TabCards   token={token} onSelectUser={onSelectUser} />}
        {sub === "chats"   && <TabChats   token={token} onSelectUser={onSelectUser} onOpenChat={onOpenChat} />}
        {sub === "matches" && <TabMatches token={token} onSelectUser={onSelectUser} onOpenChat={onOpenChat} />}
        {sub === "wallets" && <TabWallets token={token} />}
      </div>
    </>
  );
}

function TabEventsHub({ token, onSelectUser, onOpenChat }) {
  const [sub, setSub] = useStickyState("events_hub_sub", "main");
  return (
    <>
      <PillTabs
        tabs={[
          { id: "main",        label: "Events"         },
          { id: "tables-live", label: "Tables Live"    },
          { id: "comments",    label: "Event Comments" },
        ]}
        active={sub}
        onChange={setSub}
      />
      <div style={{ marginTop: 16 }}>
        {sub === "main"        && <TabEventsAll   token={token} onSelectUser={onSelectUser} onOpenChat={onOpenChat} />}
        {sub === "tables-live" && <TabTablesLive  token={token} />}
        {sub === "comments"    && <ContentComments token={token} />}
      </div>
    </>
  );
}

function TabMatchesHub({ token, onSelectUser, onOpenChat }) {
  const [sub, setSub] = useStickyState("matches_hub_sub", "main");
  return (
    <>
      <PillTabs
        tabs={[
          { id: "main",    label: "Matches"       },
          { id: "ratings", label: "Match Ratings" },
        ]}
        active={sub}
        onChange={setSub}
      />
      <div style={{ marginTop: 16 }}>
        {sub === "main"    && <TabMatches     token={token} onSelectUser={onSelectUser} onOpenChat={onOpenChat} />}
        {sub === "ratings" && <ContentRatings token={token} />}
      </div>
    </>
  );
}

// ─── Event Comments moderation ─────────────────────────────────────────────
function ContentComments({ token }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useStickyState("cmt_page", 1);
  const [eventF, setEventF]   = useStickyState("cmt_event", "");
  const [search, setSearch]   = useStickyState("cmt_search", "");
  const [deletedOnly, setDeletedOnly]     = useStickyState("cmt_deleted_only", false);
  const [includeDeleted, setIncludeDel]   = useStickyState("cmt_include_deleted", true);

  const [detail, setDetail]   = useState(null);       // модалка с деталями
  const [hardDel, setHardDel] = useState(null);
  const [busy, setBusy]       = useState(false);

  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 30 });
    if (eventF) p.set("event_id", eventF);
    if (search) p.set("search", search);
    if (deletedOnly) p.set("deleted_only", "true");
    p.set("include_deleted", includeDeleted ? "true" : "false");
    api(`/event-comments?${p}`, { token })
      .then(setData)
      .catch(e => toast(e.message, "err"))
      .finally(() => setLoading(false));
  }, [token, page, eventF, search, deletedOnly, includeDeleted, toast]);

  useEffect(() => { load(); }, [load]);

  async function softDelete(cid) {
    setBusy(true);
    try {
      await api(`/event-comments/${cid}/soft-delete`, { method: "POST", body: {}, token });
      toast(`Comment #${cid} hidden`);
      load();
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  }

  async function restore(cid) {
    setBusy(true);
    try {
      await api(`/event-comments/${cid}/restore`, { method: "POST", body: {}, token });
      toast(`Comment #${cid} restored`);
      load();
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  }

  async function confirmHardDelete() {
    if (!hardDel) return;
    setBusy(true);
    try {
      await api(`/event-comments/${hardDel.id}`, { method: "DELETE", token });
      toast(`Comment #${hardDel.id} permanently deleted`);
      setHardDel(null);
      load();
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  }

  return (
    <>
      {/* Фильтры */}
      <FilterRow>
        <div style={{ minWidth: 140 }}>
          <label style={{ display: "block", fontSize: 11, color: colors.fgSub, marginBottom: 4 }}>Event ID</label>
          <input
            className="st-input st-mono"
            type="number"
            min={1}
            placeholder="any"
            value={eventF}
            onChange={e => { setEventF(e.target.value.replace(/[^0-9]/g, "")); setPage(1); }}
            style={{ width: 140 }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ display: "block", fontSize: 11, color: colors.fgSub, marginBottom: 4 }}>Search in body</label>
          <SearchInput placeholder="text…" onChange={v => { setSearch(v); setPage(1); }} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: colors.fgMid, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={deletedOnly}
            onChange={e => { setDeletedOnly(e.target.checked); setPage(1); }}
            style={{ accentColor: colors.primary }}
          />
          Soft-deleted only
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: colors.fgMid, cursor: "pointer", opacity: deletedOnly ? 0.5 : 1 }}>
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={e => { setIncludeDel(e.target.checked); setPage(1); }}
            style={{ accentColor: colors.primary }}
            disabled={deletedOnly}
          />
          Show hidden
        </label>
      </FilterRow>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="st-card" style={{ padding: "14px 16px" }}><SkeletonCard lines={2} /></div>
          ))}
        </div>
      ) : !data?.items?.length ? (
        <div className="st-card" style={{ padding: 40, textAlign: "center", color: colors.fgSub, fontSize: 13 }}>
          No comments match the filters.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>{data.total} comments</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {data.items.map(c => (
              <div key={c.id} className="st-card" style={{
                padding: "12px 14px",
                borderLeft: `3px solid ${c.is_deleted ? colors.fgFaint : (c.parent_id ? colors.blue : colors.green)}`,
                opacity: c.is_deleted ? 0.7 : 1,
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>
                        {c.author?.name || `user#${c.user_id}`}
                      </span>
                      <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                        #{c.id} {c.parent_id ? `↪ reply to #${c.parent_id}` : ""}
                      </span>
                      {c.is_deleted && <Tag color={colors.fgSub}>hidden</Tag>}
                    </div>
                    <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 6 }}>
                      Event: {c.event_title
                        ? <span style={{ color: colors.fgMid }}>{c.event_title}</span>
                        : `#${c.event_id}`} ·{" "}
                      {c.created_at ? new Date(c.created_at).toLocaleString("en") : "—"}
                    </div>
                    {/* React экранирует строки автоматически → XSS-safe */}
                    <div style={{
                      fontSize: 13, color: colors.fg, lineHeight: 1.55,
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                      padding: "8px 10px", background: colors.cardMid,
                      borderRadius: 6, border: `1px solid ${colors.border}`,
                      fontStyle: c.is_deleted ? "italic" : "normal",
                    }}>
                      {c.body}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Btn sm variant="ghost" onClick={() => setDetail(c)}>Details</Btn>
                    {c.is_deleted ? (
                      <Btn sm variant="ghost" onClick={() => restore(c.id)} disabled={busy}>↻ Restore</Btn>
                    ) : (
                      <Btn sm variant="ghost" onClick={() => softDelete(c.id)} disabled={busy}>👁 Hide</Btn>
                    )}
                    <Btn sm variant="danger" onClick={() => setHardDel(c)} disabled={busy}>🗑</Btn>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={data.pages || 1} onChange={setPage} />
        </>
      )}

      {detail && <CommentDetailModal token={token} cid={detail.id} onClose={() => setDetail(null)} />}
      {hardDel && (
        <ConfirmModal
          title="Permanently delete comment?"
          text={`Comment #${hardDel.id} by ${hardDel.author?.name || `user#${hardDel.user_id}`} will be deleted forever. All replies in the thread will be cascade-deleted. This action cannot be undone.`}
          danger
          onConfirm={confirmHardDelete}
          onClose={() => !busy && setHardDel(null)}
        />
      )}
    </>
  );
}

function CommentDetailModal({ token, cid, onClose }) {
  const { colors } = useTheme();
  const [c, setC]     = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api(`/event-comments/${cid}`, { token })
      .then(setC).catch(e => setErr(e.message));
  }, [token, cid]);
  return (
    <FormModal title={`Comment #${cid}`} onClose={onClose} width={560}>
      {err ? (
        <div style={{ color: colors.red, fontSize: 13 }}>{err}</div>
      ) : !c ? (
        <div style={{ padding: 20, textAlign: "center" }}><Spinner /></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 13 }}>
          <div>
            <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 2 }}>Author</div>
            <div>{c.author?.name} <span className="st-mono" style={{ color: colors.fgSub, fontSize: 11 }}>{c.author?.email}</span></div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 2 }}>Event</div>
            <div>{c.event_title || `#${c.event_id}`}</div>
          </div>
          {c.parent && (
            <div>
              <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 2 }}>Parent comment</div>
              <div style={{ padding: 8, background: colors.cardMid, borderRadius: 6, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {c.parent.body}
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 2 }}>Body</div>
            <div style={{ padding: 10, background: colors.cardMid, borderRadius: 6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {c.body}
            </div>
          </div>
          {c.replies?.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 4 }}>Replies ({c.replies_count})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                {c.replies.map(r => (
                  <div key={r.id} style={{
                    padding: 8, background: colors.cardMid, borderRadius: 6, fontSize: 12,
                    borderLeft: `2px solid ${r.is_deleted ? colors.fgFaint : colors.blue}`,
                    opacity: r.is_deleted ? 0.6 : 1,
                  }}>
                    <div style={{ fontSize: 10, color: colors.fgSub, marginBottom: 2 }}>
                      {r.author?.name} · #{r.id} {r.is_deleted && "· hidden"}
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.body}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
}

// ─── Match Ratings moderation ──────────────────────────────────────────────
function ContentRatings({ token }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useStickyState("rating_page", 1);
  const [matchF, setMatchF]   = useStickyState("rating_match", "");
  const [raterF, setRaterF]   = useStickyState("rating_rater", "");
  const [rMin, setRMin]       = useStickyState("rating_min", "");
  const [rMax, setRMax]       = useStickyState("rating_max", "");

  const [delRating, setDelRating] = useState(null);
  const [busy, setBusy]           = useState(false);

  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 30 });
    if (matchF) p.set("match_id", matchF);
    if (raterF) p.set("rater_id", raterF);
    if (rMin)   p.set("rating_min", rMin);
    if (rMax)   p.set("rating_max", rMax);
    api(`/match-ratings?${p}`, { token })
      .then(setData)
      .catch(e => toast(e.message, "err"))
      .finally(() => setLoading(false));
  }, [token, page, matchF, raterF, rMin, rMax, toast]);

  useEffect(() => { load(); }, [load]);

  async function confirmDelete() {
    if (!delRating) return;
    setBusy(true);
    try {
      await api(`/match-ratings/${delRating.id}`, { method: "DELETE", token });
      toast(`Rating #${delRating.id} deleted`);
      setDelRating(null);
      load();
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  }

  const stars = n => "★".repeat(n) + "☆".repeat(Math.max(0, 5 - n));

  return (
    <>
      <FilterRow>
        <div>
          <label style={{ display: "block", fontSize: 11, color: colors.fgSub, marginBottom: 4 }}>Match ID</label>
          <input className="st-input st-mono" type="number" min={1} placeholder="any"
                 value={matchF} style={{ width: 120 }}
                 onChange={e => { setMatchF(e.target.value.replace(/[^0-9]/g, "")); setPage(1); }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, color: colors.fgSub, marginBottom: 4 }}>Rater ID</label>
          <input className="st-input st-mono" type="number" min={1} placeholder="any"
                 value={raterF} style={{ width: 120 }}
                 onChange={e => { setRaterF(e.target.value.replace(/[^0-9]/g, "")); setPage(1); }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, color: colors.fgSub, marginBottom: 4 }}>Min ★</label>
          <select className="st-input" value={rMin} style={{ width: 80 }}
                  onChange={e => { setRMin(e.target.value); setPage(1); }}>
            <option value="">any</option>
            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, color: colors.fgSub, marginBottom: 4 }}>Max ★</label>
          <select className="st-input" value={rMax} style={{ width: 80 }}
                  onChange={e => { setRMax(e.target.value); setPage(1); }}>
            <option value="">any</option>
            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </FilterRow>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={28} /></div>
      ) : !data?.items?.length ? (
        <div className="st-card" style={{ padding: 40, textAlign: "center", color: colors.fgSub, fontSize: 13 }}>
          No ratings match the filters.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>{data.total} ratings</div>
          <div className="st-card" style={{ overflow: "hidden" }}>
            <div className="st-table-scroll"><table className="st-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Match</th>
                  <th>Rater</th>
                  <th>Rating</th>
                  <th>Created</th>
                  <th style={{ textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(r => {
                  const color = r.rating <= 2 ? colors.red : r.rating === 3 ? colors.orange : colors.green;
                  return (
                    <tr key={r.id}>
                      <td className="st-mono" style={{ color: colors.fgSub }}>#{r.id}</td>
                      <td className="st-mono">#{r.match_id}</td>
                      <td>
                        <div>{r.rater?.name || `user#${r.rater_id}`}</div>
                        <div className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>{r.rater?.email}</div>
                      </td>
                      <td>
                        <span style={{ color, fontFamily: "inherit", fontWeight: 600 }}>
                          {stars(r.rating)} <span style={{ fontSize: 11, color: colors.fgSub }}>({r.rating}/5)</span>
                        </span>
                      </td>
                      <td className="st-mono" style={{ fontSize: 11, color: colors.fgSub }}>
                        {r.created_at ? new Date(r.created_at).toLocaleDateString("en") : "—"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Btn sm variant="danger" onClick={() => setDelRating(r)} disabled={busy}>🗑</Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
          <Pagination page={page} pages={data.pages || 1} onChange={setPage} />
        </>
      )}

      {delRating && (
        <ConfirmModal
          title="Delete match rating?"
          text={`Rating #${delRating.id} (${delRating.rating}★) by ${delRating.rater?.name || `user#${delRating.rater_id}`} for match #${delRating.match_id}. The action is logged.`}
          danger
          onConfirm={confirmDelete}
          onClose={() => !busy && setDelRating(null)}
        />
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TABS: BAN LOGS + ACTIVITY
// ════════════════════════════════════════════════════════════════════════════
function TabBans({ token }) {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setLoading(true);
    api(`/ban-logs?page=${page}&limit=30`, { token }).then(setData).finally(() => setLoading(false));
  }, [page]);
  return (
    <>
      {loading ? <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={28} /></div>
      : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>{data?.total ?? 0} entries</div>
          <div className="st-card" style={{ overflow: "hidden" }}>
            <div className="st-table-scroll"><table className="st-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Banned By</th>
                  <th>Reason</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map(b => (
                  <tr key={b.id} style={{ cursor: "default", borderLeft: `3px solid ${colors.red}`, background: colors.redDim + "44" }}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{b.banned_user_name}</div>
                      <div className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, display: "flex", alignItems: "center" }}>
                        ID {b.banned_user_id}<CopyBtn text={b.banned_user_id} />
                      </div>
                    </td>
                    <td>
                      <span className="st-mono" style={{ fontSize: 11 }}>{b.banned_user_email}</span>
                      <CopyBtn text={b.banned_user_email} />
                    </td>
                    <td><span style={{ fontSize: 12 }}>{b.banned_by}</span></td>
                    <td><span title={b.reason || ""} style={{ fontSize: 12, color: colors.fgSub, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{b.reason || "—"}</span></td>
                    <td><span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                      {b.banned_at ? new Date(b.banned_at).toLocaleDateString("en") : "—"}
                    </span></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: PENDING EVENTS  ·  Заявки на публикацию ивентов
// ════════════════════════════════════════════════════════════════════════════
function TabPendingEvents({ token, onSelectUser }) {
  const { colors } = useTheme();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [statusF, setStatusF]     = useState("");  // "" = all
  const [search, setSearch]       = useState("");
  const [expanded, setExpanded]   = useState(null);
  const [actModal, setActModal]   = useState(null); // { ev, mode:"reject"|"changes" }
  const [actReason, setActReason] = useState("");
  const [actLoading, setActLoading] = useState(null);
  const debRef = useRef();
  const toast  = useToast();

  const STATUS_FILTERS = [
    { v: "",                  label: "All"               },
    { v: "draft",             label: "Under Review"       },
    { v: "changes_requested", label: "✎ Changes Requested" },
    { v: "rejected",          label: "✕ Rejected"         },
  ];

  const load = useCallback((pg, sf, sq) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 20 });
    if (sf) p.set("status", sf);
    if (sq) p.set("search", sq);
    api(`/pending-events?${p}`, { token }).then(setData).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(page, statusF, search); }, [page, statusF, search]);

  async function doAction(ev, status, reason) {
    setActLoading(ev.id);
    try {
      const res = await api(`/pending-events/${ev.id}/action`, {
        method: "POST", body: { status, rejection_reason: reason || null }, token,
      });
      toast(status === "approved"
        ? `✓ Published: ${res.event_url || ev.title}`
        : `Status changed: ${status}`);
      setActModal(null); setActReason("");
      load(page, statusF, search);
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(null); }
  }

  function StatusChip({ s }) {
    const MAP = {
      draft:             [colors.orange, "Under Review"],
      changes_requested: [colors.blue,   "Changes Requested"],
      rejected:          [colors.red,    "Rejected"],
      approved:          [colors.green,  "Approved"],
      published:         [colors.green,  "Published"],
    };
    const [c, l] = MAP[s] || [colors.fgSub, s];
    return <Tag color={c}>{l}</Tag>;
  }

  const INFO_ROWS = (ev) => [
    ["Date",          ev.event_date || "—"],
    ["Time",         ev.event_time || "—"],
    ["Timezone",  ev.timezone   || "—"],
    ["Location",         ev.location   || "—"],
    ["Ticket",         ev.is_free ? "Free" : `${ev.ticket_price} ${ev.currency}`],
    ["Capacity",   ev.capacity  ?? "∞"],
    ["Organizer",   ev.organizer_name    || "—"],
    ["Contact",       ev.organizer_contact || "—"],
    ["Website",          ev.organizer_website || "—"],
    ["Invite code",   ev.invite_code       || "—"],
    ["Slug",          ev.slug              || "—"],
    ["Private",     ev.is_private ? "Yes" : "No"],
    ["Approval",     ev.require_approval ? "Yes" : "No"],
    ["User Invites",ev.allow_user_invitations ? "Yes" : "No"],
    ["Matching",       ev.allow_external_matching ? "Enabled" : "Disabled"],
    ["Phone Required", ev.require_participant_phone ? "Yes" : "No"],
  ];

  return (
    <div className="st-fade">
      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        {STATUS_FILTERS.map(f => (
          <button key={f.v || "__all"} onClick={() => { setStatusF(f.v); setPage(1); }} style={{
            padding: "7px 13px", borderRadius: 7, fontFamily: "inherit",
            fontWeight: 600, fontSize: 12, cursor: "pointer", transition: "all .15s",
            border: `1px solid ${f.v === statusF ? colors.primary : colors.border}`,
            background: f.v === statusF ? colors.primaryDim : "transparent",
            color: f.v === statusF ? colors.accent : colors.fgSub,
          }}>{f.label}</button>
        ))}
        <input className="st-input" style={{ maxWidth: 280, marginLeft: "auto" }}
          placeholder="Search by title, city, organizer..."
          onChange={e => {
            clearTimeout(debRef.current);
            const v = e.target.value;
            debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 380);
          }} />
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spinner size={32} />
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 12 }}>
            {data?.total ?? 0} submissions
          </div>

          {data?.items?.length === 0 && (
            <div style={{ textAlign: "center", color: colors.fgSub, paddingTop: 40, fontSize: 14 }}>
              No submissions
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {data?.items?.map(ev => {
              const evBorder = { approved: colors.green, published: colors.green, rejected: colors.red, changes_requested: colors.orange, pending: colors.orange }[ev.status] || colors.border;
              return (
              <div key={ev.id} className="st-card" style={{ overflow: "hidden", borderLeft: `3px solid ${evBorder}` }}>

                {/* ── Summary row ─────────────────────────────────── */}
                <div onClick={() => setExpanded(expanded === ev.id ? null : ev.id)}
                  style={{ display: "flex", gap: 14, alignItems: "flex-start",
                    padding: "14px 18px", cursor: "pointer" }}>

                  {/* Cover */}
                  {ev.cover_url ? (
                    <img src={ev.cover_url} alt="" style={{
                      width: 64, height: 44, objectFit: "cover", borderRadius: 6,
                      flexShrink: 0, border: `1px solid ${colors.border}`,
                    }} />
                  ) : (
                    <div style={{ width: 64, height: 44, borderRadius: 6, flexShrink: 0,
                      background: colors.cardMid, border: `1px solid ${colors.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 22 }}>📅</div>
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 5 }}>{ev.title}</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <StatusChip s={ev.status} />
                      {ev.location && <span style={{ fontSize: 12, color: colors.fgSub }}>📍 {ev.location}</span>}
                      {ev.event_date && <span className="st-mono" style={{ fontSize: 11, color: colors.fgSub }}>{ev.event_date}</span>}
                      {ev.is_private && <Tag color={colors.fgSub}>Private</Tag>}
                      {ev.allow_external_matching && <Tag color={colors.primary}>Matching</Tag>}
                      {!ev.is_free && <Tag color={colors.orange}>{ev.ticket_price} {ev.currency}</Tag>}
                    </div>
                    <div style={{ fontSize: 12, color: colors.fgSub, marginTop: 5 }}>
                      Org.: <strong>{ev.organizer_name}</strong>
                      {ev.creator && (
                        <span> · Created by:{" "}
                          <span style={{ color: colors.accent, cursor: "pointer", fontWeight: 600 }}
                            onClick={e => { e.stopPropagation(); onSelectUser(ev.creator.id); }}>
                            {ev.creator.name}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end",
                    gap: 4, flexShrink: 0 }}>
                    <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>#{ev.id}</span>
                    <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                      {ev.created_at ? new Date(ev.created_at).toLocaleDateString("en") : "—"}
                    </span>
                    <span style={{ color: colors.fgFaint, fontSize: 11 }}>
                      {expanded === ev.id ? "▲" : "▼"}
                    </span>
                  </div>
                </div>

                {/* ── Expanded details ───────────────────────────────── */}
                {expanded === ev.id && (
                  <div style={{ padding: "0 18px 20px",
                    borderTop: `1px solid ${colors.border}`, background: colors.cardMid }}>

                    {/* All fields grid */}
                    <div style={{ display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(175px, 1fr))",
                      gap: 7, margin: "16px 0 14px" }}>
                      {INFO_ROWS(ev).map(([k, v]) => (
                        <div key={k} style={{ background: colors.card,
                          border: `1px solid ${colors.border}`, borderRadius: 7, padding: "8px 10px" }}>
                          <div style={{ fontSize: 9, color: colors.fgSub, fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 2 }}>{k}</div>
                          <div className="st-mono" style={{ fontSize: 11, color: colors.fgMid }}>{v}</div>
                        </div>
                      ))}
                    </div>

                    {/* Matching categories */}
                    {ev.matching_categories?.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 6 }}>
                          Matching Categories
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {ev.matching_categories.map(c => <Tag key={c} color={colors.primary}>{c}</Tag>)}
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    {ev.description && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 6 }}>
                          Description
                        </div>
                        <div style={{ background: colors.card, border: `1px solid ${colors.border}`,
                          borderRadius: 8, padding: "10px 14px", fontSize: 13,
                          color: colors.fgMid, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                          {ev.description}
                        </div>
                      </div>
                    )}

                    {/* Rejection reason */}
                    {ev.rejection_reason && (
                      <div style={{ background: colors.redDim,
                        border: `1px solid ${colors.red}44`, borderRadius: 8,
                        padding: "10px 14px", fontSize: 13, color: colors.red, marginBottom: 14,
                        whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.55 }}>
                        ⚠ Reason: {ev.rejection_reason}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {ev.status !== "published" && (
                        <button disabled={!!actLoading}
                          onClick={() => doAction(ev, "approved", null)}
                          style={{ background: colors.green, color: "#fff", border: "none",
                            borderRadius: 8, padding: "9px 18px", fontFamily: "inherit",
                            fontWeight: 700, fontSize: 13, cursor: "pointer",
                            opacity: actLoading === ev.id ? .5 : 1,
                            display: "flex", alignItems: "center", gap: 6 }}>
                          {actLoading === ev.id ? <Spinner size={14} /> : "✓"} Approve & Publish
                        </button>
                      )}
                      <Btn sm variant="danger" disabled={!!actLoading}
                        onClick={() => { setActModal({ ev, mode: "reject" }); setActReason(""); }}>
                        ✕ Reject
                      </Btn>
                      <Btn sm variant="ghost" disabled={!!actLoading}
                        onClick={() => { setActModal({ ev, mode: "changes" }); setActReason(""); }}>
                        ✎ Request Changes
                      </Btn>
                      {ev.creator && (
                        <Btn sm variant="ghost" onClick={() => onSelectUser(ev.creator.id)}>
                          Creator Profile
                        </Btn>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );})}  
          </div>

          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </>
      )}

      {/* ── Rejection / changes modal ─────────────────────────────────────── */}
      {actModal && (
        <WindowModal
          windowId={`event_review_${actModal.mode}_${actModal.ev.id}`}
          title={actModal.mode === "changes" ? "Request Changes" : "Reject Submission"}
          onClose={() => setActModal(null)}
          width={540}
          height={340}
          minHeight={280}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: colors.fg }}>
                {actModal.mode === "changes" ? "Request Changes" : "Reject Submission"}
              </div>
              <button onClick={() => setActModal(null)} style={{ background: "none", border: "none",
                color: colors.fgSub, fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: colors.fgMid, marginBottom: 14 }}>
              Event: <strong>{actModal.ev.title}</strong>
            </div>
            <textarea className="st-input" rows={4}
              placeholder="Reason (required)..." value={actReason}
              onChange={e => setActReason(e.target.value)}
              style={{ resize: "vertical", marginBottom: 12 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setActModal(null)}>Cancel</Btn>
              <Btn variant="danger" loading={actLoading === actModal.ev.id}
                onClick={() => doAction(
                  actModal.ev,
                  actModal.mode === "changes" ? "changes_requested" : "rejected",
                  actReason,
                )}>
                {actModal.mode === "changes" ? "Request Changes" : "Reject"}
              </Btn>
            </div>
        </WindowModal>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  TAB: EVENT EDIT REQUESTS  ·  Заявки владельцев на правку published events
// ════════════════════════════════════════════════════════════════════════════
function TabEventEditRequests({ token, onSelectUser }) {
  const { colors } = useTheme();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [statusF, setStatusF]   = useState("pending");
  const [search, setSearch]     = useState("");
  const [expanded, setExpanded] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // { req }
  const [rejectReason, setRejectReason] = useState("");
  const [actLoading, setActLoading] = useState(null);
  const debRef = useRef();
  const toast = useToast();

  const STATUS_FILTERS = [
    { v: "",         label: "All"      },
    { v: "pending",  label: "⏳ Pending"  },
    { v: "approved", label: "✓ Approved" },
    { v: "rejected", label: "✕ Rejected" },
  ];

  const load = useCallback((pg, sf, sq) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 20 });
    if (sf) p.set("status", sf);
    if (sq) p.set("search", sq);
    api(`/event-edit-requests?${p}`, { token })
      .then(setData)
      .catch(e => toast(e.message, "err"))
      .finally(() => setLoading(false));
  }, [token, toast]);
  useEffect(() => { load(page, statusF, search); }, [page, statusF, search, load]);

  async function doApprove(req) {
    setActLoading(req.id);
    try {
      const res = await api(`/event-edit-requests/${req.id}/approve`, {
        method: "POST", token,
      });
      toast(`✓ Applied ${Object.keys(res.applied || {}).length} field(s) to event #${res.event_id}`);
      load(page, statusF, search);
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(null); }
  }

  async function doReject() {
    if (!rejectModal) return;
    const reason = (rejectReason || "").trim();
    if (!reason) { toast("Reason required", "err"); return; }
    setActLoading(rejectModal.req.id);
    try {
      await api(`/event-edit-requests/${rejectModal.req.id}/reject`, {
        method: "POST", body: { rejection_reason: reason }, token,
      });
      toast("Request rejected");
      setRejectModal(null); setRejectReason("");
      load(page, statusF, search);
    } catch (e) { toast(e.message, "err"); }
    finally { setActLoading(null); }
  }

  function StatusChip({ s }) {
    const MAP = {
      pending:  [colors.orange, "Pending"],
      approved: [colors.green,  "Approved"],
      rejected: [colors.red,    "Rejected"],
    };
    const [c, l] = MAP[s] || [colors.fgSub, s];
    return <Tag color={c}>{l}</Tag>;
  }

  const fmtVal = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "✓ Yes" : "✕ No";
    if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
    return String(v);
  };

  return (
    <div className="st-fade">
      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        {STATUS_FILTERS.map(f => (
          <button key={f.v || "__all"} onClick={() => { setStatusF(f.v); setPage(1); }} style={{
            padding: "7px 13px", borderRadius: 7, fontFamily: "inherit",
            fontWeight: 600, fontSize: 12, cursor: "pointer", transition: "all .15s",
            border: `1px solid ${f.v === statusF ? colors.primary : colors.border}`,
            background: f.v === statusF ? colors.primaryDim : "transparent",
            color: f.v === statusF ? colors.accent : colors.fgSub,
          }}>{f.label}</button>
        ))}
        <input className="st-input" style={{ maxWidth: 280, marginLeft: "auto" }}
          placeholder="Search by event title, location, organizer..."
          onChange={e => {
            clearTimeout(debRef.current);
            const v = e.target.value;
            debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 380);
          }} />
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spinner size={32} />
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 12 }}>
            {data?.total ?? 0} edit requests
          </div>

          {data?.items?.length === 0 && (
            <div style={{ textAlign: "center", color: colors.fgSub, paddingTop: 40, fontSize: 14 }}>
              No edit requests
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {data?.items?.map(req => {
              const evBorder = { approved: colors.green, rejected: colors.red, pending: colors.orange }[req.status] || colors.border;
              const fields = Object.keys(req.changes || {});
              return (
              <div key={req.id} className="st-card" style={{ overflow: "hidden", borderLeft: `3px solid ${evBorder}` }}>

                {/* ── Summary row ─────────────────────────────────── */}
                <div onClick={() => setExpanded(expanded === req.id ? null : req.id)}
                  style={{ display: "flex", gap: 14, alignItems: "flex-start",
                    padding: "14px 18px", cursor: "pointer" }}>

                  {req.event?.current_cover_url ? (
                    <img src={req.event.current_cover_url} alt="" style={{
                      width: 64, height: 44, objectFit: "cover", borderRadius: 6,
                      flexShrink: 0, border: `1px solid ${colors.border}`,
                    }} />
                  ) : (
                    <div style={{ width: 64, height: 44, borderRadius: 6, flexShrink: 0,
                      background: colors.cardMid, border: `1px solid ${colors.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 22 }}>✎</div>
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 5 }}>
                      {req.event?.title || `Event #${req.event_id}`}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <StatusChip s={req.status} />
                      <Tag color={colors.primary}>{fields.length} field{fields.length === 1 ? "" : "s"}</Tag>
                      {req.has_new_cover && <Tag color={colors.accent}>+ cover</Tag>}
                    </div>
                    <div style={{ fontSize: 12, color: colors.fgSub, marginTop: 5 }}>
                      By:{" "}
                      {req.requester ? (
                        <span style={{ color: colors.accent, cursor: "pointer", fontWeight: 600 }}
                          onClick={e => { e.stopPropagation(); onSelectUser(req.requester.id); }}>
                          {req.requester.name}
                        </span>
                      ) : "—"}
                      {" · Fields: "}
                      <span className="st-mono" style={{ fontSize: 11 }}>
                        {fields.length === 0 ? "(cover only)" : fields.slice(0, 5).join(", ")}
                        {fields.length > 5 ? "…" : ""}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end",
                    gap: 4, flexShrink: 0 }}>
                    <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>#{req.id}</span>
                    <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint }}>
                      {req.created_at ? new Date(req.created_at).toLocaleDateString("en") : "—"}
                    </span>
                    <span style={{ color: colors.fgFaint, fontSize: 11 }}>
                      {expanded === req.id ? "▲" : "▼"}
                    </span>
                  </div>
                </div>

                {/* ── Expanded diff ───────────────────────────────────── */}
                {expanded === req.id && (
                  <div style={{ padding: "16px 18px 20px",
                    borderTop: `1px solid ${colors.border}`, background: colors.cardMid }}>

                    {/* Cover comparison */}
                    {req.has_new_cover && (
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>
                          Cover change
                        </div>
                        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontSize: 10, color: colors.fgSub, marginBottom: 4 }}>Current</div>
                            <img src={req.event?.current_cover_url || ""} alt=""
                              style={{ width: 200, height: 130, objectFit: "cover", borderRadius: 8,
                                border: `1px solid ${colors.border}` }} />
                          </div>
                          <div style={{ alignSelf: "center", fontSize: 22, color: colors.fgSub }}>→</div>
                          <div>
                            <div style={{ fontSize: 10, color: colors.accent, marginBottom: 4 }}>Proposed</div>
                            <img src={req.new_cover_url || ""} alt=""
                              style={{ width: 200, height: 130, objectFit: "cover", borderRadius: 8,
                                border: `2px solid ${colors.accent}` }} />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Diff table */}
                    {fields.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 10, color: colors.fgSub, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>
                          Field changes
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1fr) minmax(120px, 1fr) auto minmax(120px, 1fr)",
                          gap: 6, fontSize: 12 }}>
                          {fields.map(f => (
                            <React.Fragment key={f}>
                              <div style={{ background: colors.card, border: `1px solid ${colors.border}`,
                                borderRadius: 6, padding: "8px 10px", color: colors.fgSub, fontWeight: 700,
                                textTransform: "uppercase", letterSpacing: ".05em", fontSize: 10 }}>
                                {f}
                              </div>
                              <div style={{ background: colors.card, border: `1px solid ${colors.border}`,
                                borderRadius: 6, padding: "8px 10px", color: colors.fgSub,
                                wordBreak: "break-word" }}>
                                {fmtVal(req.current?.[f])}
                              </div>
                              <div style={{ alignSelf: "center", textAlign: "center",
                                color: colors.fgFaint, padding: "0 4px" }}>→</div>
                              <div style={{ background: colors.card, border: `1px solid ${colors.accent}66`,
                                borderRadius: 6, padding: "8px 10px", color: colors.accent,
                                fontWeight: 600, wordBreak: "break-word" }}>
                                {fmtVal(req.changes?.[f])}
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Rejection reason if applicable */}
                    {req.rejection_reason && (
                      <div style={{ background: colors.redDim,
                        border: `1px solid ${colors.red}44`, borderRadius: 8,
                        padding: "10px 14px", fontSize: 13, color: colors.red, marginBottom: 14,
                        whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.55 }}>
                        ⚠ Reason: {req.rejection_reason}
                      </div>
                    )}

                    {/* Actions */}
                    {req.status === "pending" && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button disabled={!!actLoading}
                          onClick={() => doApprove(req)}
                          style={{ background: colors.green, color: "#fff", border: "none",
                            borderRadius: 8, padding: "9px 18px", fontFamily: "inherit",
                            fontWeight: 700, fontSize: 13, cursor: "pointer",
                            opacity: actLoading === req.id ? .5 : 1,
                            display: "flex", alignItems: "center", gap: 6 }}>
                          {actLoading === req.id ? <Spinner size={14} /> : "✓"} Approve & Apply
                        </button>
                        <Btn sm variant="danger" disabled={!!actLoading}
                          onClick={() => { setRejectModal({ req }); setRejectReason(""); }}>
                          ✕ Reject
                        </Btn>
                        {req.event && (
                          <Btn sm variant="ghost"
                            onClick={() => window.open(`/events/${req.event.slug || req.event.id}`, "_blank")}>
                            Open event ↗
                          </Btn>
                        )}
                      </div>
                    )}

                    {req.status !== "pending" && req.reviewed_at && (
                      <div style={{ fontSize: 11, color: colors.fgFaint, marginTop: 8 }}>
                        Reviewed {new Date(req.reviewed_at).toLocaleString()}
                        {req.reviewed_by ? ` by admin #${req.reviewed_by}` : ""}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );})}
          </div>

          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <WindowModal
          windowId={`edit_req_reject_${rejectModal.req.id}`}
          title="Reject Edit Request"
          onClose={() => setRejectModal(null)}
          width={540}
          height={340}
          minHeight={280}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.fg }}>
              Reject Edit Request
            </div>
            <button onClick={() => setRejectModal(null)} style={{ background: "none", border: "none",
              color: colors.fgSub, fontSize: 18, cursor: "pointer" }}>✕</button>
          </div>
          <div style={{ fontSize: 13, color: colors.fgMid, marginBottom: 14 }}>
            Event: <strong>{rejectModal.req.event?.title || `#${rejectModal.req.event_id}`}</strong>
          </div>
          <textarea className="st-input" rows={4}
            placeholder="Reason (required)..." value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            style={{ resize: "vertical", marginBottom: 12 }} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setRejectModal(null)}>Cancel</Btn>
            <Btn variant="danger" loading={actLoading === rejectModal.req.id}
              onClick={doReject}>
              Reject
            </Btn>
          </div>
        </WindowModal>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  TAB: USER TREE  ·  Дерево инвайтов
// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
//  TAB USER TREE — интерактивный SVG-граф приглашений
// ════════════════════════════════════════════════════════════════════════════
const TREE_NODE_W   = 256;
const TREE_NODE_H   = 68;
const TREE_LVL_GAP  = 100;
const TREE_ROW_GAP  = 12;
const TREE_ROOT_GAP = 28;
const TREE_START_X  = 32;
const TREE_START_Y  = 32;

/**
 * buildLayout — последовательный DFS.
 * curY монотонно растёт вниз → наложений нет.
 * Рёбра хранят точные координаты: правый край родителя → левый край ребёнка.
 */
function buildLayout(roots, expandedIds) {
  const nodes = [];
  const edges = [];
  let curY = TREE_START_Y;

  function place(node, x, parentRightX, parentMidY) {
    const y    = curY;
    const midY = y + TREE_NODE_H / 2;
    nodes.push({ node, x, y });
    if (parentRightX !== null) {
      // x1/y1 — right edge of parent, x2/y2 — left edge of child
      edges.push({ x1: parentRightX, y1: parentMidY, x2: x, y2: midY });
    }
    curY += TREE_NODE_H + TREE_ROW_GAP;
    if (expandedIds.has(node.id) && node.children?.length) {
      const childX = x + TREE_NODE_W + TREE_LVL_GAP;
      for (const child of node.children) place(child, childX, x + TREE_NODE_W, midY);
    }
  }

  for (let i = 0; i < roots.length; i++) {
    place(roots[i], TREE_START_X, null, null);
    if (i < roots.length - 1) curY += TREE_ROOT_GAP;
  }

  const svgW = nodes.reduce((m, n) => Math.max(m, n.x + TREE_NODE_W + 80), 600);
  const svgH = curY + 60;
  return { nodes, edges, svgW, svgH };
}

function TreeCtxMenu({ cx, cy, node, onClose, onSelectUser, colors }) {
  const menuRef = useRef(null);
  useEffect(() => {
    const h = e => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);
  const base = {
    padding: "10px 16px", background: "transparent", border: "none",
    textAlign: "left", fontSize: 13, fontWeight: 500, cursor: "pointer",
    width: "100%", fontFamily: "Inter,sans-serif", color: colors.fg,
  };
  return (
    <div ref={menuRef} style={{
      position: "absolute", left: cx + 8, top: cy - 12,
      background: colors.card, borderRadius: 12, padding: "6px 0",
      boxShadow: "0 8px 32px rgba(0,0,0,.3)", minWidth: 190,
      border: `1px solid ${colors.borderMid}`, zIndex: 1000,
    }}>
      <button style={base}
        onMouseEnter={e => e.currentTarget.style.background = colors.cardHover}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        onClick={() => { onClose(); onSelectUser(node.id); }}>
        Open Profile
      </button>
    </div>
  );
}

const SELFTEST_HIDE_KEY = "studio_tree_hide_selftest";
const isSelftestUser = node => {
  const n = (node?.name || "").trim();
  const u = (node?.username || "").trim();
  return n.startsWith("SelfTest_") || u.startsWith("SelfTest_");
};
function pruneSelftest(nodes) {
  if (!Array.isArray(nodes)) return [];
  const out = [];
  for (const n of nodes) {
    if (isSelftestUser(n)) continue;
    out.push({ ...n, children: pruneSelftest(n.children || []) });
  }
  return out;
}
function countNodes(nodes) {
  let c = 0;
  const walk = arr => { for (const n of arr) { c++; if (n.children?.length) walk(n.children); } };
  walk(nodes || []);
  return c;
}

function TabUserTree({ token, onSelectUser }) {
  const { colors, theme } = useTheme();
  const [treeData,      setTreeData]      = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [expandedIds,   setExpandedIds]   = useState(new Set());
  const [foundIds,      setFoundIds]      = useState(new Set());
  const [matches,       setMatches]       = useState([]);
  const [grouped,       setGrouped]       = useState([]);
  const [activeIdx,     setActiveIdx]     = useState(0);
  const [scale,         setScale]         = useState(1);
  const [ctxMenu,       setCtxMenu]       = useState(null);
  const [showList,      setShowList]      = useState(false);
  const [hoveredListId, setHoveredListId] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [hideSelftest,  setHideSelftest]  = useState(() => {
    try {
      const v = localStorage.getItem(SELFTEST_HIDE_KEY);
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  const scrollRef = useRef(null);
  const posMap    = useRef({});

  useEffect(() => {
    try { localStorage.setItem(SELFTEST_HIDE_KEY, hideSelftest ? "1" : "0"); } catch {}
  }, [hideSelftest]);

  useEffect(() => {
    api("/user-tree", { token })
      .then(d => setTreeData(d))
      .catch(() => setTreeData({ tree: [], total_users: 0 }))
      .finally(() => setLoading(false));
  }, [token]);

  const rawTree = treeData?.tree || [];
  const tree = useMemo(
    () => hideSelftest ? pruneSelftest(rawTree) : rawTree,
    [rawTree, hideSelftest],
  );
  const visibleCount = useMemo(
    () => hideSelftest ? countNodes(tree) : (treeData?.total_users ?? countNodes(tree)),
    [tree, hideSelftest, treeData],
  );

  const flyTo = useCallback(id => {
    const pos = posMap.current[id];
    const c   = scrollRef.current;
    if (!pos || !c) return;
    c.scrollTo({
      left: pos.x * scale - c.clientWidth  / 2 + TREE_NODE_W / 2,
      top:  pos.y * scale - c.clientHeight / 2 + TREE_NODE_H / 2,
      behavior: "smooth",
    });
  }, [scale]);

  // ── Search ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFoundIds(new Set()); setMatches([]); setGrouped([]); setActiveIdx(0);
      return;
    }
    const fIds = new Set(), pIds = new Set(), uniq = new Map();
    const walk = (node, path = []) => {
      const cur = [...path, node];
      const mb = [];
      if (node.name?.toLowerCase().includes(q))     mb.push("name");
      if (node.username?.toLowerCase().includes(q)) mb.push("username");
      if (node.email?.toLowerCase().includes(q))    mb.push("email");
      if (String(node.id).includes(q))              mb.push("id");
      if (mb.length) {
        fIds.add(node.id);
        if (!uniq.has(node.id)) uniq.set(node.id, { node, mb: new Set() });
        mb.forEach(t => uniq.get(node.id).mb.add(t));
        cur.forEach(n => pIds.add(n.id));
      }
      node.children?.forEach(c => walk(c, cur));
    };
    tree.forEach(r => walk(r));
    const list = Array.from(uniq.values())
      .map(({ node, mb }) => ({ node, mb: Array.from(mb) }))
      .sort((a, b) => b.mb.length - a.mb.length || a.node.name.localeCompare(b.node.name));
    // Auto-expand paths to found nodes
    setExpandedIds(prev => { const n = new Set(prev); pIds.forEach(id => n.add(id)); return n; });
    setFoundIds(fIds);
    setMatches(list.map(m => m.node.id)); setGrouped(list); setActiveIdx(0);
  }, [search, tree]);

  useEffect(() => { if (matches.length) flyTo(matches[activeIdx]); }, [activeIdx, matches, flyTo]);

  // ── Layout ──────────────────────────────────────────────────────────────
  const layout = useMemo(() => buildLayout(tree, expandedIds), [tree, expandedIds]);

  useEffect(() => {
    posMap.current = {};
    layout.nodes.forEach(({ node, x, y }) => { posMap.current[node.id] = { x, y }; });
  }, [layout]);

  function toggleNode(id) {
    setExpandedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const isDark    = theme === "dark";
  const hasSearch = matches.length > 0;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="st-fade" style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input className="st-input" style={{ maxWidth: 320 }}
          placeholder="Search by name, email, ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Btn sm variant="ghost" onClick={() => setScale(s => Math.min(s + .15, 2.5))}>+</Btn>
        <Btn sm variant="ghost" onClick={() => setScale(s => Math.max(s - .15, .3))}>−</Btn>
        <Btn sm variant="ghost" onClick={() => setScale(1)}>100%</Btn>
        <div style={{ width: 1, height: 18, background: colors.border, margin: "0 2px" }} />
        <Btn sm variant="ghost" title="Expand all nodes" onClick={() => {
          const allIds = new Set();
          const walk = n => { allIds.add(n.id); (n.children || []).forEach(walk); };
          tree.forEach(walk);
          setExpandedIds(allIds);
        }}>⊞ All</Btn>
        <Btn sm variant="ghost" title="Collapse all nodes" onClick={() => setExpandedIds(new Set())}>⊟ None</Btn>
        <div style={{ width: 1, height: 18, background: colors.border, margin: "0 2px" }} />
        <label title='Hide all users whose name starts with "SelfTest_"'
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 12, fontWeight: 500, color: colors.fg,
            cursor: "pointer", userSelect: "none",
            padding: "5px 10px", borderRadius: 8,
            border: `1px solid ${hideSelftest ? colors.primary : colors.border}`,
            background: hideSelftest ? colors.primaryDim : "transparent",
            fontFamily: "Inter,sans-serif",
          }}>
          <input type="checkbox" checked={hideSelftest}
            onChange={e => setHideSelftest(e.target.checked)}
            style={{ accentColor: colors.primary, margin: 0, cursor: "pointer" }} />
          Hide all SelfTest
        </label>
        {treeData && (
          <span style={{ fontSize: 11, color: colors.fgSub, marginLeft: "auto" }}>
            {visibleCount} users · {Math.round(scale * 100)}%
          </span>
        )}
      </div>

      {/* Search nav */}
      {hasSearch && (
        <div className="st-fade" style={{
          display: "flex", alignItems: "center", gap: 8,
          background: colors.cardMid, border: `1px solid ${colors.border}`,
          borderRadius: 10, padding: "8px 14px",
        }}>
          <Btn sm variant="ghost"
            onClick={() => setActiveIdx(i => i > 0 ? i - 1 : matches.length - 1)}>
            ← Prev
          </Btn>
          <span style={{ fontSize: 13, color: colors.fg, fontWeight: 600 }}>
            Found: {grouped.length}
          </span>
          <Btn sm variant="ghost"
            onClick={() => setActiveIdx(i => i < matches.length - 1 ? i + 1 : 0)}>
            Next →
          </Btn>
          <Btn sm variant={showList ? "primary" : "ghost"}
            onClick={() => setShowList(v => !v)}>List</Btn>
        </div>
      )}

      {/* Canvas */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
          <Spinner size={36} />
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12 }}>

          {/* SVG canvas */}
          <div style={{
            flex: 1, borderRadius: 12, border: `1px solid ${colors.border}`,
            background: colors.bgDeep,
            backgroundImage: `linear-gradient(to right,${colors.glow} 1px,transparent 1px),linear-gradient(to bottom,${colors.glow} 1px,transparent 1px)`,
            backgroundSize: "24px 24px",
            overflow: "hidden", height: "74vh",
          }}>
            <div ref={scrollRef}
              style={{ width: "100%", height: "100%", overflow: "auto", touchAction: "pan-x pan-y" }}
              onClick={() => setCtxMenu(null)}>

              <svg width={layout.svgW * scale} height={layout.svgH * scale} style={{ display: "block" }}>
                <g transform={`scale(${scale})`}>

                  {/* Edges */}
                  {layout.edges.map((e, i) => (
                    <path key={i}
                      d={`M${e.x1},${e.y1} C${e.x1 + TREE_LVL_GAP * .55},${e.y1} ${e.x2 - TREE_LVL_GAP * .55},${e.y2} ${e.x2},${e.y2}`}
                      fill="none" stroke={colors.border} strokeWidth="1.2"
                    />
                  ))}

                  {/* Nodes */}
                  {layout.nodes.map(({ node, x, y }) => {
                    const isExpanded   = expandedIds.has(node.id);
                    const isExpandable = (node.children?.length || 0) > 0;
                    const invMissing   = node.inviter_missing === true;
                    const isDimmed     = hasSearch && !foundIds.has(node.id);
                    const isFound      = foundIds.has(node.id);
                    const isHov        = hoveredNodeId === node.id;
                    const hue          = (node.id * 53) % 360;

                    const fill   = invMissing ? (isDark ? "#3a1a18" : "#fff0ee") : isHov ? colors.cardHover : colors.card;
                    const stroke = invMissing ? colors.red : isFound ? colors.primary : isHov ? colors.borderMid : colors.border;

                    return (
                      <g key={node.id}
                        style={{ cursor: isExpandable ? "pointer" : "default" }}
                        opacity={isDimmed ? 0.2 : 1}
                        onMouseEnter={() => setHoveredNodeId(node.id)}
                        onMouseLeave={() => setHoveredNodeId(null)}
                        onClick={e => { e.stopPropagation(); if (isExpandable) toggleNode(node.id); }}
                      >
                        {/* Card background */}
                        <rect x={x} y={y} width={TREE_NODE_W} height={TREE_NODE_H}
                          rx="11" fill={fill} stroke={stroke}
                          strokeWidth={invMissing || isFound ? 1.8 : 1}
                          style={{ transition: "fill .12s,stroke .12s" }}
                        />

                        {/* Inviter-missing badge */}
                        {invMissing && (
                          <g>
                            <rect x={x+8} y={y+5} width={126} height={16} rx="8" fill={colors.red} opacity={.9} />
                            <text x={x+14} y={y+15.5} fontSize="9" fontWeight="700" fill="#fff"
                              pointerEvents="none" style={{ fontFamily:"Inter,sans-serif" }}>
                              Inviter removed
                            </text>
                          </g>
                        )}

                        {/* Avatar */}
                        <circle cx={x+24} cy={y+TREE_NODE_H/2} r={13}
                          fill={`hsl(${hue},${isDark?"50%,28%":"52%,66%"})`} />
                        <text x={x+24} y={y+TREE_NODE_H/2+4.5}
                          textAnchor="middle" fontSize="11" fontWeight="700"
                          fill={`hsl(${hue},70%,${isDark?"80%":"96%"})`}
                          pointerEvents="none" style={{ fontFamily:"Inter,sans-serif" }}>
                          {(node.name||"?")[0]?.toUpperCase()}
                        </text>

                        {/* Name */}
                        <foreignObject x={x+44} y={y+(invMissing?20:11)} width={TREE_NODE_W-90} height={20}>
                          <div xmlns="http://www.w3.org/1999/xhtml" style={{
                            fontFamily:"Inter,sans-serif", fontSize:13, fontWeight:600,
                            color:colors.fg, whiteSpace:"nowrap", overflow:"hidden",
                            textOverflow:"ellipsis", pointerEvents:"none",
                          }}>{node.name}</div>
                        </foreignObject>

                        {/* Email */}
                        <foreignObject x={x+44} y={y+(invMissing?37:30)} width={TREE_NODE_W-90} height={17}>
                          <div xmlns="http://www.w3.org/1999/xhtml" style={{
                            fontFamily:"Inter,sans-serif", fontSize:11,
                            color:colors.fgSub, whiteSpace:"nowrap", overflow:"hidden",
                            textOverflow:"ellipsis", pointerEvents:"none",
                          }}>{node.email}</div>
                        </foreignObject>

                        {/* ID */}
                        <text x={x+8} y={y+TREE_NODE_H-7} fontSize="9"
                          fill={colors.fgFaint} pointerEvents="none"
                          style={{ fontFamily:"JetBrains Mono,monospace" }}>
                          #{node.id}
                        </text>

                        {/* Children badge */}
                        {isExpandable && (
                          <g>
                            <rect x={x+TREE_NODE_W-42} y={y+TREE_NODE_H/2-10}
                              width={32} height={20} rx="10"
                              fill={isExpanded ? colors.primaryMid : colors.primaryDim}
                              stroke={colors.primary} strokeWidth=".7" />
                            <text x={x+TREE_NODE_W-26} y={y+TREE_NODE_H/2+5}
                              textAnchor="middle" fontSize="11" fontWeight="700"
                              fill={colors.accent} pointerEvents="none"
                              style={{ fontFamily:"Inter,sans-serif" }}>
                              {node.children.length}
                            </text>
                          </g>
                        )}

                        {/* Expand arrow */}
                        {isExpandable && (
                          <text x={x+TREE_NODE_W-12} y={y+TREE_NODE_H-8}
                            fontSize="10" fill={colors.fgSub} pointerEvents="none">
                            {isExpanded ? "▾" : "▸"}
                          </text>
                        )}

                        {/* Menu ⋯ */}
                        <foreignObject x={x+TREE_NODE_W-48} y={y+3} width={26} height={22}>
                          <button xmlns="http://www.w3.org/1999/xhtml"
                            style={{
                              width:26, height:22, borderRadius:6, border:"none",
                              background: isHov ? colors.accentDim : "transparent",
                              color:colors.fgMid, cursor:"pointer", fontSize:14,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              opacity: isHov ? 1 : 0, transition:"opacity .12s",
                              fontFamily:"inherit",
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              const c = scrollRef.current;
                              if (!c) return;
                              const relX = (x + TREE_NODE_W) * scale;
                              const relY = (y + TREE_NODE_H / 2) * scale;
                              setCtxMenu(prev => prev?.node?.id === node.id ? null : { node, cx: relX, cy: relY });
                            }}>⋯</button>
                        </foreignObject>
                      </g>
                    );
                  })}
                </g>
              </svg>

              {ctxMenu && (
                <TreeCtxMenu
                  cx={ctxMenu.cx} cy={ctxMenu.cy}
                  node={ctxMenu.node}
                  onClose={() => setCtxMenu(null)}
                  onSelectUser={onSelectUser}
                  colors={colors}
                />
              )}
            </div>
          </div>

          {/* Found list */}
          {showList && hasSearch && (
            <div className="st-fade" style={{
              width: 290, flexShrink: 0, background: colors.card,
              border: `1px solid ${colors.border}`, borderRadius: 12,
              padding: 14, overflowY: "auto", maxHeight: "74vh",
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.fgSub,
                textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 12 }}>
                Results ({grouped.length})
              </div>
              {grouped.map(({ node, mb }) => (
                <div key={node.id}
                  style={{
                    padding: 10, borderRadius: 9, marginBottom: 8, cursor: "pointer",
                    background: hoveredListId === node.id ? colors.cardHover : colors.cardMid,
                    border: `1px solid ${hoveredListId === node.id ? colors.primary : node.inviter_missing ? colors.orange : colors.border}`,
                    transition: "all .12s",
                  }}
                  onMouseEnter={() => setHoveredListId(node.id)}
                  onMouseLeave={() => setHoveredListId(null)}
                  onClick={() => flyTo(node.id)}
                >
                  <div style={{ fontWeight:600, fontSize:13, color:colors.fg,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {node.name}
                  </div>
                  <div style={{ fontSize:11, color:colors.fgSub, marginTop:1 }}>
                    #{node.id} · {node.email}
                  </div>
                  {node.inviter_missing && (
                    <div style={{ fontSize:10, color:colors.orange, fontWeight:600, marginTop:3 }}>
                      Inviter removed
                    </div>
                  )}
                  <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginTop:6 }}>
                    {mb.map(t => (
                      <span key={t} style={{
                        fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:99,
                        background:colors.primaryDim, color:colors.accent,
                        letterSpacing:".04em", textTransform:"uppercase",
                      }}>{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// ════════════════════════════════════════════════════════════════════════════
//  MAIN SHELL
// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
//  DB VIEWER UTILITIES
// ════════════════════════════════════════════════════════════════════════════

function dbTypeColor(type, colors) {
  if (!type) return colors.fgSub;
  const t = type.toLowerCase();
  if (t.includes("int") || t.includes("serial") || t.includes("numeric") ||
      t.includes("float") || t.includes("real") || t.includes("double"))
    return colors.blue;
  if (t.includes("bool"))  return colors.orange;
  if (t.includes("timestamp") || t.includes("date") || t.includes("time"))
    return colors.cyan;
  if (t.includes("json") || t.includes("array"))
    return colors.green;
  return colors.fgSub;
}

function formatCellValue(val, colType, colors) {
  if (val === null || val === undefined)
    return { text: "NULL", color: colors.fgFaint, italic: true };
  if (val === "[REDACTED]")
    return { text: "●●●●●●", color: colors.red, italic: false };
  const t = (colType || "").toLowerCase();
  if (typeof val === "boolean")
    return { text: val ? "true" : "false", color: val ? colors.green : colors.red, italic: false };
  if ((t.includes("timestamp") || t.includes("date")) && typeof val === "string") {
    try {
      const d = new Date(val);
      if (!isNaN(d)) return { text: d.toLocaleString("en"), color: colors.cyan, italic: false };
    } catch {}
  }
  if (t.includes("json") || (typeof val === "object" && val !== null)) {
    try {
      const s = JSON.stringify(val);
      return { text: s.length > 80 ? s.slice(0, 80) + "…" : s, color: colors.green, italic: false };
    } catch {}
  }
  const str = String(val);
  return { text: str.length > 120 ? str.slice(0, 120) + "…" : str, color: null, italic: false };
}

function shortTypeName(type) {
  return (type || "")
    .replace("character varying", "varchar")
    .replace("timestamp without time zone", "timestamp")
    .replace("timestamp with time zone", "timestamptz")
    .replace("double precision", "float8");
}

// ── Хелперы для всплывашки ссылок (FK) в DB Viewer ─────────────────────────
// Возвращает kind ("user" | "match" | "chat" | "event" | "pending_event" |
// "search_request" | "message") или null, если колонка не похожа на FK.

const _USER_ID_COL_EXPLICIT = new Set([
  "sender_id", "recipient_id", "creator_id", "owner_id",
  "rater_id", "best_match_id", "target_user_id",
  "added_by", "banned_by", "moderator_id", "admin_id",
  "other_user_id", "partner_id", "from_user_id", "to_user_id",
  "banned_user_id", "inviter_user_id", "used_by_user_id",
]);
function getRefKindForColumn(name) {
  if (!name) return null;
  const n = String(name).toLowerCase();

  // user
  if (n === "user_id") return "user";
  if (/^user\d+_id$/.test(n)) return "user";
  if (n.endsWith("_user_id")) return "user";
  if (_USER_ID_COL_EXPLICIT.has(n)) return "user";

  // match
  if (n === "match_id" || n === "best_match_id") return n === "best_match_id" ? "user" : "match";

  // chat
  if (n === "chat_id") return "chat";

  // event
  if (n === "event_id" || n === "published_event_id" || n === "active_event_id") return "event";
  if (n === "pending_event_id") return "pending_event";

  // search_request (включая search_request_user1_id/user2_id, которые тоже FK на SR)
  if (n === "search_request_id") return "search_request";
  if (n === "search_request_user1_id" || n === "search_request_user2_id") return "search_request";

  // message
  if (n === "message_id" || n === "reply_to_message_id" || n === "parent_message_id") return "message";

  return null;
}

// Глобальный кеш карточек — Map<"kind:id", {data?, error?, promise?}>
const _refCardCache = new Map();
function _fetchRefCardCached(kind, id, token) {
  const key = `${kind}:${id}`;
  const hit = _refCardCache.get(key);
  if (hit && (hit.data || hit.error)) return Promise.resolve(hit);
  if (hit && hit.promise) return hit.promise;
  const p = api(`/refs/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/card`, { token })
    .then(data => {
      const entry = { data };
      _refCardCache.set(key, entry);
      return entry;
    })
    .catch(err => {
      const entry = { error: err?.message || "Failed to load" };
      _refCardCache.set(key, entry);
      return entry;
    });
  _refCardCache.set(key, { promise: p });
  return p;
}

function _kindLabel(kind) {
  return ({
    user: "User", match: "Match", chat: "Chat",
    event: "Event", pending_event: "Pending event",
    search_request: "Search request", message: "Message",
  })[kind] || kind;
}

function _kindAccent(kind, colors) {
  return ({
    user:           colors.primary,
    match:          colors.accent,
    chat:           colors.green,
    event:          colors.primary,
    pending_event:  colors.fgSub,
    search_request: colors.accent,
    message:        colors.green,
  })[kind] || colors.fg;
}

// Маленькая ссылка-юзер внутри карточки
function _UserChip({ u, colors }) {
  if (!u) return <span style={{ color: colors.fgFaint }}>—</span>;
  return (
    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      <span style={{ color: colors.fg }}>{u.name || u.username || `#${u.id}`}</span>
      {u.username && <span style={{ color: colors.fgSub }}> @{u.username}</span>}
      <span className="st-mono" style={{ color: colors.fgFaint, marginLeft: 4, fontSize: 10 }}>#{u.id}</span>
    </span>
  );
}

// Универсальная hover-карточка для FK-колонок
function RefHoverCard({ kind, id, token, anchorRect, colors }) {
  const [state, setState] = useState(() => {
    const hit = _refCardCache.get(`${kind}:${id}`);
    return hit && hit.data ? hit : { loading: true };
  });

  useEffect(() => {
    let cancelled = false;
    if (state.data || state.error) return;
    _fetchRefCardCached(kind, id, token).then(entry => {
      if (!cancelled) setState(entry);
    });
    return () => { cancelled = true; };
  }, [kind, id, token]);

  // Позиционирование с прижатием к краям
  const W = 320;
  let left = (anchorRect?.left || 0);
  let top  = (anchorRect?.bottom || 0) + 6;
  if (typeof window !== "undefined") {
    if (left + W > window.innerWidth - 8) left = window.innerWidth - W - 8;
    if (left < 8) left = 8;
    // Если не помещается снизу — показываем сверху
    const estH = 220;
    if (top + estH > window.innerHeight - 8 && anchorRect) {
      top = Math.max(8, anchorRect.top - estH - 6);
    }
  }

  const accent = _kindAccent(kind, colors);
  const wrap = {
    position: "fixed", left, top, width: W, zIndex: 9999,
    background: colors.card, color: colors.fg,
    border: `1px solid ${colors.border}`, borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,.35)", padding: 12,
    fontSize: 12, pointerEvents: "none",
  };

  if (state.loading) {
    return <div style={wrap}>Loading {_kindLabel(kind).toLowerCase()} #{id}…</div>;
  }
  if (state.error) {
    return <div style={{ ...wrap, color: colors.red }}>{_kindLabel(kind)} #{id}: {state.error}</div>;
  }

  const d = state.data || {};

  // Шапка: тип сущности + ID
  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
      paddingBottom: 8, marginBottom: 8, borderBottom: `1px solid ${colors.border}` }}>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".07em", color: accent }}>{_kindLabel(kind)}</span>
      <span className="st-mono" style={{ color: colors.fgFaint, fontSize: 11 }}>#{d.id}</span>
    </div>
  );

  // Универсальная сетка ключ→значение
  const Grid = ({ rows }) => (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr",
      gap: "5px 10px", fontSize: 11, alignItems: "baseline" }}>
      {rows.filter(r => r).map(([k, v], i) => (
        <Fragment key={i}>
          <span style={{ color: colors.fgFaint, whiteSpace: "nowrap" }}>{k}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis",
            whiteSpace: typeof v === "string" ? "nowrap" : "normal" }}>{v}</span>
        </Fragment>
      ))}
    </div>
  );

  if (kind === "user") {
    return (
      <div style={wrap}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {d.avatar_url ? (
            <img src={d.avatar_url} alt="" width={44} height={44}
              style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0,
                border: `1px solid ${colors.border}` }} />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: "50%",
              background: colors.bg, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 18, color: colors.fgFaint,
              flexShrink: 0, border: `1px solid ${colors.border}` }}>
              {(d.name || d.username || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {d.name || d.username || `User #${d.id}`}
            </div>
            {d.username && (
              <div style={{ fontSize: 11, color: colors.fgSub,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                @{d.username}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <Grid rows={[
            ["ID",     <span className="st-mono">#{d.id}</span>],
            d.email     && ["Email", d.email],
            d.role      && ["Role",  d.role],
            d.created_at && ["Joined", <span className="st-mono">{String(d.created_at).slice(0, 10)}</span>],
          ]} />
        </div>
      </div>
    );
  }

  if (kind === "match") {
    return (
      <div style={wrap}>
        {header}
        <Grid rows={[
          d.status   && ["Status",   d.status],
          d.category && ["Category", d.category],
          (d.compatibility_score != null) && ["Score", <span className="st-mono">{Math.round(d.compatibility_score)}%</span>],
          ["Users", <span>
            <_UserChip u={d.user1} colors={colors} />
            <span style={{ color: colors.fgFaint, margin: "0 6px" }}>×</span>
            <_UserChip u={d.user2} colors={colors} />
          </span>],
          (d.user1_status || d.user2_status) && ["Sides",
            <span><span className="st-mono">{d.user1_status || "—"}</span>
            <span style={{ color: colors.fgFaint, margin: "0 4px" }}>·</span>
            <span className="st-mono">{d.user2_status || "—"}</span></span>],
          d.match_source && ["Source", d.match_source],
          (d.chat_created != null) && ["Chat", d.chat_created ? "yes" : "no"],
          d.match_reason && ["Reason",
            <span style={{ whiteSpace: "normal", color: colors.fgSub }}>{d.match_reason}</span>],
          d.created_at && ["Created", <span className="st-mono">{String(d.created_at).replace("T", " ").slice(0, 16)}</span>],
        ]} />
      </div>
    );
  }

  if (kind === "chat") {
    return (
      <div style={wrap}>
        {header}
        <Grid rows={[
          (d.match_id != null) && ["Match", <span className="st-mono">#{d.match_id}</span>],
          ["Users", <span>
            <_UserChip u={d.user1} colors={colors} />
            <span style={{ color: colors.fgFaint, margin: "0 6px" }}>×</span>
            <_UserChip u={d.user2} colors={colors} />
          </span>],
          d.match_category && ["Category", d.match_category],
          (d.compatibility_score != null) && ["Score", <span className="st-mono">{Math.round(d.compatibility_score)}%</span>],
          (d.is_active != null) && ["Active", d.is_active ? "yes" : "no"],
          d.match_reason && ["Reason",
            <span style={{ whiteSpace: "normal", color: colors.fgSub }}>{d.match_reason}</span>],
          d.last_message_at && ["Last msg", <span className="st-mono">{String(d.last_message_at).replace("T", " ").slice(0, 16)}</span>],
          d.created_at     && ["Created",  <span className="st-mono">{String(d.created_at).replace("T", " ").slice(0, 16)}</span>],
        ]} />
      </div>
    );
  }

  if (kind === "event" || kind === "pending_event") {
    return (
      <div style={wrap}>
        {header}
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {d.title || `Event #${d.id}`}
        </div>
        <Grid rows={[
          d.status     && ["Status",   d.status],
          d.event_date && ["Date",     <span className="st-mono">{d.event_date}{d.event_time ? ` ${String(d.event_time).slice(0,5)}` : ""}</span>],
          d.location   && ["Location", d.location],
          (d.capacity != null)         && ["Capacity", <span className="st-mono">{d.capacity}</span>],
          (d.number_of_tables != null) && ["Tables",   <span className="st-mono">{d.number_of_tables}</span>],
          d.creator    && ["Creator",  <_UserChip u={d.creator} colors={colors} />],
          d.slug       && ["Slug",     <span className="st-mono" style={{ color: colors.fgSub }}>{d.slug}</span>],
          d.created_at && ["Created",  <span className="st-mono">{String(d.created_at).replace("T", " ").slice(0, 16)}</span>],
        ]} />
      </div>
    );
  }

  if (kind === "search_request") {
    return (
      <div style={wrap}>
        {header}
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {d.title || `Search request #${d.id}`}
        </div>
        <Grid rows={[
          d.user      && ["User",     <_UserChip u={d.user} colors={colors} />],
          d.category  && ["Category", d.category],
          d.status    && ["Status",   d.status],
          (d.is_active != null) && ["Active", d.is_active ? "yes" : "no"],
          (d.event_id != null)  && ["Event",  <span className="st-mono">#{d.event_id}</span>],
          (d.total_matches != null) && ["Matches",
            <span className="st-mono">{d.total_matches}{d.pending_matches != null ? ` (${d.pending_matches} pending)` : ""}</span>],
          d.request_text && ["Text",
            <span style={{ whiteSpace: "normal", color: colors.fgSub }}>{d.request_text}</span>],
          d.last_matched_at && ["Last match", <span className="st-mono">{String(d.last_matched_at).replace("T", " ").slice(0, 16)}</span>],
          d.created_at      && ["Created",    <span className="st-mono">{String(d.created_at).replace("T", " ").slice(0, 16)}</span>],
        ]} />
      </div>
    );
  }

  if (kind === "message") {
    return (
      <div style={wrap}>
        {header}
        <Grid rows={[
          ["From", <_UserChip u={d.sender}    colors={colors} />],
          ["To",   <_UserChip u={d.recipient} colors={colors} />],
          d.content    && ["Content",
            <span style={{ whiteSpace: "normal", color: colors.fgSub }}>{d.content}</span>],
          d.created_at && ["Sent", <span className="st-mono">{String(d.created_at).replace("T", " ").slice(0, 16)}</span>],
        ]} />
      </div>
    );
  }

  // Fallback
  return <div style={wrap}>{_kindLabel(kind)} #{d.id}</div>;
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: DATABASE VIEWER
// ════════════════════════════════════════════════════════════════════════════
function TabDatabase({ token }) {
  const { colors } = useTheme();
  const toast = useToast();

  const [tables, setTables]           = useState(null);
  const [tablesLoading, setTL]        = useState(true);
  const [activeTable, setActive]      = useState(null);
  const [schema, setSchema]           = useState(null);
  const [allRows, setAllRows]         = useState([]);   // накопленные строки
  const [totalRows, setTotalRows]     = useState(0);
  const [loading, setLoading]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]         = useState(false);

  const pageRef    = useRef(1);
  const LIMIT      = 100;

  const [sortCol, setSortCol]         = useState(null);
  const [sortDir, setSortDir]         = useState("desc");
  const [searchCol, setSearchCol]     = useState("");
  const [searchVal, setSearchVal]     = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [frozenCols, setFrozenCols]     = useState(0);
  const [pinnedRows, setPinnedRows]     = useState([]);
  const [hiddenCols, setHiddenCols]     = useState(new Set());
  const [expandedCell, setExpandedCell] = useState(null);
  const [colWidths, setColWidths]       = useState({});

  const containerRef  = useRef();
  const scrollRef2    = useRef();   // ref на скроллящийся tbody-контейнер
  const debRef        = useRef();

  // Hover-карточка для FK-колонок (user/match/chat/event/...)
  const [refHover, setRefHover] = useState(null); // { kind, id, rect } | null
  const hoverTimerRef = useRef(null);
  const handleRefCellEnter = useCallback((kind, id, target) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    const rect = target.getBoundingClientRect();
    hoverTimerRef.current = setTimeout(() => {
      setRefHover({ kind, id, rect });
    }, 250);
  }, []);
  const handleRefCellLeave = useCallback(() => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    setRefHover(null);
  }, []);

  // Load table list
  useEffect(() => {
    api("/db/tables", { token })
      .then(d => setTables(d.tables || []))
      .catch(e => toast(e.message, "err"))
      .finally(() => setTL(false));
  }, []);

  // Fetch одной страницы и либо сбросить, либо добавить строки
  const fetchPage = useCallback((tname, pg, sc, sd, sCol, sVal, reset = false) => {
    if (!tname) return;
    if (reset) setLoading(true); else setLoadingMore(true);
    const p = new URLSearchParams({ page: pg, limit: LIMIT });
    if (sc)           p.set("sort_col", sc);
    if (sd)           p.set("sort_dir", sd);
    if (sCol && sVal) { p.set("search_col", sCol); p.set("search_val", sVal); }
    api(`/db/tables/${encodeURIComponent(tname)}/rows?${p}`, { token })
      .then(d => {
        setSchema(d.columns || []);
        setTotalRows(d.total || 0);
        const incoming = d.rows || [];
        setAllRows(prev => reset ? incoming : [...prev, ...incoming]);
        setHasMore(pg < (d.pages || 1));
        pageRef.current = pg;
      })
      .catch(e => toast(e.message, "err"))
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, [token]);

  // При смене таблицы — сброс
  useEffect(() => {
    if (activeTable) {
      setSortCol(null); setSortDir("desc");
      setSearchCol(""); setSearchVal(""); setSearchInput("");
      setPinnedRows([]); setFrozenCols(0); setHiddenCols(new Set()); setColWidths({});
      setAllRows([]); setHasMore(false); pageRef.current = 1;
      fetchPage(activeTable, 1, null, "desc", "", "", true);
    }
  }, [activeTable]);

  // При изменении сортировки / поиска — сброс
  useEffect(() => {
    if (activeTable) {
      setAllRows([]); setHasMore(false); pageRef.current = 1;
      fetchPage(activeTable, 1, sortCol, sortDir, searchCol, searchVal, true);
    }
  }, [sortCol, sortDir, searchCol, searchVal]);

  // Infinite scroll — следим за скроллом внутри таблицы
  useEffect(() => {
    const el = scrollRef2.current;
    if (!el) return;
    function onScroll() {
      if (loadingMore || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        fetchPage(activeTable, pageRef.current + 1, sortCol, sortDir, searchCol, searchVal, false);
      }
    }
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeTable, hasMore, loadingMore, sortCol, sortDir, searchCol, searchVal]);

  function handleSearchInput(v) {
    setSearchInput(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setSearchVal(v); }, 450);
  }

  function handleSort(colName) {
    if (sortCol === colName) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(colName); setSortDir("asc"); }
  }

  function toggleFreeze(colIdx) {
    setFrozenCols(prev => prev === colIdx + 1 ? 0 : colIdx + 1);
  }

  function togglePinRow(row) {
    const key = JSON.stringify(row);
    setPinnedRows(prev => {
      const exists = prev.some(r => JSON.stringify(r) === key);
      return exists ? prev.filter(r => JSON.stringify(r) !== key) : [row, ...prev];
    });
  }

  function isPinned(row) {
    return pinnedRows.some(r => JSON.stringify(r) === JSON.stringify(row));
  }

  function toggleHideCol(colName) {
    setHiddenCols(prev => { const n = new Set(prev); n.has(colName) ? n.delete(colName) : n.add(colName); return n; });
  }

  function startResize(e, colName, currentW) {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = currentW || 140;
    const onMove = me => {
      setColWidths(prev => ({ ...prev, [colName]: Math.max(52, startW + me.clientX - startX) }));
    };
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  function handleExport() {
    if (!activeTable) return;
    const p = new URLSearchParams();
    if (sortCol)                { p.set("sort_col", sortCol); p.set("sort_dir", sortDir); }
    if (searchCol && searchVal) { p.set("search_col", searchCol); p.set("search_val", searchVal); }
    const { API_URL: AU } = window;
    fetch(`${AU || ""}/api/mod/db/tables/${encodeURIComponent(activeTable)}/export?${p}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `${activeTable}_export.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast("Export error", "err"));
  }

  const visibleCols = useMemo(
    () => (schema || []).filter(c => !hiddenCols.has(c.name)),
    [schema, hiddenCols]
  );

  const stickyOffsets = useMemo(() => {
    const off = {}; let acc = 40;
    visibleCols.forEach((c, i) => {
      if (i < frozenCols) { off[c.name] = acc; acc += (colWidths[c.name] || 140); }
    });
    return off;
  }, [visibleCols, frozenCols, colWidths]);

  function renderRow(row, rowIdx, pinned) {
    const isRowPinned = isPinned(row);
    return (
      <tr key={`${pinned ? "p" : ""}${rowIdx}`} style={{
        background: isRowPinned ? colors.primaryDim + (pinned ? "" : "60") : "transparent",
      }}>
        <td style={{
          position: "sticky", left: 0, zIndex: 3,
          background: isRowPinned ? colors.primaryDim : colors.card,
          padding: "5px 4px", borderBottom: `1px solid ${colors.border}`,
          width: 40, minWidth: 40, borderRight: `1px solid ${colors.borderMid}`,
        }}>
          <button title={isRowPinned ? "Unpin" : "Pin row"} onClick={() => togglePinRow(row)}
            style={{ background: "none", border: "none", cursor: "pointer",
              fontSize: 12, color: isRowPinned ? colors.primary : colors.fgFaint,
              padding: "2px 3px", lineHeight: 1 }}>
            {isRowPinned ? "📌" : "⊙"}
          </button>
        </td>
        {visibleCols.map((col, ci) => {
          const val = row[col.name];
          const { text, color, italic } = formatCellValue(val, col.type, colors);
          const isFz = ci < frozenCols;
          const w = colWidths[col.name] || 140;
          const refKind = (val !== null && val !== undefined && val !== "[REDACTED]"
            && (typeof val === "number" || /^\d+$/.test(String(val))))
            ? getRefKindForColumn(col.name) : null;
          const isRef = !!refKind;
          return (
            <td key={col.name}
              onClick={() => {
                if (val !== null && val !== undefined && val !== "[REDACTED]")
                  setExpandedCell({ col: col.name, val, type: col.type });
              }}
              onMouseEnter={isRef ? (e) => handleRefCellEnter(refKind, Number(val), e.currentTarget) : undefined}
              onMouseLeave={isRef ? handleRefCellLeave : undefined}
              style={{
                position: isFz ? "sticky" : "relative",
                left: isFz ? stickyOffsets[col.name] : undefined,
                zIndex: isFz ? 2 : 0,
                background: isFz ? (isRowPinned ? colors.primaryDim : colors.card) : "transparent",
                boxShadow: isFz && ci === frozenCols - 1 ? "3px 0 8px rgba(0,0,0,.2)" : undefined,
                padding: "5px 9px",
                borderBottom: `1px solid ${colors.border}`,
                borderRight: isFz && ci === frozenCols - 1
                  ? `2px solid ${colors.borderMid}` : `1px solid ${colors.border}`,
                fontSize: 12,
                fontFamily: col.type && (col.type.includes("int") || col.type.includes("numeric") || col.type.includes("float"))
                  ? "'JetBrains Mono',monospace" : "inherit",
                color: color || colors.fg,
                fontStyle: italic ? "italic" : "normal",
                width: w, minWidth: w, maxWidth: w,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                cursor: val !== null && val !== undefined && val !== "[REDACTED]" ? "pointer" : "default",
                userSelect: "text",
                textDecoration: isRef ? "underline dotted" : undefined,
                textDecorationColor: isRef ? colors.fgFaint : undefined,
              }}
              title={val === "[REDACTED]" ? "Sensitive data hidden" : undefined}
            >
              {text}
            </td>
          );
        })}
      </tr>
    );
  }

  // ── Table list view ──────────────────────────────────────────────────────
  if (tablesLoading) return (
    <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner size={32} /></div>
  );

  if (!activeTable) return (
    <div className="st-fade">
      <div style={{ marginBottom: 20 }}>
        <SectionTitle>Database Tables</SectionTitle>
        <div style={{ fontSize: 12, color: colors.fgSub, marginBottom: 16 }}>
          {tables?.length || 0} tables. Sensitive columns auto-redacted.
          <span style={{ marginLeft: 12, background: colors.redDim, color: colors.red,
            padding: "2px 8px", borderRadius: 6, fontSize: 11 }}>
            🔒 Read-only
          </span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px,1fr))", gap: 10 }}>
        {(tables || []).map(t => (
          <div key={t.name} className="st-card"
            style={{ padding: "14px 16px", cursor: "pointer" }}
            onClick={() => setActive(t.name)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13,
                  fontWeight: 700, color: colors.accent, marginBottom: 6 }}>{t.name}</div>
                <div style={{ fontSize: 11, color: colors.fgSub }}>
                  <span style={{ color: colors.fg, fontWeight: 700 }}>{(t.row_count || 0).toLocaleString("en")}</span>
                  {" rows · "}
                  <span>{t.col_count} cols</span>
                </div>
              </div>
              <span style={{ fontSize: 20, opacity: .35 }}>🗄</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Table data view ──────────────────────────────────────────────────────
  const displayRows = allRows;

  return (
    <div className="st-fade" style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => { setActive(null); setAllRows([]); setSchema(null); setTotalRows(0); setHasMore(false); }}
          style={{ background: "none", border: "none", cursor: "pointer",
            color: colors.fgSub, fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
          ← Tables
        </button>
        <span style={{ color: colors.borderMid }}>|</span>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 14,
          fontWeight: 700, color: colors.accent }}>{activeTable}</span>
        {totalRows > 0 && <span style={{ fontSize: 11, color: colors.fgSub }}>
          {totalRows.toLocaleString("en")} rows · showing {allRows.length}
        </span>}
        <div style={{ flex: 1 }} />
        <select className="st-input" style={{ width: 130, fontSize: 11 }}
          value={searchCol} onChange={e => { setSearchCol(e.target.value); setSearchVal(""); setSearchInput(""); }}>
          <option value="">— search col —</option>
          {(schema || []).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        {searchCol && (
          <input className="st-input" style={{ maxWidth: 200, fontSize: 11 }}
            placeholder="Search value…"
            value={searchInput}
            onChange={e => handleSearchInput(e.target.value)} />
        )}
        <span style={{ fontSize: 11, color: colors.fgSub, marginLeft: "auto", whiteSpace: "nowrap" }}>
          {allRows.length > 0 && `${allRows.length.toLocaleString("en")} / ${totalRows.toLocaleString("en")}`}
        </span>
        <Btn sm variant="ghost" onClick={handleExport}>↓ CSV</Btn>
      </div>

      {/* Column controls */}
      <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap",
        padding: "7px 12px", background: colors.cardMid,
        border: `1px solid ${colors.border}`, borderRadius: 10, fontSize: 11 }}>
        <span style={{ color: colors.fgSub, fontWeight: 700, fontSize: 9,
          textTransform: "uppercase", letterSpacing: ".07em", flexShrink: 0 }}>Cols:</span>
        {(schema || []).map((col, ci) => {
          const hidden = hiddenCols.has(col.name);
          const frozen = ci < frozenCols;
          return (
            <div key={col.name} style={{ display: "flex", alignItems: "center", gap: 2 }}>
              {!hidden && (
                <button title={frozen ? "Unfreeze" : "Freeze column"} onClick={() => toggleFreeze(ci)}
                  style={{ border: `1px solid ${frozen ? colors.primary : colors.border}`,
                    background: frozen ? colors.primaryDim : "transparent",
                    borderRadius: 5, padding: "2px 7px", fontSize: 10, cursor: "pointer",
                    color: frozen ? colors.accent : colors.fgSub, display: "flex",
                    alignItems: "center", gap: 3, fontFamily: "inherit" }}>
                  {frozen && <span style={{ fontSize: 9 }}>❄</span>}
                  <span style={{ fontFamily: "'JetBrains Mono',monospace",
                    color: dbTypeColor(col.type, colors) }}>{col.name}</span>
                  {col.is_redacted && <span style={{ color: colors.red, fontSize: 9 }}>🔒</span>}
                </button>
              )}
              <button title={hidden ? "Show" : "Hide"} onClick={() => toggleHideCol(col.name)}
                style={{ background: "none", border: "none", cursor: "pointer",
                  color: hidden ? colors.fgFaint : colors.fgSub, fontSize: 11, padding: "1px 2px" }}>
                {hidden ? "+" : "×"}
              </button>
            </div>
          );
        })}
        {hiddenCols.size > 0 && (
          <button onClick={() => setHiddenCols(new Set())}
            style={{ border: `1px solid ${colors.border}`, background: "none",
              borderRadius: 5, padding: "2px 7px", fontSize: 10,
              color: colors.fgSub, cursor: "pointer", marginLeft: 4 }}>
            Show all ({hiddenCols.size} hidden)
          </button>
        )}
        {pinnedRows.length > 0 && (
          <button onClick={() => setPinnedRows([])}
            style={{ border: `1px solid ${colors.border}`, background: "none",
              borderRadius: 5, padding: "2px 7px", fontSize: 10,
              color: colors.primary, cursor: "pointer", marginLeft: "auto" }}>
            Clear {pinnedRows.length} pinned
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ borderRadius: 12, border: `1px solid ${colors.border}`,
        overflow: "hidden", background: colors.card }}>
        <div ref={el => { containerRef.current = el; scrollRef2.current = el; }}
          style={{ overflowX: "auto", overflowY: "auto", maxHeight: "72vh" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 50 }}><Spinner size={28} /></div>
          ) : (
            <table style={{ borderCollapse: "separate", borderSpacing: 0,
              width: "max-content", minWidth: "100%", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  {/* Corner */}
                  <th style={{ position: "sticky", left: 0, top: 0, zIndex: 10,
                    background: colors.bgDeep, width: 40, minWidth: 40,
                    borderBottom: `2px solid ${colors.borderMid}`,
                    borderRight: `1px solid ${colors.borderMid}`, padding: 0 }} />
                  {visibleCols.map((col, ci) => {
                    const isFz = ci < frozenCols;
                    const w = colWidths[col.name] || 140;
                    const isActive = sortCol === col.name;
                    return (
                      <th key={col.name} style={{
                        position: "sticky", top: 0,
                        left: isFz ? stickyOffsets[col.name] : undefined,
                        zIndex: isFz ? 9 : 8,
                        background: colors.bgDeep,
                        borderBottom: `2px solid ${colors.borderMid}`,
                        borderRight: isFz && ci === frozenCols - 1
                          ? `2px solid ${colors.borderMid}` : `1px solid ${colors.border}`,
                        width: w, minWidth: w, maxWidth: w, padding: 0,
                        userSelect: "none",
                        boxShadow: isFz && ci === frozenCols - 1 ? "3px 0 6px rgba(0,0,0,.2)" : undefined,
                      }}>
                        <div style={{ display: "flex", alignItems: "stretch" }}>
                          <div onClick={() => handleSort(col.name)} style={{
                            flex: 1, display: "flex", alignItems: "center", gap: 5,
                            padding: "8px 8px 8px 10px", cursor: "pointer",
                            background: isActive ? colors.primaryDim : "transparent",
                          }}>
                            <span style={{ fontFamily: "'JetBrains Mono',monospace",
                              fontSize: 11, fontWeight: 700,
                              color: isActive ? colors.accent : colors.fg,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {col.name}
                            </span>
                            {col.is_redacted && <span style={{ color: colors.red, fontSize: 9 }}>🔒</span>}
                            {isActive && <span style={{ color: colors.primary, fontSize: 10, flexShrink: 0 }}>
                              {sortDir === "asc" ? "↑" : "↓"}
                            </span>}
                            <span style={{ fontSize: 9, flexShrink: 0,
                              color: dbTypeColor(col.type, colors),
                              background: dbTypeColor(col.type, colors) + "18",
                              padding: "1px 4px", borderRadius: 4,
                              fontFamily: "'JetBrains Mono',monospace" }}>
                              {shortTypeName(col.type)}
                            </span>
                          </div>
                          {/* Resize handle */}
                          <div onMouseDown={e => startResize(e, col.name, w)}
                            style={{ width: 5, cursor: "col-resize", flexShrink: 0,
                              borderLeft: `1px solid ${colors.border}` }}
                            onMouseEnter={e => e.currentTarget.style.background = colors.primary}
                            onMouseLeave={e => e.currentTarget.style.background = ""} />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pinnedRows.length > 0 && pinnedRows.map((r, i) => renderRow(r, i, true))}
                {pinnedRows.length > 0 && (
                  <tr><td colSpan={visibleCols.length + 1} style={{ height: 3,
                    background: `linear-gradient(${colors.primaryDim},transparent)`,
                    borderBottom: `2px dashed ${colors.primary}` }} /></tr>
                )}
                {displayRows.length === 0 ? (
                  <tr><td colSpan={visibleCols.length + 1}
                    style={{ padding: 40, textAlign: "center", color: colors.fgSub, fontSize: 13 }}>
                    No data
                  </td></tr>
                ) : displayRows.map((r, i) => renderRow(r, i, false))}
                {/* Infinite scroll sentinel row */}
                {loadingMore && (
                  <tr><td colSpan={visibleCols.length + 1} style={{ padding: "12px 0", textAlign: "center" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
                      fontSize: 11, color: colors.fgSub }}>
                      <div className="st-spin" style={{
                        width: 14, height: 14, borderRadius: "50%",
                        border: `2px solid ${colors.border}`, borderTopColor: colors.primary,
                      }} />
                      Loading more…
                    </div>
                  </td></tr>
                )}
                {!loadingMore && !hasMore && allRows.length > 0 && (
                  <tr><td colSpan={visibleCols.length + 1} style={{ padding: "10px 0", textAlign: "center" }}>
                    <span style={{ fontSize: 10, color: colors.fgFaint, letterSpacing: ".06em",
                      textTransform: "uppercase", fontWeight: 700 }}>
                      ─ All {totalRows.toLocaleString("en")} rows loaded ─
                    </span>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Cell expand modal */}
      {expandedCell && (
        <WindowModal
          windowId={_safeWindowId("cell", `${expandedCell.col}_${expandedCell.type}`)}
          title={expandedCell.col || "Cell value"}
          onClose={() => setExpandedCell(null)}
          width={1100}
          height={620}
          minWidth={520}
        >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, alignItems: "center" }}>
              <div>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", color: colors.accent,
                  fontSize: 13, fontWeight: 700 }}>{expandedCell.col}</span>
                <span style={{ color: colors.fgSub, fontSize: 11, marginLeft: 8 }}>
                  ({shortTypeName(expandedCell.type)})
                </span>
              </div>
              <button onClick={() => setExpandedCell(null)}
                style={{ background: "none", border: "none", color: colors.fgSub, fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ background: colors.bgDeep, borderRadius: 8, padding: "14px 16px",
              fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: colors.fg,
              lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-all",
              maxHeight: "70vh", overflowY: "auto", border: `1px solid ${colors.border}` }}>
              {typeof expandedCell.val === "object"
                ? JSON.stringify(expandedCell.val, null, 2)
                : String(expandedCell.val)}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <Btn sm variant="ghost" onClick={() => {
                navigator.clipboard.writeText(
                  typeof expandedCell.val === "object"
                    ? JSON.stringify(expandedCell.val, null, 2)
                    : String(expandedCell.val)
                );
              }}>Copy</Btn>
            </div>
        </WindowModal>
      )}

      {/* Hover-карточка для FK-колонок (user/match/chat/event/...) */}
      {refHover && ReactDOM.createPortal(
        <RefHoverCard
          kind={refHover.kind}
          id={refHover.id}
          token={token}
          anchorRect={refHover.rect}
          colors={colors}
        />,
        document.body
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: AUDIT LOGS (Access Log + Action Log)
// ════════════════════════════════════════════════════════════════════════════
function TabAccessLogs({ token }) {
  const { colors } = useTheme();
  const toast = useToast();
  const [subTab, setSubTab]   = useStickyState("auditlogs_subtab", "actions");
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useStickyState("auditlogs_page", 1);
  const [actionF, setActionF] = useStickyState("auditlogs_action", "");
  const [entityF, setEntityF] = useStickyState("auditlogs_entity", "");
  const [diffRow, setDiffRow] = useState(null);
  const debRef = useRef();

  const load = useCallback((pg, af, ef) => {
    setLoading(true);
    if (subTab === "actions") {
      const p = new URLSearchParams({ page: pg, limit: 50 });
      if (af) p.set("action", af);
      if (ef) p.set("entity", ef);
      api(`/action-logs?${p}`, { token })
        .then(setData).catch(e => toast(e.message, "err")).finally(() => setLoading(false));
    } else {
      const offset = (pg - 1) * 100;
      api(`/access-logs?limit=100&offset=${offset}`, { token })
        .then(setData).catch(e => toast(e.message, "err")).finally(() => setLoading(false));
    }
  }, [token, subTab]);

  useEffect(() => { setPage(1); load(1, actionF, entityF); }, [subTab]);
  useEffect(() => { load(page, actionF, entityF); }, [page]);

  function actionColor(action) {
    if (!action) return colors.fgSub;
    const a = action.toLowerCase();
    if (a.includes("delete") || a.includes("reject") || a.includes("ban")) return colors.red;
    if (a.includes("approve") || a.includes("publish") || a.includes("success")) return colors.green;
    if (a.includes("login") || a.includes("view") || a.includes("list")) return colors.blue;
    if (a.includes("edit") || a.includes("change") || a.includes("update")) return colors.orange;
    return colors.fgSub;
  }

  const subTabs = [
    { id: "actions", label: "Admin Actions" },
    { id: "access",  label: "Access Log"    },
  ];

  return (
    <div className="st-fade">
      <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <PillTabs tabs={subTabs} active={subTab} onChange={setSubTab} />
        {subTab === "actions" && (
          <>
            <input className="st-input" style={{ maxWidth: 180, fontSize: 11 }}
              placeholder="Filter action…"
              onChange={e => {
                clearTimeout(debRef.current);
                const v = e.target.value; setActionF(v);
                debRef.current = setTimeout(() => { setPage(1); load(1, v, entityF); }, 400);
              }} />
            <input className="st-input" style={{ maxWidth: 140, fontSize: 11 }}
              placeholder="Filter entity…"
              onChange={e => {
                clearTimeout(debRef.current);
                const v = e.target.value; setEntityF(v);
                debRef.current = setTimeout(() => { setPage(1); load(1, actionF, v); }, 400);
              }} />
          </>
        )}
        {data && (
          <span style={{ marginLeft: "auto", fontSize: 11, color: colors.fgSub }}>
            {(data.total || (data.items || []).length || 0).toLocaleString("en")} entries
          </span>
        )}
        <button
          className="st-btn st-btn-ghost st-btn-sm"
          onClick={() => {
            const tbl = subTab === "actions" ? "admin_action_logs" : "admin_access_logs";
            window.open(`${API_URL}/api/mod/db/tables/${tbl}/export`, "_blank");
          }}
          title="Export log to CSV"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          CSV
        </button>
      </div>

      {loading ? (
        <div className="st-card" style={{ overflow: "hidden" }}>
          <table className="st-table"><thead><tr>
            {[1,2,3,4,5,6].map(i => <th key={i}><Skeleton height={10} width="55%" /></th>)}
          </tr></thead><tbody>
            {Array.from({length:10}).map((_,i) => <SkeletonRow key={i} cols={6}/>)}
          </tbody></table></div>
      ) : subTab === "actions" ? (
        <>
          <div className="st-card" style={{ overflow: "hidden" }}>
            <div className="st-table-scroll"><table className="st-table">
              <thead><tr>
                <th>Time</th><th>Admin</th><th>Action</th>
                <th>Entity</th><th>Notes</th><th>IP hash</th>
              </tr></thead>
              <tbody>
                {(data?.items || []).map(r => (
                  <tr key={r.id} style={{ cursor: "default" }}>
                    <td><span className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                      {r.ts ? new Date(r.ts).toLocaleString("en") : "—"}
                    </span></td>
                    <td>
                      <span style={{ fontWeight: 600, fontSize: 12 }}>{r.admin_name}</span>
                      <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, marginLeft: 5 }}>
                        #{r.admin_id}
                      </span>
                    </td>
                    <td><Tag color={actionColor(r.action)}>{r.action}</Tag></td>
                    <td>
                      {r.entity && <Tag color={colors.fgSub}>{r.entity}</Tag>}
                      {r.entity_id && <span className="st-mono" style={{ fontSize: 10,
                        color: colors.fgFaint, marginLeft: 4 }}>#{r.entity_id}</span>}
                    </td>
                    <td>
                      <span style={{ fontSize: 11, color: colors.fgSub, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {r.notes || "—"}
                      </span>
                      {(r.old_data || r.new_data) && (
                        <button
                          onClick={e => { e.stopPropagation(); setDiffRow(r); }}
                          title="View changes"
                          style={{
                            marginLeft: 6, padding: "1px 6px", borderRadius: 4,
                            border: `1px solid ${colors.border}`, background: "none",
                            cursor: "pointer", fontSize: 10, color: colors.fgSub,
                            fontFamily: "inherit", transition: "all .12s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.color = colors.accent; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.color = colors.fgSub; }}
                        >diff</button>
                      )}
                    </td>
                    <td><div style={{ display:"flex", alignItems:"center" }}><span className="st-mono" title={r.ip || ""} style={{ fontSize: 9, color: colors.fgFaint }}>
                      {r.ip ? r.ip.slice(0, 12) + "…" : "—"}
                    </span>{r.ip && <CopyBtn text={r.ip} />}</div></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
          {diffRow && (
            <DiffViewer
              oldData={diffRow.old_data}
              newData={diffRow.new_data}
              onClose={() => setDiffRow(null)}
            />
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: colors.fgSub, marginBottom: 12,
            padding: "8px 12px", background: colors.orangeDim,
            border: `1px solid ${colors.orange}44`, borderRadius: 8 }}>
            ⚠ IP addresses are stored as SHA-256 hashes only. Reverse lookup is impossible.
          </div>
          <div className="st-card" style={{ overflow: "hidden" }}>
            <div className="st-table-scroll"><table className="st-table">
              <thead><tr>
                <th>Time</th><th>Method</th><th>Path</th>
                <th>User</th><th>Event</th><th>Result</th><th>IP hash</th>
              </tr></thead>
              <tbody>
                {(data?.items || []).map(r => (
                  <tr key={r.id} style={{ cursor: "default" }}>
                    <td><span className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                      {r.ts ? new Date(r.ts).toLocaleString("en") : "—"}
                    </span></td>
                    <td><Tag color={r.method === "GET" ? colors.blue
                      : r.method === "POST" ? colors.green
                      : r.method === "DELETE" ? colors.red : colors.fgSub}>
                      {r.method}
                    </Tag></td>
                    <td><span className="st-mono" title={r.path || ""} style={{ fontSize: 11, color: colors.fgMid, wordBreak: "break-all" }}>
                      {r.path}
                    </span></td>
                    <td><span className="st-mono" style={{ fontSize: 11, color: colors.fgSub }}>
                      {r.user_id || "—"}
                    </span></td>
                    <td><Tag color={colors.fgSub}>{r.event_type || "—"}</Tag></td>
                    <td><Tag color={r.success ? colors.green : colors.red}>
                      {r.success ? "✓" : "✕"}
                    </Tag></td>
                    <td><div style={{ display:"flex", alignItems:"center" }}><span className="st-mono" title={r.ip || ""} style={{ fontSize: 9, color: colors.fgFaint }}>
                      {r.ip ? r.ip.slice(0, 12) + "…" : "—"}
                    </span>{r.ip && <CopyBtn text={r.ip} />}</div></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </div>
  );
}



// ════════════════════════════════════════════════════════════════════════════
//  TabEventsAll — Events + Submissions + Edit Requests
// ════════════════════════════════════════════════════════════════════════════
function TabEventsAll({ token, onSelectUser, onOpenChat }) {
  const [sub, setSub] = useState("events");
  const subTabs = [
    { id: "events",        label: "Published Events" },
    { id: "pending",       label: "Submissions"      },
    { id: "edit-requests", label: "Edit Requests"    },
  ];
  return (
    <div className="st-fade" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <PillTabs tabs={subTabs} active={sub} onChange={setSub} />
      {sub === "events"        && <TabEvents            token={token} onSelectUser={onSelectUser} onOpenChat={onOpenChat} />}
      {sub === "pending"       && <TabPendingEvents     token={token} onSelectUser={onSelectUser} />}
      {sub === "edit-requests" && <TabEventEditRequests token={token} onSelectUser={onSelectUser} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TabSafety — Blacklist + Bans в одной вкладке
// ════════════════════════════════════════════════════════════════════════════
function TabSafety({ token }) {
  const [sub, setSub] = useState("blacklist");
  const subTabs = [
    { id: "blacklist", label: "Blacklist" },
    { id: "bans",      label: "Ban Logs"  },
  ];
  return (
    <div className="st-fade" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <PillTabs tabs={subTabs} active={sub} onChange={setSub} />
      {sub === "blacklist" && <TabBlacklist token={token} />}
      {sub === "bans"      && <TabBans      token={token} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: WALLETS — балансы пользователей и история изменений
// ════════════════════════════════════════════════════════════════════════════
function TabWallets({ token }) {
  const { colors } = useTheme();
  const toast = useToast();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useStickyState("wallets_page", 1);
  const [search, setSearch]   = useStickyState("wallets_search", "");
  const [selected, setSelected] = useState(null); // user_id выбранного юзера
  const [detail, setDetail]   = useState(null);   // {user, wallet, history}
  const [detailLoading, setDetailLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [amountStr, setAmountStr]   = useState("");
  const [noteStr, setNoteStr]       = useState("");
  const [busy, setBusy]             = useState(false);
  const debRef = useRef();

  const load = useCallback((pg, sq) => {
    setLoading(true);
    const p = new URLSearchParams({ page: pg, limit: 30 });
    if (sq) p.set("search", sq);
    api(`/wallets?${p}`, { token })
      .then(setData)
      .catch(e => toast(e.message, "err"))
      .finally(() => setLoading(false));
  }, [token, toast]);

  useEffect(() => { load(page, search); }, [page, search, load]);

  const loadDetail = useCallback((userId) => {
    setDetailLoading(true);
    api(`/wallets/${userId}?history_limit=100`, { token })
      .then(setDetail)
      .catch(e => toast(e.message, "err"))
      .finally(() => setDetailLoading(false));
  }, [token, toast]);

  function openUser(userId) {
    setSelected(userId);
    setDetail(null);
    loadDetail(userId);
  }

  async function submitAdjust() {
    const amt = parseFloat(String(amountStr).replace(",", "."));
    if (!isFinite(amt) || amt === 0) {
      toast("Amount must be a number ≠ 0", "err");
      return;
    }
    setBusy(true);
    try {
      const res = await api(`/wallets/${selected}/adjust`, {
        method: "POST",
        body: { amount: amt, note: noteStr || null },
        token,
      });
      toast(`Balance: ${res.old_balance.toFixed(2)} → ${res.new_balance.toFixed(2)}`);
      setAdjustOpen(false);
      setAmountStr("");
      setNoteStr("");
      loadDetail(selected);
      load(page, search);
    } catch (e) {
      toast(e.message, "err");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
      {/* ── ЛЕВО: список кошельков ── */}
      <div style={{ flex: "1 1 480px", minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <input className="st-input" placeholder="Search by name, email, id..."
            defaultValue={search}
            onChange={e => {
              clearTimeout(debRef.current);
              const v = e.target.value;
              debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 380);
            }}
            style={{ maxWidth: 280 }} />
        </div>

        {loading ? (
          <div className="st-card" style={{ overflow: "hidden" }}>
            <div className="st-table-scroll"><table className="st-table"><thead><tr>
              {[1,2,3,4].map(i => <th key={i}><Skeleton height={10} width="60%" /></th>)}
            </tr></thead><tbody>
              {Array.from({length:6}).map((_,i) => <SkeletonRow key={i} cols={4}/>)}
            </tbody></table></div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, color: colors.fgSub, marginBottom: 10 }}>
              {data?.total ?? 0} wallets
            </div>
            <div className="st-card" style={{ overflow: "hidden" }}>
              <div className="st-table-scroll"><table className="st-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th style={{ textAlign: "right" }}>Balance</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data?.wallets?.map(w => {
                    const isSel = selected === w.user_id;
                    return (
                      <tr key={w.wallet_id}
                          onClick={() => openUser(w.user_id)}
                          style={{
                            cursor: "pointer",
                            background: isSel ? colors.primaryDim + "55" : undefined,
                            borderLeft: isSel ? `3px solid ${colors.primary}` : "3px solid transparent",
                          }}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13, color: colors.fg }}>{w.user_name}</div>
                          <div className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                            #{w.user_id} · {w.user_email || "—"}
                          </div>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className="st-mono" style={{
                            fontWeight: 700, fontSize: 13,
                            color: w.balance > 0 ? colors.green : (w.balance < 0 ? colors.red : colors.fg),
                          }}>
                            {w.balance.toFixed(2)} {w.currency}
                          </span>
                        </td>
                        <td>
                          <span className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                            {w.updated_at ? new Date(w.updated_at).toLocaleString("en") : "—"}
                          </span>
                        </td>
                        <td style={{ color: colors.fgSub, fontSize: 16 }}>›</td>
                      </tr>
                    );
                  })}
                  {(!data?.wallets || data.wallets.length === 0) && (
                    <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: colors.fgSub }}>
                      No wallets
                    </td></tr>
                  )}
                </tbody>
              </table></div>
            </div>
            <Pagination page={page} pages={data?.pages || 1} onChange={p => setPage(p)} />
          </>
        )}
      </div>

      {/* ── ПРАВО: детали выбранного юзера ── */}
      <div style={{ flex: "1 1 380px", minWidth: 0 }}>
        {!selected && (
          <div className="st-card" style={{ padding: 24, color: colors.fgSub, fontSize: 13 }}>
            Select a user on the left to view balance and change history.
          </div>
        )}

        {selected && detailLoading && (
          <div className="st-card" style={{ padding: 24 }}>
            <Skeleton height={20} width="60%" />
            <div style={{ height: 12 }} />
            <Skeleton height={32} width="40%" />
            <div style={{ height: 16 }} />
            {[1,2,3,4].map(i => (
              <div key={i} style={{ marginBottom: 8 }}><Skeleton height={14} /></div>
            ))}
          </div>
        )}

        {selected && !detailLoading && detail && (
          <div className="st-card" style={{ padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: colors.fg }}>
                  {detail.user.name}
                </div>
                <div className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                  #{detail.user.id} · {detail.user.email || "—"}
                </div>
              </div>
              <Btn sm variant="primary" onClick={() => setAdjustOpen(true)}>+ / − Balance</Btn>
            </div>

            <div style={{
              padding: 14, borderRadius: 12,
              background: colors.bgDeep || colors.bg,
              border: `1px solid ${colors.border}`,
              marginBottom: 16,
              display: "flex", alignItems: "baseline", gap: 8,
            }}>
              <span style={{ fontSize: 11, color: colors.fgSub, textTransform: "uppercase", letterSpacing: ".08em" }}>
                Balance
              </span>
              <span className="st-mono" style={{
                fontSize: 22, fontWeight: 800,
                color: detail.wallet.balance > 0 ? colors.green : (detail.wallet.balance < 0 ? colors.red : colors.fg),
              }}>
                {detail.wallet.balance.toFixed(2)}
              </span>
              <span style={{ fontSize: 13, color: colors.fgSub, fontWeight: 600 }}>
                {detail.wallet.currency}
              </span>
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em",
                          color: colors.fgSub, marginBottom: 8 }}>
              History ({detail.history.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 480, overflowY: "auto" }}>
              {detail.history.length === 0 && (
                <div style={{ fontSize: 12, color: colors.fgSub, padding: 8 }}>
                  No changes yet.
                </div>
              )}
              {detail.history.map(h => (
                <div key={h.id} style={{
                  padding: "8px 10px", borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                  background: colors.bg,
                  display: "flex", justifyContent: "space-between", gap: 8,
                }}>
                  <span className="st-mono" style={{ fontSize: 12, color: colors.fg, wordBreak: "break-word" }}>
                    {h.change}
                  </span>
                  <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, flexShrink: 0 }}>
                    {h.created_at ? new Date(h.created_at).toLocaleString("en") : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── МОДАЛКА: ручная корректировка ── */}
      {adjustOpen && detail && (
        <FormModal title={`Adjust Balance — ${detail.user.name}`} windowId={`wallet_${detail.user.id}_balance`} onClose={() => !busy && setAdjustOpen(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 12, color: colors.fgSub }}>
              Current balance: <span className="st-mono" style={{ color: colors.fg, fontWeight: 700 }}>
                {detail.wallet.balance.toFixed(2)} {detail.wallet.currency}
              </span>
            </div>
            <label style={{ fontSize: 12, color: colors.fgMid, fontWeight: 600 }}>
              Amount (positive — add, negative — deduct)
            </label>
            <input className="st-input" type="text" inputMode="decimal"
              placeholder="e.g.: 100 or -25.50"
              value={amountStr}
              onChange={e => setAmountStr(e.target.value)}
              autoFocus
            />
            <label style={{ fontSize: 12, color: colors.fgMid, fontWeight: 600 }}>
              Comment (optional)
            </label>
            <input className="st-input" type="text" maxLength={200}
              placeholder="e.g.: refund, bonus, manual top-up"
              value={noteStr}
              onChange={e => setNoteStr(e.target.value)}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <Btn variant="ghost" onClick={() => setAdjustOpen(false)} disabled={busy}>Cancel</Btn>
              <Btn variant="primary" loading={busy} onClick={submitAdjust}>Apply</Btn>
            </div>
          </div>
        </FormModal>
      )}
    </div>
  );
}

function PaymentAmountSetting({ label, value, onSave, loading, colors }) {
  const [draft, setDraft] = useState(String(value ?? ""));

  useEffect(() => { setDraft(String(value ?? "")); }, [value]);

  const dirty = Number(draft) !== Number(value ?? 0);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <div style={{ fontSize: 12, color: colors.fgMid, minWidth: 180 }}>{label}</div>
      <input
        className="st-input"
        type="number"
        min="0"
        step="0.01"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => e.key === "Enter" && dirty && onSave(Number(draft || 0))}
        style={{ width: 120, textAlign: "right" }}
      />
      {dirty && (
        <Btn sm loading={loading} onClick={() => onSave(Number(draft || 0))}>
          Save
        </Btn>
      )}
    </div>
  );
}

function TabPayments({ token }) {
  const { colors } = useTheme();
  const toast = useToast();
  const [sub, setSub] = useStickyState("payments_subtab", "pending");
  const [settingsData, setSettingsData] = useState(null);
  const [pending, setPending] = useState(null);
  const [txs, setTxs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [acting, setActing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settings, pendingData, txData] = await Promise.all([
        api("/payments/settings", { token }),
        api("/payments/pending", { token }),
        api("/payments/transactions?limit=80", { token }),
      ]);
      setSettingsData(settings);
      setPending(pendingData);
      setTxs(txData);
    } catch (e) {
      toast(e.message, "err");
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => { load(); }, [load]);

  async function saveSetting(key, value) {
    setSaving(p => ({ ...p, [key]: true }));
    try {
      const next = await api("/payments/settings", { method: "PUT", token, body: { [key]: value } });
      setSettingsData(next);
      toast("Payment settings saved", "ok");
    } catch (e) {
      toast(e.message, "err");
    } finally {
      setSaving(p => ({ ...p, [key]: false }));
    }
  }

  async function act(groupId, action) {
    setActing(p => ({ ...p, [groupId + action]: true }));
    try {
      await api(`/payments/${groupId}/${action}`, {
        method: "POST",
        token,
        body: action === "reject" ? { reason: "Rejected by moderator" } : {},
      });
      toast(action === "approve" ? "Payment approved" : "Payment rejected", "ok");
      await load();
    } catch (e) {
      toast(e.message, "err");
    } finally {
      setActing(p => ({ ...p, [groupId + action]: false }));
    }
  }

  const tabs = [
    { id: "pending", label: `Pending (${pending?.total || 0})` },
    { id: "transactions", label: "Transactions" },
    { id: "settings", label: "Settings" },
  ];
  const s = settingsData?.settings || {};
  const platformWallet = settingsData?.platform_wallet;

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="st-fade" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <PillTabs tabs={tabs} active={sub} onChange={setSub} />
        <Btn sm variant="ghost" onClick={load}>Refresh</Btn>
        {platformWallet && (
          <span className="st-mono" style={{ marginLeft: "auto", fontSize: 12, color: colors.fgSub }}>
            Platform: <b style={{ color: colors.fg }}>{Number(platformWallet.balance || 0).toFixed(2)} {platformWallet.currency}</b>
          </span>
        )}
      </div>

      {sub === "settings" && (
        <SettingsSection
          title="Payments"
          description="Wallet billing, platform fees and moderator approval mode."
          colors={colors}
        >
          <SettingsRow label="Payments enabled" hint="Disables all automatic billing when off." colors={colors}>
            <ToggleSwitch
              value={!!s.payments_enabled}
              onChange={v => saveSetting("payments_enabled", v)}
              loading={saving.payments_enabled}
              colors={colors}
            />
          </SettingsRow>
          <SettingsRow label="Moderator approval" hint="When enabled, debits stay pending until an admin approves the group." colors={colors}>
            <ToggleSwitch
              value={!!s.payment_admin_approval_required}
              onChange={v => saveSetting("payment_admin_approval_required", v)}
              loading={saving.payment_admin_approval_required}
              colors={colors}
            />
          </SettingsRow>
          <SettingsRow label="Allow negative balances" hint="If off, debits fail when a wallet has insufficient funds." colors={colors}>
            <ToggleSwitch
              value={!!s.payment_allow_negative_balance}
              onChange={v => saveSetting("payment_allow_negative_balance", v)}
              loading={saving.payment_allow_negative_balance}
              colors={colors}
            />
          </SettingsRow>
          <SettingsRow label="Fees" hint="Match fee applies only outside events. Event fee is included in paid tickets and charged to organizers for free events." colors={colors}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <PaymentAmountSetting
                label="Non-event match fee, EUR"
                value={s.payment_match_fee_eur}
                onSave={v => saveSetting("payment_match_fee_eur", v)}
                loading={saving.payment_match_fee_eur}
                colors={colors}
              />
              <PaymentAmountSetting
                label="Event platform fee, EUR"
                value={s.payment_event_platform_fee_eur}
                onSave={v => saveSetting("payment_event_platform_fee_eur", v)}
                loading={saving.payment_event_platform_fee_eur}
                colors={colors}
              />
            </div>
          </SettingsRow>
        </SettingsSection>
      )}

      {sub === "pending" && (
        <div className="st-card" style={{ overflow: "hidden" }}>
          <div className="st-table-scroll"><table className="st-table">
            <thead><tr>
              <th>Created</th><th>Type</th><th>Debit</th><th>Entries</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {(pending?.items || []).map(g => (
                <tr key={g.group_id}>
                  <td><span className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                    {g.created_at ? new Date(g.created_at).toLocaleString("en") : "-"}
                  </span></td>
                  <td>
                    <Tag color={colors.orange}>{(g.types || []).join(", ")}</Tag>
                    <div className="st-mono" style={{ fontSize: 9, color: colors.fgFaint, marginTop: 4 }}>
                      {g.operation_key}
                    </div>
                  </td>
                  <td><span className="st-mono" style={{ color: colors.red, fontWeight: 700 }}>
                    -{Number(g.debit_total || 0).toFixed(2)} EUR
                  </span></td>
                  <td>
                    {(g.rows || []).slice(0, 4).map(r => (
                      <div key={r.id} style={{ fontSize: 11, color: colors.fgSub, marginBottom: 3 }}>
                        <span className="st-mono">{r.amount > 0 ? "+" : ""}{Number(r.amount).toFixed(2)}</span>
                        {" "}{r.user_name || (r.platform_wallet_id ? "Platform" : "Unknown")}
                      </div>
                    ))}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Btn sm variant="primary" loading={acting[g.group_id + "approve"]} onClick={() => act(g.group_id, "approve")}>
                        Approve
                      </Btn>
                      <Btn sm variant="danger" loading={acting[g.group_id + "reject"]} onClick={() => act(g.group_id, "reject")}>
                        Reject
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
              {(!pending?.items || pending.items.length === 0) && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: colors.fgSub }}>
                  No pending payments
                </td></tr>
              )}
            </tbody>
          </table></div>
        </div>
      )}

      {sub === "transactions" && (
        <div className="st-card" style={{ overflow: "hidden" }}>
          <div className="st-table-scroll"><table className="st-table">
            <thead><tr>
              <th>Time</th><th>User</th><th>Type</th><th>Status</th><th>Amount</th><th>Context</th>
            </tr></thead>
            <tbody>
              {(txs?.items || []).map(r => (
                <tr key={r.id}>
                  <td><span className="st-mono" style={{ fontSize: 10, color: colors.fgSub }}>
                    {r.created_at ? new Date(r.created_at).toLocaleString("en") : "-"}
                  </span></td>
                  <td>
                    <span style={{ fontSize: 12, fontWeight: 600, color: colors.fg }}>
                      {r.user_name || (r.platform_wallet_id ? "Platform" : "Unknown")}
                    </span>
                    {r.user_id && <span className="st-mono" style={{ fontSize: 10, color: colors.fgFaint, marginLeft: 4 }}>#{r.user_id}</span>}
                  </td>
                  <td><Tag color={colors.blue}>{r.transaction_type}</Tag></td>
                  <td><Tag color={r.status === "completed" ? colors.green : r.status === "pending" ? colors.orange : colors.red}>{r.status}</Tag></td>
                  <td><span className="st-mono" style={{ fontWeight: 700, color: r.amount >= 0 ? colors.green : colors.red }}>
                    {r.amount > 0 ? "+" : ""}{Number(r.amount).toFixed(2)} {r.currency}
                  </span></td>
                  <td>
                    <span style={{ fontSize: 11, color: colors.fgSub }}>{r.description || "-"}</span>
                    <div className="st-mono" style={{ fontSize: 9, color: colors.fgFaint }}>
                      {r.event_id ? `event #${r.event_id} ` : ""}{r.match_id ? `match #${r.match_id} ` : ""}{r.registration_id ? `reg #${r.registration_id}` : ""}
                    </div>
                  </td>
                </tr>
              ))}
              {(!txs?.items || txs.items.length === 0) && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: colors.fgSub }}>
                  No payment transactions
                </td></tr>
              )}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}

function TabSettings({ token }) {
  const { colors } = useTheme();
  const toast = useToast();

  const [settings, setSettings]   = useState({});
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState({});

  // ── Загрузка ──────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api("/site-settings", { token });
      setSettings(d);
    } catch (e) {
      toast("Failed to load settings: " + e.message, "err");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ── Сохранение одного ключа ────────────────────────────────────────────
  const save = useCallback(async (key, value) => {
    setSaving(p => ({ ...p, [key]: true }));
    try {
      await api("/site-settings", { method: "PUT", token, body: { key, value } });
      setSettings(p => ({ ...p, [key]: value }));
      toast("Saved", "ok");
    } catch (e) {
      toast("Save error: " + e.message, "err");
    } finally {
      setSaving(p => ({ ...p, [key]: false }));
    }
  }, [token]);

  // ── Helpers ────────────────────────────────────────────────────────────
  const getBool = (key, def = "true") => (settings[key] ?? def) !== "false";
  const getString = (key, def = "") => settings[key] ?? def;

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="st-fade" style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: "24px 28px",
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: colors.fg, marginBottom: 6 }}>
          Site Settings
        </div>
        <div style={{ fontSize: 13, color: colors.fgSub }}>
          Global configuration for the landing page and public-facing features.
        </div>
      </div>

      {/* ══ SECTION: STATS COUNTER ════════════════════════════════════════ */}
      <SettingsSection
        title="Landing Page — Stats Counter"
        description="Show animated statistics on the landing page (users registered, connections made, events held)."
        colors={colors}
      >

        {/* Toggle */}
        <SettingsRow
          label="Show stats counter"
          hint="Displays the animated number counters below the banner on the landing page."
          colors={colors}
        >
          <ToggleSwitch
            value={getBool("show_stats_counter")}
            onChange={v => save("show_stats_counter", v ? "true" : "false")}
            loading={saving["show_stats_counter"]}
            colors={colors}
          />
        </SettingsRow>

        {/* Override fields */}
        <div style={{
          opacity: getBool("show_stats_counter") ? 1 : 0.4,
          pointerEvents: getBool("show_stats_counter") ? "auto" : "none",
          transition: "opacity 0.2s",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          marginTop: 4,
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: ".1em",
            textTransform: "uppercase", color: colors.fgSub, marginBottom: 2,
          }}>
            Override values (leave empty to use real DB counts)
          </div>

          <OverrideNumberRow
            label="👥 Users count override"
            settingKey="stats_users_override"
            value={getString("stats_users_override")}
            onSave={save}
            loading={saving["stats_users_override"]}
            colors={colors}
          />
          <OverrideNumberRow
            label="🤝 Connections count override"
            settingKey="stats_matches_override"
            value={getString("stats_matches_override")}
            onSave={save}
            loading={saving["stats_matches_override"]}
            colors={colors}
          />
          <OverrideNumberRow
            label="🎉 Events count override"
            settingKey="stats_events_override"
            value={getString("stats_events_override")}
            onSave={save}
            loading={saving["stats_events_override"]}
            colors={colors}
          />
        </div>

        {/* Live preview hint */}
        {getBool("show_stats_counter") && (
          <div style={{
            marginTop: 16,
            padding: "12px 16px",
            background: colors.greenDim,
            border: `1px solid ${colors.green}30`,
            borderRadius: 10,
            fontSize: 12,
            color: colors.green,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}>
            <span style={{ fontSize: 14 }}>✓</span>
            Stats counter is <strong>visible</strong> on the landing page.
            {(getString("stats_users_override") || getString("stats_matches_override") || getString("stats_events_override"))
              && " Some values are manually overridden."}
          </div>
        )}
        {!getBool("show_stats_counter") && (
          <div style={{
            marginTop: 16,
            padding: "12px 16px",
            background: colors.orangeDim,
            border: `1px solid ${colors.orange}30`,
            borderRadius: 10,
            fontSize: 12,
            color: colors.orange,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}>
            <span style={{ fontSize: 14 }}>○</span>
            Stats counter is <strong>hidden</strong> on the landing page.
          </div>
        )}

      </SettingsSection>

    </div>
  );
}

// ─── Sub-components for TabSettings ────────────────────────────────────────

function SettingsSection({ title, description, children, colors }) {
  return (
    <div style={{
      background: colors.card,
      border: `1px solid ${colors.border}`,
      borderRadius: 16,
      overflow: "hidden",
    }}>
      <div style={{
        padding: "18px 24px 14px",
        borderBottom: `1px solid ${colors.border}`,
        background: colors.cardMid,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.fg }}>{title}</div>
        {description && (
          <div style={{ fontSize: 12, color: colors.fgSub, marginTop: 3, lineHeight: 1.5 }}>{description}</div>
        )}
      </div>
      <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {children}
      </div>
    </div>
  );
}

function SettingsRow({ label, hint, children, colors }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 24,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: colors.fg }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: colors.fgSub, marginTop: 2, lineHeight: 1.45 }}>{hint}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function ToggleSwitch({ value, onChange, loading, colors }) {
  return (
    <button
      onClick={() => !loading && onChange(!value)}
      disabled={loading}
      style={{
        position: "relative",
        width: 44,
        height: 24,
        borderRadius: 12,
        border: "none",
        cursor: loading ? "not-allowed" : "pointer",
        background: value
          ? `linear-gradient(135deg, ${colors.green}, ${colors.green}cc)`
          : colors.fgFaint,
        transition: "background 0.2s",
        padding: 0,
        flexShrink: 0,
      }}
    >
      <div style={{
        position: "absolute",
        top: 3,
        left: value ? 23 : 3,
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        transition: "left 0.18s cubic-bezier(0.4,0,0.2,1)",
      }} />
      {loading && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: 12,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Spinner size={12} />
        </div>
      )}
    </button>
  );
}

function OverrideNumberRow({ label, settingKey, value, onSave, loading, colors }) {
  const [draft, setDraft] = useState(value);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setDraft(value); setDirty(false); }, [value]);

  const handleChange = (v) => {
    // Only allow digits
    const clean = v.replace(/\D/g, "");
    setDraft(clean);
    setDirty(clean !== value);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ fontSize: 12, color: colors.fgMid, minWidth: 200 }}>{label}</div>
      <input
        className="st-input"
        style={{ width: 120, textAlign: "right" }}
        placeholder="auto"
        value={draft}
        onChange={e => handleChange(e.target.value)}
        onKeyDown={e => e.key === "Enter" && dirty && onSave(settingKey, draft)}
      />
      {dirty && (
        <Btn
          sm
          loading={loading}
          onClick={() => onSave(settingKey, draft)}
          style={{ minWidth: 56 }}
        >
          Save
        </Btn>
      )}
      {!dirty && value && (
        <Btn
          sm
          variant="ghost"
          onClick={() => { setDraft(""); onSave(settingKey, ""); }}
          style={{ fontSize: 10, minWidth: 52 }}
        >
          Clear
        </Btn>
      )}
    </div>
  );
}

// Sidebar навигация: иерархическая. Родительские пункты с `children`
// раскрываются по клику. URL по-прежнему хранит конкретный tab.id
// (включая id ребёнка) — раскрытие лишь визуальное.
const TABS = [
  { id: "stats",       label: "Overview" },
  { id: "users",       label: "Users", children: [
    { id: "cards",   label: "Cards"   },
    { id: "chats",   label: "Chats"   },
    { id: "matches", label: "Matches" },
    { id: "wallets", label: "Wallets" },
  ]},
  { id: "payments",    label: "Payments" },
  { id: "user-tree",   label: "Tree" },
  { id: "events-all",  label: "Events", children: [
    { id: "tables-live",    label: "Tables Live"    },
    { id: "event-comments", label: "Event Comments" },
  ]},
  { id: "safety",      label: "Safety"   },
  { id: "feedback",    label: "Feedback" },
  { id: "database",    label: "Database" },
  { id: "access-logs", label: "Audit Logs" },
  { id: "settings",    label: "Settings" },
];

// Плоский список для валидации URL, find-by-id и Ctrl+цифра-навигации.
const FLAT_TABS = TABS.flatMap(t => t.children ? [t, ...t.children] : [t]);

// Карта ребёнок → родитель (для auto-expand родителя при активном ребёнке).
const PARENT_OF = (() => {
  const m = {};
  TABS.forEach(t => (t.children || []).forEach(c => { m[c.id] = t.id; }));
  return m;
})();

// ─── SVG ICON COMPONENTS ─────────────────────────────────────────────────────
const Ic = ({ size = 16, strokeWidth = 1.75, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round"
    style={{ display:"block", flexShrink:0 }}>
    {children}
  </svg>
);
const IcStats    = p => <Ic {...p}><rect x="2" y="3" width="4" height="18" rx="1"/><rect x="9" y="8" width="4" height="13" rx="1"/><rect x="16" y="12" width="4" height="9" rx="1"/></Ic>;
const IcUsers    = p => <Ic {...p}><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.87"/></Ic>;
const IcTree     = p => <Ic {...p}><circle cx="12" cy="5" r="2"/><circle cx="5" cy="19" r="2"/><circle cx="19" cy="19" r="2"/><line x1="12" y1="7" x2="5" y2="17"/><line x1="12" y1="7" x2="19" y2="17"/></Ic>;
const IcMatches  = p => <Ic {...p}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></Ic>;
const IcChats    = p => <Ic {...p}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></Ic>;
const IcEvents   = p => <Ic {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Ic>;
const IcSafety   = p => <Ic {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Ic>;
const IcFeedback = p => <Ic {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></Ic>;
const IcContent  = p => <Ic {...p}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/><polyline points="9 11 12 14 15 11"/></Ic>;
const IcDatabase = p => <Ic {...p}><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></Ic>;
const IcLogs     = p => <Ic {...p}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ic>;
const IcTable    = p => <Ic {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="9" x2="9" y2="21"/></Ic>;
const IcWallet   = p => <Ic {...p}><path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-1"/><path d="M16 12h5v4h-5a2 2 0 0 1 0-4z"/></Ic>;
const IcMore     = p => <Ic {...p}><circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none"/></Ic>;
const IcSettings = p => (<Ic {...p}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></Ic>);

// Cards icon
const IcCards = ({ size = 16, strokeWidth = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="M7 8h10M7 12h6"/>
  </svg>
);

// Helper: render TAB icon with explicit size
const TabIcon = ({ id, size = 16 }) => {
  const map = {
    "stats": IcStats, "users": IcUsers, "user-tree": IcTree,
    "matches": IcMatches, "chats": IcChats, "events-all": IcEvents,
    "tables-live": IcTable, "wallets": IcWallet, "payments": IcWallet,
    "safety": IcSafety, "feedback": IcFeedback, "content": IcContent, "database": IcDatabase,
    "access-logs": IcLogs,
    "settings": IcSettings,
    "cards": IcCards,
    "event-comments": IcContent,
  };
  const C = map[id];
  return C ? <C size={size} strokeWidth={1.75} /> : null;
};

// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
//  useStickyState — sessionStorage-персистентное состояние
//  Использование: const [v, setV] = useStickyState("key", defaultVal)
// ════════════════════════════════════════════════════════════════════════════
function useStickyState(key, defaultValue) {
  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem("st_sticky_" + key);
      return raw !== null ? JSON.parse(raw) : defaultValue;
    } catch { return defaultValue; }
  });
  const set = useCallback(val => {
    setState(prev => {
      const next = typeof val === "function" ? val(prev) : val;
      try { sessionStorage.setItem("st_sticky_" + key, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [key]);
  return [state, set];
}


// ════════════════════════════════════════════════════════════════════════════
//  CopyBtn — кнопка копирования текста в буфер
// ════════════════════════════════════════════════════════════════════════════
function CopyBtn({ text, style = {} }) {
  const { colors } = useTheme();
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  function doCopy(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(String(text)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  }
  return (
    <button
      onClick={doCopy}
      title={copied ? "Copied!" : `Copy: ${text}`}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 18, height: 18, borderRadius: 4, border: "none",
        background: "none", cursor: "pointer", flexShrink: 0,
        color: copied ? colors.green : colors.fgFaint,
        transition: "color .15s, transform .1s",
        transform: copied ? "scale(1.2)" : "scale(1)",
        verticalAlign: "middle", marginLeft: 3,
        ...style,
      }}
    >
      {copied
        ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
      }
    </button>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  DiffViewer — side-by-side diff для old_data / new_data из Audit Logs
// ════════════════════════════════════════════════════════════════════════════
function DiffViewer({ oldData, newData, onClose }) {
  const { colors, theme } = useTheme();
  const isDark = theme === "dark";

  function parseJSON(v) {
    if (!v) return {};
    if (typeof v === "object") return v;
    try { return JSON.parse(v); } catch { return { raw: String(v) }; }
  }

  const old = parseJSON(oldData);
  const nw  = parseJSON(newData);
  const allKeys = Array.from(new Set([...Object.keys(old), ...Object.keys(nw)]));

  const changes = allKeys.map(k => {
    const ov = old[k] !== undefined ? String(old[k]) : undefined;
    const nv = nw[k]  !== undefined ? String(nw[k])  : undefined;
    const changed = ov !== nv;
    return { key: k, old: ov, new: nv, changed };
  }).filter(c => c.changed || (c.old !== undefined && c.new !== undefined));

  if (!changes.length) return null;

  return (
    <WindowModal
      windowId="audit_diff"
      title="Changes"
      onClose={onClose}
      width={760}
      height={560}
      minWidth={560}
      bodyPadding={0}
    >
      <div style={{
        position: "relative", width: "min(720px, 96vw)",
        background: colors.card, border: `1px solid ${colors.borderMid}`,
        borderRadius: 16, overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,.45)",
        animation: "stSlideIn .2s ease both",
        maxHeight: "80vh", display: "flex", flexDirection: "column",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: colors.fg }}>
            Changes
          </span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer",
            color: colors.fgSub, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: "auto", padding: "12px 0" }}>
          {/* column headers */}
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 1fr",
            gap: 0, padding: "4px 20px 8px", borderBottom: `1px solid ${colors.border}` }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".1em",
              textTransform: "uppercase", color: colors.fgSub }}>Field</span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".1em",
              textTransform: "uppercase", color: colors.red, paddingLeft: 8 }}>Before</span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".1em",
              textTransform: "uppercase", color: colors.green, paddingLeft: 8 }}>After</span>
          </div>
          {changes.map(({ key, old: ov, new: nv, changed }) => (
            <div key={key} style={{
              display: "grid", gridTemplateColumns: "160px 1fr 1fr",
              gap: 0, padding: "6px 20px",
              background: changed ? (isDark ? "rgba(180,100,180,0.04)" : "rgba(56,25,49,0.025)") : "transparent",
              borderBottom: `1px solid ${colors.border}22`,
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: colors.fgSub,
                fontFamily: "'JetBrains Mono', monospace", paddingRight: 8,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{key}</span>
              <div style={{
                padding: "2px 8px", borderRadius: 4, fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                background: ov !== undefined && changed ? colors.redDim : "transparent",
                color: ov !== undefined ? (changed ? colors.red : colors.fgMid) : colors.fgFaint,
                wordBreak: "break-word",
              }}>
                {ov !== undefined ? ov : <span style={{ fontStyle: "italic" }}>—</span>}
                {ov !== undefined && <CopyBtn text={ov} />}
              </div>
              <div style={{
                padding: "2px 8px", borderRadius: 4, fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                background: nv !== undefined && changed ? colors.greenDim : "transparent",
                color: nv !== undefined ? (changed ? colors.green : colors.fgMid) : colors.fgFaint,
                wordBreak: "break-word",
              }}>
                {nv !== undefined ? nv : <span style={{ fontStyle: "italic" }}>—</span>}
                {nv !== undefined && <CopyBtn text={nv} />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </WindowModal>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  FeedbackBadge — бейдж с числом непрочитанных из /mod/stats
// ════════════════════════════════════════════════════════════════════════════
const FeedbackBadgeCtx = React.createContext({ count: 0, refresh: () => {} });

function FeedbackBadgeProvider({ token, children }) {
  const [count, setCount] = useState(0);
  const refresh = useCallback(() => {
    api("/stats", { token })
      .then(d => setCount(d?.moderation?.feedback_new || 0))
      .catch(() => {});
  }, [token]);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 90000);
    return () => clearInterval(id);
  }, [refresh]);
  return (
    <FeedbackBadgeCtx.Provider value={{ count, refresh }}>
      {children}
    </FeedbackBadgeCtx.Provider>
  );
}
const useFeedbackBadge = () => useContext(FeedbackBadgeCtx);

function FeedbackBadgeDot({ collapsed }) {
  const { colors } = useTheme();
  const { count } = useFeedbackBadge();
  if (!count) return null;
  return (
    <span style={{
      minWidth: 16, height: 16, borderRadius: 99,
      background: colors.orange, color: "#fff",
      fontSize: 9, fontWeight: 800, lineHeight: "16px",
      textAlign: "center", padding: "0 4px",
      position: collapsed ? "absolute" : "static",
      top: collapsed ? 6 : undefined,
      right: collapsed ? 6 : undefined,
      marginLeft: collapsed ? 0 : "auto",
      flexShrink: 0,
    }}>{count > 99 ? "99+" : count}</span>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  NAV ITEMS — иерархическое левое меню
//  • Пункты с `children` раскрываются по клику на chevron, либо
//    автоматически если активный таб — этот родитель или один из его детей.
//  • Клик по самому родителю всегда переходит на его экран (HUB).
//  • В collapsed-режиме (узкая колонка) подпункты не показываем — нет места;
//    клик по иконке родителя ведёт на его HUB, оттуда можно перейти по
//    внутренним pill-табам.
// ════════════════════════════════════════════════════════════════════════════
function NavItems({ tab, setTab, colors, neuInset, sidebarCollapsed }) {
  // Сохраняем какие родители раскрыты вручную (вне зависимости от активного таба).
  const [openParents, setOpenParents] = useStickyState("sidebar_open_parents", { users: true, "events-all": true });

  function isExpanded(parent) {
    if (!parent.children) return false;
    if (parent.id === tab) return true;
    if (parent.children.some(c => c.id === tab)) return true;
    return !!openParents[parent.id];
  }

  function toggleOpen(id) {
    setOpenParents(p => ({ ...p, [id]: !p[id] }));
  }

  const Chevron = ({ open }) => (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"
         style={{ transition: "transform .15s", transform: open ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0, opacity: 0.65 }}>
      <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  function renderRow(t, { isChild = false } = {}) {
    const isActive  = tab === t.id;
    const isPending = t.id === "pending-events";
    const isFeedback = t.id === "feedback";
    const hasKids   = !!t.children?.length;
    const expanded  = isExpanded(t);

    return (
      <button
        key={t.id}
        onClick={() => {
          setTab(t.id);
          if (hasKids && !sidebarCollapsed) {
            // Гарантируем что после перехода на родителя его дети видны
            setOpenParents(p => ({ ...p, [t.id]: true }));
          }
        }}
        title={sidebarCollapsed ? t.label : undefined}
        style={{
          display: "flex", alignItems: "center",
          gap: sidebarCollapsed ? 0 : 9,
          justifyContent: sidebarCollapsed ? "center" : "flex-start",
          padding: sidebarCollapsed ? "11px 0" : "10px 12px",
          paddingLeft: !sidebarCollapsed && isChild ? 30 : undefined,
          borderRadius: 13, border: "none",
          cursor: "pointer", fontFamily: "inherit",
          fontSize: isChild ? 12 : 12.5,
          fontWeight: isActive ? 700 : 500,
          background: isActive ? colors.primaryDim : "transparent",
          boxShadow: isActive ? neuInset : "none",
          color: isActive ? colors.accent
            : isPending ? colors.orange
            : isChild ? colors.fgSub
            : colors.fgMid,
          transition: "all .15s",
          whiteSpace: "nowrap", overflow: "hidden",
          position: "relative",
        }}
      >
        <span style={{
          width: 18, height: 18, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: isActive ? colors.primary : isPending ? colors.orange : colors.fgSub,
        }}>
          <TabIcon id={t.id} size={isChild ? 14 : 16} />
        </span>
        {!sidebarCollapsed && (
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
            {t.label}
          </span>
        )}
        {/* chevron для родительских пунктов (только в expanded-режиме сайдбара) */}
        {hasKids && !sidebarCollapsed && (
          <span
            role="button"
            tabIndex={0}
            onClick={e => { e.stopPropagation(); toggleOpen(t.id); }}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault(); e.stopPropagation(); toggleOpen(t.id);
              }
            }}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 18, height: 18, borderRadius: 6, cursor: "pointer",
              color: isActive ? colors.accent : colors.fgSub,
            }}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <Chevron open={expanded} />
          </span>
        )}
        {isFeedback && <FeedbackBadgeDot collapsed={sidebarCollapsed} />}
        {isPending && (
          <span style={{
            position: sidebarCollapsed ? "absolute" : "static",
            top: sidebarCollapsed ? 7 : undefined,
            right: sidebarCollapsed ? 9 : undefined,
            marginLeft: sidebarCollapsed ? 0 : "auto",
            width: 7, height: 7, borderRadius: "50%",
            background: colors.orange, flexShrink: 0,
          }} />
        )}
      </button>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {TABS.map(t => {
        const expanded = isExpanded(t);
        return (
          <Fragment key={t.id}>
            {renderRow(t)}
            {/* подпункты — только в развернутом сайдбаре и при открытом родителе */}
            {t.children && !sidebarCollapsed && expanded && (
              <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 1, marginBottom: 4 }}>
                {t.children.map(c => renderRow(c, { isChild: true }))}
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}


//  SKELETON LOADER
// ════════════════════════════════════════════════════════════════════════════
function Skeleton({ width = "100%", height = 16, radius = 6, style = {} }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: isDark
        ? "linear-gradient(90deg, #1a1820 25%, #241f2e 50%, #1a1820 75%)"
        : "linear-gradient(90deg, #f0e8e0 25%, #e8dcd4 50%, #f0e8e0 75%)",
      backgroundSize: "200% 100%",
      animation: "skeletonShimmer 1.4s infinite",
      flexShrink: 0,
      ...style,
    }} />
  );
}

function SkeletonRow({ cols = 4 }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} style={{ padding: "12px 16px" }}>
          <Skeleton height={14} radius={4} width={i === 0 ? "60%" : i === cols - 1 ? "40%" : "80%"} />
        </td>
      ))}
    </tr>
  );
}

function SkeletonCard({ lines = 3 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "16px 20px" }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={i === 0 ? 18 : 13} width={i === 0 ? "50%" : i % 2 === 0 ? "90%" : "70%"} />
      ))}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  CONFIRM DIALOG — двойное подтверждение опасных действий
// ════════════════════════════════════════════════════════════════════════════
const ConfirmCtx = React.createContext({ confirm: () => Promise.resolve(false) });
function ConfirmProvider({ children }) {
  const { colors } = useTheme();
  const [state, setState] = useState(null); // { title, message, danger, resolve }

  const confirm = useCallback((title, message, { danger = true } = {}) => {
    return new Promise(resolve => {
      setState({ title, message, danger, resolve });
    });
  }, []);

  function handle(yes) {
    if (state?.resolve) state.resolve(yes);
    setState(null);
  }

  return (
    <ConfirmCtx.Provider value={{ confirm }}>
      {children}
      {state && (
        <WindowModal
          windowId={_safeWindowId("confirm_provider", state.title)}
          title={state.title || "Confirm"}
          onClose={() => handle(false)}
          width={420}
          height={260}
          minHeight={220}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              {state.danger && (
                <span style={{
                  width: 36, height: 36, borderRadius: 10, background: colors.redDim,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </span>
              )}
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: colors.fg }}>{state.title}</div>
              </div>
            </div>
            {state.message && (
              <div style={{ fontSize: 13, color: colors.fgMid, marginBottom: 20, lineHeight: 1.5 }}>
                {state.message}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="st-btn st-btn-ghost" onClick={() => handle(false)}>Cancel</button>
              <button
                className={`st-btn ${state.danger ? "st-btn-danger" : "st-btn-primary"}`}
                onClick={() => handle(true)}
              >
                Confirm
              </button>
            </div>
        </WindowModal>
      )}
    </ConfirmCtx.Provider>
  );
}
const useConfirm = () => useContext(ConfirmCtx).confirm;


// ════════════════════════════════════════════════════════════════════════════
//  GLOBAL SEARCH — Cmd/Ctrl+K
// ════════════════════════════════════════════════════════════════════════════
function GlobalSearch({ token, onSelectUser, onClose }) {
  const { colors } = useTheme();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api(`/users/simple?search=${encodeURIComponent(q.trim())}`, { token });
        setResults(data.slice(0, 8));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(t);
  }, [q, token]);

  function pick(user) {
    onSelectUser(user.id);
    onClose();
  }

  const roleColors = { admin: colors.red, user: colors.fgMid };

  return (
    <WindowModal
      windowId="global_search"
      title="Search users"
      onClose={onClose}
      width={600}
      height={460}
      bodyPadding={0}
    >
      <div style={{
        position: "relative", width: "min(560px, 94vw)",
        background: colors.card, border: `1px solid ${colors.borderMid}`,
        borderRadius: 16, overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,.45)",
        animation: "stSlideIn .18s ease both",
      }}>
        {/* Search input */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: `1px solid ${colors.border}` }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.fgSub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === "Escape") onClose(); }}
            placeholder="Search users — name, email, username or #id..."
            style={{
              flex: 1, background: "none", border: "none", outline: "none",
              fontSize: 15, color: colors.fg, fontFamily: "inherit",
            }}
          />
          {loading && <div className="st-spin" style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${colors.border}`, borderTopColor: colors.primary }} />}
          <kbd style={{ fontSize: 10, color: colors.fgSub, border: `1px solid ${colors.border}`, borderRadius: 4, padding: "2px 5px" }}>ESC</kbd>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div style={{ maxHeight: 340, overflowY: "auto" }}>
            {results.map(u => (
              <button
                key={u.id}
                onClick={() => pick(u)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "11px 18px", border: "none", background: "none",
                  cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                  borderBottom: `1px solid ${colors.border}`,
                  transition: "background .1s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = colors.cardHover}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                  background: colors.primaryDim, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700, color: colors.primary,
                }}>
                  {(u.name || u.username || "?")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.name || u.username || `#${u.id}`}
                  </div>
                  <div style={{ fontSize: 11, color: colors.fgSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.email} {u.username ? `· @${u.username}` : ""}
                  </div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                  color: roleColors[u.role] || colors.fgSub,
                }}>
                  {u.role}
                </span>
              </button>
            ))}
          </div>
        )}

        {q.trim() && !loading && results.length === 0 && (
          <div style={{ padding: "24px 18px", textAlign: "center", fontSize: 13, color: colors.fgSub }}>
            No results for «{q}»
          </div>
        )}

        {!q.trim() && (
          <div style={{ padding: "16px 18px", fontSize: 12, color: colors.fgSub }}>
            Enter name, email, @username or #id
          </div>
        )}
      </div>
    </WindowModal>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  SESSION TIMER — предупреждение за 5 мин до истечения токена
// ════════════════════════════════════════════════════════════════════════════
function SessionTimer({ onLogout }) {
  const { colors } = useTheme();
  const [remaining, setRemaining] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Читаем exp из JWT (без верификации — только для UI)
    const token = sessionStorage.getItem("studio_token") || "";
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const exp = payload.exp;
      if (!exp) return;

      const tick = () => {
        const left = exp - Math.floor(Date.now() / 1000);
        setRemaining(left);
      };
      tick();
      const id = setInterval(tick, 10000);
      return () => clearInterval(id);
    } catch { /* ignore */ }
  }, []);

  // Показываем только если < 5 мин и не скрыто
  if (remaining === null || remaining > 300 || dismissed) return null;
  if (remaining <= 0) {
    return (
      <div style={{
        position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
        zIndex: 490, background: colors.redDim, border: `1px solid ${colors.red}`,
        borderRadius: 12, padding: "12px 20px", display: "flex", alignItems: "center", gap: 10,
        boxShadow: "0 8px 32px rgba(0,0,0,.3)", fontSize: 13, color: colors.red, fontWeight: 600,
      }}>
        Session expired.
        <button className="st-btn st-btn-danger st-btn-sm" onClick={onLogout}>Log in again</button>
      </div>
    );
  }

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  return (
    <div style={{
      position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
      zIndex: 490, background: colors.orangeDim, border: `1px solid ${colors.orange}44`,
      borderRadius: 12, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10,
      boxShadow: "0 8px 32px rgba(0,0,0,.2)", fontSize: 12, color: colors.orange, fontWeight: 600,
      animation: "stFade .3s ease",
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
      Session expires in {mins}:{String(secs).padStart(2, "0")}
      <button
        onClick={setDismissed.bind(null, true)}
        style={{ background: "none", border: "none", cursor: "pointer", color: colors.orange, fontSize: 16, lineHeight: 1 }}
      >×</button>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  NotificationBell — звонок с числом непрочитанных уведомлений в хедере
//  Тянет /mod/notifications/unread каждые 60с. На клик открывает дропдаун со
//  списком непрочитанных фидбеков и новых заявок на ивенты. Бейдж не уходит,
//  пока модер не откроет дропдаун или не нажмёт "Mark all seen".
// ════════════════════════════════════════════════════════════════════════════
function NotificationBell({ token, onNavigateTab }) {
  const { colors, theme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const [data, setData]   = useState(null);
  const [open, setOpen]   = useState(false);
  const [pos, setPos]     = useState(null); // {top, right} в координатах viewport
  const btnRef  = useRef(null);
  const popRef  = useRef(null);

  const refresh = useCallback(() => {
    api("/notifications/unread", { token })
      .then(setData)
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  // Пересчитываем позицию дропдауна относительно кнопки.
  // Позиционируем через position:fixed + top/right в координатах viewport,
  // чтобы дропдаун не клипался overflow родителя (header / sticky / rounded card).
  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({
      top:   Math.round(r.bottom + 8),                  // 8px gap под кнопкой
      right: Math.max(8, Math.round(window.innerWidth - r.right)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const onWin = () => updatePos();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true); // capture: ловим скролл любых предков
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, updatePos]);

  // Закрываем дропдаун по клику вне него
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBtn && !inPop) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const total    = data?.total ?? 0;
  const fbItems  = data?.feedback?.items ?? [];
  const peItems  = data?.pending_events?.items ?? [];
  const fbTotal  = data?.feedback?.total ?? 0;
  const peTotal  = data?.pending_events?.total ?? 0;

  const markAll = async () => {
    try { await api("/notifications/mark-seen", { method: "POST", token, body: { kind: "all" } }); }
    catch {}
    refresh();
  };

  const goTo = (tab) => {
    setOpen(false);
    if (onNavigateTab) onNavigateTab(tab);
  };

  // Сам дропдаун — рендерим в document.body через portal,
  // чтобы он не обрезался overflow родителей (header / sticky-карточка).
  const dropdownNode = open && pos ? ReactDOM.createPortal(
    <>
      {/* Прозрачный backdrop поверх всего, кроме самого попапа.
          На мобиле — слегка затемнённый, чтобы было понятно что это модалка. */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 5000,
          background: isMobile ? "rgba(5,5,14,.35)" : "transparent",
        }}
      />
      <div
        ref={popRef}
        style={{
          position: "fixed",
          top:   isMobile ? "auto" : pos.top,
          right: isMobile ? 12   : pos.right,
          left:  isMobile ? 12   : "auto",
          bottom:isMobile ? 12   : "auto",
          width: isMobile ? "auto" : 360,
          maxWidth:  "calc(100vw - 24px)",
          maxHeight: isMobile ? "70vh" : "calc(100vh - 100px)",
          background: colors.card,
          border: `1px solid ${colors.borderMid}`,
          borderRadius: 12,
          boxShadow: "0 16px 48px rgba(0,0,0,.32)",
          zIndex: 5001,
          overflow: "hidden",
          display: "flex", flexDirection: "column",
          animation: "stFade .14s ease both",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px", borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.fg }}>
            Notifications {total > 0 && (
              <span style={{ color: colors.fgSub, fontWeight: 500 }}>· {total}</span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {total > 0 && (
              <button onClick={markAll} style={{
                background: "none", border: "none", color: colors.primary,
                fontSize: 11, fontWeight: 600, cursor: "pointer", padding: 0,
              }}>Mark all seen</button>
            )}
            <button onClick={() => setOpen(false)} style={{
              background: "none", border: "none", color: colors.fgSub,
              fontSize: 16, cursor: "pointer", padding: "0 2px", lineHeight: 1,
            }}>✕</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {total === 0 && (
            <div style={{ padding: "32px 20px", textAlign: "center", color: colors.fgSub, fontSize: 12 }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>✓</div>
              All caught up
            </div>
          )}

          {/* Feedback group */}
          {fbItems.length > 0 && (
            <div>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 14px", background: colors.cardMid,
                borderBottom: `1px solid ${colors.border}`,
                position: "sticky", top: 0, zIndex: 1,
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: colors.red,
                  letterSpacing: ".06em", textTransform: "uppercase" }}>
                  📬 Feedback · {fbTotal}
                </span>
                <button onClick={() => goTo("feedback")} style={{
                  background: "none", border: "none", color: colors.fgSub,
                  fontSize: 10, cursor: "pointer", padding: 0,
                }}>open tab →</button>
              </div>
              {fbItems.map(f => (
                <button key={`fb-${f.id}`} onClick={() => goTo("feedback")} style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "10px 14px", background: "none", border: "none",
                  cursor: "pointer", color: colors.fg,
                  borderBottom: `1px solid ${colors.border}`,
                }}
                onMouseEnter={e => e.currentTarget.style.background = colors.cardMid}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.name || f.email}
                    </span>
                    <span style={{ fontSize: 10, color: colors.fgFaint, flexShrink: 0 }}>
                      {f.at ? new Date(f.at).toLocaleString("en", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      }) : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: colors.fgSub, lineHeight: 1.4,
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                    overflow: "hidden" }}>
                    {f.snippet || "(empty)"}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Pending events group */}
          {peItems.length > 0 && (
            <div>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 14px", background: colors.cardMid,
                borderBottom: `1px solid ${colors.border}`,
                position: "sticky", top: 0, zIndex: 1,
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: colors.orange,
                  letterSpacing: ".06em", textTransform: "uppercase" }}>
                  🗓 New Events · {peTotal}
                </span>
                <button onClick={() => goTo("events-all")} style={{
                  background: "none", border: "none", color: colors.fgSub,
                  fontSize: 10, cursor: "pointer", padding: 0,
                }}>open tab →</button>
              </div>
              {peItems.map(e => (
                <button key={`pe-${e.id}`} onClick={() => goTo("events-all")} style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "10px 14px", background: "none", border: "none",
                  cursor: "pointer", color: colors.fg,
                  borderBottom: `1px solid ${colors.border}`,
                }}
                onMouseEnter={ev => ev.currentTarget.style.background = colors.cardMid}
                onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.title}
                    </span>
                    <span style={{ fontSize: 10, color: colors.fgFaint, flexShrink: 0 }}>
                      {e.at ? new Date(e.at).toLocaleString("en", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      }) : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: colors.fgSub }}>
                    {e.organizer || "—"}
                    {e.event_date && <span> · {new Date(e.event_date).toLocaleDateString("en")}</span>}
                    {e.location && <span> · {e.location}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  ) : null;

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        title={total > 0 ? `${total} new notifications` : "No notifications"}
        style={{
          width: 32, height: 32, borderRadius: 9,
          border: `1px solid ${total > 0 ? colors.orange + "80" : colors.border}`,
          background: isDark ? colors.bgDeep : colors.bg,
          cursor: "pointer", color: total > 0 ? colors.orange : colors.fgSub,
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", transition: "all .15s",
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {total > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4, minWidth: 16, height: 16,
            borderRadius: 99, background: colors.orange, color: "#fff",
            fontSize: 9, fontWeight: 800, lineHeight: "16px",
            textAlign: "center", padding: "0 4px",
            boxShadow: `0 0 0 2px ${isDark ? colors.bgDeep : colors.bg}`,
          }}>
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>
      {dropdownNode}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  HEADER STATS BAR — мини-метрики в хедере (читает /mod/stats)
// ════════════════════════════════════════════════════════════════════════════
function HeaderStats({ token }) {
  const { colors } = useTheme();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api("/stats", { token }).then(setStats).catch(() => {});
    const id = setInterval(() => {
      api("/stats", { token }).then(setStats).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [token]);

  if (!stats) return null;

  const items = [
    { label: "Online", value: stats.users?.new_today ?? "—", color: colors.green },
    { label: "Feedback", value: stats.moderation?.feedback_new ?? "—", color: stats.moderation?.feedback_new > 0 ? colors.orange : colors.fgSub },
    { label: "Bans", value: stats.moderation?.total_bans ?? "—", color: colors.red },
  ];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
      {items.map(it => (
        <div key={it.label} style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          padding: "0 10px", borderRight: `1px solid ${colors.border}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: it.color, lineHeight: 1 }}>{it.value}</span>
          <span style={{ fontSize: 9, color: colors.fgSub, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 2 }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
//  TabTablesLive — живое состояние столов (SSE — бэк пушит каждую секунду)
// ════════════════════════════════════════════════════════════════════════════
function TabTablesLive({ token }) {
  const { colors, theme } = useTheme();
  const isDark = theme === "dark";

  const [data, setData]         = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError]       = useState(null);
  const [filterEventId, setFilterEventId] = useState("");
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [clearing, setClearing]   = useState(null);
  const esRef = useRef(null);

  // SSE-соединение — открывается при монтировании, переоткрывается при смене фильтра
  const connect = useCallback(() => {
    if (esRef.current) esRef.current.close();
    setError(null);
    setConnected(false);

    const qs = new URLSearchParams({ token });
    if (filterEventId) qs.set("event_id", filterEventId);
    const url = `${API_URL}/api/mod/tables/live/stream?${qs}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => setConnected(true);

    es.onmessage = (e) => {
      try {
        const parsed = JSON.parse(e.data);
        // Обновляем data без сброса selectedEventId → нет ремаунта
        setData(parsed);
        setConnected(true);
      } catch {}
    };

    es.onerror = () => {
      setConnected(false);
      setError("Connection lost. Reconnecting…");
      // EventSource автоматически переподключается, просто показываем статус
    };
  }, [token, filterEventId]);

  useEffect(() => {
    connect();
    return () => { if (esRef.current) esRef.current.close(); };
  }, [connect]);

  const handleRelease = async (slotId) => {
    setReleasing(slotId);
    try {
      await api(`/tables/slots/${slotId}`, { method: "DELETE", token });
    } catch {}
    setReleasing(null);
  };

  const [extending, setExtending] = useState(null); // slot_id
  const [restarting, setRestarting] = useState(null); // match_id

  const handleExtend = async (slotId, minutes) => {
    setExtending(slotId);
    try {
      await api(`/tables/slots/${slotId}/extend?minutes=${minutes}`, { method: "POST", token });
    } catch {}
    setExtending(null);
  };

  const handleRestart = async (eventId, matchId) => {
    setRestarting(matchId);
    try {
      await api(`/tables/events/${eventId}/matches/${matchId}/restart`, { method: "POST", token });
    } catch {}
    setRestarting(null);
  };

  const handleClear = async (eventId) => {
    setClearing(eventId);
    try {
      await api(`/tables/events/${eventId}/clear`, { method: "DELETE", token });
    } catch {}
    setClearing(null);
  };

  const handleNextRound = async (eventId) => {
    setClearing(eventId); // переиспользуем состояние loading
    try {
      await api(`/tables/events/${eventId}/next-round`, { method: "POST", token });
    } catch {}
    setClearing(null);
  };

  // ── Format seconds ──
  const fmtSec = (s) => {
    if (s <= 0) return "0:00";
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  // ── Timer bar — перерисовывается только при изменении secondsLeft ──
  const TimerBar = React.memo(({ secondsLeft, totalSeconds }) => {
    const pct = Math.min(100, Math.max(0, (secondsLeft / Math.max(1, totalSeconds)) * 100));
    const color = pct < 20 ? colors.red : pct < 50 ? colors.orange : colors.green;
    return (
      <div style={{ height: 5, borderRadius: 3, background: colors.bgDeep, overflow: "hidden", marginTop: 4 }}>
        <div style={{
          height: "100%", width: `${pct}%`, borderRadius: 3, background: color,
          // Нет transition — данные с бэка уже точные, моргания не будет
        }} />
      </div>
    );
  });

  // ── Slot card — memo по slot_id + seconds_left ──
  const SlotCard = React.memo(({ slot, timerSeatMin }) => {
    const totalSec = (timerSeatMin || 10) * 60;
    const urgent = slot.seconds_left < 60;
    return (
      <div style={{
        background: isDark ? colors.card : colors.cardMid,
        border: `1px solid ${urgent ? colors.orange + "60" : colors.border}`,
        borderRadius: 12, padding: "12px 14px",
        display: "flex", flexDirection: "column", gap: 6,
        position: "relative",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: colors.primaryDim, border: `1px solid ${colors.borderMid}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 800, color: colors.accent,
            }}>{slot.table_number}</div>
            <div>
              <div style={{ fontSize: 10, color: colors.fgSub }}>Table {slot.table_number} · Pair {slot.seat_pair}</div>
              <div style={{ fontSize: 11, color: colors.fgMid }}>Match #{slot.match_id}</div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{
              fontSize: 16, fontWeight: 800, lineHeight: 1, fontVariantNumeric: "tabular-nums",
              color: slot.seconds_left < 30 ? colors.red : urgent ? colors.orange : colors.green,
            }}>
              {fmtSec(slot.seconds_left)}
            </div>
            {slot.rotation_minutes && (
              <div style={{ fontSize: 9, color: colors.fgSub, marginTop: 2 }}>+{slot.rotation_minutes}m rot.</div>
            )}
          </div>
        </div>

        <TimerBar secondsLeft={slot.seconds_left} totalSeconds={totalSec} />

        <div style={{ display: "flex", gap: 6 }}>
          {[{ id: slot.user1_id, name: slot.user1_name, i: 0 }, { id: slot.user2_id, name: slot.user2_name, i: 1 }].map(u => (
            <div key={u.i} style={{
              flex: 1, background: colors.bgDeep, borderRadius: 7, padding: "5px 8px",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                background: u.i === 0 ? colors.primaryDim : colors.blueDim,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, fontWeight: 700, color: u.i === 0 ? colors.accent : colors.blue,
              }}>{(u.name || "?").charAt(0).toUpperCase()}</div>
              <span style={{ fontSize: 11, color: colors.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {u.name || `#${u.id}`}
              </span>
            </div>
          ))}
        </div>

        {/* + Time controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
          <span style={{ fontSize: 9, color: colors.fgSub, textTransform: "uppercase", letterSpacing: ".05em", marginRight: 2 }}>
            +time
          </span>
          {[1, 2, 5].map(m => (
            <button key={m} type="button"
              onClick={() => handleExtend(slot.slot_id, m)}
              disabled={extending === slot.slot_id}
              style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700,
                border: `1px solid ${colors.border}`,
                background: extending === slot.slot_id ? colors.primaryDim : "transparent",
                color: colors.fgMid, cursor: "pointer", transition: "all .15s",
                lineHeight: 1,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.color = colors.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.color = colors.fgMid; }}
            >
              {extending === slot.slot_id ? "…" : `+${m}m`}
            </button>
          ))}
          {/* Release button */}
          <button
            onClick={() => handleRelease(slot.slot_id)}
            disabled={releasing === slot.slot_id}
            style={{
              marginLeft: "auto", background: "none", border: "none",
              cursor: "pointer", color: colors.fgFaint, fontSize: 12, padding: "2px 5px",
              borderRadius: 5, lineHeight: 1,
            }}
            title="Release slot"
            onMouseEnter={e => e.currentTarget.style.color = colors.red}
            onMouseLeave={e => e.currentTarget.style.color = colors.fgFaint}
          >
            {releasing === slot.slot_id ? "…" : "✕"}
          </button>
        </div>
      </div>
    );
  }, (prev, next) =>
    prev.slot.slot_id === next.slot.slot_id &&
    prev.slot.seconds_left === next.slot.seconds_left &&
    prev.slot.status === next.slot.status
  );

  // ── Детальный вид ивента ──
  const selectedEvent = data?.events?.find(e => e.event_id === selectedEventId) ?? null;

  const EventDetail = ({ ev }) => {
    const fillPct = ev.total_capacity > 0
      ? Math.round((ev.occupied / ev.total_capacity) * 100) : 0;
    const byTable = {};
    for (let t = 1; t <= ev.number_of_tables; t++) byTable[t] = [];
    for (const slot of ev.slots) {
      if (!byTable[slot.table_number]) byTable[slot.table_number] = [];
      byTable[slot.table_number].push(slot);
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ background: isDark ? colors.card : colors.card, border: `1px solid ${colors.border}`, borderRadius: 14, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: connected ? colors.green : colors.orange, flexShrink: 0 }} />
                <div style={{ fontSize: 16, fontWeight: 700, color: colors.fg }}>{ev.event_title}</div>
              </div>
              <div style={{ fontSize: 11, color: colors.fgSub, marginTop: 3 }}>ID {ev.event_id} · {ev.event_date || "—"}</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn sm variant="ghost" onClick={() => setSelectedEventId(null)}>← Back</Btn>
              {ev.round_complete && (
                <Btn sm onClick={() => handleNextRound(ev.event_id)} disabled={clearing === ev.event_id}
                  style={{ background: colors.green, color: "#fff", border: "none", fontWeight: 700 }}>
                  {clearing === ev.event_id ? "…" : "▶ Next Round"}
                </Btn>
              )}
              <Btn sm variant="danger" disabled={clearing === ev.event_id} onClick={() => handleClear(ev.event_id)}>
                {clearing === ev.event_id ? "…" : "Clear All"}
              </Btn>
            </div>
          </div>

          <div style={{ display: "flex", gap: 0, marginTop: 14, borderRadius: 10, overflow: "hidden", border: `1px solid ${colors.border}` }}>
            {[
              { label: "At tables", value: ev.occupied,       color: colors.primary },
              { label: "Queue",     value: ev.queue_length,   color: colors.orange  },
              { label: "Done ✓",   value: ev.total_seated,   color: colors.green   },
              { label: "Waiting",   value: ev.unseated_count, color: colors.fgMid   },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", padding: "10px 6px", borderRight: i < 3 ? `1px solid ${colors.border}` : "none", background: isDark ? colors.bgDeep : colors.bg }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 9, color: colors.fgSub, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: colors.fgSub, marginBottom: 4 }}>
              <span>Capacity {ev.occupied}/{ev.total_capacity}</span>
              <span style={{ fontWeight: 700, color: colors.fg }}>{fillPct}%</span>
            </div>
            <div style={{ height: 5, borderRadius: 3, background: colors.bgDeep, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${fillPct}%`, background: fillPct > 80 ? colors.orange : colors.primary, borderRadius: 3 }} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 11, color: colors.fgMid }}>
            <span>⏱ Seat: <b style={{ color: colors.fg }}>{ev.table_timer_seat ?? "—"} min</b></span>
            <span>🔄 Rotation: <b style={{ color: colors.fg }}>{ev.table_timer_rotation ?? "—"} min</b></span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
          {Object.entries(byTable).map(([tNum, slots]) => (
            <div key={tNum} style={{
              background: slots.length === 0 ? (isDark ? colors.bgDeep : colors.bg) : (isDark ? colors.cardMid : colors.cardMid),
              border: `1px solid ${slots.length ? colors.borderMid : colors.border}`,
              borderRadius: 12, padding: "10px 12px", opacity: slots.length ? 1 : 0.5,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.fgSub, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                🪑 Table {tNum}
                {slots.length === 0 && <span style={{ color: colors.green, fontWeight: 600 }}>· Free</span>}
              </div>
              {slots.length === 0
                ? <div style={{ fontSize: 11, color: colors.fgFaint, textAlign: "center", padding: "10px 0" }}>Waiting for match</div>
                : slots.map(slot => <SlotCard key={slot.slot_id} slot={slot} timerSeatMin={ev.table_timer_seat} />)
              }
            </div>
          ))}
        </div>

        {/* Queue section */}
        {ev.queue && ev.queue.length > 0 && (
          <div style={{
            background: isDark ? colors.card : colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 14, overflow: "hidden",
          }}>
            <div style={{
              padding: "12px 18px",
              borderBottom: `1px solid ${colors.border}`,
              display: "flex", alignItems: "center", gap: 8,
              background: isDark ? colors.bgDeep : colors.bg,
            }}>
              <span style={{ fontSize: "1rem" }}>⏳</span>
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: colors.fg }}>
                Queue
              </div>
              <div style={{
                marginLeft: "auto",
                padding: "2px 10px", borderRadius: 20,
                background: colors.orangeDim, color: colors.orange,
                border: `1px solid ${colors.orange}40`,
                fontSize: 11, fontWeight: 700,
              }}>
                {ev.queue.length} waiting
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              {ev.queue.map((m, idx) => (
                <div key={m.match_id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 18px",
                  borderBottom: idx < ev.queue.length - 1 ? `1px solid ${colors.border}` : "none",
                  background: idx % 2 === 0 ? "transparent" : (isDark ? colors.bgDeep + "60" : colors.bg + "60"),
                }}>
                  {/* Position badge */}
                  <div style={{
                    width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                    background: idx === 0 ? colors.orangeDim : colors.primaryDim,
                    border: `1px solid ${idx === 0 ? colors.orange + "60" : colors.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 800,
                    color: idx === 0 ? colors.orange : colors.fgSub,
                  }}>
                    {m.position}
                  </div>

                  {/* Match ID */}
                  <div style={{ fontSize: 10, color: colors.fgSub, flexShrink: 0, minWidth: 56 }}>
                    Match #{m.match_id}
                  </div>

                  {/* Users */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                    {/* User 1 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                        background: colors.primaryDim,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9, fontWeight: 700, color: colors.accent,
                      }}>
                        {(m.user1_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 12, color: colors.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {m.user1_name}
                      </span>
                    </div>

                    <span style={{ color: colors.fgFaint, fontSize: 11, flexShrink: 0 }}>×</span>

                    {/* User 2 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                        background: colors.blueDim,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9, fontWeight: 700, color: colors.blue,
                      }}>
                        {(m.user2_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 12, color: colors.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {m.user2_name}
                      </span>
                    </div>
                  </div>

                  {/* Next badge for first in queue */}
                  {idx === 0 && (
                    <div style={{
                      padding: "2px 8px", borderRadius: 6, flexShrink: 0,
                      background: colors.orangeDim, color: colors.orange,
                      fontSize: 9, fontWeight: 700, letterSpacing: ".06em",
                      textTransform: "uppercase",
                    }}>
                      Next
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Round Complete banner */}
        {ev.round_complete && (
          <div style={{
            background: `linear-gradient(135deg, ${colors.greenDim}, ${colors.green}18)`,
            border: `1px solid ${colors.green}50`,
            borderRadius: 14, padding: "18px 22px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: colors.green, marginBottom: 3 }}>
                ✅ Round complete!
              </div>
              <div style={{ fontSize: 12, color: colors.fgMid }}>
                All {ev.total_seated} matches have had their table session. Start the next round to re-seat everyone.
              </div>
            </div>
            <button
              onClick={() => handleNextRound(ev.event_id)}
              disabled={clearing === ev.event_id}
              style={{
                padding: "10px 22px", borderRadius: 10, fontSize: "0.9rem", fontWeight: 700,
                border: "none", background: colors.green, color: "#fff",
                cursor: clearing === ev.event_id ? "not-allowed" : "pointer",
                opacity: clearing === ev.event_id ? 0.7 : 1, flexShrink: 0,
                boxShadow: `0 2px 12px ${colors.green}40`,
              }}>
              {clearing === ev.event_id ? "…" : "▶ Next Round"}
            </button>
          </div>
        )}

        {/* Seated (done) matches */}
        {ev.seated && ev.seated.length > 0 && (
          <div style={{
            background: isDark ? colors.card : colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 14, overflow: "hidden",
          }}>
            <div style={{
              padding: "12px 18px",
              borderBottom: `1px solid ${colors.border}`,
              display: "flex", alignItems: "center", gap: 8,
              background: isDark ? colors.bgDeep : colors.bg,
            }}>
              <span style={{ fontSize: "1rem" }}>✅</span>
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: colors.fg }}>Done this round</div>
              <div style={{
                marginLeft: "auto", padding: "2px 10px", borderRadius: 20,
                background: colors.greenDim, color: colors.green,
                border: `1px solid ${colors.green}40`,
                fontSize: 11, fontWeight: 700,
              }}>
                {ev.seated.length} completed
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {ev.seated.map((m, idx) => (
                <div key={m.match_id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "9px 18px",
                  borderBottom: idx < ev.seated.length - 1 ? `1px solid ${colors.border}` : "none",
                  background: idx % 2 === 0 ? "transparent" : (isDark ? colors.bgDeep + "50" : colors.bg + "50"),
                  opacity: restarting === m.match_id ? 0.5 : 0.75,
                }}>
                  <span style={{ fontSize: 13, flexShrink: 0 }}>✓</span>
                  <div style={{ fontSize: 10, color: colors.fgSub, flexShrink: 0, minWidth: 56 }}>
                    Match #{m.match_id}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                        background: colors.primaryDim, display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 8, fontWeight: 700, color: colors.accent,
                      }}>{(m.user1_name || "?").charAt(0).toUpperCase()}</div>
                      <span style={{ fontSize: 11, color: colors.fgMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.user1_name}</span>
                    </div>
                    <span style={{ color: colors.fgFaint, fontSize: 10 }}>×</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                        background: colors.blueDim, display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 8, fontWeight: 700, color: colors.blue,
                      }}>{(m.user2_name || "?").charAt(0).toUpperCase()}</div>
                      <span style={{ fontSize: 11, color: colors.fgMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.user2_name}</span>
                    </div>
                  </div>
                  {/* Restart button */}
                  <button
                    onClick={() => handleRestart(ev.event_id, m.match_id)}
                    disabled={restarting === m.match_id}
                    title="Re-queue this match"
                    style={{
                      flexShrink: 0, padding: "3px 9px", borderRadius: 7, fontSize: 10, fontWeight: 700,
                      border: `1px solid ${colors.border}`, background: "transparent",
                      color: colors.fgSub, cursor: "pointer", transition: "all .15s",
                      lineHeight: 1.4,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.color = colors.accent; e.currentTarget.style.background = colors.primaryDim; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.color = colors.fgSub; e.currentTarget.style.background = "transparent"; }}
                  >
                    {restarting === m.match_id ? "…" : "↩ Restart"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Loading / Error ──
  if (!data && !error) return (
    <div className="st-card" style={{ padding: 40, textAlign: "center", color: colors.fgSub }}>
      <Spinner /> Connecting…
    </div>
  );

  if (selectedEvent) return (
    <div className="st-card" style={{ padding: 20 }}>
      <EventDetail ev={selectedEvent} />
    </div>
  );

  const events = data?.events || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Toolbar */}
      <div className="st-card" style={{ padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: connected ? colors.green : colors.orange, flexShrink: 0 }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: colors.primary, letterSpacing: ".08em", textTransform: "uppercase" }}>
                🪑 Tables Live
              </div>
              <div style={{ fontSize: 10, color: connected ? colors.green : colors.orange, fontWeight: 600 }}>
                {connected ? "● Live" : "○ Reconnecting…"}
              </div>
            </div>
            <div style={{ fontSize: 11, color: colors.fgSub }}>
              {events.length} event{events.length !== 1 ? "s" : ""} · server push every 1s
            </div>
          </div>
          <input
            type="number"
            placeholder="Event ID…"
            value={filterEventId}
            onChange={e => { setFilterEventId(e.target.value); setSelectedEventId(null); }}
            style={{
              width: 140, padding: "7px 10px", borderRadius: 8,
              background: colors.bgDeep, border: `1px solid ${colors.border}`,
              color: colors.fg, fontSize: 12, fontFamily: "inherit", outline: "none",
            }}
          />
          <Btn sm variant="ghost" onClick={connect}>↻ Reconnect</Btn>
        </div>
        {error && <div style={{ fontSize: 11, color: colors.orange, marginTop: 8 }}>⚠ {error}</div>}
      </div>

      {events.length === 0 && (
        <div className="st-card" style={{ padding: 40, textAlign: "center", color: colors.fgSub }}>
          No published events with tables enabled.
        </div>
      )}

      {events.map(ev => {
        const fillPct = ev.total_capacity > 0 ? Math.round((ev.occupied / ev.total_capacity) * 100) : 0;
        return (
          <div key={ev.event_id} className="st-card"
            style={{ padding: "16px 20px", cursor: "pointer" }}
            onClick={() => setSelectedEventId(ev.event_id)}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: colors.fg }}>{ev.event_title}</div>
                <div style={{ fontSize: 11, color: colors.fgSub, marginTop: 2 }}>
                  ID {ev.event_id} · {ev.event_date || "—"} · {ev.number_of_tables}T × {ev.seats_per_table}S
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                {ev.queue_length > 0 && (
                  <div style={{ padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: colors.orangeDim, color: colors.orange, border: `1px solid ${colors.orange}40` }}>
                    {ev.queue_length} queued
                  </div>
                )}
                <span style={{ fontSize: 11, color: colors.fgSub }}>View →</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
              {[
                { label: "Occupied", value: ev.occupied,            color: colors.primary },
                { label: "Free",     value: ev.free,                color: colors.green   },
                { label: "Matches",  value: ev.total_active_matches, color: colors.cyan   },
                { label: "Cycles",   value: ev.total_cycles_done,   color: colors.fgMid  },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 9, color: colors.fgSub, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ height: 5, borderRadius: 3, background: colors.bgDeep, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ height: "100%", width: `${fillPct}%`, background: fillPct > 80 ? colors.orange : colors.primary, borderRadius: 3 }} />
            </div>

            {ev.slots.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {ev.slots.slice(0, 8).map(slot => {
                  const urgent = slot.seconds_left < 60;
                  return (
                    <div key={slot.slot_id} style={{
                      padding: "4px 9px", borderRadius: 7, fontSize: 10, fontWeight: 600,
                      background: urgent ? colors.orangeDim : colors.primaryDim,
                      color: urgent ? colors.orange : colors.accent,
                      border: `1px solid ${urgent ? colors.orange + "40" : colors.borderMid}`,
                      fontVariantNumeric: "tabular-nums",
                    }}>
                      T{slot.table_number} · {fmtSec(slot.seconds_left)}
                    </div>
                  );
                })}
                {ev.slots.length > 8 && <div style={{ fontSize: 10, color: colors.fgSub, padding: "4px 2px" }}>+{ev.slots.length - 8}</div>}
              </div>
            )}

            <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 10, color: colors.fgSub }}>
              <span>⏱ {ev.table_timer_seat ?? "—"} min/table</span>
              <span>🔄 {ev.table_timer_rotation ?? "—"} min rotation</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function StudioShell({ token, adminName, onLogout, adminRoute }) {
  const { colors, theme, toggleTheme } = useTheme();
  const { tab: urlTab } = useParams();
  const navigate = useNavigate();
  const validTabIds = FLAT_TABS.map(t => t.id);
  const tab = validTabIds.includes(urlTab) ? urlTab : "stats";

  // Если urlTab невалидный — редиректим на stats
  useEffect(() => {
    if (urlTab && !validTabIds.includes(urlTab)) {
      navigate(`/${adminRoute}/stats`, { replace: true });
    }
  }, [urlTab, adminRoute, navigate, validTabIds]);

  function setTab(newTab) {
    navigate(`/${adminRoute}/${newTab}`);
    logAction("tab_switch", "tab", null, newTab);
  }

  const [openUserIds, setOpenUserIds] = useState([]);
  const [openChatPairs, setOpenChatPairs] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const winMgr = useWindowMgr();

  function openUser(id) {
    if (!id) return;
    const uid = Number(id);
    setOpenUserIds(prev => prev.includes(uid) ? prev : [...prev, uid]);
    winMgr.bringToFront(`user_${uid}`);
    logAction("open_user", "user", uid);
  }
  function closeUser(id) {
    const uid = Number(id);
    setOpenUserIds(prev => prev.filter(x => x !== uid));
  }
  function openChat(u1, u2) {
    if (!u1 || !u2) return;
    const a = Number(u1), b = Number(u2);
    const key = `${Math.min(a, b)}_${Math.max(a, b)}`;
    setOpenChatPairs(prev => prev.some(p => p.key === key) ? prev : [...prev, { u1: a, u2: b, key }]);
    winMgr.bringToFront(`chat_${key}`);
  }
  function closeChat(key) {
    setOpenChatPairs(prev => prev.filter(p => p.key !== key));
  }

  // Fire-and-forget audit log helper
  const logAction = useCallback((action, entity, entityId, notes) => {
    api("/activity", { method: "POST", token, body: {
      action, entity: entity || null, entity_id: entityId || null, notes: notes || null,
    }}).catch(() => {});
  }, [token]);

  const [searchOpen, setSearchOpen] = useState(false);

  // ── Глобальные горячие клавиши ──────────────────────────────────────────
  useEffect(() => {
    function onKey(e) {
      // Cmd/Ctrl+K — глобальный поиск
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(v => !v);
      }
      // Escape — закрыть поиск
      if (e.key === "Escape") setSearchOpen(false);
      // Цифровые клавиши 1-9 для переключения вкладок (без модификаторов)
      if (!e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        const n = parseInt(e.key, 10);
        if (n >= 1 && n <= TABS.length) {
          const target = e.target;
          const isInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
          // Цифры 1..N — только по верхним пунктам левого меню (родители + одиночные).
          if (!isInput) { e.preventDefault(); setTab(TABS[n - 1].id); }
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const sidebarW = sidebarCollapsed ? 72 : 210;

  // Neumorphic shadow helpers
  const isDark = theme === "dark";
  const neuShadow = isDark
    ? "8px 8px 20px rgba(0,0,0,0.55), -4px -4px 12px rgba(255,255,255,0.03)"
    : "8px 8px 20px rgba(163,177,198,0.5), -6px -6px 16px rgba(255,255,255,0.85)";
  const neuShadowSm = isDark
    ? "4px 4px 10px rgba(0,0,0,0.5), -2px -2px 6px rgba(255,255,255,0.03)"
    : "4px 4px 10px rgba(163,177,198,0.45), -3px -3px 8px rgba(255,255,255,0.8)";
  const neuInset = isDark
    ? "inset 3px 3px 8px rgba(0,0,0,0.5), inset -2px -2px 6px rgba(255,255,255,0.03)"
    : "inset 3px 3px 8px rgba(163,177,198,0.35), inset -3px -3px 8px rgba(255,255,255,0.75)";
  const cardBg = isDark ? colors.card : colors.card;

  // Mobile: show overflow nav drawer
  const MOBILE_NAV_VISIBLE = 6; // show first 6 tabs in bottom bar
  const mobileNavTabs = TABS.slice(0, MOBILE_NAV_VISIBLE);
  const mobileMoreTabs = TABS.slice(MOBILE_NAV_VISIBLE);
  const moreIsActive = mobileMoreTabs.some(t => t.id === tab);

  return (
    /* ── ROOT: padded canvas ── */
    <div className="st" style={{
      minHeight: "100vh",
      padding: isMobile ? "8px 8px 0" : "14px",
      display: "flex",
      flexDirection: "column",
      gap: isMobile ? 8 : 12,
      boxSizing: "border-box",
    }}>

      {/* ══════════════════════════════════════
          TOP HEADER CARD
      ══════════════════════════════════════ */}
      <div style={{
        position: "sticky", top: isMobile ? 8 : 14, zIndex: 100,
        background: cardBg,
        borderRadius: 20,
        boxShadow: neuShadow,
        display: "flex", alignItems: "center",
        height: isMobile ? 54 : 62, flexShrink: 0,
        padding: isMobile ? "0 6px 0 6px" : "0 8px 0 6px",
        gap: isMobile ? 6 : 8,
        overflow: "hidden",
      }}>
        {/* Logo */}
        <div style={{ display:"flex", alignItems:"center", flexShrink:0, marginRight: isMobile ? 0 : 4 }}>
          <img src={logoImg} alt="Logo" style={{ height: isMobile ? 28 : 34, width:"auto", display:"block", paddingLeft: isMobile ? 10 : 18, paddingRight: isMobile ? 6 : 12 }} />
        </div>

        {/* Collapse toggle — desktop only */}
        {!isMobile && (
          <button
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? "Expand" : "Collapse"}
            style={{
              width: 34, height: 34, borderRadius: 10,
              border: "none", cursor: "pointer", fontFamily: "inherit",
              background: isDark ? colors.bgDeep : colors.bg,
              boxShadow: neuShadowSm,
              color: colors.fgSub, fontSize: 12,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, transition: "all .15s",
            }}
          >
            {sidebarCollapsed ? "▶" : "◀"}
          </button>
        )}

        {/* Tab breadcrumb */}
        <div style={{
          flex: 1, display: "flex", alignItems: "center", gap: isMobile ? 6 : 10, padding: "0 6px", minWidth: 0,
        }}>
          <span style={{
            width: 20, height: 20, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: colors.primary,
          }}>
            <TabIcon id={tab} size={18} />
          </span>
          <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: colors.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {FLAT_TABS.find(t => t.id === tab)?.label}
          </span>
        </div>

        {/* Header stats — desktop only */}
        {!isMobile && <HeaderStats token={token} />}

        {/* Уведомления — звонок виден на десктопе и мобиле */}
        <NotificationBell token={token} onNavigateTab={setTab} />

        {/* Global search button */}
        <button
          onClick={() => setSearchOpen(true)}
          title="Search users (Cmd+K)"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: isMobile ? "0" : "0 10px",
            width: isMobile ? 32 : "auto",
            height: 32, borderRadius: 9,
            border: `1px solid ${colors.border}`,
            background: isDark ? colors.bgDeep : colors.bg,
            boxShadow: neuShadowSm,
            cursor: "pointer", color: colors.fgSub,
            fontFamily: "inherit", fontSize: 12, flexShrink: 0,
            justifyContent: "center",
            transition: "all .15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = colors.fg; e.currentTarget.style.borderColor = colors.borderMid; }}
          onMouseLeave={e => { e.currentTarget.style.color = colors.fgSub; e.currentTarget.style.borderColor = colors.border; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          {!isMobile && <span>Search</span>}
          {!isMobile && <kbd style={{ fontSize: 9, border: `1px solid ${colors.border}`, borderRadius: 3, padding: "1px 4px", color: colors.fgFaint }}>⌘K</kbd>}
        </button>

        {/* Admin controls — mobile only (desktop moved to sidebar bottom) */}
        {isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              background: isDark ? colors.bgDeep : colors.bg,
              borderRadius: 12, padding: "5px 8px",
              boxShadow: neuInset,
            }}>
              <div className="st-pulse" style={{ width: 7, height: 7, borderRadius: "50%", background: colors.green }} />
            </div>
            <button onClick={toggleTheme} style={{
              width: 32, height: 32, borderRadius: 10, border: "none", cursor: "pointer",
              background: isDark ? colors.bgDeep : colors.bg,
              boxShadow: neuShadowSm, color: colors.fgSub, fontSize: 15,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {theme === "dark" ? "☀" : "☾"}
            </button>
            <button onClick={onLogout} style={{
              width: 32, height: 32, borderRadius: 10,
              border: `1px solid ${colors.red}44`,
              cursor: "pointer",
              background: "transparent",
              color: colors.red,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all .15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = colors.redDim; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════
          BODY ROW: SIDEBAR CARD + CONTENT CARD
      ══════════════════════════════════════ */}
      <div style={{ display: "flex", flex: 1, gap: isMobile ? 0 : 12, minHeight: 0, alignItems: "flex-start" }}>

        {/* ── LEFT SIDEBAR CARD — desktop only ── */}
        {!isMobile && (
          <aside style={{
            width: sidebarW, flexShrink: 0,
            background: cardBg,
            borderRadius: 20,
            boxShadow: neuShadow,
            display: "flex", flexDirection: "column",
            padding: "14px 8px 160px",
            transition: "width .22s cubic-bezier(.4,0,.2,1)",
            overflowX: "hidden",
            overflowY: "auto",
            position: "sticky", top: 90,
            maxHeight: "calc(100vh - 104px)",
          }}>
            {/* Nav items (с иерархией: Users / Events раскрываются по клику) */}
            <NavItems
              tab={tab}
              setTab={setTab}
              colors={colors}
              neuInset={neuInset}
              sidebarCollapsed={sidebarCollapsed}
            />

            <div style={{ flex: 1 }} />
          </aside>
        )}

        {/* ── FIXED BOTTOM-LEFT ACCOUNT BLOCK ── */}
        {!isMobile && (
          <div style={{
            position: "fixed",
            bottom: 14,
            left: 14,
            width: sidebarW - 16,
            zIndex: 60,
            transition: "width .22s cubic-bezier(.4,0,.2,1)",
          }}>
            <div style={{
              background: cardBg,
              borderRadius: 18,
              boxShadow: neuShadow,
              padding: sidebarCollapsed ? "10px 8px" : "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              border: `1px solid ${colors.border}`,
            }}>
              {/* Account row */}
              {!sidebarCollapsed && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 9,
                }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%",
                    background: colors.gradient, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700, color: "#fff",
                  }}>
                    {(adminName || "A").charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors.fg,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {adminName || "Admin"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div className="st-pulse" style={{ width: 5, height: 5, borderRadius: "50%", background: colors.green }} />
                      <span style={{ fontSize: 10, color: colors.green, fontWeight: 600 }}>Online</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Collapsed: just avatar */}
              {sidebarCollapsed && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%",
                    background: colors.gradient,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700, color: "#fff",
                  }}>
                    {(adminName || "A").charAt(0).toUpperCase()}
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div style={{
                display: "flex", gap: 6,
                justifyContent: sidebarCollapsed ? "center" : "stretch",
              }}>
                <button onClick={toggleTheme}
                  title={isDark ? "Light mode" : "Dark mode"}
                  style={{
                    flex: sidebarCollapsed ? "0 0 auto" : 1,
                    height: 30, borderRadius: 10,
                    border: "none", cursor: "pointer",
                    fontFamily: "inherit", fontWeight: 600, fontSize: 11,
                    background: isDark ? colors.bgDeep : colors.bg,
                    boxShadow: neuShadowSm,
                    color: colors.fgSub,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                    transition: "all .15s",
                    minWidth: sidebarCollapsed ? 34 : undefined,
                    padding: sidebarCollapsed ? 0 : "0 10px",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = colors.fg; }}
                  onMouseLeave={e => { e.currentTarget.style.color = colors.fgSub; }}
                >
                  <span style={{ fontSize: 13 }}>{isDark ? "☀" : "☾"}</span>
                  {!sidebarCollapsed && <span>{isDark ? "Light" : "Dark"}</span>}
                </button>

                <button onClick={onLogout}
                  title="Sign out"
                  style={{
                    flex: sidebarCollapsed ? "0 0 auto" : 1,
                    height: 30, borderRadius: 10,
                    border: `1px solid ${colors.red}33`,
                    cursor: "pointer",
                    fontFamily: "inherit", fontWeight: 600, fontSize: 11,
                    background: "transparent",
                    color: colors.red,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                    transition: "all .15s",
                    minWidth: sidebarCollapsed ? 34 : undefined,
                    padding: sidebarCollapsed ? 0 : "0 10px",
                    letterSpacing: ".02em",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = colors.redDim;
                    e.currentTarget.style.borderColor = colors.red + "66";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = colors.red + "33";
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  {!sidebarCollapsed && <span>Exit</span>}
                </button>
              </div>

              {/* Hotkey hints */}
              {!sidebarCollapsed && (
                <div style={{
                  marginTop: 8, padding: "8px 10px",
                  background: isDark ? colors.bgDeep : colors.bg,
                  borderRadius: 8, border: `1px solid ${colors.border}`,
                }}>
                  {[
                    ["⌘K", "Search"],
                    ["1–9", "Tabs"],
                    ["ESC", "Close"],
                  ].map(([k, label]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 10, color: colors.fgSub }}>{label}</span>
                      <kbd style={{
                        fontSize: 9, fontFamily: "inherit", fontWeight: 700,
                        color: colors.fgSub, background: isDark ? colors.card : colors.cardMid,
                        border: `1px solid ${colors.border}`, borderRadius: 4, padding: "1px 5px",
                      }}>{k}</kbd>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── MAIN: transparent area, each inner block is its own card ── */}
        <main style={{
          flex: 1, minWidth: 0,
          background: "transparent",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minHeight: isMobile ? "auto" : "calc(100vh - 104px)",
          overflowY: "auto",
        }}>
          <style>{`
            .st-main-area .st-card {
              border: none !important;
              border-radius: ${isMobile ? "14px" : "18px"} !important;
              box-shadow: ${isDark
                ? "8px 8px 20px rgba(0,0,0,0.55), -4px -4px 12px rgba(255,255,255,0.03)"
                : "8px 8px 20px rgba(163,177,198,0.5), -6px -6px 16px rgba(255,255,255,0.85)"} !important;
              margin-bottom: 4px;
            }
            .st-main-area .st-card:hover {
              box-shadow: ${isDark
                ? "10px 10px 26px rgba(0,0,0,0.6), -4px -4px 14px rgba(255,255,255,0.04)"
                : "10px 10px 26px rgba(163,177,198,0.55), -6px -6px 18px rgba(255,255,255,0.9)"} !important;
            }
          `}</style>
          <div className="st-main-area" style={{
            display: "flex", flexDirection: "column", gap: 12,
            padding: isMobile ? "8px 0 16px" : "24px",
          }}>
            {tab === "stats"       && <TabStats      token={token} onNavigateTab={setTab} />}
            {/* HUB-вкладки: Users / Events / Matches показывают свои родственные
                разделы как внутренние pill-табы, оставляя их же как самостоятельные
                пункты в левом меню (cards/chats/wallets/tables-live). */}
            {tab === "users"       && <TabUsersHub   token={token} onSelectUser={openUser} onOpenChat={openChat} />}
            {tab === "cards"       && <TabCards      token={token} onSelectUser={openUser} />}
            {tab === "user-tree"   && <TabUserTree   token={token} onSelectUser={openUser} />}
            {tab === "matches"     && <TabMatchesHub token={token} onSelectUser={openUser} onOpenChat={openChat} />}
            {tab === "chats"       && <TabChats      token={token} onSelectUser={openUser} onOpenChat={openChat} />}
            {tab === "events-all"  && <TabEventsHub  token={token} onSelectUser={openUser} onOpenChat={openChat} />}
            {tab === "tables-live" && <TabTablesLive token={token} />}
            {tab === "wallets"     && <TabWallets    token={token} />}
            {tab === "payments"    && <TabPayments   token={token} />}
            {tab === "safety"      && <TabSafety     token={token} />}
            {tab === "feedback"    && <TabFeedback   token={token} />}
            {/* Прямой переход на Event Comments — копия того, что показывает
                подвкладка Events → Comments. Удобно, когда модер пришёл по
                URL /event-comments из ссылки или закладки. */}
            {tab === "event-comments" && <ContentComments token={token} />}
            {tab === "database"    && <TabDatabase   token={token} />}
            {tab === "access-logs" && <TabAccessLogs token={token} />}
            {tab === "settings"    && <TabSettings   token={token} />}
          </div>
        </main>
      </div>

      {/* ══════════════════════════════════════
          MOBILE BOTTOM NAV BAR
      ══════════════════════════════════════ */}
      <nav className="st-mobile-nav">
        {mobileNavTabs.map(t => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              className={`st-mobile-nav-btn${isActive ? " active" : ""}`}
              onClick={() => { setTab(t.id); setMobileMenuOpen(false); }}
            >
              <span className="st-mobile-nav-icon" style={{
                color: isActive ? colors.primary : colors.fgSub,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <TabIcon id={t.id} size={20} />
              </span>
              <span className="st-mobile-nav-label">{t.label}</span>
            </button>
          );
        })}
        {/* "More" button if extra tabs exist */}
        {mobileMoreTabs.length > 0 && (
          <button
            className={`st-mobile-nav-btn${moreIsActive || mobileMenuOpen ? " active" : ""}`}
            onClick={() => setMobileMenuOpen(v => !v)}
          >
            <span className="st-mobile-nav-icon" style={{
              color: moreIsActive || mobileMenuOpen ? colors.primary : colors.fgSub,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <IcMore size={20} strokeWidth={1.75} />
            </span>
            <span className="st-mobile-nav-label">More</span>
          </button>
        )}
      </nav>

      {/* ── MOBILE MORE DRAWER ── */}
      {isMobile && mobileMenuOpen && (
        <>
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: "fixed", inset: 0, zIndex: 140,
              background: "rgba(5,5,14,.5)", backdropFilter: "blur(4px)",
            }}
          />
          <div style={{
            position: "fixed", bottom: 62, left: 0, right: 0, zIndex: 145,
            background: cardBg,
            borderRadius: "20px 20px 0 0",
            boxShadow: neuShadow,
            padding: "16px 12px 12px",
            display: "flex", flexDirection: "column", gap: 4,
            animation: "stFade .2s ease both",
          }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase",
              color: colors.fgFaint, marginBottom: 8, paddingLeft: 8 }}>More Sections</div>
            {mobileMoreTabs.map(t => {
              const isActive = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); setMobileMenuOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 14px", borderRadius: 13, border: "none",
                    cursor: "pointer", fontFamily: "inherit",
                    fontSize: 14, fontWeight: isActive ? 700 : 500,
                    background: isActive ? colors.primaryDim : "transparent",
                    boxShadow: isActive ? neuInset : "none",
                    color: isActive ? colors.accent : colors.fgMid,
                    transition: "all .15s", textAlign: "left",
                  }}
                >
                  <span style={{
                    width: 22, height: 22, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: isActive ? colors.primary : colors.fgSub,
                  }}>
                    <TabIcon id={t.id} size={18} />
                  </span>
                  {t.label}
                </button>
              );
            })}
            <div style={{ textAlign: "center", fontSize: 10, color: colors.fgFaint, fontWeight: 700,
              letterSpacing: ".08em", textTransform: "uppercase", marginTop: 8, paddingBottom: 4 }}>
              DolbaibikiPanel v0.1
            </div>
          </div>
        </>
      )}

      {openUserIds.map(uid => (
        <UserDrawer
          key={`user_${uid}`}
          userId={uid}
          token={token}
          onClose={() => closeUser(uid)}
          onSelectUser={openUser}
          onOpenChat={openChat}
        />
      ))}
      {openChatPairs.map(pair => (
        <ChatViewer
          key={`chat_${pair.key}`}
          uid1={pair.u1}
          uid2={pair.u2}
          token={token}
          onClose={() => closeChat(pair.key)}
          onSelectUser={openUser}
        />
      ))}

      {/* Global search overlay */}
      {searchOpen && (
        <GlobalSearch
          token={token}
          onSelectUser={id => { setSearchOpen(false); openUser(id); }}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {/* Session expiry warning */}
      <SessionTimer onLogout={onLogout} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  ROOT EXPORT
//  Авторизация вынесена в ModeratorLoginPage.jsx.
//  Если токен отсутствует — App.js перенаправляет на /{adminRoute}/login.
//  ModeratorPanel рендерит только StudioShell для уже авторизованных.
// ════════════════════════════════════════════════════════════════════════════
export default function ModeratorPanel({ adminRoute }) {
  const [token, setToken] = useState(() => sessionStorage.getItem("studio_token") || "");
  const [name, setName]   = useState(() => sessionStorage.getItem("studio_name")  || "");
  const [theme, setTheme] = useState(() => localStorage.getItem("studio_theme")   || "dark");
  const colors = theme === "dark" ? D : T;

  useEffect(() => { injectCSS(colors); }, [theme, colors]);
  useEffect(() => { localStorage.setItem("studio_theme", theme); }, [theme]);

  function handleLogout() {
    sessionStorage.removeItem("studio_token");
    sessionStorage.removeItem("studio_name");
    sessionStorage.removeItem("studio_admin_route");
    setToken(""); setName("");
    window.location.href = "/";
  }
  function toggleTheme() {
    setTheme(t => t === "dark" ? "light" : "dark");
  }

  // Страховка: если токен пропал — App.js через ProtectedModRoute перенаправит на /login.
  if (!token) return null;

  return (
    <ThemeContext.Provider value={{ theme, colors, toggleTheme }}>
      <ToastProvider>
        <WindowMgrProvider>
          <ConfirmProvider>
            <FeedbackBadgeProvider token={token}>
              <StudioShell token={token} adminName={name} onLogout={handleLogout} adminRoute={adminRoute} />
            </FeedbackBadgeProvider>
          </ConfirmProvider>
          <MinimizedWindowsTray />
        </WindowMgrProvider>
      </ToastProvider>
    </ThemeContext.Provider>
  );

}
